import { safeRoundHours, MaxTimeModifiers, DEFAULT_PROJECT_TOTALS_ORDER, Totals, DEFAULT_PROJECT_TABLES_ORDER, Tables, DEFAULT_PROPOSAL_COLUMNS_ORDER, SummaryColumns, DescriptionModes, TimeModifiers, SectionFormats, PriceFormats, BreakdownTableDepths, TimelineMode, DEFAULT_BREAKDOWN_COLUMNS_ORDER, DEFAULT_BREAKDOWN_COLUMNS_WIDTHS, BreakdownColumns, DEFAULT_MTA_VALUE, DEFAULT_CURRENCY_KEY, MinAvgMaxKeys, cleanObject, DEFAULT_MIN_VALUE_COEFFICIENT, DEFAULT_MAX_VALUE_COEFFICIENT, WorkTypeValueState, MIN_AVG_MAX_KEYS, safeRoundHoursV2, unzip, zip, CommentTypes, MAX_UNDO_HISTORY_SIZE, PriceModifierModes, PriceModifiers, roundFloat, Rounding, MONTH_WEEKS, TaskTypes, simplifyTextString, isEmptyHtml, BT_MERGED_VALUE, MIN_MAX_KEYS, AVG_KEYS, cleanHtmlContent, MAX_DESCRIPTION_COLUMNS, getContrastingFontColor, safeRoundRate, MAX_TIMELINE_LENGTH, getCurrency, getExchangeRate, RoundIntervals, hashCode, parsePrice } from '@repo/utils';
import { format, startOfWeek, isSameMonth, startOfMonth, startOfDay, isWeekend, addDays, endOfWeek, endOfMonth, isEqual, isBefore, differenceInCalendarDays, eachWeekendOfInterval, getDaysInMonth, eachWeekendOfMonth, addMonths, isAfter, parse, differenceInCalendarMonths, isValid } from 'date-fns';
import { getParent, applyPatch, resolvePath, destroy, createActionTrackingMiddleware2, recordPatches, isActionContextThisOrChildOf, types, decorate, flow, getRoot, addDisposer, addMiddleware, getParentOfType, cast, isRoot, getSnapshot, isAlive, getPathParts, getPath, clone, detach, applySnapshot } from 'mobx-state-tree';
import { isArray, intersection, uniqBy, omit, findLastIndex, isObject, without, pick, uniq, union, merge, mergeWith, cloneDeep, difference, concat } from 'lodash';
import Delta from 'quill-delta';
import { reaction } from 'mobx';

const generateBufferId = ()=>`ov${new Date().getTime()}`;

const generateEntryId = (index = 0)=>`e${index.toString()}${new Date().getTime()}`;

const generateLabelId = (index = 0)=>`l${index.toString()}${new Date().getTime()}`;

const generatePriceModifierId = ()=>`pm${new Date().getTime()}`;

const generateResourceId = (index = 0)=>`r${index.toString()}${new Date().getTime()}`;

const generateSectionId = (index = 0)=>`s${index}${new Date().getTime()}`;

const generateTaskDescriptionId = (index = 0)=>`description_${index}`;

const generateWorkTypeId = (index = 0)=>`wT${index.toString()}${new Date().getTime()}`;

const getExpansionDateKey = (value, month)=>format(month ? getFirstWorkdayOfMonth(value) : getFirstWorkdayOfWeek(value), "yyyy-MM-dd");
const getDateKey = (value)=>format(value, "yyyy-MM-dd");

const getNewDistributionLength = (newEndDate, startDate, endDate, providedProportion, parentStartDate, parentEndDate)=>{
    const originalStartDate = startDate;
    const originalEndDate = endDate ?? originalStartDate;
    const oldLength = countWorkdaysInInterval(originalStartDate, originalEndDate);
    const useProvidedProportion = providedProportion !== undefined && parentStartDate !== undefined && parentEndDate !== undefined && (originalEndDate.getTime() < parentEndDate.getTime() || originalStartDate.getTime() === parentStartDate.getTime() && originalEndDate.getTime() === parentEndDate.getTime());
    const distributionLength = useProvidedProportion ? Math.round(providedProportion * oldLength) : countWorkdaysInInterval(originalStartDate, newEndDate);
    if (!distributionLength) throw new RangeError("Timeline end date must be on or after the start date");
    return {
        distributionLength,
        newProportion: useProvidedProportion ? providedProportion : distributionLength / oldLength
    };
};

const validatePositiveFinite = (value, label)=>{
    if (!Number.isFinite(value) || value <= 0) {
        throw new RangeError(`Timeline ${label} must be a positive finite number`);
    }
};
const validateValue = (value, label)=>{
    if (!Number.isFinite(value) || value < 0) {
        throw new RangeError(`Timeline ${label} must be a non-negative finite number`);
    }
};
const validateTimelineEndDate = (endDate, startDate)=>{
    if (endDate < startDate) {
        throw new RangeError("Timeline end date cannot be earlier than start date");
    }
};

const roundMinutes = (value)=>safeRoundHours(value, MaxTimeModifiers.HOUR);
const redistributeWorkload = (source, maxMinutesPerDay, workload, apparentMinutesPerDay)=>{
    if (workload === undefined) {
        throw new RangeError("Timeline workload is required");
    }
    if (workload === 0) return [];
    validateValue(workload, "workload");
    validatePositiveFinite(maxMinutesPerDay, "daily allocation");
    if (apparentMinutesPerDay !== undefined) {
        validatePositiveFinite(apparentMinutesPerDay, "previous daily allocation");
    }
    const originalAllocation = roundMinutes(maxMinutesPerDay);
    const apparentAllocation = apparentMinutesPerDay ? roundMinutes(apparentMinutesPerDay) : undefined;
    const allocation = originalAllocation;
    const result = source.map((value)=>({
            ...value,
            value: value.ov ? apparentAllocation !== undefined ? roundMinutes(value.value * originalAllocation / apparentAllocation) : value.value : 0
        }));
    let remaining = roundMinutes(workload - result.reduce((total, value)=>total + value.value, 0));
    if (remaining < 0) {
        for(let index = result.length - 1; index >= 0 && remaining < 0; index -= 1){
            if (!result[index].ov) continue;
            const reduction = Math.min(result[index].value, -remaining);
            result[index].value -= reduction;
            remaining += reduction;
            if (result[index].value === 0) delete result[index].ov;
        }
    }
    if (remaining < 0) throw new RangeError("Timeline overridden workload exceeds workload");
    const lastOverriddenValueIndex = result.findLastIndex(({ ov })=>ov);
    let index = -1;
    while(remaining > 0){
        index++;
        if (!result[index]) result[index] = {
            value: 0
        };
        if (result[index].ov || remaining <= 0) continue;
        const allocated = roundMinutes(Math.min(remaining, allocation));
        if (allocated <= 0) {
            throw new RangeError("Timeline workload cannot be represented");
        }
        result[index].value = allocated;
        remaining = roundMinutes(remaining - allocated);
    }
    const lastIndex = Math.max(index, lastOverriddenValueIndex);
    if (result.length > lastIndex + 1) {
        result.splice(lastIndex + 1);
    }
    return result;
};

const nextWorkDay = (date)=>{
    let result = startOfDay(date);
    while(isWeekend(result))result = addDays(result, 1);
    return result;
};
const prevWorkDay = (date)=>{
    let result = startOfDay(date);
    while(isWeekend(result))result = addDays(result, -1);
    return result;
};
const addWorkDays = (date, count)=>{
    let result = nextWorkDay(date);
    let remaining = Math.abs(count);
    const sub = count < 0;
    while(remaining > 0){
        result = addDays(result, sub ? -1 : 1);
        if (!isWeekend(result)) remaining -= 1;
    }
    return result;
};
const getFirstWorkdayOfWeek = (date)=>{
    const potentialStartDay = nextWorkDay(startOfWeek(date, {
        weekStartsOn: 1
    }));
    return isSameMonth(potentialStartDay, date) ? potentialStartDay : getFirstWorkdayOfMonth(date);
};
const getLastWorkdayOfWeek = (date)=>{
    const potentialEndDay = prevWorkDay(endOfWeek(date, {
        weekStartsOn: 1
    }));
    return isSameMonth(potentialEndDay, date) ? potentialEndDay : getLastWorkdayOfMonth(date);
};
const getFirstWorkdayOfMonth = (date)=>nextWorkDay(startOfMonth(date));
const getLastWorkdayOfMonth = (date)=>prevWorkDay(endOfMonth(date));
const differenceInWorkDays = (laterDate, earlierDate)=>{
    if (isEqual(laterDate, earlierDate)) return 0;
    return isBefore(laterDate, earlierDate) ? -differenceInWorkDays(earlierDate, laterDate) : differenceInCalendarDays(laterDate, earlierDate) - eachWeekendOfInterval({
        start: earlierDate,
        end: laterDate
    }).length;
};
const countWorkdaysInInterval = (startDate, endDate)=>isBefore(endDate, startDate) ? 0 : differenceInWorkDays(endDate, startDate) + 1;
const workDaysInMonth = (date)=>getDaysInMonth(date) - eachWeekendOfMonth(date).length;

const TIMELINE_MAX_MONTHS = 60;
const generateTimelineCalendar = (startDate, monthCount)=>{
    if (!Number.isInteger(monthCount) || monthCount < 1 || monthCount > TIMELINE_MAX_MONTHS) {
        throw new RangeError(`Timeline month count must be between 1 and ${TIMELINE_MAX_MONTHS}`);
    }
    const firstMonth = startOfMonth(startDate);
    return Array.from({
        length: monthCount
    }, (_, monthIndex)=>{
        const monthStart = addMonths(firstMonth, monthIndex);
        const monthEnd = endOfMonth(monthStart);
        const isFirstMonth = monthIndex === 0;
        const monthDayStart = isFirstMonth ? nextWorkDay(startDate) : getFirstWorkdayOfMonth(monthStart);
        const firstWeek = startOfWeek(isFirstMonth ? startDate : monthStart, {
            weekStartsOn: 1
        });
        const weeks = [];
        for(let weekStart = firstWeek; !isAfter(weekStart, monthEnd); weekStart = getFirstWorkdayOfWeek(addDays(weekStart, 7))){
            const days = Array.from({
                length: 5
            }, (_, dayIndex)=>addDays(weekStart, dayIndex)).filter((day)=>!isBefore(day, monthDayStart) && !isAfter(day, monthEnd));
            if (days.length) weeks.push({
                startDate: days[0],
                days
            });
        }
        return {
            startDate: monthDayStart,
            weeks
        };
    });
};

const DEFAULT_TIMELINE_COLOR = "#4098F4";
const DEFAULT_TIMELINE_FONT_COLOR = "#ffffff";
const resolveTimelineColor = (ownColor, summaryColor, parentColor)=>ownColor || summaryColor || parentColor || DEFAULT_TIMELINE_COLOR;

const getTimelineRoot = (node)=>{
    for(let depth = 1; depth <= 8; depth++){
        try {
            const parent = getParent(node, depth);
            if (parent.workloadKey) return parent;
        } catch  {
            continue;
        }
    }
    throw new Error("Timeline root was not found");
};

const getTimelineEndDate = (startDate, distributionLength)=>distributionLength > 0 ? addWorkDays(startDate, distributionLength - 1) : undefined;
const getDistributionPeriodIndices = (startDay, period, periodStart)=>{
    const timelineStart = nextWorkDay(startDay);
    const periodStartDate = period === "day" ? startOfDay(periodStart) : period === "week" ? getFirstWorkdayOfWeek(periodStart) : getFirstWorkdayOfMonth(periodStart);
    if (period === "day" && isWeekend(periodStartDate)) return [];
    const normalizedPeriodEnd = period === "day" ? periodStartDate : period === "week" ? getLastWorkdayOfWeek(periodStart) : getLastWorkdayOfMonth(periodStart);
    if (normalizedPeriodEnd < timelineStart) return [];
    const normalizedPeriodStart = isBefore(periodStartDate, timelineStart) ? timelineStart : periodStartDate;
    const firstIndex = differenceInWorkDays(normalizedPeriodStart, timelineStart);
    const periodLength = countWorkdaysInInterval(normalizedPeriodStart, normalizedPeriodEnd);
    return Array.from({
        length: periodLength
    }, (_, index)=>firstIndex + index);
};
const mergeChunkedDistributions = (distributions, startDate)=>{
    const dailyValues = new Map();
    distributions.flat().forEach((month)=>month.weeks.forEach((week)=>week.distribution.forEach((day, index)=>{
                const date = day.startDate ?? addWorkDays(week.startDate, index);
                const key = startOfDay(date).getTime();
                const value = dailyValues.get(key) ?? {
                    value: 0
                };
                value.value += day.value;
                if (day.ov === true) value.ov = true;
                if (day.empty !== true) delete value.empty;
                else if (!dailyValues.has(key)) value.empty = true;
                dailyValues.set(key, value);
            })));
    const childDates = [
        ...dailyValues.keys()
    ].sort((left, right)=>left - right);
    if (!childDates.length) return [];
    const sortedDates = [];
    childDates.forEach((timestamp, index)=>{
        if (index > 0) {
            let missingDate = addWorkDays(new Date(childDates[index - 1]), 1);
            while(missingDate.getTime() < timestamp){
                const missingTimestamp = missingDate.getTime();
                dailyValues.set(missingTimestamp, {
                    value: 0
                });
                sortedDates.push(missingTimestamp);
                missingDate = addWorkDays(missingDate, 1);
            }
        }
        sortedDates.push(timestamp);
    });
    const firstChildDate = sortedDates[0];
    const prevDates = [];
    if (startDate && Number.isFinite(firstChildDate) && startDate.getTime() < firstChildDate) {
        let missingDate = nextWorkDay(startDate);
        while(missingDate.getTime() < firstChildDate){
            const t = missingDate.getTime();
            dailyValues.set(t, {
                value: 0,
                empty: true
            });
            prevDates.push(t);
            missingDate = addWorkDays(missingDate, 1);
        }
    }
    sortedDates.splice(0, 0, ...prevDates);
    const months = new Map();
    sortedDates.forEach((timestamp)=>{
        const date = new Date(timestamp);
        const dailyValue = dailyValues.get(timestamp);
        const monthStart = getFirstWorkdayOfMonth(date);
        const monthKey = getDateKey(monthStart);
        let month = months.get(monthKey);
        if (!month) {
            month = {
                startDate: monthStart,
                endDate: date,
                value: 0,
                ov: false,
                empty: true,
                weeks: []
            };
            months.set(monthKey, month);
        }
        month.endDate = date;
        month.value += dailyValue.value;
        if (dailyValue.empty !== true) delete month.empty;
        month.ov ||= dailyValue.ov === true;
        const weekStart = getFirstWorkdayOfWeek(date);
        const weekKey = getDateKey(weekStart);
        let week = month.weeks.find((candidate)=>getDateKey(candidate.startDate) === weekKey);
        if (!week) {
            week = {
                startDate: weekStart,
                endDate: date,
                value: 0,
                ov: false,
                empty: true,
                distribution: []
            };
            month.weeks.push(week);
        }
        week.endDate = date;
        week.value += dailyValue.value;
        week.ov ||= dailyValue.ov === true;
        if (dailyValue.empty !== true) delete week.empty;
        week.distribution = [
            ...week.distribution,
            {
                ...dailyValue,
                startDate: date
            }
        ];
    });
    return [
        ...months.values()
    ].sort((left, right)=>left.startDate.getTime() - right.startDate.getTime()).map((month)=>({
            ...month,
            weeks: month.weeks.sort((left, right)=>left.startDate.getTime() - right.startDate.getTime())
        }));
};
const mergeDistributions = (distributions, startDate, percent)=>{
    const dailyValues = new Map();
    distributions.forEach(({ startDay, distribution })=>{
        distribution.forEach(({ value }, index)=>{
            const date = getTimelineEndDate(startDay, index + 1);
            if (!date) return;
            const timestamp = startOfDay(date).getTime();
            dailyValues.set(timestamp, (dailyValues.get(timestamp) || 0) + value);
        });
    });
    const childDates = [
        ...dailyValues.keys()
    ].sort((left, right)=>left - right);
    if (!childDates.length) return [];
    const sortedDates = [];
    childDates.forEach((timestamp, index)=>{
        if (index > 0) {
            let missingDate = addWorkDays(new Date(childDates[index - 1]), 1);
            while(missingDate.getTime() < timestamp){
                const missingTimestamp = missingDate.getTime();
                dailyValues.set(missingTimestamp, 0);
                sortedDates.push(missingTimestamp);
                missingDate = addWorkDays(missingDate, 1);
            }
        }
        sortedDates.push(timestamp);
    });
    if (startDate && startDate.getTime() < sortedDates[0]) {
        let missingDate = nextWorkDay(startDate);
        const precedingDates = [];
        while(missingDate.getTime() < sortedDates[0]){
            const timestamp = missingDate.getTime();
            dailyValues.set(timestamp, 0);
            precedingDates.push(timestamp);
            missingDate = addWorkDays(missingDate, 1);
        }
        sortedDates.splice(0, 0, ...precedingDates);
    }
    if (percent !== undefined) {
        const originalTotal = sortedDates.reduce((total, timestamp)=>total + dailyValues.get(timestamp), 0);
        let roundedTotal = 0;
        let lastValueTimestamp;
        sortedDates.forEach((timestamp)=>{
            const value = safeRoundHours(dailyValues.get(timestamp) * percent / 100);
            dailyValues.set(timestamp, value);
            roundedTotal += value;
            if (value !== 0) lastValueTimestamp = timestamp;
        });
        if (lastValueTimestamp !== undefined) {
            const targetTotal = safeRoundHours(originalTotal * percent / 100);
            dailyValues.set(lastValueTimestamp, safeRoundHours(dailyValues.get(lastValueTimestamp) + targetTotal - roundedTotal));
        }
    }
    return sortedDates.map((timestamp)=>({
            value: dailyValues.get(timestamp),
            startDate: new Date(timestamp)
        }));
};
const getChunkedDistribution = (distribution, startDay)=>{
    const months = new Map();
    distribution.forEach((dailyValue, index)=>{
        const date = addWorkDays(startDay, index);
        const monthStart = getFirstWorkdayOfMonth(date);
        const monthKey = getDateKey(monthStart);
        let month = months.get(monthKey);
        if (!month) {
            month = {
                startDate: monthStart,
                endDate: date,
                value: 0,
                ov: false,
                weeks: []
            };
            months.set(monthKey, month);
        }
        const weekStart = getFirstWorkdayOfWeek(date);
        const weekKey = getDateKey(weekStart);
        let week = month.weeks.find((candidate)=>getDateKey(candidate.startDate) === weekKey);
        if (!week) {
            week = {
                startDate: weekStart,
                endDate: date,
                value: 0,
                ov: false,
                distribution: []
            };
            month.weeks.push(week);
        }
        month.value += dailyValue.value;
        month.endDate = date;
        month.ov ||= dailyValue.ov === true;
        week.value += dailyValue.value;
        week.endDate = date;
        week.ov ||= dailyValue.ov === true;
        week.distribution = [
            ...week.distribution,
            {
                ...dailyValue,
                startDate: date
            }
        ];
    });
    return [
        ...months.values()
    ];
};
const transferDistributionWorkload = (source, workload, sourceIndices, targetIndices, timeAllocation)=>{
    validatePositiveFinite(workload, "transferred workload");
    validatePositiveFinite(timeAllocation, "daily allocation");
    const result = source.map((value)=>({
            ...value
        }));
    let remaining = workload;
    for (const index of [
        ...sourceIndices
    ].reverse()){
        if (index < 0) continue;
        if (!result[index]) continue;
        const removed = Math.min(result[index].value, remaining);
        result[index].value -= removed;
        if (result[index].value !== timeAllocation) result[index].ov = true;
        remaining -= removed;
        if (remaining <= 0) break;
    }
    if (remaining > 0) {
        throw new RangeError("Timeline distribution does not have enough workload");
    }
    const targetPeriodWorkload = targetIndices.reduce((total, index)=>total + (result[index]?.value ?? 0), 0);
    remaining = workload;
    if (remaining + targetPeriodWorkload >= targetIndices.length * timeAllocation) {
        const splitWorkload = weightedValueRoundedSplit(remaining + targetPeriodWorkload, targetIndices.map(()=>1), MaxTimeModifiers.HOUR);
        targetIndices.forEach((index, dayIndex)=>{
            result[index].value = splitWorkload[dayIndex];
            result[index].ov = true;
        });
    } else {
        for (const index of targetIndices){
            if (index < 0) continue;
            while(result.length <= index)result.push({
                value: 0
            });
            const added = Math.min(Math.max(0, timeAllocation - result[index].value), remaining);
            result[index].value += added;
            if (result[index].value !== timeAllocation) result[index].ov = true;
            remaining -= added;
            if (remaining <= 0) break;
        }
    }
    return result;
};

const shiftTimelineStartDate = (startDate, oldTimelineStartDate, newTimelineStartDate)=>{
    const offset = differenceInCalendarDays(newTimelineStartDate, oldTimelineStartDate);
    let nextStartDate = addDays(startDate, offset);
    if (isBefore(nextStartDate, newTimelineStartDate)) {
        nextStartDate = newTimelineStartDate;
    }
    return nextWorkDay(nextStartDate);
};

const addBilling = (target, source)=>{
    Object.entries(source).forEach(([workTypeId, value])=>{
        target[workTypeId] = (target[workTypeId] || 0) + value;
    });
};
const getBillingTotal = (billing)=>Object.values(billing).reduce((total, value)=>total + value, 0);
const createBillingMonth = (monthStart, dailyByDate, timelineEndDate)=>{
    const normalizedMonthStart = nextWorkDay(monthStart);
    const normalizedMonthEnd = timelineEndDate && timelineEndDate < getLastWorkdayOfMonth(monthStart) ? prevWorkDay(timelineEndDate) : getLastWorkdayOfMonth(monthStart);
    const weeks = [];
    let weekStart = normalizedMonthStart;
    while(weekStart <= normalizedMonthEnd && normalizedMonthStart <= normalizedMonthEnd){
        const weekEnd = getLastWorkdayOfWeek(weekStart);
        const days = [];
        for(let day = weekStart; day <= weekEnd; day = addDays(day, 1)){
            if (isWeekend(day)) continue;
            const dayByWorkType = dailyByDate.get(startOfDay(day).getTime()) || {};
            days.push({
                startDate: day,
                endDate: day,
                byWorkType: dayByWorkType,
                total: getBillingTotal(dayByWorkType)
            });
        }
        const weekByWorkType = {};
        days.forEach((day)=>addBilling(weekByWorkType, day.byWorkType));
        weeks.push({
            startDate: weekStart,
            endDate: weekEnd,
            byWorkType: weekByWorkType,
            total: getBillingTotal(weekByWorkType),
            days
        });
        weekStart = getFirstWorkdayOfWeek(addDays(weekStart, 7));
    }
    const byWorkType = {};
    weeks.forEach((week)=>addBilling(byWorkType, week.byWorkType));
    return {
        startDate: getFirstWorkdayOfMonth(normalizedMonthStart),
        endDate: normalizedMonthEnd,
        byWorkType,
        total: getBillingTotal(byWorkType),
        weeks
    };
};

const CURRENT_STRUCTURE_VERSION = "3.8.1";
const CURRENT_STRUCTURE_BUILD = "180926";
const DEFAULT_MIN_AVG_MAX_VALUE = {
    min: 0,
    avg: 0,
    max: 0
};
const EMPTY_MIN_AVG_MAX_VALUE = {
    min: null,
    avg: null,
    max: null
};
const DEFAULT_TEXT_DESCRIPTION = [
    {
        insert: "\n"
    }
];
const DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER = generateTaskDescriptionId(0);
const DEFAULT_PROJECT_SETTINGS = {
    currency: DEFAULT_CURRENCY_KEY,
    companyCurrency: DEFAULT_CURRENCY_KEY,
    timeModifier: 1,
    showSummary: true,
    showTimeline: true,
    showBreakdown: true,
    showTextDescription: true,
    showBreakdownChildCounters: true,
    timelineMode: TimelineMode.MIN,
    roundPrice: false,
    useMinMax: false,
    viewLevel: BreakdownTableDepths.TASK,
    valueLevel: BreakdownTableDepths.TASK,
    priceFormat: PriceFormats.EUROPEAN,
    sectionFormat: SectionFormats.ROW,
    dayModifier: TimeModifiers.DAY,
    descriptionMode: DescriptionModes.COLUMN,
    mta: {
        min: DEFAULT_MTA_VALUE,
        avg: DEFAULT_MTA_VALUE,
        max: DEFAULT_MTA_VALUE
    },
    usedSummaryColumns: DEFAULT_PROPOSAL_COLUMNS_ORDER.map((name)=>({
            name,
            visible: name !== SummaryColumns.TEAM && name !== SummaryColumns.TIME,
            noDev: name === SummaryColumns.COST || name === SummaryColumns.RATE
        })),
    usedTables: DEFAULT_PROJECT_TABLES_ORDER.map((name)=>({
            name,
            visible: name !== Tables.TIMELINE && name !== Tables.DESCRIPTION
        })),
    usedTotals: DEFAULT_PROJECT_TOTALS_ORDER.map((name)=>({
            name,
            visible: true,
            noDev: name === Totals.COST
        })),
    useTimelineChart: false,
    useTimelineSections: true,
    useTimelineUtilization: true,
    useTimelineBilling: true,
    expandTimelineSections: true,
    expandTimelineUtilization: true,
    expandTimelineBilling: true
};
const DEFAULT_BUFFERS = [
    {
        id: "defaultRisk",
        name: "Risk",
        percent: 10,
        workTypes: [],
        used: false
    },
    {
        id: "defaultMeetings",
        name: "Meetings",
        percent: 5,
        workTypes: [],
        used: false
    }
];
const DEFAULT_BREAKDOWN_COLUMNS = DEFAULT_BREAKDOWN_COLUMNS_ORDER.map((name)=>{
    const d = {
        name,
        // used: name !== BreakdownColumns.NUMERATION,
        visible: name !== BreakdownColumns.LABELS && name !== BreakdownColumns.NUMERATION,
        width: DEFAULT_BREAKDOWN_COLUMNS_WIDTHS[name]
    };
    if (DEFAULT_BREAKDOWN_COLUMNS_WIDTHS[name]) d.pinLeft = true;
    if (name === BreakdownColumns.PRICE) d.pinRight = true;
    if (name === BreakdownColumns.TITLE || name === BreakdownColumns.PRICE) d.applyPin = true;
    if (name === BreakdownColumns.DESCRIPTION) d.descriptionId = DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER;
    return d;
});
const DEFAULT_DESCRIPTION_COLUMN = {
    name: BreakdownColumns.DESCRIPTION,
    visible: true,
    width: DEFAULT_BREAKDOWN_COLUMNS_WIDTHS[BreakdownColumns.DESCRIPTION],
    pinLeft: Boolean(DEFAULT_BREAKDOWN_COLUMNS_WIDTHS[BreakdownColumns.DESCRIPTION]),
    applyPin: false
};

const DEFAULT_ROOT_OBJECT = {
    historyManager: undefined,
    sections: [],
    workTypes: [],
    usedLabels: [],
    estimateViewLevel: BreakdownTableDepths.TASK,
    estimateValueLevel: BreakdownTableDepths.TASK,
    visibility: [],
    settings: {
        modifier: MaxTimeModifiers.HOUR,
        timeModifier: 1,
        dayModifier: TimeModifiers.DAY,
        timelineHoursKey: MinAvgMaxKeys.AVG,
        usedRoundHours: 0,
        roundDecimals: 2,
        mta: DEFAULT_MIN_AVG_MAX_VALUE,
        parsedRiskBuffer: 1,
        hasTeamMembersCol: true,
        viewLevel: BreakdownTableDepths.TASK,
        priceFormat: PriceFormats.EUROPEAN,
        sectionFormat: SectionFormats.ROW,
        currency: "",
        companyCurrency: "",
        valueLevel: BreakdownTableDepths.TASK,
        descriptionMode: DescriptionModes.COLUMN,
        titleCellWidth: 0,
        descriptionCellWidth: 0,
        labelCellWidth: 0,
        showSummary: true,
        showTimeline: true,
        showBreakdown: true,
        showTextDescription: true,
        timelineMode: TimelineMode.MIN,
        timelineStartDate: startOfWeek(new Date(), {
            weekStartsOn: 1
        }),
        //@ts-expect-error - mst type mismatch; does no real harm here
        usedSummaryColumns: [],
        //@ts-expect-error - mst type mismatch; does no real harm here
        usedTables: [],
        btWorkTypes: undefined
    },
    hasSearchPhrase: undefined
};

// export const checkBuildLock = (buildLock: string, currentBuild: string) => {
//   const l = new Date(),
//     c = new Date();
//   l.setDate(Number(buildLock.slice(0, 2)));
//   l.setMonth(Number(buildLock.slice(2, 4)) - 1);
//   l.setFullYear(Number("20" + buildLock.slice(4)));
//
//   c.setDate(Number(currentBuild.slice(0, 2)));
//   c.setMonth(Number(currentBuild.slice(2, 4)) - 1);
//   c.setFullYear(Number("20" + currentBuild.slice(4)));
//
//   return c < l;
// };
const hasOlderOriginBuild = (buildLock, currentBuild)=>!currentBuild || parse(currentBuild, "ddMMyy", new Date()) < parse(buildLock, "ddMMyy", new Date());

/**
 * Should hide "Hard Rounding" for projects with sOriginBuild before that date;
 */ const HARD_ROUNDING_LOCK_BUILD = "110324";
/**
 * Should hide "RiskBuffer" for projects with sOriginBuild before that date;
 */ const RISK_BUFFER_LOCK_BUILD = "070824";
/**
 * Applies coefficients to min-avg-max values for projects with sOriginBuild after and including that date;
 */ const AVG_PROPOSAL_VALUES_LOCK_BUILD = "061124";
/**
 * Should use WT & TM "cTime" in months for projects with sOriginBuild before that date;
 */ const WEEK_TIME_LOCK_BUILD = "061124";
/**
 * Models use hoursSet, valuesSet, cPriceSet & valueOverrides before that date;
 */ const VALUE_UPDATE_BUILD = "190226";
/**
 * WorkTypeModel uses inBreakdown, percent & of before that date;
 */ const PERCENT_UPDATE_BUILD = "200626";

function applyCommentStorePatch(target, patch) {
    (isArray(patch) ? patch : [
        patch
    ]).forEach((p)=>{
        // eslint-disable-next-line prefer-const
        let { path, elementPath, ...patchContent } = p;
        if (!target.useExternalComments && path.includes("external")) return;
        if (!target.useInternalComments && path.includes("internal")) return;
        // console.log(elementPath, patchContent.op);
        if (!elementPath) return;
        if (patchContent.op !== "remove" && path.match(/^\/groups\/\d+$/)) {
            path = path.replace(/\/groups\/\d+\//, `/groups/${target.groups.length}/`);
            applyPatch(target, {
                path,
                ...patchContent
            });
            return;
        }
        const group = target.groups.find((g)=>g.path === elementPath);
        if (!group) return; // if we don't have such group - skip
        path = path.replace(/\/groups\/\d+/, "");
        applyPatch(group, {
            path,
            ...patchContent
        });
    });
}

function applyPatchExtended(target, patch) {
    (isArray(patch) ? patch : [
        patch
    ]).forEach((p)=>{
        const { op, path, value } = p;
        if (op === "comment") return;
        if (op !== "delta") {
            applyPatch(target, p);
            return;
        }
        const el = resolvePath(target, path);
        if (el instanceof Delta) {
            // console.log("is delta");
            applyPatch(target, {
                op: "replace",
                path,
                value: value ? el.compose(value) : value
            });
        } else {
            // console.log("is not delta: " + typeof el);
            applyPatch(target, {
                op: "add",
                path,
                value: value ? new Delta().compose(value) : value
            });
        }
    });
}

function cascadedPercentWorkTypesSort(workTypes, ofListName, startingWorkTypeId) {
    const cascade = workTypes.slice().sort((a, b)=>{
        if (ofListName === "allPercentTargetsInSections") {
            if (!a.hasGlobalPercent && b.hasGlobalPercent) return -1;
            if (a.hasGlobalPercent && !b.hasGlobalPercent) return 1;
        }
        if (!a.hasPercent && b.hasPercent) return -1;
        if (a.hasPercent && !b.hasPercent) return 1;
        const aInB = b[ofListName]?.includes(a.id);
        const bInA = a[ofListName]?.includes(b.id);
        if (bInA && !aInB) return 1;
        if (!bInA && aInB) return -1;
        return 0;
    });
    if (typeof startingWorkTypeId !== "string") return cascade;
    const index = cascade.findIndex((wT)=>wT.id === startingWorkTypeId);
    if (index < 0) return cascade;
    const prevIds = [];
    return cascade.slice(index).filter((wT)=>{
        const toUpdate = wT.id === startingWorkTypeId || wT.hasPercent && intersection(wT[ofListName], prevIds)?.length > 0 || ofListName === "allPercentTargetsInSections" && wT.hasGlobalPercent && intersection(wT[ofListName], prevIds)?.length > 0;
        if (toUpdate) prevIds.push(wT.id);
        return toUpdate;
    });
}

const getDuplicateName = (name, index)=>{
    const nameMatch = name?.match(/\((\d+)\)$/);
    const newIndex = index || (nameMatch?.[1] ? parseInt(nameMatch?.[1]) + 1 : 1);
    return nameMatch ? name?.replace(/\((\d+)\)$/, `(${newIndex})`) : `${name} (${newIndex})`;
};
const getDuplicateIndex = (name)=>{
    const nameMatch = name?.match(/\((\d+)\)$/);
    return nameMatch?.[1] ? parseInt(nameMatch?.[1]) + 1 : 1;
};

function getMinMaxRange(values = []) {
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    if (maxValue !== minValue) return `${minValue} - ${maxValue}`;
    return minValue;
}

const getStructureMetadata = (structure, stringify)=>{
    const m = {
        totalCost: structure.totalCost,
        totalHours: structure.totalHours,
        useMinMax: structure.settings.useMinMax,
        workTypes: uniqBy(structure.workTypes.map(({ id, systemId, name, color })=>cleanObject({
                id,
                systemId,
                name,
                backgroundColor: color
            })), (v)=>v.systemId ?? v.id),
        labels: structure?.addedLabels?.map((l)=>cleanObject({
                ...l
            })) || []
    };
    return stringify ? JSON.stringify(m) : m;
};

const sumObjectsData = (...objects)=>{
    return objects.reduce((p, c)=>{
        if (c) Object.keys(c).forEach((k)=>{
            const pk = p[k], ck = c[k];
            if (typeof pk !== typeof ck || !ck) Object.assign(p, {
                [k]: pk || ck
            });
            if (typeof pk === "boolean" && typeof ck === "boolean") Object.assign(p, {
                [k]: pk && ck
            });
            else if (isArray(pk) && isArray(ck)) Object.assign(p, {
                [k]: sumArrayData(pk || [], ck || [])
            });
            else if (typeof pk === "number" || typeof ck === "number") Object.assign(p, {
                [k]: (typeof pk === "number" ? pk || 0 : 0) + (typeof ck === "number" ? ck || 0 : 0)
            });
            else Object.assign(p, {
                [k]: ck
            });
        });
        return p;
    }, {});
};
const sumArrayData = (...arrays)=>{
    return arrays.reduce((p, c)=>{
        c?.forEach((v, i)=>{
            switch(typeof v){
                case "number":
                    p[i] = (typeof p[i] === "number" ? p[i] : 0) + (v || 0);
                    break;
                case "boolean":
                    p[i] = typeof p[i] === "boolean" ? p[i] && v : v;
                    break;
                case "object":
                    if (v === null || v?.length) p[i] = p[i] || undefined;
                    else p[i] = sumObjectsData(v, typeof p[i] === "object" && !p[i].length ? p[i] : {});
                    break;
                default:
                    p[i] = p[i] || undefined;
            }
        });
        return p;
    }, []);
};
// export const sumAndRoundTaskChildrenData = (
//   hourModifier: number,
//   rounding: number,
//   riskBuffer: number,
//   ...maps: Map<any, any>[]
// ) => {
//   return maps.reduce((mergedMap, map) => {
//     if (map) {
//       const iterator = map[Symbol.iterator]();
//       for (const item of iterator) {
//         const existing = mergedMap.get(item[0]);
//         const parsed = item[1]
//           ? Object.keys(item[1])
//             .reduce((t,key) => ({
//               ...t,
//               [key]: Number(roundFloat((item[1][key] || 0 ) * hourModifier, rounding)) / hourModifier
//             }), {})
//           : {}
//         mergedMap.set(
//           item[0],
//           existing ? sumObjectsData(existing, parsed) : parsed,
//         );
//       }
//     }
//     return mergedMap;
//   }, new Map());
// };
/**
 *
 * @param object {Object} - any object to parse
 * @param parser {any | ((value: any, key: string) => any)} - transform current object value to a new one
 */ const parseObject = (object, parser)=>{
    return Object.keys(object).reduce((newObj, key)=>({
            ...newObj,
            [key]: typeof parser === "function" ? parser(object[key], key) : parser
        }), {});
};
const serializeMapWithObjects = (map, cleanValues)=>{
    const o = Object.fromEntries(map);
    Object.keys(o).forEach((key)=>{
        if (cleanValues) {
            const cleaned = cleanObject({
                ...o[key]
            }, true);
            if (!Object.keys(cleaned).length) delete o[key];
            else o[key] = cleaned;
        } else o[key] = {
            ...o[key]
        };
    });
    return o;
};
const differentObjectValues = (o1, o2)=>{
    if (!o1 || !o2) return true;
    return Boolean(Object.keys(DEFAULT_MIN_AVG_MAX_VALUE).find((key)=>{
        if (typeof o1[key] !== "number" && typeof o2[key] !== "number") return false;
        return o1[key] !== o2[key];
    }));
};
const serializeMapWithObjectsByKey = (map, useMinMax)=>{
    const o = Object.fromEntries(map);
    const rO = {};
    Object.keys(o).forEach((k)=>{
        rO[k] = useMinMax ? o[k].max || o[k].min || 0 : o[k].avg || 0;
    });
    return rO;
};
const objectHasValue = (object)=>{
    if (!object) return false;
    return Boolean(Object.values(object).find((value)=>!!value));
};

const hoursRoundedSplit = (value, targetValues, modifier = MaxTimeModifiers.HOUR, rounding = 0)=>{
    const total = {
        ...value
    };
    const values = [];
    for(let i = 0; i < targetValues; i++){
        values.push(parseObject(value, (v, key)=>{
            if (typeof total[key] !== "number") return total[key];
            const h = safeRoundHours(v / targetValues, modifier, rounding);
            if (total[key] < h || i === targetValues - 1) {
                return total[key];
            }
            total[key] = parseFloat((total[key] - h).toPrecision(12));
            return h;
        }));
    }
    return values;
};

const lastNameUsageIndex = (dataList, name)=>{
    const list = dataList?.filter((el)=>el.name?.toLowerCase().includes(name.toLowerCase().replace(/ \d+$/, "")));
    if (!list.length) return 0;
    let number = 1;
    list.forEach((el)=>{
        const num = parseInt(el.name.match(/\d+$/)?.[0] || "1");
        if (num > number) number = num;
    });
    return number;
};

const incrementedUsedName = (dataList, name, index)=>{
    const number = typeof index === "number" ? index : lastNameUsageIndex(dataList, name);
    return number > 0 ? `${name.replace(/ \d+$/, "")} ${number + 1}` : name;
};

const isBetween = (x, a, b)=>{
    const min = Math.min(a || 0, b || 0);
    const max = Math.max(a || 0, b || 0);
    return x >= min && x <= max;
};

const mergeBufferFunc = (objValue, srcValue)=>({
        name: (srcValue || objValue)?.name || "",
        itemBuffer: (srcValue || objValue)?.itemBuffer,
        value: Number(((objValue?.value || 0) + (srcValue?.value || 0)).toPrecision(10))
    });

const modifyNewValuesWithCoefficients = (values, keys, currentHours, modifier = 1, usedRounding, forceMinMaxFixes)=>{
    const avg = MinAvgMaxKeys.AVG;
    const min = MinAvgMaxKeys.MIN;
    const max = MinAvgMaxKeys.MAX;
    if (keys.includes(min) || keys.includes(max)) {
        if (forceMinMaxFixes && !keys.includes(min)) values[min] = currentHours?.[min] || 0;
        if (!keys.includes(min) && (currentHours?.[min] || 0) >= values[max]) values[min] = values[max];
        if (forceMinMaxFixes && !keys.includes(max)) values[max] = currentHours?.[max] || 0;
        if (!keys.includes(max) && (currentHours?.[max] || 0) <= values[min]) values[max] = values[min];
        if (!keys.includes(avg) && (!currentHours?.avgSet || currentHours.forcedState)) {
            values[avg] = safeRoundHours(((values[min] ?? currentHours?.[min] ?? 0) + (values[max] ?? currentHours?.[max] ?? 0)) / 2, modifier, usedRounding);
        }
    } else if (keys.includes(avg) && (!currentHours?.minMaxSet || currentHours.forcedState)) {
        values[min] = safeRoundHours((values[avg] || 0) * DEFAULT_MIN_VALUE_COEFFICIENT, modifier, usedRounding);
        values[max] = safeRoundHours((values[avg] || 0) * DEFAULT_MAX_VALUE_COEFFICIENT, modifier, usedRounding);
    }
    return values;
};

const roundedHoursSplit = (value, targetValues, modifier, rounding = 0)=>{
    const total = {
        ...value
    };
    const values = [];
    for(let i = 0; i < targetValues; i++){
        values.push(parseObject(value, (v, key)=>{
            if (typeof total[key] !== "number") return total[key];
            const h = safeRoundHours(v / targetValues, modifier, rounding);
            if (total[key] < h || i === targetValues - 1) {
                return total[key];
            }
            total[key] = parseFloat((total[key] - h).toPrecision(12));
            return h;
        }));
    }
    return values;
};

const roundingInTaskValuesMap = (taskValues, modifier = MaxTimeModifiers.HOUR, roundHours, riskBuffer, percentageWorkTypes)=>{
    const o = Object.fromEntries(taskValues);
    Object.keys(o).forEach((wtId)=>{
        o[wtId] = {
            ...o[wtId]
        };
        MIN_AVG_MAX_KEYS.forEach((vKey)=>{
            const v = o[wtId][vKey];
            if (!v) return;
            const noRiskBuffer = Boolean(percentageWorkTypes?.find((wT)=>wT.id.toString() === wtId)) && vKey === MinAvgMaxKeys.AVG ? o[wtId].avgOv : o[wtId].minMaxOv;
            let h = v;
            if (!noRiskBuffer && riskBuffer && riskBuffer > 1) h = h * riskBuffer;
            o[wtId][vKey] = safeRoundHours(h, modifier, roundHours);
        });
    });
    return o;
};
const hardRoundingInTaskValuesMap = (taskValues, modifier = MaxTimeModifiers.HOUR, roundings)=>{
    const o = Object.fromEntries(taskValues);
    Object.keys(o).forEach((workTypeId)=>{
        o[workTypeId] = {
            ...o[workTypeId]
        };
        const usedRounding = roundings.find((r)=>r.workTypeId.toString() === workTypeId);
        if (!usedRounding) return;
        MIN_AVG_MAX_KEYS.forEach((vKey)=>{
            const h = o[workTypeId][vKey];
            if (!h) return;
            o[workTypeId][vKey] = safeRoundHoursV2(h, modifier, usedRounding.type, usedRounding.interval);
        });
    });
    return o;
};
const pickNonAutomatedTaskValuesMap = (taskValues, omittedWorkTypeId)=>{
    return omit(Object.fromEntries(taskValues), omittedWorkTypeId);
};
const buffersInTaskValuesMap = (taskValues, usedBuffers, modifier = MaxTimeModifiers.HOUR, omittedWorkTypeId)=>{
    const o = omittedWorkTypeId ? pickNonAutomatedTaskValuesMap(taskValues, omittedWorkTypeId) : Object.fromEntries(taskValues);
    if (!usedBuffers?.length) return o;
    const applyBuffers = (wtId, workload)=>usedBuffers.filter((ov)=>ov.workTypes.includes(wtId)).reduce((t, { parsedPercent })=>t + safeRoundHours(workload * parsedPercent, modifier), workload);
    Object.keys(o).forEach((wtId)=>{
        o[wtId] = omit({
            ...o[wtId]
        }, "automation");
        // if(maxValuesAsBuffer) { // set max value as
        //   const avgVal = o[wtId][MinAvgMaxKeys.AVG];
        //   o[wtId][MinAvgMaxKeys.MAX] = avgVal
        //     ? applyBuffers(wtId, avgVal)
        //     : undefined
        // } else
        MIN_AVG_MAX_KEYS.forEach((vKey)=>{
            const v = o[wtId][vKey];
            if (!v) return;
            o[wtId][vKey] = Math.max(0, applyBuffers(wtId, v));
        });
    });
    return o;
};
const sumTaskChildrenData = (...maps)=>{
    return maps.reduce((mergedMap, map)=>{
        if (map) {
            for (const item of map){
                const existing = mergedMap.get(item[0]);
                mergedMap.set(item[0], existing ? sumObjectsData(existing, item[1]) : item[1]);
            }
        }
        return mergedMap;
    }, new Map());
};
const sumTaskChildrenDataV2 = (maps, forcedMinMaxSibling, useMinMax, maxValuesAsBuffer)=>{
    const newMap = maps.reduce((mergedMap, map)=>{
        if (map) {
            for (const [wtId, values] of map){
                const existing = mergedMap.get(wtId);
                const newValue = {
                    ...existing || {}
                };
                const forceAvgKey = values.forcedState === WorkTypeValueState.SINGLE || !useMinMax && forcedMinMaxSibling[wtId] === WorkTypeValueState.MIN_MAX && values.forcedState !== WorkTypeValueState.MIN_MAX;
                MIN_AVG_MAX_KEYS.forEach((key)=>{
                    newValue[key] = (newValue[key] || 0) + (values[forceAvgKey && !maxValuesAsBuffer ? "avg" : key] || 0);
                });
                mergedMap.set(wtId, newValue);
            }
        }
        return mergedMap;
    }, new Map());
    Object.keys(forcedMinMaxSibling).forEach((wtId)=>{
        const v = newMap.get(wtId);
        if (v) newMap.set(wtId, {
            ...v,
            forcedState: forcedMinMaxSibling[wtId]
        });
    });
    return newMap;
};
const sumTaskValues = (values, forcedMinMaxSibling, useMinMax)=>{
    const sum = values.reduce((total, workload)=>{
        const forceAvgKey = workload.forcedState === WorkTypeValueState.SINGLE || !useMinMax && forcedMinMaxSibling === WorkTypeValueState.MIN_MAX && workload.forcedState !== WorkTypeValueState.MIN_MAX;
        MIN_AVG_MAX_KEYS.forEach((key)=>{
            total[key] = (total[key] || 0) + (workload[forceAvgKey ? "avg" : key] || 0);
        });
        return total;
    }, {});
    if (forcedMinMaxSibling) sum.forcedState = forcedMinMaxSibling;
    return sum;
};
const sumBTData = (...maps)=>{
    return maps.reduce((mergedMap, map)=>{
        if (map) {
            for (const item of map){
                const existing = mergedMap.get(item[0]);
                mergedMap.set(item[0], existing ? existing + item[1] : item[1]);
            }
        }
        return mergedMap;
    }, new Map());
};
const pickHigherTaskValue = (childValueMap, taskMap, usedWorkTypes, percentageWorkTypes, reverse)=>{
    const resultMap = new Map();
    const workTypes = intersection(Array.from(childValueMap.keys(), String), usedWorkTypes);
    const tC = Array.from(taskMap.keys(), String);
    tC.forEach((id)=>{
        if (!workTypes.includes(id)) workTypes.push(id);
    });
    workTypes.forEach((id)=>{
        const childMapValue = childValueMap.get(id);
        const taskMapValue = taskMap.get(id);
        const hasPercent = Boolean(percentageWorkTypes?.find((wT)=>wT.id.toString() === id));
        const pickedValues = parseObject(EMPTY_MIN_AVG_MAX_VALUE, (val, key)=>{
            return hasPercent && (key === MinAvgMaxKeys.AVG ? !taskMapValue?.avgOv : !taskMapValue?.minMaxOv) ? (childMapValue?.[key] || 0) + (taskMapValue?.[key] || 0) : reverse ? childMapValue?.[key] || taskMapValue?.[key] || val : taskMapValue?.[key] || childMapValue?.[key] || val;
        });
        // forced == "var" -> min = min+avg, max = max+avg
        if (childMapValue?.forcedState || taskMapValue?.forcedState) pickedValues.forcedState = reverse ? childMapValue?.forcedState || taskMapValue?.forcedState : taskMapValue?.forcedState || childMapValue?.forcedState;
        resultMap.set(id, pickedValues);
    });
    return resultMap;
};
const pickHigherBTTaskValue = (childValueMap, taskBTMap)=>{
    if (taskBTMap.size > childValueMap.size) return taskBTMap;
    for (const item of childValueMap){
        const taskMapValue = taskBTMap.get(item[0]);
        if (taskMapValue) childValueMap.set(item[0], taskMapValue || item[1] || 0);
    }
    return childValueMap;
};
const parseWorkloadValues = (object, parser)=>{
    const o = MIN_AVG_MAX_KEYS.reduce((newObj, key)=>({
            ...newObj,
            [key]: typeof parser !== "function" ? parser : parser(object[key], key)
        }), object);
    if (object.forcedState) o.forcedState = object.forcedState;
    return o;
};

const FILTERED_PATCH_PATHS = [
    // "/showSummary",
    // "/showBreakdown",
    // "/showTimeline",
    // "/isOpened",
    // "/isTimelineOpened",
    // "/showMoreContent",
    "/autoFocus",
    "/valueMode"
];
const ELEMENT_VISIBILITY_PATCH_PATHS = [
    "/showSummary",
    "/showBreakdown",
    "/showTimeline",
    "/isOpened",
    "/showCombinedHours",
    "/isTimelineOpened",
    "/showMoreContent",
    "/autoFocus",
    "/valueMode",
    "/timelineExpandedMonths",
    "/timelineExpandedMonths/\\d+"
];
const COMMENT_PATCH_PATHS = [
    "/comments",
    "/commentsFetched"
];
const REMOVE_ACTIONS = [
    "removeSelf",
    "removeChild",
    "removeWorkType",
    "removeResource"
];

const WT_PREFIX = "WT";
const FC_PREFIX = "fixedCosts";
const MAT_PREFIX = "materials";
const P_MODIFIER_PREFIX = "pModifiers/";
const TOTALS_PREFIX = "totals/";
const getTotalsTreePath = (name)=>`${TOTALS_PREFIX}${name}`;
const getFixedCostPath = ()=>FC_PREFIX;
const getMaterialsPath = ()=>MAT_PREFIX;
const getPriceModifiersPath = (id)=>`${P_MODIFIER_PREFIX}${id}`;
const getWorkTypeTreePath = (id)=>`${WT_PREFIX}${id}`;
const isTotalsTreePath = (path)=>path[0].startsWith(TOTALS_PREFIX);
const isFixedCostTreePath = (path)=>path[0] === FC_PREFIX;
const isWorkTypeTreePath = (path)=>path[0].startsWith(WT_PREFIX);

const updateSortedList = (array, orderArray, key = "id", noRemoval = false)=>{
    if (!array || !orderArray) return;
    const sortOrder = orderArray.map(({ id })=>id);
    const omitted = array.filter((o)=>sortOrder.indexOf(o[key]) < 0).map((o)=>{
        const identifier = o[key];
        if (!noRemoval) destroy(o);
        return identifier;
    });
    return array.filter((a)=>noRemoval || sortOrder.indexOf(a[key]) >= 0).sort((a, b)=>omitted.includes(a[key]) ? omitted.includes(b[key]) ? 0 : 1 : omitted.includes(b[key]) ? -1 : sortOrder.indexOf(a[key]) - sortOrder.indexOf(b[key]));
};

const valueRoundedSplit = (value, targetValues, modifier = MaxTimeModifiers.HOUR, rounding = 0, sortDesc)=>{
    const values = [];
    let total = value;
    for(let i = 0; i < targetValues; i++){
        let h = safeRoundHours(value / targetValues, modifier, rounding);
        if (total < h || i === targetValues - 1) {
            h = total;
        }
        total = parseFloat((total - h).toPrecision(12));
        values.push(h);
    }
    if (sortDesc) return values.sort((v1, v2)=>v1 > v2 ? -1 : 1);
    return values;
};

const weightedHoursRoundedSplit = (value, weights, modifier = MaxTimeModifiers.HOUR, rounding = 0)=>{
    const total = {
        ...value
    };
    const values = [];
    const totalWeight = weights.reduce((t, w)=>sumObjectsData(t, w), DEFAULT_MIN_AVG_MAX_VALUE);
    const lastNonZeroWeight = parseObject(value, (_, key)=>findLastIndex(weights, (el)=>{
            const elV = el[key] || 0;
            return elV > 0;
        }));
    weights.forEach((weight, i)=>{
        values.push(parseObject(value, (v, key)=>{
            if (typeof total[key] !== "number" || key !== "avg" && key !== "min" && key !== "max") return total[key];
            const h = weight[key] && totalWeight[key] ? safeRoundHours(v * (weight[key] / totalWeight[key]), modifier, rounding) : 0;
            if (total[key] < h || i === lastNonZeroWeight[key]) {
                return total[key];
            }
            total[key] = parseFloat((total[key] - h).toPrecision(12));
            return h;
        }));
    });
    return values;
};

const weightedValueRoundedSplit = (value, weights, modifier = MaxTimeModifiers.HOUR, rounding = 0)=>{
    const values = [];
    let total = value;
    const totalWeight = weights.reduce((t, w)=>t + w, 0);
    const lastNonZeroWeight = findLastIndex(weights, (el)=>el > 0);
    weights.forEach((weight, i)=>{
        let h = safeRoundHours(value * (weight / totalWeight), modifier, rounding);
        if (total < h || i === lastNonZeroWeight) {
            h = total;
        }
        total = parseFloat((total - h).toPrecision(12));
        values.push(h);
    });
    return values;
};

const wTExistsDeep = (allWorkTypes, matchId, workTypes)=>{
    return !!workTypes?.find((id)=>{
        const wT = allWorkTypes.find((wT)=>wT.id === id);
        return wT?.of?.includes(matchId) || wTExistsDeep(allWorkTypes, matchId, wT?.of?.filter((o)=>o !== wT.id));
    });
};

const targetVersion = "3.8.1";
const targetBuild = "180926";
const remapId = (id, idMap)=>idMap.get(Number(id)) ?? String(id);
const remapIds = (ids, idMap)=>ids?.map((id)=>remapId(id, idMap));
const migrateSettings = (settings, idMap)=>{
    const currentSettings = {
        ...settings
    };
    const { useTimelineAllocation, displayTimelineSections, displayTimelineAllocation, displayTimelineBilling } = currentSettings;
    delete currentSettings.expandedTimelineMonths;
    delete currentSettings.useTimelineAllocation;
    delete currentSettings.useTimelineChart;
    delete currentSettings.displayTimelineSections;
    delete currentSettings.displayTimelineAllocation;
    delete currentSettings.displayTimelineBilling;
    delete currentSettings.btWorkTypes;
    return {
        ...currentSettings,
        useTimelineUtilization: currentSettings.useTimelineUtilization ?? useTimelineAllocation,
        expandTimelineSections: currentSettings.expandTimelineSections ?? displayTimelineSections,
        expandTimelineUtilization: currentSettings.expandTimelineUtilization ?? displayTimelineAllocation,
        expandTimelineBilling: currentSettings.expandTimelineBilling ?? displayTimelineBilling,
        usedTables: currentSettings.usedTables.map((usedTable)=>({
                ...usedTable,
                visible: usedTable.name === Tables.TIMELINE ? false : usedTable.visible
            })),
        buffers: currentSettings.buffers?.map((buffer)=>({
                ...buffer,
                workTypes: remapIds(buffer.workTypes, idMap)
            }))
    };
};
const migrateWorkTypeResource = (resource, utilizationOrder)=>{
    const currentResource = {
        ...resource
    };
    delete currentResource.timeline;
    delete currentResource.timelineOrder;
    delete currentResource.hideOnTimeline;
    delete currentResource.hoursOverwritten;
    delete currentResource.timelineStart;
    delete currentResource.timelineOverrides;
    delete currentResource.focusedNameUsers;
    delete currentResource.focusedRateUsers;
    delete currentResource.focusedHoursUsers;
    delete currentResource.focusedPriceUsers;
    delete currentResource.autoFocusTimeline;
    return {
        ...currentResource,
        utilizationOrder,
        id: String(currentResource.id)
    };
};
const migrateWorkType = (workType, idMap, utilizationOrder)=>{
    const currentWorkType = {
        ...workType
    };
    delete currentWorkType.isTimelineOpened;
    delete currentWorkType.useTimelinePercent;
    if (currentWorkType.id > 0) currentWorkType.systemId = currentWorkType.id;
    return {
        ...migrateWorkTypeResource(currentWorkType, utilizationOrder),
        id: remapId(currentWorkType.id, idMap),
        resources: currentWorkType.resources?.map((resource, index)=>migrateWorkTypeResource(resource, index))
    };
};
const migrateSectionWorkTypeResource = (resource)=>{
    const currentResource = {
        ...resource
    };
    delete currentResource.startWeek;
    return {
        ...currentResource,
        id: String(currentResource.id)
    };
};
const migrateSectionWorkType = (workType, idMap)=>{
    const currentWorkType = {
        ...workType
    };
    return {
        ...migrateSectionWorkTypeResource(currentWorkType),
        id: remapId(currentWorkType.id, idMap),
        of: remapIds(currentWorkType.of, idMap),
        resources: currentWorkType.resources?.map(migrateSectionWorkTypeResource)
    };
};
const migrateSection = (section, idMap)=>{
    const currentSection = {
        ...section
    };
    const { workTypes } = currentSection;
    delete currentSection.timelineStartWeek;
    delete currentSection.timelineEndWeek;
    delete currentSection.hideOnTimeline;
    return cleanObject({
        ...migrateTask(currentSection, idMap, BreakdownTableDepths.PROJECT),
        usedBreakdownColumns: currentSection.usedBreakdownColumns,
        workTypes: workTypes?.map((workType)=>migrateSectionWorkType(workType, idMap))
    });
};
function migrateTask(task, idMap, parentLevel) {
    const currentTask = {
        ...task
    };
    delete currentTask.timelineStartWeek;
    delete currentTask.timelineEndWeek;
    delete currentTask.hideOnTimeline;
    return cleanObject({
        ...currentTask,
        lvl: parentLevel + 1,
        buffers: currentTask.buffers?.map((buffer)=>({
                ...buffer,
                workTypes: remapIds(buffer.workTypes, idMap)
            })),
        values: currentTask.values && Object.fromEntries(Object.entries(currentTask.values).map(([id, value])=>[
                remapId(id, idMap),
                value
            ])),
        workloadAutomation: currentTask.workloadAutomation?.map((automation)=>({
                ...automation,
                workTypeId: remapId(automation.workTypeId, idMap),
                ofWorkTypes: remapIds(automation.ofWorkTypes, idMap)
            })),
        children: currentTask.children?.map((child)=>migrateTask(child, idMap, parentLevel + 1))
    });
}
const migratePriceModifiers = (priceModifiers, idMap)=>priceModifiers?.map((priceModifier)=>({
            ...priceModifier,
            ofEntities: remapIds(priceModifier.ofEntities, idMap)
        }));
const buildIdMap = (workTypes)=>{
    const idMap = new Map();
    let index = 0;
    workTypes.forEach((workType)=>{
        if (typeof workType.id === "number") idMap.set(workType.id, generateWorkTypeId(index++));
    });
    return idMap;
};
var timelineUpdate = ((structure)=>{
    const migratedStructure = structure;
    const idMap = buildIdMap(migratedStructure.workTypes);
    return {
        ...migratedStructure,
        sVersion: targetVersion,
        sBuild: targetBuild,
        settings: migrateSettings(migratedStructure.settings, idMap),
        workTypes: migratedStructure.workTypes.map((workType, index)=>migrateWorkType(workType, idMap, index)),
        priceModifiers: migratePriceModifiers(migratedStructure.priceModifiers, idMap),
        sections: migratedStructure.sections.map((section)=>migrateSection(section, idMap))
    };
});

const migrations = [
    {
        migration: timelineUpdate,
        sBuild: targetBuild
    }
];
const structureMigrator = (zippedStructure, returnAsObject = false)=>{
    if (typeof zippedStructure !== "string" || !zippedStructure.length) throw "Incorrect structure format!";
    const unzipped = unzip(zippedStructure);
    const structure = JSON.parse(unzipped);
    let migratedStructure;
    if (!structure.sVersion) throw "Incorrect structure sVersion tag!";
    if (!structure.sBuild) throw "Incorrect structure sBuild tag!";
    if (structure.sBuild === CURRENT_STRUCTURE_BUILD && structure.sVersion === CURRENT_STRUCTURE_VERSION) return zippedStructure;
    migrations.forEach(({ migration, sBuild })=>{
        if (hasOlderOriginBuild(sBuild, structure.sBuild)) migratedStructure = migration(structure);
    });
    if (!migratedStructure) throw "Unexpected migration result!";
    return returnAsObject ? migratedStructure : zip(JSON.stringify(migratedStructure));
};

const atomic = createActionTrackingMiddleware2({
    filter (call) {
        // only call the methods above for actions that were not being recorded,
        // but do not call them for child acions (which inherit a copy of the env)
        return !call.env; // already recording if call.env exists
    },
    onStart (call) {
        const recorder = recordPatches(call.tree, (_patch, _inversePatch, actionContext)=>{
            // only record patches that were generated by this action or children of this action
            return !!actionContext && isActionContextThisOrChildOf(actionContext, call.id);
        });
        recorder.resume();
        call.env = {
            recorder
        };
    },
    onFinish (call, error) {
        const recorder = call.env.recorder;
        call.env = undefined;
        recorder.stop();
        if (error !== undefined) {
            recorder.undo();
        }
    }
});

const Entry$1 = types.model("UndoManagerEntry", {
    patches: types.frozen(),
    inversePatches: types.frozen()
});
const CommentHistoryManager = types.model("CommentHistoryManager", {
    history: types.array(Entry$1)
}).actions((self)=>{
    let targetStore;
    let recordingDisabled = 0;
    const groupRecorders = [];
    const undoRedoMiddleware = createActionTrackingMiddleware2({
        filter (call) {
            if (call.env) {
                // already recording
                return false;
            }
            return call.context !== self;
        },
        onStart (call) {
            const recorder = recordPatches(call.tree, (_patch, _inversePatch, actionContext)=>{
                if (recordingDisabled) {
                    return false;
                }
                // only record patches that were generated by this action or children of this action
                return !!actionContext && isActionContextThisOrChildOf(actionContext, call.id);
            });
            recorder.resume();
            call.env = {
                recorder
            };
        },
        onFinish (call, error) {
            const recorder = call.env.recorder;
            call.env = undefined;
            recorder.stop();
            if (error === undefined) {
                if (groupRecorders.length > 0) {
                    const groupRecorder = groupRecorders[groupRecorders.length - 1];
                    groupRecorder.patches = groupRecorder.patches.concat(recorder.patches);
                    groupRecorder.inversePatches = groupRecorder.inversePatches.concat(recorder.inversePatches);
                } else {
                    self.addUndoState(recorder);
                }
            } else {
                recorder.undo();
            }
        }
    });
    const skipRecording = (fn)=>{
        recordingDisabled++;
        try {
            return fn();
        } finally{
            recordingDisabled--;
        }
    };
    return {
        addUndoState (recorder) {
            const maxLength = getRoot(self)?.maxHistoryLength;
            if (maxLength === 0) return;
            this.withoutUndo(()=>{
                const filteredPatches = [];
                const filteredInversePatches = [];
                recorder.patches.forEach((p, i)=>{
                    if (p.path.match(/^\/groups\//)) {
                        filteredPatches.push(recorder.patches[i]);
                        filteredInversePatches.push(recorder.inversePatches[i]);
                    }
                });
                if (filteredPatches.length === 0) {
                    // skip recording if patches is empty
                    return;
                }
                const parsePatches = (p, index)=>{
                    const groupPath = p.path.match(/\/groups\/(\d+)/);
                    if (groupPath?.[1] && !isNaN(Number(groupPath[1]))) {
                        try {
                            const elementPath = getRoot(self).groups[Number(groupPath[1])]?.path || filteredInversePatches[index].value?.path || filteredPatches[index].value?.path;
                            if (elementPath) return {
                                ...p,
                                elementPath
                            };
                            else return p;
                        } catch (e) {
                            console.error(e, "|", p, groupPath[1]);
                            return p;
                        }
                    }
                    return p;
                };
                self.history.push({
                    patches: filteredPatches.map(parsePatches),
                    inversePatches: filteredInversePatches.map(parsePatches)
                });
            });
        },
        afterCreate () {
            targetStore = getRoot(self);
            if (targetStore === self) {
                throw new Error("UndoManager should be created as part of a tree");
            }
            addDisposer(self, addMiddleware(targetStore, undoRedoMiddleware /*, includeHooks*/ ));
        },
        withoutUndo (fn) {
            return skipRecording(fn);
        },
        withoutUndoFlow (generatorFn) {
            return flow(function* __withoutUndoFlow__() {
                recordingDisabled++;
                try {
                    return yield* generatorFn();
                } finally{
                    recordingDisabled--;
                }
            });
        },
        startGroup () {
            if (groupRecorders.length >= 1) {
                throw new Error("a previous startGroup is still running, did you forget to call stopGroup?");
            }
            groupRecorders.push({
                patches: [],
                inversePatches: []
            });
        },
        stopGroup () {
            const groupRecorder = groupRecorders.pop();
            if (!groupRecorder) {
                console.error("each call to stopGroup requires a previous call to startGroup, did you forget to call startGroup?");
                return;
            }
            this.addUndoState(groupRecorder);
        },
        clear: decorate(atomic, ()=>{
            skipRecording(()=>{
                self.history.clear();
            });
        })
    };
});

const Entry = types.model("UndoManagerEntry", {
    patches: types.frozen(),
    inversePatches: types.frozen()
});
const UndoManager = types.model("UndoManager", {
    history: types.array(Entry),
    undoIdx: 0
}).views((self)=>({
        get undoLevels () {
            return self.undoIdx;
        },
        get redoLevels () {
            return self.history.length - self.undoIdx;
        },
        get canUndo () {
            return this.undoLevels > 0;
        },
        get canRedo () {
            return this.redoLevels > 0;
        }
    })).actions((self)=>{
    let targetStore;
    let recordingDisabled = 0;
    const groupRecorders = [];
    const undoRedoMiddleware = createActionTrackingMiddleware2({
        filter (call) {
            if (call.env) {
                // already recording
                return false;
            }
            return call.context !== self;
        },
        onStart (call) {
            const recorder = recordPatches(call.tree, (_patch, _inversePatch, actionContext)=>{
                if (recordingDisabled) {
                    return false;
                }
                // only record patches that were generated by this action or children of this action
                return !!actionContext && isActionContextThisOrChildOf(actionContext, call.id);
            });
            recorder.resume();
            call.env = {
                recorder
            };
        },
        onFinish (call, error) {
            const recorder = call.env.recorder;
            call.env = undefined;
            recorder.stop();
            if (error === undefined) {
                if (groupRecorders.length > 0) {
                    const groupRecorder = groupRecorders[groupRecorders.length - 1];
                    groupRecorder.patches = groupRecorder.patches.concat(recorder.patches);
                    groupRecorder.inversePatches = groupRecorder.inversePatches.concat(recorder.inversePatches);
                } else {
                    self.addUndoState(recorder);
                }
            } else {
                recorder.undo();
            }
        }
    });
    const skipRecording = (fn)=>{
        recordingDisabled++;
        try {
            return fn();
        } finally{
            recordingDisabled--;
        }
    };
    return {
        addUndoState (recorder) {
            const maxLength = getRoot(self)?.maxHistoryLength;
            if (maxLength === 0) return;
            const filterPatches = (p)=>!FILTERED_PATCH_PATHS.find((m)=>p.path.match(new RegExp(`${m}$`)));
            const parseValue = (value)=>{
                if (!isObject(value) || isArray(value)) return value;
                const v = value;
                return Object.keys(v).reduce((t, k)=>({
                        ...t,
                        [k]: [
                            "autoFocus"
                        ].includes(k) ? false : v[k]
                    }), {});
            };
            this.withoutUndo(()=>{
                const filteredPatches = recorder.patches.filter(filterPatches);
                if (filteredPatches.length === 0) {
                    // skip recording if patches is empty
                    return;
                }
                const filteredInversePatches = recorder.inversePatches.filter(filterPatches);
                const parsePatches = (p, index)=>{
                    const pI = {
                        ...p,
                        value: parseValue(p.value)
                    };
                    try {
                        const cleanPath = pI.path?.match(/^\/[a-zA-Z0-9]*\/[^vt]*\d+/)?.[0];
                        const isChildPath = Boolean(p.path?.match(/^\/[a-zA-Z0-9]*\/[^vt]*\d+\/children$/)?.[0] || p.path?.match(/^\/[a-zA-Z0-9]*\/[^vt]*\d+\/resources$/)?.[0]);
                        pI.identifier = filteredPatches[index]?.value?.id || filteredInversePatches[index]?.value?.id || (cleanPath ? resolvePath(getRoot(self), cleanPath)?.id : undefined);
                        if (pI.op === "remove" && cleanPath === pI.path) pI.removesElement = true;
                        if (isChildPath && p.op === "replace" && !p.value /* || (isArray(p.value) && !p.value.length)*/ ) pI.clearsList = true;
                    } catch (e) {
                    /* do nothing */ }
                    return pI;
                };
                self.history.splice(self.undoIdx);
                self.history.push({
                    patches: filteredPatches.map(parsePatches),
                    inversePatches: filteredInversePatches.map(parsePatches)
                });
                self.history.splice(0, self.history.length - maxLength);
                // console.log(
                //   self.history.map(h => [h.patches.length, [...h.patches]]),
                // );
                self.undoIdx = self.history.length;
            });
        },
        afterCreate () {
            targetStore = getRoot(self);
            if (targetStore === self) {
                throw new Error("UndoManager should be created as part of a tree");
            }
            // if (typeof getEnv(self).includeHooks === "boolean") {
            //   includeHooks = getEnv(self).includeHooks;
            // }
            addDisposer(self, addMiddleware(targetStore, undoRedoMiddleware /*, includeHooks*/ ));
        },
        undo: decorate(atomic, ()=>{
            skipRecording(()=>{
                if (!self.canUndo) {
                    throw new Error("undo not possible, nothing to undo");
                }
                const root = getRoot(targetStore);
                // applyPatch(
                //   root,
                //   self.history[self.undoIdx - 1].inversePatches
                //     .slice()
                //     .reverse()
                // );
                self.history[self.undoIdx - 1].inversePatches.slice().reverse().forEach((p)=>{
                    const pI = p;
                    if (p.op === "comment" || p.op === "commentName") return;
                    if (pI.clearsList && resolvePath(root, p.path)?.length > 0) return;
                    applyPatchExtended(root, p);
                });
                // );
                self.undoIdx--;
            });
        }),
        redo: decorate(atomic, ()=>{
            skipRecording(()=>{
                if (!self.canRedo) {
                    throw new Error("redo not possible, nothing to redo");
                }
                const root = getRoot(targetStore);
                // applyPatch(getRoot(targetStore), self.history[self.undoIdx].patches);
                self.history[self.undoIdx].patches.forEach((p)=>{
                    const pI = p;
                    if (p.op === "comment" || p.op === "commentName") return;
                    if (pI.clearsList && resolvePath(root, p.path)?.length > 0) return;
                    applyPatchExtended(root, p);
                });
                self.undoIdx++;
            });
        }),
        withoutUndo (fn) {
            return skipRecording(fn);
        },
        withoutUndoFlow (generatorFn) {
            return flow(function* __withoutUndoFlow__() {
                recordingDisabled++;
                try {
                    return yield* generatorFn();
                } finally{
                    recordingDisabled--;
                }
            });
        },
        startGroup () {
            if (groupRecorders.length >= 1) {
                console.warn("a previous startGroup is still running, did you forget to call stopGroup?");
                this.stopGroup();
            }
            groupRecorders.push({
                patches: [],
                inversePatches: []
            });
        },
        addCommentPathPatch (oldPath, newPath) {
            if (!groupRecorders.length) return;
            const patch = {
                op: "comment",
                path: oldPath,
                value: newPath
            };
            const inversePatch = {
                op: "comment",
                path: newPath,
                value: oldPath
            };
            const groupRecorder = groupRecorders[groupRecorders.length - 1];
            groupRecorder.patches = groupRecorder.patches.concat([
                patch
            ]);
            groupRecorder.inversePatches = groupRecorder.inversePatches.concat([
                inversePatch
            ]);
        },
        addCommentNamePatch (path, oldName, newName) {
            if (!groupRecorders.length) return;
            const patch = {
                op: "commentName",
                path,
                value: newName
            };
            const inversePatch = {
                op: "commentName",
                path,
                value: oldName
            };
            const groupRecorder = groupRecorders[groupRecorders.length - 1];
            groupRecorder.patches = groupRecorder.patches.concat([
                patch
            ]);
            groupRecorder.inversePatches = groupRecorder.inversePatches.concat([
                inversePatch
            ]);
        },
        stopGroup () {
            const groupRecorder = groupRecorders.pop();
            if (!groupRecorder) {
                console.error("each call to stopGroup requires a previous call to startGroup, did you forget to call startGroup?");
                return;
            }
            this.addUndoState(groupRecorder);
        },
        clear: decorate(atomic, ()=>{
            skipRecording(()=>{
                self.history.clear();
                self.undoIdx = 0;
            });
        }),
        clearByTargetId: decorate(atomic, (id)=>{
            skipRecording(()=>{
                let newUndoIdx = self.undoIdx;
                const filterPatches = (p)=>{
                    const pI = p;
                    return !pI?.identifier || pI.identifier !== id;
                };
                self.history.forEach((entry, index)=>{
                    entry.patches = entry.patches.filter(filterPatches);
                    entry.inversePatches = entry.inversePatches.filter(filterPatches);
                    if (!entry.patches.length && !entry.inversePatches.length) {
                        destroy(entry);
                        if (index <= self.undoIdx) newUndoIdx--;
                    }
                });
                self.undoIdx = Math.max(newUndoIdx, 0);
            });
        }),
        clearUndo: decorate(atomic, ()=>{
            skipRecording(()=>{
                self.history.splice(0, self.undoLevels);
                self.undoIdx = 0;
            });
        }),
        clearRedo: decorate(atomic, ()=>{
            skipRecording(()=>{
                self.history.splice(self.undoIdx, self.redoLevels);
            });
        }),
        addDeltaPatch (path, delta, inverseDelta) {
            self.history.push({
                patches: [
                    {
                        op: "delta",
                        path,
                        value: delta
                    }
                ],
                inversePatches: [
                    {
                        op: "delta",
                        path,
                        value: inverseDelta
                    }
                ]
            });
            self.undoIdx = self.history.length;
        }
    };
});

const CommenterModel = types.model("Commenter", {
    uuid: types.string,
    fullName: types.maybe(types.string),
    color: types.string
}).actions((self)=>({
        setName (name) {
            self.fullName = name;
        }
    }));

const CommentModel = types.model("Comment", {
    uuid: types.string,
    parentUuid: types.maybe(types.maybeNull(types.string)),
    body: types.string,
    createdAt: types.string,
    user: CommenterModel
}).views((self)=>({
        get isOwn () {
            const root = getRoot(self);
            return Boolean(root.commenterData?.uuid === self.user.uuid);
        }
    })).actions((self)=>({
        updateBody (text) {
            self.body = text;
        },
        removeSelf () {
            const parent = getParentOfType(self, CommentThreadModel);
            parent.removeReply(self.uuid);
        }
    }));

const CommentThreadModel = CommentModel.named("CommentThread").props({
    isRead: types.maybe(types.boolean),
    replies: types.optional(types.array(CommentModel), [])
}).actions((self)=>({
        markRead () {
            getRoot(self).historyManager?.withoutUndo(()=>{
                self.isRead = true;
            });
        },
        markUnread () {
            getRoot(self).historyManager?.withoutUndo(()=>{
                self.isRead = false;
            });
        },
        addReply (data) {
            if (!self.replies) self.replies = cast([]);
            self.replies.push(cast(data));
        },
        updateReplyBody (uuid, body) {
            if (!self.replies) self.replies = cast([]);
            self.replies.find((reply)=>reply.uuid === uuid)?.updateBody(body);
        },
        removeReply (uuid) {
            if (!self.replies) return;
            const reply = self.replies.find((reply)=>reply.uuid === uuid);
            if (reply) destroy(reply);
        },
        removeSelf (viewedType) {
            const parent = getParentOfType(self, CommentGroupModel);
            parent.removeThread(viewedType);
        }
    }));

const CommentGroupModel = types.model("CommentGroup", {
    version: types.number,
    path: types.string,
    name: types.maybe(types.maybeNull(types.string)),
    usedType: types.maybe(types.maybeNull(types.string)),
    internal: types.maybe(types.maybeNull(CommentThreadModel)),
    external: types.maybe(types.maybeNull(CommentThreadModel))
}).views((self)=>({
        get isCreated () {
            return !isRoot(self);
        },
        get hasBothThreads () {
            const root = getRoot(self);
            return Boolean(self.internal && root.useInternalComments && self.external && root.useExternalComments);
        },
        get hasAnyThreads () {
            const root = getRoot(self);
            if (self.external && root.useExternalComments) return CommentTypes.EXTERNAL;
            if (self.internal && root.useInternalComments) return CommentTypes.INTERNAL;
            return false;
        },
        get viewedType () {
            const root = getRoot(self);
            if (self.usedType && (!this.isCreated || root.useExternalComments && root.useInternalComments)) return self.usedType; /** @note: allow switching if user has access to both types */ 
            if (root.useInternalComments && self.internal) return CommentTypes.INTERNAL;
            return CommentTypes.EXTERNAL;
        },
        get thread () {
            if (this.viewedType === CommentTypes.EXTERNAL) return self.external;
            return self.internal;
        },
        get isUnread () {
            const root = getRoot(self);
            return Boolean(root.useInternalComments && self.internal && !self.internal.isRead || root.useExternalComments && self.external && !self.external.isRead);
        }
    })).actions((self)=>({
        setUsedType (usedType) {
            if (self.isCreated) getRoot(self).historyManager?.withoutUndo(()=>{
                self.usedType = usedType;
            });
            else self.usedType = usedType;
        },
        addThread (viewedType, data) {
            self[viewedType] = cast({
                ...data,
                isRead: false
            });
        },
        removeThread (viewedType) {
            destroy(self[viewedType]);
        },
        updateName (name) {
            self.name = name;
        },
        updatePath (oldPath, path) {
            if (self.path.match(`^${oldPath}`)) self.path = self.path.replace(oldPath, path);
        }
    }));

const CommentRootModel = types.model("CommentRoot", {
    groups: types.array(CommentGroupModel),
    commenterData: types.maybe(CommenterModel),
    currentVersion: types.optional(types.number, 0),
    useInternalComments: types.maybe(types.boolean),
    useExternalComments: types.maybe(types.boolean),
    /** not serialized */ historyManager: types.optional(CommentHistoryManager, {}),
    maxHistoryLength: types.optional(types.number, 0)
}).views((self)=>({
        get allVisibleComments () {
            return self.groups?.filter((group)=>self.useInternalComments && group.internal || self.useExternalComments && group.external);
        },
        get visibleComments () {
            return self.groups?.filter((group)=>{
                const isAllowed = self.useInternalComments && group.internal || self.useExternalComments && group.external;
                if (self.currentVersion > 0 && isAllowed) return group.version === self.currentVersion;
                return isAllowed;
            });
        },
        visibleGroupComments (versionKey) {
            return self.groups?.filter((group)=>{
                const isAllowed = self.useInternalComments && group.internal || self.useExternalComments && group.external;
                return isAllowed && group.version === versionKey;
            });
        },
        getPathGroup (path, currentVersionKey) {
            return self.groups?.find((group)=>group.path === path && group.version === currentVersionKey);
        },
        getPathChildComments (path, currentVersionKey) {
            return self.groups?.filter((group)=>group.path !== path && group.path.match(`^${path}/`) && group.version === currentVersionKey && (self.useInternalComments && group.internal || self.useExternalComments && group.external))?.length;
        },
        get unreadComments () {
            return self.groups?.filter((group)=>group.isUnread)?.length || 0;
        }
    })).actions((self)=>({
        initHistoryManager () {
            self.historyManager.withoutUndo(()=>{
                self.maxHistoryLength = MAX_UNDO_HISTORY_SIZE;
            });
        }
    })).actions((self)=>({
        setCurrentVersion (currentVersion) {
            self.historyManager.withoutUndo(()=>{
                self.currentVersion = currentVersion;
            });
        },
        setCommenterData (commenterData) {
            self.historyManager.withoutUndo(()=>{
                self.commenterData = cast(commenterData);
            });
        },
        setUseInternalComments (useInternalComments) {
            self.historyManager.withoutUndo(()=>{
                self.useInternalComments = useInternalComments;
            });
        },
        setUseExternalComments (useExternalComments) {
            self.historyManager.withoutUndo(()=>{
                self.useExternalComments = useExternalComments;
                if (!useExternalComments) self.groups.forEach((group)=>{
                    if (group.usedType === CommentTypes.EXTERNAL) group.setUsedType(CommentTypes.INTERNAL);
                });
            });
        },
        addThread (viewedType, data, groupData) {
            const group = self.groups.find((group)=>group.path === groupData.path && group.version === groupData.version);
            if (group) group.addThread(viewedType, data);
            else self.groups.push({
                ...groupData,
                [viewedType]: data
            });
        },
        addGroup (group) {
            self.groups.push(getSnapshot(group));
        },
        setGroups (groups) {
            self.groups = cast(groups);
        },
        addGroups (groups) {
            self.groups.push(...groups);
        },
        removeCommentByPath (path) {
            if (!self.groups) return;
            self.groups.forEach((group)=>{
                if (group.path.match(`^${path}`)) destroy(group);
            });
        },
        removeCommentByVersion (versionKey) {
            if (!self.groups) return;
            self.groups.forEach((group)=>{
                if (group.version === versionKey) destroy(group);
            });
        },
        updateCommentPath (oldPath, path) {
            if (!self.groups) return;
            self.groups.forEach((group)=>{
                if (group.path.match(`^${oldPath}`)) group.updatePath(oldPath, path);
            });
        },
        updateCommentElementName (path, newName) {
            if (!self.groups) return;
            self.groups.forEach((group)=>{
                if (group.path === path) group.updateName(newName);
            });
        }
    }));

const BufferModel = types.model("Buffer", {
    id: types.identifier,
    name: types.string,
    percent: types.number,
    workTypes: types.array(types.string),
    used: types.boolean,
    /** not serialized */ wasUsed: types.maybe(types.boolean)
}).views((self)=>({
        get parsedPercent () {
            return parseFloat((self.percent / 100).toPrecision(12));
        },
        serialize () {
            return cleanObject({
                id: self.id,
                name: self.name,
                percent: self.percent,
                workTypes: [
                    ...self.workTypes
                ],
                used: self.used
            });
        }
    })).views((self)=>({
        get lvl () {
            const parent = getParent(self, 2);
            return "lvl" in parent ? parent.lvl : -1;
        }
    })).actions((self)=>({
        updateRelatedWorkTypeId (workTypeId, newWorkTypeId) {
            if (!self.workTypes.includes(workTypeId)) return;
            const i = self.workTypes.indexOf(workTypeId);
            self.workTypes[i] = newWorkTypeId;
        },
        addWorkType (workTypeId) {
            if (self.workTypes.includes(workTypeId)) return;
            self.workTypes.push(workTypeId);
        },
        removeWorkType (workTypeId) {
            if (!self.workTypes.includes(workTypeId)) return;
            const i = self.workTypes.indexOf(workTypeId);
            self.workTypes.splice(i, 1);
        },
        setName (name) {
            self.name = name;
        },
        setPercent (percent) {
            self.percent = percent;
            this._internalUpdateSectionPercentValues();
        },
        setUseState (used) {
            self.used = used;
            if (used && typeof self.wasUsed === "boolean") self.wasUsed = true;
            this._internalUpdateSectionPercentValues();
        },
        setUseStateWithHistory (off) {
            if (off) {
                self.wasUsed = Boolean(self.used);
                self.used = false;
            } else if (typeof self.wasUsed === "boolean") {
                self.used = Boolean(self.wasUsed);
                self.wasUsed = undefined;
            }
        },
        setWorkTypes (workTypes) {
            self.workTypes = cast(workTypes);
            this._internalUpdateSectionPercentValues();
        },
        removeSelf () {
            const parent = getParent(self, 2);
            parent.removeBuffer(self.id);
        },
        _internalUpdateSectionPercentValues () {
            const parent = getParent(self, 2);
            parent?._internalResetBufferPercentValues?.(self.workTypes);
            // if ("id" in parent && parent.id) return;
            const root = getRoot(self);
            root._internalUpdateSelectedWorkTypes();
        // root.generateTimeline();
        }
    }));

const FixedPriceAutomationModel = types.model("FixedPriceAutomation", {
    percent: types.number,
    ofSections: types.array(types.string)
}).views((self)=>({
        get parsedPercent () {
            return parseFloat((self.percent / 100).toPrecision(12));
        }
    })).actions((self)=>({
        setAutomation (percent, ofSections) {
            self.percent = percent;
            self.ofSections = cast(ofSections);
        }
    }));

const MinAvgMaxFlagModel = types.model("MinAvgMaxFlag", {
    min: types.maybe(types.maybeNull(types.boolean)),
    avg: types.maybe(types.maybeNull(types.boolean)),
    max: types.maybe(types.maybeNull(types.boolean))
});
const MinAvgFlagModel = types.model("MinAvgMaxFlag", {
    min: types.maybe(types.maybeNull(types.boolean)),
    avg: types.maybe(types.maybeNull(types.boolean))
});

const MinAvgMaxValueModel = types.model("MinAvgMaxValue", {
    min: types.maybe(types.maybeNull(types.number)),
    avg: types.maybe(types.maybeNull(types.number)),
    max: types.maybe(types.maybeNull(types.number))
});
const MinAvgMaxValueExtendedModel = MinAvgMaxValueModel.named("MinAvgMaxValueExtendedModel").props({
    /** if set forces to display such work type in a specific value mode */ forcedState: types.maybe(types.enumeration("WorkTypeValueState", Object.values(WorkTypeValueState))),
    /** true if min or max value was manually set */ minMaxSet: types.maybe(types.maybeNull(types.boolean)),
    /** true if avg value was manually set */ avgSet: types.maybe(types.maybeNull(types.boolean)),
    /** true if % avg value is manually overwritten */ avgOv: types.maybe(types.maybeNull(types.boolean)),
    /** true if % min or max values were manually overwritten */ minMaxOv: types.maybe(types.maybeNull(types.boolean))
}).views((self)=>({
        hasOtherValues (keys) {
            return Boolean(without(MIN_AVG_MAX_KEYS, ...keys).find((k)=>self[k] || (typeof self[k] === "number" && k === MinAvgMaxKeys.AVG ? self.avgOv : self.minMaxOv)));
        },
        isOverridden (minMax) {
            return minMax ? self.minMaxOv : self.avgOv;
        }
    })).actions((self)=>({
        setForcedState (forcedState) {
            self.forcedState = forcedState;
        },
        setMin (min, manual) {
            self.min = min;
            if (manual && !self.minMaxSet) self.minMaxSet = true;
        },
        setAvg (avg, manual) {
            self.avg = avg;
            if (manual && !self.avgSet) self.avgSet = true;
        },
        setMax (max, manual) {
            self.max = max;
            if (manual && !self.minMaxSet) self.minMaxSet = true;
        },
        setValues (values) {
            if (typeof values.avg === "number") this.setAvg(values.avg, Boolean(values.avgSet));
            if (typeof values.min === "number") this.setMin(values.min, Boolean(values.minMaxSet));
            if (typeof values.max === "number") this.setMax(values.max, Boolean(values.minMaxSet));
            if (values.avgOv) self.avgOv = true;
            if (values.minMaxOv) self.minMaxOv = true;
        },
        clearSetStatus (minMax) {
            if (minMax) self.minMaxSet = undefined;
            else self.avgSet = undefined;
        },
        clearValue (key, removeOverride) {
            self[key] = undefined;
            this.clearSetStatus(key !== MinAvgMaxKeys.AVG);
            if (removeOverride) this.clearOverride(key !== MinAvgMaxKeys.AVG);
        },
        clearValues (keys, removeOverride) {
            keys.forEach((k)=>{
                self[k] = undefined;
                this.clearSetStatus(k !== MinAvgMaxKeys.AVG);
                if (removeOverride) this.clearOverride(k !== MinAvgMaxKeys.AVG);
            });
        },
        setOverride (minMax) {
            if (minMax) self.minMaxOv = true;
            else self.avgOv = true;
        },
        clearOverride (minMax) {
            if (minMax) self.minMaxOv = undefined;
            else self.avgOv = undefined;
        },
        clearAllOverrides () {
            self.minMaxOv = undefined;
            self.avgOv = undefined;
        }
    }));

const PriceModifierModel = types.model("PriceModifier", {
    id: types.identifier,
    type: types.enumeration("PriceModifiers", Object.values(PriceModifiers)),
    mode: types.enumeration("PriceModifierModes", Object.values(PriceModifierModes)),
    name: types.string,
    used: types.boolean,
    percent: types.maybe(types.number),
    ofEntities: types.optional(types.array(types.string), []),
    fixedPrice: types.maybe(types.number),
    hide: types.maybe(types.boolean),
    turnOff: types.maybe(types.boolean)
}).views((self)=>({
        get parsedPercent () {
            return self.percent ? parseFloat((self.percent / 100).toPrecision(12)) : 0;
        },
        get forcedState () {
            const root = getRoot(self);
            if (root.settings.maxValuesAsBuffer || self.mode === PriceModifierModes.FIXED_PRICE) return;
            const usedWorkTypesForcedState = pick(root.forcedStateWorkTypes, ...self.ofEntities);
            if (self.ofEntities.includes(FC_PREFIX) && root.hasBreakdownFixedPrice && !root.turnOffFixedPrice) {
                usedWorkTypesForcedState[FC_PREFIX] = root.fixedPriceForcedState;
            }
            const vals = uniq(Object.values(usedWorkTypesForcedState));
            if (vals.find((v)=>v === WorkTypeValueState.MIN_MAX)) return WorkTypeValueState.MIN_MAX;
            if (root.displayTwoValues && vals.filter((v)=>v === WorkTypeValueState.SINGLE).length === vals.length) return WorkTypeValueState.SINGLE;
            return;
        },
        get displayTwoValues () {
            if (self.mode === PriceModifierModes.FIXED_PRICE) return false;
            const root = getRoot(self);
            return root.displayTwoValues && this.forcedState !== WorkTypeValueState.SINGLE || !root.settings.useMinMax && this.forcedState === WorkTypeValueState.MIN_MAX;
        },
        serialize () {
            return cleanObject({
                id: self.id,
                type: self.type,
                mode: self.mode,
                name: self.name,
                used: self.used,
                percent: self.percent,
                ofEntities: self.ofEntities ? [
                    ...self.ofEntities
                ] : undefined,
                fixedPrice: self.fixedPrice,
                hide: self.hide,
                turnOff: self.turnOff
            });
        }
    })).views((self)=>({
        get nonWorkTypePrices () {
            if (!isAlive(self) || self.mode === PriceModifierModes.FIXED_PRICE) return [];
            const root = getRoot(self);
            const prices = [];
            if (self.ofEntities.includes(FC_PREFIX) && root.hasBreakdownFixedPrice && !root.turnOffFixedPrice) {
                prices.push({
                    ...root.breakdownFixedPrice || {},
                    ...parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (_, key)=>Number(((root.breakdownFixedPrice?.[key] || 0) * self.parsedPercent).toPrecision(12)))
                });
            }
            if (self.ofEntities.includes(MAT_PREFIX) && root.hasBreakdownMaterialPrice && !root.turnOffMaterials) {
                prices.push({
                    ...parseObject(DEFAULT_MIN_AVG_MAX_VALUE, Number((root.breakdownMaterialPrice * self.parsedPercent).toPrecision(12)))
                });
            }
            return prices;
        },
        get unbufferedPrice () {
            if (!isAlive(self) || self.mode === PriceModifierModes.FIXED_PRICE) return {};
            const root = getRoot(self);
            const prices = [
                ...this.nonWorkTypePrices
            ];
            self.ofEntities.forEach((workType)=>{
                const globalWorkType = root.workTypes.find((wT)=>wT.id === workType);
                if (!globalWorkType || globalWorkType.turnOff) return;
                prices.push({
                    ...globalWorkType.unbufferedPrice || {},
                    ...parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (_, key)=>Number(((globalWorkType.unbufferedPrice?.[key] || 0) * self.parsedPercent).toPrecision(12)))
                });
            });
            return sumTaskValues(prices, self.forcedState, root.settings.useMinMax);
        },
        get price () {
            if (!isAlive(self)) return {};
            if (self.mode === PriceModifierModes.FIXED_PRICE) return {
                [MinAvgMaxKeys.AVG]: self.fixedPrice
            };
            const root = getRoot(self);
            const prices = [
                ...this.nonWorkTypePrices
            ];
            self.ofEntities.forEach((workType)=>{
                const globalWorkType = root.workTypes.find((wT)=>wT.id === workType);
                if (!globalWorkType || globalWorkType.turnOff) return;
                prices.push({
                    ...globalWorkType.price || {},
                    ...parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (_, key)=>Number(((globalWorkType.price?.[key] || 0) * self.parsedPercent).toPrecision(12)))
                });
            });
            return sumTaskValues(prices, self.forcedState, root.settings.useMinMax);
        },
        get displayPrice () {
            if (!isAlive(self)) return 0;
            const root = getRoot(self);
            const roundPrice = root.settings?.roundPrice;
            const v = root.settings.maxValuesAsBuffer && root.displayMinValue && self.mode === PriceModifierModes.PERCENT ? this.unbufferedPrice : this.price;
            return roundFloat(root.getDisplayValue(v), roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        },
        get displayPriceMax () {
            if (!isAlive(self)) return 0;
            const root = getRoot(self);
            const roundPrice = root.settings?.roundPrice;
            const v = root.settings.maxValuesAsBuffer && self.mode === PriceModifierModes.PERCENT ? root.getDisplayValue(this.price) : root.getDisplayValueMax(this.price);
            return roundFloat(v, roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        },
        get showMaxPrice () {
            if (!isAlive(self)) return 0;
            const root = getRoot(self);
            return (root.displayTwoValues || self.displayTwoValues) && this.displayPrice !== this.displayPriceMax;
        }
    })).views((self)=>({
        get displayPriceCellSize () {
            if (!isAlive(self)) return 0;
            const root = getRoot(self);
            if (!root.renderPrice) return 104;
            let size = root.renderPrice(self.displayPrice);
            if (self.showMaxPrice) size += root.renderPrice(self.displayPriceMax) + 16;
            return size;
        }
    })).actions((self)=>({
        addWorkType (workTypeId) {
            if (self.ofEntities.includes(workTypeId)) return;
            self.ofEntities.push(workTypeId);
        },
        removeWorkType (workTypeId) {
            if (!self.ofEntities.includes(workTypeId)) return;
            const i = self.ofEntities.indexOf(workTypeId);
            self.ofEntities.splice(i, 1);
        },
        setMode (mode) {
            self.mode = mode;
        },
        setName (name) {
            self.name = name;
        },
        setPercent (percent) {
            self.percent = percent;
        },
        setUseState (used) {
            self.used = used;
        },
        setEntities (ofEntities) {
            self.ofEntities = cast(ofEntities);
        },
        setFixedPrice (fixedPrice) {
            self.fixedPrice = fixedPrice;
        },
        setTurnOffState (turnOff) {
            self.turnOff = turnOff;
        },
        setHide (hide) {
            self.hide = hide;
        },
        removeSelf () {
            const root = getRoot(self);
            root.removePriceModifier(self.id);
        }
    }));

const WorkloadAutomationModel = types.model("WorkloadAutomation", {
    workTypeId: types.string,
    percent: types.number,
    ofWorkTypes: types.array(types.string),
    ofSections: types.array(types.string)
}).views((self)=>({
        get parsedPercent () {
            return parseFloat((self.percent / 100).toPrecision(12));
        }
    })).actions((self)=>({
        updateRelatedWorkTypeId (workTypeId, newWorkTypeId) {
            if (!self.ofWorkTypes.includes(workTypeId)) return;
            const i = self.ofWorkTypes.indexOf(workTypeId);
            self.ofWorkTypes[i] = newWorkTypeId;
        },
        setAutomation (percent, ofWorkTypes, ofSections) {
            self.percent = percent;
            self.ofWorkTypes = cast(ofWorkTypes);
            self.ofSections = cast(ofSections);
        },
        removeWorkType (workTypeId) {
            if (!self.ofWorkTypes.includes(workTypeId)) return;
            const i = self.ofWorkTypes.indexOf(workTypeId);
            self.ofWorkTypes.splice(i, 1);
        }
    }));

const SectionWorkTypeResourceModel = types.model("WorkTypeResourceRelation", {
    id: types.string,
    mta: types.maybe(MinAvgMaxValueModel)
}).views((self)=>({
        get root () {
            const root = getRoot(self);
            return root?.settings ? root : DEFAULT_ROOT_OBJECT;
        },
        get parent () {
            try {
                return getParent(self, 2);
            } catch (e) {
                return undefined;
            }
        },
        get section () {
            try {
                return getParent(self, 4);
            } catch (e) {
                return undefined;
            }
        }
    })).views((self)=>({
        get workTypeId () {
            return self.parent.id;
        },
        get parsedMta () {
            return parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (v, key)=>self.mta?.[key] || self.parent && self.root._internalGetWorkTypeResourceMta?.(self.parent?.id, self.id, key) || self.root.settings?.mta?.[key] || DEFAULT_MTA_VALUE);
        },
        get timeModifiedMta () {
            const { timeModifier, dayModifier } = self.root.settings;
            return parseObject(this.parsedMta, (v)=>v / (timeModifier || dayModifier || 1));
        },
        get maxTimePerMonth () {
            const key = self.root.timelineHoursKey;
            return this.parsedMta[key] * MaxTimeModifiers.HOUR;
        },
        get maxTimePerWeek () {
            return Number(Number(this.maxTimePerMonth / MONTH_WEEKS).toPrecision(12));
        },
        serialize () {
            return cleanObject({
                id: self.id,
                mta: self.mta ? {
                    ...self.mta
                } : undefined
            });
        }
    })).actions((self)=>({
        _internalSetMta (mta) {
            const { timeModifier, dayModifier } = self.root.settings;
            const keys = Object.keys(mta);
            const mod = keys.reduce((t, m)=>({
                    ...t,
                    [m]: typeof mta[m] === "number" ? mta[m] * (timeModifier || dayModifier || 1) : null
                }), {});
            self.mta = cast({
                ...self.mta || EMPTY_MIN_AVG_MAX_VALUE,
                ...mod
            });
        },
        setMta (mta) {
            this._internalSetMta(mta);
        },
        resetMta () {
            if (!self.mta) return;
            if (self.root?.settings?.useMinMax) {
                self.mta.min = null;
                self.mta.max = null;
            } else self.mta.avg = null;
            if (Object.values(self.mta).reduce((t, v)=>t && v === null, true)) self.mta = undefined;
        }
    }));

const SectionWorkTypeModel = SectionWorkTypeResourceModel.named("WorkTypeRelation").props({
    invisible: types.maybe(types.boolean),
    percent: types.maybe(types.number),
    of: types.maybe(types.array(types.string)),
    roundType: types.maybe(types.number),
    roundInterval: types.maybe(types.number),
    roundLevel: types.maybe(types.number),
    hideWorkType: types.maybe(types.boolean),
    resources: types.maybe(types.array(types.late(()=>SectionWorkTypeResourceModel)))
}).views((self)=>({
        get parent () {
            try {
                return getParent(self, 2);
            } catch (e) {
                return undefined;
            }
        },
        get isHidden () {
            return Boolean(self.hideWorkType || self.root.getWorkTypeById?.(self.id)?.isHidden);
        },
        get turnOff () {
            return Boolean(self.root.getWorkTypeById?.(self.id)?.turnOff);
        }
    })).views((self)=>({
        serialize () {
            return cleanObject({
                id: self.id,
                invisible: self.invisible,
                percent: self.percent || undefined,
                of: self.of?.length ? [
                    ...self.of
                ] : undefined,
                roundType: self.roundType,
                roundInterval: self.roundInterval,
                roundLevel: self.roundLevel,
                mta: self.mta ? {
                    ...self.mta
                } : undefined,
                resources: self.resources?.map((res)=>res.serialize()),
                hideWorkType: self.hideWorkType
            });
        },
        serializeWithoutAutomation () {
            return cleanObject({
                id: self.id,
                invisible: self.invisible,
                mta: self.mta ? {
                    ...self.mta
                } : undefined,
                resources: self.resources?.map((res)=>res.serialize()),
                hideWorkType: self.hideWorkType
            });
        },
        get workTypeId () {
            return self.id;
        },
        get systemId () {
            return self.root.workTypes?.find((workType)=>workType.id === self.id)?.systemId;
        },
        get name () {
            return self.root.workTypes?.find((workType)=>workType.id === self.id)?.name;
        },
        get color () {
            return self.root.workTypes?.find((workType)=>workType.id === self.id)?.color;
        },
        get hasPercent () {
            return Boolean(self.percent && self.percent > 0 && self.of?.length);
        },
        get hasRounding () {
            return typeof self.roundType === "number" && typeof self.roundInterval === "number" && typeof self.roundLevel === "number";
        },
        get percentageWorkTypes () {
            return self.of?.map((workTypeId)=>pick(self.root.workTypes?.find((workType)=>workType.id === workTypeId), [
                    "id",
                    "name"
                ]));
        },
        get parsedMta () {
            return parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (v, key)=>self.mta?.[key] || self.root._internalGetWorkTypeMta?.(self.id, key) || self.root.settings?.mta?.[key] || DEFAULT_MTA_VALUE);
        },
        getResourceById (resourceId) {
            return self.resources?.find((res)=>res.id === resourceId);
        }
    })).actions((self)=>({
        _internalSetPercent (percent) {
            self.percent = percent;
        },
        setRelationOf (of) {
            self.of = cast(of);
        },
        setAutomation (percent, of) {
            this._internalSetPercent(percent);
            this.setRelationOf(of);
        },
        updateRelatedWorkTypeId (workTypeId, newWorkTypeId) {
            if (!self.of?.includes(workTypeId)) return;
            const i = self.of.indexOf(workTypeId);
            self.of[i] = newWorkTypeId;
        },
        _internalRemoveRelationOf (workTypeId) {
            if (self.of && self.of?.indexOf(workTypeId) >= 0) self.of?.splice(self.of.indexOf(workTypeId), 1);
            if (!self.of?.length) {
                self.of = undefined;
                this._internalSetPercent(0);
            }
        },
        setInvisible (invisible) {
            self.invisible = invisible;
        },
        setRounding (roundType, roundInterval, roundLevel, manuallySet) {
            self.roundType = roundType;
            self.roundInterval = roundInterval;
            self.roundLevel = roundLevel;
            if (manuallySet) self.root._internalClearSummaryWorkTypeRounding?.(self.id);
        },
        setRoundType (roundType) {
            self.roundType = roundType;
        },
        setRoundInterval (roundInterval) {
            self.roundInterval = roundInterval;
        },
        setRoundLevel (roundLevel) {
            self.roundLevel = roundLevel;
        },
        setMta (mta) {
            self._internalSetMta(mta);
        },
        resetMta () {
            if (!self.mta) return;
            if (self.root?.settings?.useMinMax) {
                self.mta.min = null;
                self.mta.max = null;
            } else self.mta.avg = null;
            if (Object.values(self.mta).reduce((t, v)=>t && v === null, true)) self.mta = undefined;
        },
        addResource (id) {
            if (!self.resources?.length) self.resources = cast([]);
            const d = {
                id
            };
            if (self.mta) d.mta = {
                ...self.mta
            };
            self.resources.push(d);
        },
        removeResource (id) {
            if (!self.resources?.length) return;
            const resource = self.resources.find((r)=>r.id === id);
            if (resource) destroy(resource);
            if (!self.resources?.length) {
                self.resources = undefined;
            }
        },
        setHideWorkType (hideWorkType) {
            self.hideWorkType = hideWorkType;
        }
    }));

const TaskDescriptionModel = types.model("TaskDescription", {
    content: types.string,
    showMore: types.maybe(types.boolean)
}).actions((self)=>({
        setContent (content) {
            self.content = content;
        },
        setShowMore (showMore) {
            self.showMore = showMore;
        }
    }));

const TaskModel = types.model("Task", {
    id: types.string,
    type: types.enumeration("TaskTypes", Object.values(TaskTypes)),
    prevType: types.maybe(types.enumeration("TaskTypes", Object.values(TaskTypes))),
    /** utility attribute: */ lvl: types.number,
    /** derived from library component: */ componentId: types.maybe(types.number),
    companyId: types.maybe(types.string),
    name: types.maybe(types.string),
    descriptions: types.maybe(types.map(TaskDescriptionModel)),
    cNum: types.maybe(types.string),
    labels: types.maybe(types.array(types.number)),
    buffers: types.maybe(types.array(BufferModel)),
    fixedPrice: types.maybe(MinAvgMaxValueExtendedModel),
    values: types.maybe(types.map(MinAvgMaxValueExtendedModel)),
    fixedPriceAutomation: types.maybe(FixedPriceAutomationModel),
    workloadAutomation: types.maybe(types.array(WorkloadAutomationModel)),
    generatedStory: types.maybe(types.string),
    units: types.optional(types.maybe(types.number), 0),
    unitPrice: types.optional(types.maybe(types.number), 0),
    unitsName: types.maybe(types.string),
    unitPriceName: types.maybe(types.string),
    turnOff: types.maybe(types.boolean),
    labelTurnOff: types.maybe(types.boolean),
    keepLabelTurnOff: types.maybe(types.boolean),
    isOpened: types.maybe(types.boolean),
    children: types.maybe(types.array(types.late(()=>TaskModel))),
    /** only for library; not serialized: */ libraryStatus: types.maybe(types.number),
    libraryTask: types.maybe(types.boolean),
    /** BT Data; not serialized */ btValues: types.maybe(types.map(types.maybeNull(types.number))),
    /** Temporary params (for search, editor UX and proposals); not serialized */ autoFocus: types.maybe(types.boolean),
    blockChildReorder: types.maybe(types.boolean),
    hasSearchPhrase: types.maybe(types.boolean),
    tmpIsOpened: types.maybe(types.boolean),
    clientTurnOff: types.maybe(types.boolean),
    clientLabelTurnOff: types.maybe(types.boolean)
})/** ================ Core ================ */ .views((self)=>({
        get root () {
            const root = getRoot(self);
            return root;
        },
        get parent () {
            try {
                return getParent(self, 2);
            } catch (e) {
                return undefined;
            }
        },
        get section () {
            try {
                if (self.lvl === BreakdownTableDepths.SECTION) return self;
                const path = "/" + getPathParts(self).slice(0, 2).join("/");
                return resolvePath(this.root, path);
            } catch (e) {
                return undefined;
            }
        }
    }))/** ================ Common getters & helpers ================ */ .views((self)=>({
        get numeration () {
            return getPath(self).replace("/sections/", "").replace(/\/children/g, "").split("/").map((t)=>Number(t) + 1).join(".") + ".";
        },
        get taskWorkTypes () {
            return self.parent?.taskWorkTypes || [];
        },
        get btTaskWorkTypes () {
            return self.parent?.btTaskWorkTypes || [];
        },
        get allTaskWorkTypes () {
            return self.parent?.allTaskWorkTypes || [];
        },
        getTaskWorkType (workTypeId) {
            return this.taskWorkTypes.find((wT)=>wT.id === workTypeId);
        },
        getBreakdownWorkType (workTypeId, onlyNonOffWorkTypes) {
            const m = self.root.workTypes?.find((s)=>s.id === workTypeId);
            if (!m || // (!m.inBreakdown && (m.areHoursOverwritten || m.hasOnlyCost || m.usesSummaryPercent)) ||
            m.hasFixedValues || onlyNonOffWorkTypes && (m.turnOff || m.clientTurnOff)) return;
            // return this.allTaskWorkTypes.find(wT => wT.id === workTypeId);
            return m;
        },
        get defaultSystemValues () {
            return self.parent?.defaultSystemValues || {};
        },
        get sectionModulesHaveChildren () {
            return self.parent?.sectionModulesHaveChildren;
        },
        isValueOverridden (workTypeId, checkAll) {
            const v = self.values?.get(workTypeId.toString());
            if (checkAll) return v?.minMaxOv || v?.avgOv;
            return self.root.settings.useMinMax ? v?.minMaxOv : v?.avgOv;
        },
        get isFixedPriceOverridden () {
            if (!self.fixedPriceAutomation) return false;
            return self.root.settings.useMinMax ? self.fixedPrice?.minMaxOv : self.fixedPrice?.avgOv;
        },
        get siblings () {
            if (!isAlive(self)) return [];
            return self.parent?.children?.filter((m)=>m.id !== self.id)?.map((m)=>({
                    id: m.id,
                    componentId: m.componentId,
                    name: simplifyTextString(m.name)
                })) || [];
        },
        isWorkTypeOff (workTypeId) {
            return self.root.isWorkTypeOff?.(workTypeId);
        },
        get areAllWorkTypesOff () {
            return !this.taskWorkTypes.find((wT)=>!self.root.isWorkTypeOff?.(wT.id));
        },
        get areAllWorkTypesHidden () {
            return !this.taskWorkTypes.find((wT)=>!wT.isHidden);
        },
        get parentId () {
            return self.parent?.id; //|| self.parent?.newId;
        },
        get parentComponentId () {
            return self.parent?.componentId; //|| self.parent?.newId;
        },
        get parentOrder () {
            return self.parent?.children?.findIndex((m)=>self.id && m.id === self.id) || 0; // || (self.newId && m.newId === self.newId));
        },
        get isManuallyTurnedOff () {
            if (self.root?.displayTurnOffLabels?.length) return Boolean(self.labelTurnOff || self.clientLabelTurnOff);
            return Boolean(self.turnOff || self.clientTurnOff);
        },
        get isSelfTurnedOff () {
            if (self.type === TaskTypes.FIXED_PRICE) return self.root?.settings?.turnOffFixedPrice || this.isManuallyTurnedOff;
            if (self.type === TaskTypes.MATERIAL) return self.root?.settings?.turnOffMaterials || this.isManuallyTurnedOff;
            return this.isManuallyTurnedOff;
        },
        get isTurnedOff () {
            return this.isSelfTurnedOff || this.allChildrenOff || this.parentIsOff;
        },
        get allFixedPricesOff () {
            if (self.type === TaskTypes.FIXED_PRICE) return this.isTurnedOff;
            if (!self.children?.length || this.isTurnedOff) return true;
            return !self.children.find((child)=>child.type === TaskTypes.FIXED_PRICE && !child.isTurnedOff || !child.allFixedPricesOff);
        // return self.children.reduce((allChildrenTurnedOff, child) => (
        //   allChildrenTurnedOff && (
        //     (child.type === TaskTypes.FIXED_PRICE && child.isManuallyTurnedOff)
        //     || child.allFixedPricesOff
        //   )
        // ), true);
        },
        get allMaterialsOff () {
            if (self.type === TaskTypes.MATERIAL) return this.isTurnedOff;
            if (!self.children?.length || this.isTurnedOff) return true;
            return !self.children.find((child)=>child.type === TaskTypes.MATERIAL && !child.isTurnedOff || !child.allMaterialsOff);
        // return self.children.reduce((allChildrenTurnedOff, child) => (
        //   allChildrenTurnedOff && (
        //     (child.type === TaskTypes.MATERIAL && child.isManuallyTurnedOff)
        //     || child.allMaterialsOff
        //   )
        // ), true);
        },
        get isSavedTurnedOff () {
            return (self.root?.displayTurnOffLabels?.length ? self.labelTurnOff : self.turnOff) || this.allChildrenSavedOff || this.parentIsSavedOff;
        },
        get isSelfOrChildrenManuallyTurnedOff () {
            return this.isManuallyTurnedOff || this.allChildrenManuallyOff;
        },
        get isSelfOrChildrenTurnedOff () {
            return this.isSelfTurnedOff || this.allChildrenOff;
        },
        get parentIsOff () {
            return self.parent?.isSelfTurnedOff || self.parent?.parentIsOff;
        },
        get parentIsSavedOff () {
            return self.parent?.turnOff || self.parent?.labelTurnOff || self.parent?.parentIsSavedOff;
        },
        get allChildrenOff () {
            return self.children?.length ? self.children.reduce((allChildrenTurnedOff, child)=>allChildrenTurnedOff && child.isSelfOrChildrenTurnedOff, true) : false;
        },
        get allChildrenManuallyOff () {
            return self.children?.length ? self.children.reduce((allChildrenTurnedOff, child)=>allChildrenTurnedOff && child.isSelfOrChildrenManuallyTurnedOff, true) : false;
        },
        get allChildrenSavedOff () {
            return self.children?.length ? self.children.reduce((allChildrenTurnedOff, child)=>allChildrenTurnedOff && (child.turnOff || child.allChildrenSavedOff), true) : false;
        },
        get hasLabels () {
            return Boolean(self.labels && self.labels.length > 0);
        },
        get childrenHaveLabels () {
            return Boolean(self.children?.find((child)=>child.hasLabels || child.childrenHaveLabels));
        },
        get allChildrenHaveLabels () {
            if (!self.children?.length) return false;
            return self.children.filter((child)=>child.hasLabels || child.childrenHaveLabels).length === self.children.length;
        },
        get hasFixedPrice () {
            return self.type === TaskTypes.FIXED_PRICE && (self.root.settings.useMinMax ? !!(self.fixedPrice?.min || self.fixedPrice?.max) : !!self.fixedPrice?.avg);
        },
        get parentHoldsFixedPrice () {
            return self.parent?.id ? self.parent.hasFixedPrice || self.parent.parentHoldsFixedPrice : false;
        },
        get hasFixedPriceElements () {
            return Boolean(self.children?.find((child)=>child.type === TaskTypes.FIXED_PRICE || child.hasFixedPriceElements));
        },
        get childrenHaveFixedPrice () {
            return Boolean(self.children?.find((child)=>child.hasFixedPrice || child.childrenHaveFixedPrice));
        },
        get hasMaterialPrice () {
            return self.type === TaskTypes.MATERIAL && Boolean(self.units || self.unitPrice);
        },
        get parentHoldsMaterialPrice () {
            return self.parent?.id ? self.parent.hasMaterialPrice || self.parent.parentHoldsMaterialPrice : false;
        },
        get hasMaterialElements () {
            return Boolean(self.children?.find((child)=>child.type === TaskTypes.MATERIAL || child.hasMaterialElements));
        },
        get childrenHaveMaterialPrice () {
            return Boolean(self.children?.find((child)=>child.hasMaterialPrice || child.childrenHaveMaterialPrice));
        },
        get hasChildren () {
            return self.children && self.children.length > 0;
        },
        get hasMaterialChildren () {
            return Boolean(self.children && self.children?.find((child)=>child.hasMaterialCell));
        },
        get hasDescriptions () {
            if (!self.descriptions?.size) return {};
            const d = {};
            const iterator = self.descriptions[Symbol.iterator]();
            for (const [id, { content }] of iterator){
                if (Boolean(content) && !isEmptyHtml(content)) d[id] = true;
            }
            return d;
        },
        get childrenWithDescriptions () {
            const viewLevel = self.root.estimateViewLevel;
            if (self.lvl && viewLevel <= self.lvl) return {};
            return self.children?.reduce((total, child)=>merge(total, child.childrenWithDescriptions, child.hasDescriptions), {}) || {};
        },
        get hasAnyDescriptions () {
            return Object.values(this.hasDescriptions).includes(true);
        },
        get hasMaterialCell () {
            return self.type === TaskTypes.MATERIAL || this.hasMaterialChildren;
        },
        // get hasChildrenHours() {
        //   // if (!this.hasFixedPrice || this.hasCustomPrice) return false;
        //   if(self.type === TaskTypes.WORKLOAD)
        //     return Boolean(
        //       self.children?.find(ch => ch.hasChildrenHours || !ch.hasFixedPrice),
        //     );
        //   return false;
        // },
        getExistingChild (id, componentId, name) {
            return self.children?.find((child)=>[
                    child.id,
                    child.newId
                ].includes(id) || componentId && child.componentId === componentId || simplifyTextString(child.name) === simplifyTextString(name));
        },
        get parentHoldsAllHours () {
            if (!self.parent?.id || self.libraryTask && typeof self.parent.id === "string") return false;
            return Boolean(self.parent.holdsAllHours || self.parent.parentHoldsAllHours);
        },
        get parentHoldsHours () {
            if (!isAlive(self)) return {};
            if (!self.parent?.id || self.libraryTask && typeof self.parent.id === "string") return {};
            return sumObjectsData(self.parent.holdsViewedHours, self.parent.parentHoldsHours);
        },
        get parentHoldsNonOverriddenPercentHours () {
            if (!self.parent?.id) return {};
            return sumObjectsData(self.parent.holdsNonOverriddenPercentHours, self.parent.parentHoldsNonOverriddenPercentHours);
        },
        get lowestLevelChildren () {
            return self.children?.reduce((total, child)=>total + child.lowestLevelChildren, 0) || 1;
        },
        getSibling (name, id, componentId) {
            return this.siblings?.find((child)=>id && child.id === id || componentId && child.componentId === componentId || child.name === name);
        },
        hasSibling (name, id, componentId) {
            return !!this.getSibling(simplifyTextString(name), id, componentId);
        },
        get canGenerateUserStory () {
            return !self.generatedStory || !self.name || self.generatedStory.toLocaleLowerCase() !== self.name.toLowerCase();
        },
        getChildById (id) {
            return self.children?.find((child)=>child.id === id);
        },
        get treePath () {
            return self.parent?.treePath ? [
                ...self.parent.treePath,
                self.id
            ] : [
                self.id
            ];
        },
        get includesSearchPhrase () {
            return Boolean(self.hasSearchPhrase || self.children?.find((c)=>c.includesSearchPhrase));
        },
        get namedLabels () {
            return self.root.addedLabels?.filter((l)=>self.labels?.includes(l.systemId)) || [];
        },
        get usedLabels () {
            const filterByTurnOff = self.root.excludeTurnedOffElements;
            if (filterByTurnOff && this.isSavedTurnedOff) return [];
            return union(self.labels || [], ...self.children?.map((c)=>c.usedLabels || []) || []);
        },
        getDuplicateName (name) {
            let newIndex = getDuplicateIndex(name);
            let newName = getDuplicateName(name, newIndex);
            // checks if sibling with updated name already exists
            while(this.hasSibling(newName)){
                newIndex++;
                newName = newName?.replace(/\((\d+)\)$/, `(${newIndex})`);
            }
            return newName;
        }
    }))/** ================ Automation getters ================ */ .views((self)=>({
        get percentageWorkTypes () {
            return self.parent?.percentageWorkTypes || [];
        },
        // get percentageWorkTypeTargets() {
        //   return self.parent?.percentageWorkTypeTargets || [];
        // },
        get hasAutomatedFixedPrice () {
            return Boolean(self.fixedPriceAutomation) || Boolean(self.children?.find((c)=>c.hasAutomatedFixedPrice));
        },
        hasCellAutomatedWorkType (workTypeId) {
            return Boolean(this.cellAutomatedWorkTypes.find((a)=>a.id === workTypeId));
        },
        get childCellAutomatedWorkTypes () {
            return self.children?.reduce((acc, child)=>{
                child.cellAutomatedWorkTypes.forEach((wT)=>{
                    const ex = acc.find((e)=>e.id === wT.id);
                    if (ex) Object.assign(ex, {
                        of: uniq([
                            ...ex.of || [],
                            ...wT.of || []
                        ]),
                        counter: ex.counter + wT.counter
                    });
                    else acc.push({
                        ...wT
                    });
                });
                return acc;
            }, []) || [];
        },
        get cellAutomatedWorkTypes () {
            return self.allTaskWorkTypes.reduce((aWT, wT)=>{
                const childAutomation = this.childCellAutomatedWorkTypes.find((a)=>a.id === wT.id);
                const selfAutomation = self.workloadAutomation?.find((a)=>a.workTypeId === wT.id);
                if (selfAutomation || childAutomation) aWT.push({
                    id: wT.id,
                    counter: (selfAutomation ? 1 : 0) + (childAutomation?.counter || 0),
                    of: uniq([
                        ...childAutomation?.of || [],
                        ...selfAutomation?.ofWorkTypes || []
                    ])
                });
                return aWT;
            }, []);
        },
        hasWorkloadCellAutomation (workTypeId) {
            return Boolean(this.cellAutomatedWorkTypes?.find((a)=>a.id === workTypeId));
        },
        hasChildWorkloadCellAutomation (workTypeId) {
            return Boolean(this.childCellAutomatedWorkTypes?.find((a)=>a.id === workTypeId));
        },
        hasOwnWorkloadCellAutomation (workTypeId) {
            return Boolean(self.workloadAutomation?.find((a)=>a.workTypeId === workTypeId));
        },
        get hasFixedPriceAutomation () {
            return Boolean(self.fixedPriceAutomation || self.children?.find((child)=>child.hasFixedPriceAutomation));
        },
        get hasOwnFixedPriceAutomation () {
            return Boolean(self.fixedPriceAutomation);
        }
    }))/** ================ Buffer getters & helpers ================ */ .views((self)=>({
        get parentBuffers () {
            return self?.parent?.breakdownBuffers || [];
        },
        get breakdownBuffers () {
            return [
                ...this.parentBuffers,
                ...self?.buffers || []
            ];
        },
        get elementBuffers () {
            return [
                ...self.root.settings?.buffers || [],
                ...this.breakdownBuffers
            ];
        },
        get childUsedBuffers () {
            return self.children?.reduce((buffers, child)=>[
                    ...buffers,
                    ...(child?.buffers || []).filter((b)=>b.used),
                    ...child?.childUsedBuffers || []
                ], []) || [];
        },
        get childPreviouslyUsedBuffers () {
            return self.children?.reduce((buffers, child)=>[
                    ...buffers,
                    ...(child?.buffers || []).filter((b)=>b.used || b.wasUsed),
                    ...child?.childPreviouslyUsedBuffers || []
                ], []) || [];
        },
        get usedBuffers () {
            return this.elementBuffers.filter((b)=>b.used);
        },
        get usedBranchBuffers () {
            return [
                ...this.usedBuffers,
                ...this.childUsedBuffers
            ];
        },
        hasUsedWorkTypeBuffers (workTypeId) {
            let checks = [];
            if (typeof workTypeId === "number") checks = [
                workTypeId
            ];
            else checks = self.taskWorkTypes.map((wT)=>wT.id);
            return this.usedBranchBuffers.filter((b)=>b.percent !== 0 && b.workTypes.length > 0 && intersection(b.workTypes, checks).length).length > 0;
        }
    }))/** ================ Rounding getters & helpers ================ */ .views((self)=>({
        get usedWorkTypeRoundings () {
            return self.allTaskWorkTypes.filter((wT)=>wT.hasRounding && typeof self.lvl === "number" && wT.roundLevel >= self.lvl).map((wT)=>({
                    workTypeId: wT.id,
                    type: wT.roundType,
                    level: wT.roundLevel,
                    interval: wT.roundInterval
                }));
        }
    }))/** ================ Value getters & helpers ================ */ .views((self)=>({
        gatherForcedStateWorkTypes (workTypeId) {
            const states = {};
            if (self.type !== TaskTypes.WORKLOAD) return states;
            const availableChildren = self.children?.filter((child)=>child.type === TaskTypes.WORKLOAD && !child.isSelfOrChildrenTurnedOff) || [];
            self.allTaskWorkTypes.forEach((workType)=>{
                states[workType.id] = undefined;
                // if (self.root.settings.maxValuesAsBuffer) return;
                const selfState = self.values?.get(workType.id.toString())?.forcedState;
                const childStates = availableChildren.map((child)=>(typeof workTypeId === "number" ? child.taskValuesWithoutAutomatedWorkType(workType.id) : child.taskValues)?.get(workType.id.toString())?.forcedState) || [];
                if (selfState === WorkTypeValueState.MIN_MAX || childStates.includes(WorkTypeValueState.MIN_MAX)) states[workType.id] = WorkTypeValueState.MIN_MAX;
                else if (self.root.displayTwoValues && (selfState === WorkTypeValueState.SINGLE || availableChildren.length && childStates.filter((s)=>s === WorkTypeValueState.SINGLE).length === availableChildren.length)) states[workType.id] = WorkTypeValueState.SINGLE;
            });
            return states;
        },
        get forcedStateWorkTypes () {
            return this.gatherForcedStateWorkTypes();
        },
        forcedStateWorkTypesWithoutAutomatedWorkType (workTypeId) {
            return this.gatherForcedStateWorkTypes(workTypeId);
        },
        get fixedPriceForcedState () {
            if (self.root.settings.maxValuesAsBuffer) return;
            const availableChildren = (self.allFixedPricesOff ? self.children : self.children?.filter((child)=>!child.isSelfOrChildrenTurnedOff)) || [];
            const selfState = self.type === TaskTypes.FIXED_PRICE && self.fixedPrice?.forcedState;
            const childStates = availableChildren.map((child)=>child.calculatedFixedPriceSummary?.forcedState) || [];
            if (selfState === WorkTypeValueState.MIN_MAX || childStates.includes(WorkTypeValueState.MIN_MAX)) return WorkTypeValueState.MIN_MAX;
            else if (self.root.displayTwoValues && (selfState === WorkTypeValueState.SINGLE || availableChildren.length && childStates.filter((s)=>s === WorkTypeValueState.SINGLE).length === availableChildren.length)) return WorkTypeValueState.SINGLE;
            return;
        },
        get nonAutomatedFixedPriceForcedState () {
            if (self.root.settings.maxValuesAsBuffer || self.fixedPriceAutomation) return;
            const availableChildren = self.children?.filter((child)=>!child.isSelfOrChildrenTurnedOff) || [];
            const selfState = self.type === TaskTypes.FIXED_PRICE && self.fixedPrice?.forcedState;
            const childStates = availableChildren.map((child)=>child.nonAutomatedCalculatedFixedPrice?.forcedState) || [];
            if (selfState === WorkTypeValueState.MIN_MAX || childStates.includes(WorkTypeValueState.MIN_MAX)) return WorkTypeValueState.MIN_MAX;
            else if (self.root.displayTwoValues && (selfState === WorkTypeValueState.SINGLE || availableChildren.length && childStates.filter((s)=>s === WorkTypeValueState.SINGLE).length === availableChildren.length)) return WorkTypeValueState.SINGLE;
            return;
        },
        get workloadForcedState () {
            if (self.root.settings.maxValuesAsBuffer) return;
            const vals = uniq(Object.values(this.forcedStateWorkTypes));
            if (vals.find((v)=>v === WorkTypeValueState.MIN_MAX)) return WorkTypeValueState.MIN_MAX;
            if (self.root.displayTwoValues && vals.length && vals.filter((v)=>v === WorkTypeValueState.SINGLE).length === vals.length) return WorkTypeValueState.SINGLE;
            return;
        },
        get priceForcedState () {
            if (self.root.settings.maxValuesAsBuffer) return;
            if (this.fixedPriceForcedState === WorkTypeValueState.MIN_MAX || this.workloadForcedState === WorkTypeValueState.MIN_MAX) return WorkTypeValueState.MIN_MAX;
            if (self.root.displayTwoValues && this.fixedPriceForcedState === WorkTypeValueState.SINGLE && this.workloadForcedState === WorkTypeValueState.SINGLE) return WorkTypeValueState.SINGLE;
            return;
        },
        get localValuesWithBuffers () {
            if (!self.values) return new Map();
            if (!self.usedBuffers?.length) return self.values;
            const { modifier } = self.root?.settings || {};
            return new Map(Object.entries(buffersInTaskValuesMap(self.values, self.usedBuffers, modifier)));
        },
        localValuesWithBuffersWithoutAutomatedWorkType (workTypeId) {
            if (!self.hasOwnWorkloadCellAutomation(workTypeId)) return this.localValuesWithBuffers;
            if (!self.values) return new Map();
            if (!self.usedBuffers?.length) return new Map(Object.entries(pickNonAutomatedTaskValuesMap(self.values, workTypeId)));
            const { modifier } = self.root?.settings || {};
            return new Map(Object.entries(buffersInTaskValuesMap(self.values, self.usedBuffers, modifier, workTypeId)));
        },
        get childrenTaskValues () {
            if (!self.children?.length) return;
            return sumTaskChildrenDataV2(self.children.filter((child)=>self.allChildrenOff || !child.isSelfOrChildrenTurnedOff).map((child)=>child.taskValues), this.forcedStateWorkTypes, self.root.settings.useMinMax, self.root.settings.maxValuesAsBuffer);
        },
        childrenTaskValuesWithoutAutomatedWorkType (workTypeId) {
            if (!self.children?.length) return;
            return sumTaskChildrenDataV2(self.children.filter((child)=>self.allChildrenOff || !child.isSelfOrChildrenTurnedOff).map((child)=>child.taskValuesWithoutAutomatedWorkType(workTypeId)), this.forcedStateWorkTypesWithoutAutomatedWorkType(workTypeId), self.root.settings.useMinMax, self.root.settings.maxValuesAsBuffer);
        },
        get taskValues () {
            if (self.type !== TaskTypes.WORKLOAD) return new Map();
            const modifier = self.root.settings.modifier;
            const values = this.childrenTaskValues ? pickHigherTaskValue(this.childrenTaskValues, this.localValuesWithBuffers, self.allTaskWorkTypes.map((wT)=>wT.id.toString()), self.percentageWorkTypes, self.libraryTask) : this.localValuesWithBuffers;
            if (self.usedWorkTypeRoundings?.length) {
                return new Map(Object.entries(hardRoundingInTaskValuesMap(values, modifier, self.usedWorkTypeRoundings)));
            }
            return values;
        },
        taskValuesWithoutAutomatedWorkType (workTypeId) {
            if (self.type !== TaskTypes.WORKLOAD) return new Map();
            const modifier = self.root.settings.modifier;
            const values = self.children?.length ? pickHigherTaskValue(this.childrenTaskValuesWithoutAutomatedWorkType(workTypeId), this.localValuesWithBuffersWithoutAutomatedWorkType(workTypeId), self.allTaskWorkTypes.map((wT)=>wT.id.toString()), self.percentageWorkTypes, self.libraryTask) : this.localValuesWithBuffersWithoutAutomatedWorkType(workTypeId);
            if (self.usedWorkTypeRoundings?.length) {
                return new Map(Object.entries(hardRoundingInTaskValuesMap(values, modifier, self.usedWorkTypeRoundings)));
            }
            return values;
        },
        get btTaskValues () {
            if (self.type !== TaskTypes.WORKLOAD) return new Map();
            return self.children?.length ? pickHigherBTTaskValue(sumBTData(...self.children.filter((child)=>self.allChildrenOff || !child.isSelfOrChildrenTurnedOff).map((child)=>child.btTaskValues)), self.btValues || new Map()) : self.btValues || new Map();
        },
        get unbufferedTaskValues () {
            if (self.type !== TaskTypes.WORKLOAD) return new Map();
            return self.children?.length ? pickHigherTaskValue(sumTaskChildrenDataV2(self.children.filter((child)=>self.allChildrenOff || !child.isSelfOrChildrenTurnedOff).map((child)=>child.unbufferedTaskValues), this.forcedStateWorkTypes, self.root.settings.useMinMax, self.root.settings.maxValuesAsBuffer), new Map(self.values), self.allTaskWorkTypes.map((wT)=>wT.id.toString()), self.percentageWorkTypes, self.libraryTask) : self.values || new Map();
        },
        get nonZeroValues () {
            const o = {};
            if (!this.taskValues) return o;
            const iterator = this.taskValues[Symbol.iterator]();
            for (const [key, values] of iterator){
                if (values?.min || values?.max || values?.avg) {
                    const minMax = !!(values?.min || values?.max);
                    o[Number(key)] = {
                        min: minMax,
                        avg: !!values?.avg,
                        max: minMax
                    };
                }
            }
            return o;
        },
        get holdsHours () {
            const o = {};
            if (!self.values) return o;
            const iterator = self.values[Symbol.iterator]();
            for (const [key, values] of iterator){
                if (values?.min || values?.max || values?.avg) {
                    const minMax = !!(values?.min || values?.max);
                    if (self.libraryTask || self.percentageWorkTypes?.find((wT)=>wT.id.toString() === key)) {
                        const childrenVal = this.childrenTaskValues?.get(key.toString());
                        const childMinMax = minMax && !(childrenVal?.min || childrenVal?.max);
                        o[key] = {
                            min: childMinMax,
                            avg: !!values?.avg && !childrenVal?.avg,
                            max: childMinMax
                        };
                    } else o[key] = {
                        min: minMax,
                        avg: !!values?.avg,
                        max: minMax
                    };
                }
            }
            return o;
        },
        get holdsViewedHours () {
            const { useMinMax } = self.root.settings;
            const o = {};
            Object.keys(this.holdsHours).forEach((id)=>{
                const v = this.holdsHours[id];
                if (useMinMax ? v?.min : v?.avg) o[id] = true;
            });
            return o;
        },
        get holdsAllHours () {
            return Object.keys(this.holdsHours).length === Object.keys(this.nonZeroValues).length;
        },
        get holdsNonOverriddenPercentHours () {
            const { useMinMax } = self.root.settings;
            const o = {};
            self.percentageWorkTypes.forEach(({ id })=>{
                const holdsHours = this.holdsHours[id];
                if (!holdsHours) return;
                const v = self.values?.get(id.toString());
                if (useMinMax ? (holdsHours.min || holdsHours.max) && !v?.minMaxOv : holdsHours.avg && !v?.avgOv) o[id] = true;
            });
            return o;
        },
        get hasValues () {
            const iterator = this.taskValues[Symbol.iterator]();
            for (const item of iterator){
                const nonZeroValue = Object.values(item[1]).find((value)=>typeof value === "number" && value > 0);
                if (nonZeroValue) return true;
            }
            return false;
        },
        getWorkTypeForcedState (workTypeId) {
            return this.taskValues?.get(workTypeId.toString())?.forcedState;
        },
        get displayWorkTypeTwoValues () {
            return self.allTaskWorkTypes.reduce((states, workType)=>({
                    ...states,
                    [workType.id]: self.root.settings.maxValuesAsBuffer ? Boolean(self.usedBranchBuffers?.find((b)=>b.workTypes?.includes(workType.id))) : self.root.displayTwoValues && this.forcedStateWorkTypes[workType.id] !== WorkTypeValueState.SINGLE || !self.root.settings.useMinMax && this.forcedStateWorkTypes[workType.id] === WorkTypeValueState.MIN_MAX
                }), {});
        },
        get displayWorkloadTwoValues () {
            if (self.type === TaskTypes.MATERIAL) return false;
            if (self.root.settings.maxValuesAsBuffer) return Boolean(self.usedBranchBuffers?.find((b)=>intersection(b.workTypes, self.taskWorkTypes.map((wT)=>wT.id)).length > 0));
            return self.root.displayTwoValues && this.workloadForcedState !== WorkTypeValueState.SINGLE || !self.root.settings.useMinMax && this.workloadForcedState === WorkTypeValueState.MIN_MAX;
        },
        get displayPriceTwoValues () {
            if (self.type === TaskTypes.MATERIAL) return false;
            if (self.root.settings.maxValuesAsBuffer) return Boolean(self.usedBranchBuffers?.find((b)=>intersection(b.workTypes, self.taskWorkTypes.map((wT)=>wT.id)).length > 0));
            return self.root.displayTwoValues && this.priceForcedState !== WorkTypeValueState.SINGLE || !self.root.settings.useMinMax && this.priceForcedState === WorkTypeValueState.MIN_MAX;
        }
    }))/** ================ Buffer values getters & helpers ================ */ .views((self)=>({
        getBufferedValue (workTypeId, key, valueModifier) {
            let bVal = {};
            if (self.holdsHours[workTypeId]?.[key]) {
                const value = self.values?.get(workTypeId.toString())?.[key];
                if (value) {
                    bVal = self.usedBuffers.filter((b)=>b.workTypes.includes(workTypeId)).reduce((res, buffer)=>({
                            ...res,
                            [buffer.id]: {
                                name: buffer.name,
                                itemBuffer: buffer.lvl >= BreakdownTableDepths.SECTION,
                                value: Number((safeRoundHours(value * buffer.parsedPercent) * (valueModifier || 1)).toPrecision(12))
                            }
                        }), {});
                }
                return bVal;
            }
            self.children?.forEach((child)=>{
                bVal = mergeWith(bVal, child.getBufferedValue(workTypeId, key, valueModifier), mergeBufferFunc);
            });
            return bVal;
        },
        getAllBufferedValues (key) {
            let bVal = {};
            self.taskWorkTypes.forEach((workType)=>{
                const workTypeKey = self.root.settings.maxValuesAsBuffer || self.forcedStateWorkTypes[workType.id] === WorkTypeValueState.SINGLE || !self.root.settings.useMinMax && self.forcedStateWorkTypes[workType.id] !== WorkTypeValueState.MIN_MAX ? MinAvgMaxKeys.AVG : key;
                bVal = mergeWith(bVal, this.getBufferedValue(workType.id, workTypeKey), mergeBufferFunc);
            });
            return bVal;
        },
        getAllBufferedPrices (key) {
            const { workTypes } = self.root;
            let bVal = {};
            self.taskWorkTypes.forEach((workType)=>{
                const rate = workTypes?.find((s)=>s.id === workType.id)?.averageRate[key];
                if (!rate) return;
                const workTypeKey = self.root.settings.maxValuesAsBuffer || self.forcedStateWorkTypes[workType.id] === WorkTypeValueState.SINGLE || !self.root.settings.useMinMax && self.forcedStateWorkTypes[workType.id] !== WorkTypeValueState.MIN_MAX ? MinAvgMaxKeys.AVG : key;
                bVal = mergeWith(bVal, this.getBufferedValue(workType.id, workTypeKey, rate), mergeBufferFunc);
            });
            return bVal;
        }
    }))/** ================ Serializers ================ */ .views((self)=>({
        serialize (defaultNameForBlanks, useOldDescriptionFormat) {
            const o = cleanObject({
                id: self.id,
                type: self.type,
                prevType: self.prevType,
                lvl: self.lvl,
                componentId: self.componentId,
                companyId: self.companyId,
                name: self.name || defaultNameForBlanks,
                descriptions: self.descriptions?.toJSON(),
                cNum: self.cNum,
                labels: self.labels ? [
                    ...self.labels
                ] : undefined,
                buffers: self.buffers?.map((b)=>b.serialize()),
                fixedPrice: self.fixedPrice ? {
                    ...self.fixedPrice
                } : undefined,
                values: self.values ? serializeMapWithObjects(self.values, true) : undefined,
                fixedPriceAutomation: self.fixedPriceAutomation ? {
                    ...self.fixedPriceAutomation
                } : undefined,
                workloadAutomation: self.workloadAutomation?.map((b)=>cleanObject({
                        ...getSnapshot(b)
                    })),
                generatedStory: self.generatedStory || undefined,
                units: self.units || undefined,
                unitPrice: self.unitPrice || undefined,
                unitsName: self.unitsName,
                unitPriceName: self.unitPriceName,
                turnOff: self.turnOff,
                labelTurnOff: self.labelTurnOff,
                keepLabelTurnOff: self.keepLabelTurnOff,
                isOpened: self.isOpened,
                children: self.children?.map((child)=>child.serialize(defaultNameForBlanks, useOldDescriptionFormat))
            });
            if (useOldDescriptionFormat && o.descriptions && o.descriptions[DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER]?.content) {
                o.content = o.descriptions[DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER]?.content || "";
            }
            return o;
        }
    }))/** ================ Hours getters ================ */ .views((self)=>({
        getUnbufferedTotalHoursPerWorkType (workTypeId) {
            return {
                ...DEFAULT_MIN_AVG_MAX_VALUE,
                ...self.unbufferedTaskValues?.get(workTypeId.toString()) || {}
            };
        },
        getTotalHoursPerWorkType (workTypeId) {
            if (workTypeId === BT_MERGED_VALUE) {
                const objectsToSum = self.root?.settings.btWorkTypes?.map((workType)=>self.taskValues?.get(workType.toString())) || [];
                const filteredObjectsToSum = objectsToSum.filter((obj)=>obj !== undefined);
                return sumObjectsData(...filteredObjectsToSum);
            }
            return {
                ...DEFAULT_MIN_AVG_MAX_VALUE,
                ...self.taskValues?.get(workTypeId.toString()) || {}
            };
        },
        get combinedHours () {
            if (!isAlive(self) || !self.taskWorkTypes?.length) return DEFAULT_MIN_AVG_MAX_VALUE;
            return sumTaskValues(self.taskWorkTypes.map((workType)=>this.getTotalHoursPerWorkType(workType.id)), self.workloadForcedState, self.root.settings.useMinMax);
        },
        get combinedUnbufferedHours () {
            if (!isAlive(self) || !self.taskWorkTypes?.length) return DEFAULT_MIN_AVG_MAX_VALUE;
            return sumTaskValues(self.taskWorkTypes.map((workType)=>this.getUnbufferedTotalHoursPerWorkType(workType.id)), self.workloadForcedState, self.root.settings.useMinMax);
        }
    }))/** ================ Price subtotals' getters ================ */ .views((self)=>({
        calculateMaterialPrice (allowValuesWhenAllTurnedOff) {
            if (self.type === TaskTypes.MATERIAL) {
                const selfMaterialPrice = (self.units || 0) * (self.unitPrice || 0);
                if (selfMaterialPrice) return selfMaterialPrice;
            }
            return self.children?.filter((child)=>allowValuesWhenAllTurnedOff ? self.root?.allMaterialBreakdownElementsOff || !child.isSelfOrChildrenManuallyTurnedOff : self.allChildrenOff || !child.isSelfOrChildrenTurnedOff)?.reduce((total, child)=>{
                if (allowValuesWhenAllTurnedOff) return total + child.calculatedMaterialPriceSummary;
                return total + child.calculatedMaterialPrice;
            }, 0) || 0;
        },
        get calculatedMaterialPrice () {
            return this.calculateMaterialPrice();
        },
        get calculatedMaterialPriceSummary () {
            return this.calculateMaterialPrice(true);
        },
        calculateFixedPrice (allowValuesWhenAllTurnedOff, countOnlyNonAutomatedValues) {
            const { useMinMax } = self.root?.settings || {};
            const forcedState = countOnlyNonAutomatedValues ? self.nonAutomatedFixedPriceForcedState : self.fixedPriceForcedState;
            const childrenFixedPrice = sumTaskValues(self.children?.filter((child)=>{
                if (countOnlyNonAutomatedValues) return !child.isTurnedOff;
                if (allowValuesWhenAllTurnedOff) return self.root?.allFixedPriceBreakdownElementsOff || !child.isSelfOrChildrenManuallyTurnedOff;
                return self.allChildrenOff || !child.isSelfOrChildrenTurnedOff;
            })?.map((child)=>{
                if (countOnlyNonAutomatedValues) return child.nonAutomatedCalculatedFixedPrice;
                if (allowValuesWhenAllTurnedOff) return child.calculatedFixedPriceSummary;
                return child.calculatedFixedPrice;
            }) || [], forcedState, useMinMax);
            let selfFixedPrice = {};
            if (self.type === TaskTypes.FIXED_PRICE && self.fixedPrice && (!countOnlyNonAutomatedValues || !self.fixedPriceAutomation)) selfFixedPrice = self.fixedPrice || {};
            const priceObj = parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (_, key)=>selfFixedPrice[key] || childrenFixedPrice[key]);
            priceObj.forcedState = forcedState;
            return priceObj;
        },
        get calculatedFixedPrice () {
            return this.calculateFixedPrice();
        },
        get calculatedFixedPriceSummary () {
            return this.calculateFixedPrice(true);
        },
        get nonAutomatedCalculatedFixedPrice () {
            return this.calculateFixedPrice(false, true);
        }
    }))/** ================ Price getters ================ */ .views((self)=>({
        getTotalPricePerWorkType (workTypeId, useUnbufferedWorkloadValues) {
            const m = self.getBreakdownWorkType(workTypeId, true);
            if (!m) return {};
            const val = useUnbufferedWorkloadValues ? self.getUnbufferedTotalHoursPerWorkType(workTypeId) : self.getTotalHoursPerWorkType(workTypeId);
            const priceObj = parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (_, key)=>safeRoundHours((m.averageRate[key] || 0) * (val?.[key] || 0), 1));
            if (self.forcedStateWorkTypes?.[workTypeId]) priceObj.forcedState = self.forcedStateWorkTypes[workTypeId];
            return priceObj;
        },
        getTotalPricePerWorkTypeForKey (workTypeId, key, useTurnedOffWT) {
            const m = self.getBreakdownWorkType(workTypeId, !useTurnedOffWT);
            if (!m) return 0;
            const val = self.getTotalHoursPerWorkType(workTypeId);
            return safeRoundHours((m.averageRate[key] || 0) * (val?.[key] || 0), 1);
        },
        calculatePrice (useUnbufferedWorkloadValues, countOnlyNonAutomatedValues) {
            if (!isAlive(self)) return {};
            const usedWorkTypes = self.root.settings.showPriceFromHiddenWorkTypes ? self.allTaskWorkTypes : self.taskWorkTypes;
            const values = self.type === TaskTypes.WORKLOAD ? usedWorkTypes.map((workType)=>this.getTotalPricePerWorkType(workType.id, useUnbufferedWorkloadValues)) : [];
            if (countOnlyNonAutomatedValues) {
                if (self.nonAutomatedCalculatedFixedPrice) values.push(self.nonAutomatedCalculatedFixedPrice);
            } else {
                if (self.calculatedFixedPrice) values.push(self.calculatedFixedPrice);
            }
            if (!values.length) return {};
            return sumTaskValues(values, self.priceForcedState, self.root.settings.useMinMax);
        },
        get calculatedPrice () {
            return this.calculatePrice();
        },
        get calculatedUnbufferedPrice () {
            return this.calculatePrice(true);
        },
        get nonAutomatedCalculatedPrice () {
            return this.calculatePrice(false, true);
        },
        get calculatedWorkloadPrice () {
            if (!isAlive(self) || self.type !== TaskTypes.WORKLOAD) return {};
            const usedWorkTypes = self.root.settings.showPriceFromHiddenWorkTypes ? self.allTaskWorkTypes : self.taskWorkTypes;
            return sumTaskValues(usedWorkTypes.map((workType)=>this.getTotalPricePerWorkType(workType.id)), self.workloadForcedState, self.root.settings.useMinMax);
        }
    }))/** ================ Data display getters ================ */ .views((self)=>({
        get displayNumeration () {
            return self.cNum || self.numeration;
        },
        get displayChildren () {
            const filterBySearchPhrase = self.root.hasSearchPhrase && self.lvl === 0;
            const filterByTurnOff = self.root.excludeTurnedOffElements;
            if (filterBySearchPhrase || filterByTurnOff) return self.children?.filter((s)=>(!filterBySearchPhrase || s.includesSearchPhrase) && (!filterByTurnOff || !s.isSavedTurnedOff)) || [];
            else return self.children || [];
        },
        get allVisibleDescendants () {
            let total = -1;
            const countVisibleDescendants = (child)=>{
                total++;
                if (!child.displayOpened) return;
                if (child.lvl === BreakdownTableDepths.FEATURE) total += child.displayChildren.length || 0;
                else child.displayChildren?.forEach(countVisibleDescendants);
            };
            countVisibleDescendants(self);
            return total;
        },
        get displayOpened () {
            return self.root.hasSearchPhrase ? self.tmpIsOpened : self.isOpened;
        },
        get hasDisplayChildren () {
            return Boolean(this.displayChildren.length);
        },
        getDisplayHoursForWorkType (workTypeId) {
            if (!isAlive(self)) return 0;
            const modifier = self.root.settings.modifier;
            const v = self.root.settings.maxValuesAsBuffer && self.root.displayMinValue ? self.getUnbufferedTotalHoursPerWorkType(workTypeId) : self.getTotalHoursPerWorkType(workTypeId);
            return Number((self.root.getDisplayValue(v) / modifier).toPrecision(12));
        },
        getDisplayHoursMaxForWorkType (workTypeId) {
            if (!isAlive(self)) return 0;
            const modifier = self.root.settings.modifier;
            const v = self.root.settings.maxValuesAsBuffer ? self.root.getDisplayValue(self.getTotalHoursPerWorkType(workTypeId)) : self.root.getDisplayValueMax(self.getTotalHoursPerWorkType(workTypeId));
            return Number((v / modifier).toPrecision(12));
        },
        getUnbufferedHoursForWorkType (workTypeId) {
            if (!isAlive(self)) return 0;
            const modifier = self.root.settings.modifier;
            return Number((self.root.getDisplayValue(self.getUnbufferedTotalHoursPerWorkType(workTypeId)) / modifier).toPrecision(12));
        },
        getUnbufferedHoursMaxForWorkType (workTypeId) {
            if (!isAlive(self)) return 0;
            const modifier = self.root.settings.modifier;
            const v = self.root.settings.maxValuesAsBuffer ? self.root.getDisplayValue(self.getUnbufferedTotalHoursPerWorkType(workTypeId)) : self.root.getDisplayValueMax(self.getUnbufferedTotalHoursPerWorkType(workTypeId));
            return Number((v / modifier).toPrecision(12));
        },
        get combinedDisplayHours () {
            if (!isAlive(self)) return 0;
            const modifier = self.root.settings.modifier;
            const v = self.root.settings.maxValuesAsBuffer && self.root.displayMinValue ? self.root.getDisplayValue(self.combinedUnbufferedHours) : self.root.getDisplayValue(self.combinedHours);
            return Number((v / modifier).toPrecision(12));
        },
        get combinedDisplayHoursMax () {
            if (!isAlive(self)) return 0;
            const modifier = self.root.settings.modifier;
            const v = self.root.settings.maxValuesAsBuffer ? self.root.getDisplayValue(self.combinedHours) : self.root.getDisplayValueMax(self.combinedHours);
            return Number((v / modifier).toPrecision(12));
        },
        get showMaxCombinedHours () {
            return (self.root.displayTwoValues || self.displayWorkloadTwoValues) && this.combinedDisplayHours !== this.combinedDisplayHoursMax;
        },
        get combinedUnbufferedDisplayHours () {
            if (!isAlive(self)) return 0;
            const modifier = self.root.settings.modifier;
            return Number((self.root.getDisplayValue(self.combinedUnbufferedHours) / modifier).toPrecision(12));
        },
        get combinedUnbufferedDisplayHoursMax () {
            if (!isAlive(self)) return 0;
            const modifier = self.root.settings.modifier;
            const v = self.root.settings.maxValuesAsBuffer ? self.root.getDisplayValue(self.combinedUnbufferedHours) : self.root.getDisplayValueMax(self.combinedUnbufferedHours);
            return Number((v / modifier).toPrecision(12));
        },
        get displayPrice () {
            if (!isAlive(self)) return 0;
            const roundPrice = self.root.settings?.roundPrice;
            const v = self.root.settings.maxValuesAsBuffer && self.root.displayMinValue ? self.calculatedUnbufferedPrice : self.calculatedPrice;
            return roundFloat(self.root.getDisplayValue(v) + self.calculatedMaterialPrice, roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        },
        get displayPriceMax () {
            if (!isAlive(self)) return 0;
            const roundPrice = self.root.settings?.roundPrice;
            const v = self.root.settings.maxValuesAsBuffer ? self.root.getDisplayValue(self.calculatedPrice) : self.root.getDisplayValueMax(self.calculatedPrice);
            return roundFloat(v + self.calculatedMaterialPrice, roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        },
        get showMaxPrice () {
            return (self.root.displayTwoValues || self.displayPriceTwoValues) && this.displayPrice !== this.displayPriceMax;
        },
        get displayUnbufferedPrice () {
            if (!isAlive(self)) return 0;
            return roundFloat(self.root.getDisplayValue(self.calculatedUnbufferedPrice) + self.calculatedMaterialPrice, Rounding.DECIMALS, true);
        },
        get displayUnbufferedPriceMax () {
            if (!isAlive(self)) return 0;
            const v = self.root.settings.maxValuesAsBuffer ? self.root.getDisplayValue(self.calculatedUnbufferedPrice) : self.root.getDisplayValueMax(self.calculatedUnbufferedPrice);
            return roundFloat(v + self.calculatedMaterialPrice, Rounding.DECIMALS, true);
        }
    }))/** ================ BT getters ================ */ .views((self)=>({
        getTotalBTHoursPerWorkType (workTypeId) {
            return self.btTaskValues?.get(workTypeId.toString()) || 0;
        },
        getBTHourDiff (workTypeId) {
            const h = self.getTotalHoursPerWorkType(workTypeId);
            const btH = this.getTotalBTHoursPerWorkType(workTypeId);
            if (!btH) return DEFAULT_MIN_AVG_MAX_VALUE;
            return parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (_, key)=>roundFloat(btH - (h?.[key] || 0)));
        },
        getBTHourDiffPercent (workTypeId) {
            const btH = this.getTotalBTHoursPerWorkType(workTypeId);
            const h = self.getTotalHoursPerWorkType(workTypeId);
            // if (!btH) return parseObject(DEFAULT_MIN_AVG_MAX_VALUE, () => (-100));
            return parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (_, key)=>(h?.[key] || 0) === 0 ? 0 : roundFloat((btH / (h?.[key] || 0) - 1) * 100));
        }
    }))/** ================ Type actions ================ */ .actions((self)=>({
        setParentType (targetType, noChildUpdates) {
            if (targetType === TaskTypes.WORKLOAD || self.type === TaskTypes.FIXED_PRICE || self.children?.length === 1) {
                self.type = targetType;
                self.parent?.setParentType?.(targetType, true);
                if (!noChildUpdates) self.children?.forEach((child)=>child.setChildType(targetType));
            }
        },
        setChildType (targetType) {
            if (targetType === TaskTypes.MATERIAL && self.type === TaskTypes.WORKLOAD || targetType === TaskTypes.FIXED_PRICE && self.type !== TaskTypes.FIXED_PRICE || self.lvl && (self.lvl > self.root?.estimateValueLevel || self.lvl > self.root?.estimateViewLevel)) {
                self.type = targetType;
                self.children?.forEach((child)=>child.setChildType(targetType));
            }
        },
        setType (type, manuallySet) {
            self.type = type;
            self.parent?.setParentType?.(type, true);
            if (type !== TaskTypes.WORKLOAD || self.lvl === self.root?.estimateValueLevel || self.lvl === self.root?.estimateViewLevel) {
                self.children?.forEach((child)=>child.setChildType(type));
            }
            if (type === TaskTypes.MATERIAL && self.lvl === BreakdownTableDepths.MODULE && self.parent?.children?.length === 1) {
                self.parent.setMaterialFirst(true);
            }
            if (manuallySet) self.root._internalUpdateSelectedWorkTypes?.();
        },
        setMaterialFirstType (materialFirst) {
            if (materialFirst && self.type === TaskTypes.WORKLOAD) {
                self.prevType = TaskTypes.WORKLOAD;
                self.type = TaskTypes.MATERIAL;
            } else if (!materialFirst && self.prevType) {
                self.type = self.prevType;
                self.prevType = undefined;
            }
            self.children?.forEach((child)=>child.setMaterialFirstType(materialFirst));
        }
    }))/** ================ Common Actions ================ */ .actions((self)=>({
        /**
     * Clears autoFocus from newly created model
     */ removeAutofocus () {
            self.root.historyManager?.withoutUndo(()=>{
                self.autoFocus = undefined;
            });
        },
        setCustomNumeration (value) {
            self.cNum = value;
        },
        resetCustomNumeration () {
            self.cNum = undefined;
        },
        setId (id) {
            self.id = id;
        },
        setComponentId (id) {
            self.componentId = id;
        },
        setContent (content, aiGenerated) {
            if (!self.descriptions) self.descriptions = cast({
                [DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER]: {
                    content: content || ""
                }
            });
            else self.descriptions.set(DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER, {
                content: content || ""
            });
            if (aiGenerated) self.generatedStory = self.name;
        },
        setDescription (identifier, content) {
            if (!self.descriptions) self.descriptions = cast({});
            const existing = self.descriptions.get(identifier);
            if (existing) existing.setContent(content);
            else self.descriptions.set(identifier, {
                content
            });
        },
        removeDescription (identifier) {
            if (identifier === DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER || !self.descriptions?.size) return;
            self.descriptions.delete(identifier);
            self.children?.forEach((child)=>{
                child.removeDescription(identifier);
            });
        },
        expandAllDescriptionColumns () {
            if (!self.descriptions?.size) return;
            for (const description of self.descriptions.values()){
                description.setShowMore(true);
            }
        },
        setName (name, componentId) {
            self.name = name;
            self.autoFocus = false;
            // if (id) this.setId(id);
            if (componentId) this.setComponentId(componentId);
            else if (self.componentId) this.setComponentId(undefined);
        },
        _internalSetLevel (lvl) {
            self.lvl = lvl;
        }
    }))/** ================ Workload & Automation Actions ================ */ .actions((self)=>({
        setWorkTypeForcedState (workTypeId, newState, manuallySet) {
            // self.values?.get(workTypeId.toString())?.setForcedState(newState);
            if (self.children?.length) {
                self.children.forEach((child)=>child.setWorkTypeForcedState(workTypeId, newState));
            // return; //@check
            }
            if (!self.values) {
                self.values = cast({
                    [workTypeId]: {
                        forcedState: newState
                    }
                });
                return;
            }
            const val = self.values.get(workTypeId.toString());
            self.values.set(workTypeId.toString(), {
                ...val || {},
                forcedState: newState
            });
            self.parent?.clearForcedStateValuesUp?.(workTypeId);
            this._internalResetRelatedWorkTypePercentValues(workTypeId);
            if (manuallySet) self.root._internalUpdateRelatedWorkTypes?.(workTypeId);
        },
        setWorkTypeAutomation (workTypeId, percent, ofWorkTypes, ofSections) {
            const val = self.workloadAutomation?.find((a)=>a.workTypeId === workTypeId);
            if (percent > 0 && ofWorkTypes?.length && ofSections?.length) {
                self.values?.delete(workTypeId.toString());
                self.children?.forEach((child)=>child.clearWorkloadValues(workTypeId, MIN_AVG_MAX_KEYS, false, false, false, true));
            } else {
                if (self.workloadAutomation) self.workloadAutomation = self.workloadAutomation.length > 1 ? cast(self.workloadAutomation.filter((a)=>a.workTypeId !== workTypeId)) : undefined;
                return;
            }
            const o = {
                workTypeId,
                percent,
                ofWorkTypes,
                ofSections
            };
            if (!self.workloadAutomation) self.workloadAutomation = cast([
                o
            ]);
            else if (val) val.setAutomation(percent, ofWorkTypes, ofSections);
            else self.workloadAutomation.push(cast(o));
            self.root._internalUpdateRelatedWorkTypes?.(workTypeId);
        },
        setWorkloadValues (values, forNewImportedElement, reloadWorkTypes) {
            const modifier = self.root?.settings.modifier;
            const workTypes = Object.keys(values);
            workTypes.forEach((workTypeId)=>{
                let v = values[workTypeId];
                let keys = Object.keys(v);
                if (forNewImportedElement) {
                    if (!Object.values(v).find((v)=>v)) return;
                    if (self.percentageWorkTypes.find((wT)=>wT.id === workTypeId)) {
                        if (typeof v.avg === "number") v.avgOv = true;
                        if (typeof v.min === "number" || typeof v.max === "number") v.minMaxOv = true;
                    // self.valueOverrides = cast([...(self.valueOverrides || []), workTypeId]);
                    }
                    keys = keys.filter((k)=>v[k]);
                    v = modifyNewValuesWithCoefficients(v, keys, // self.fixedPrice || DEFAULT_MIN_AVG_MAX_VALUE, // ?? what did you mean here? only mark as set when there are no FP?
                    undefined, modifier);
                }
                this._internalSetWorkloadValue(workTypeId, v, false, forNewImportedElement);
            });
            if (reloadWorkTypes) self.root._internalUpdateSelectedWorkTypes?.(workTypes);
        },
        setBTValues (values) {
            self.btValues = cast(values);
        },
        _internalSetWorkloadValue (workTypeId, values, forceNew, noFlagsApply, manuallySetKeys) {
            if (!self.values) self.values = cast({});
            if ((forceNew || !noFlagsApply) && (self.percentageWorkTypes.find((wT)=>wT.id === workTypeId) || self.workloadAutomation?.find((wT)=>wT.workTypeId === workTypeId))) {
                if (typeof values.avg === "number") values.avgOv = true;
                if (typeof values.min === "number" || typeof values.max === "number") values.minMaxOv = true;
            }
            if (manuallySetKeys && manuallySetKeys.length) {
                if (manuallySetKeys.includes(MinAvgMaxKeys.AVG)) values.avgSet = true;
                if (manuallySetKeys.includes(MinAvgMaxKeys.MIN) || manuallySetKeys.includes(MinAvgMaxKeys.MAX)) values.minMaxSet = true;
            }
            const old = self.values?.get(workTypeId.toString()) || {};
            if (noFlagsApply && !values.forcedState && old.forcedState) old.forcedState = undefined;
            const newValue = forceNew ? {
                ...values
            } : {
                ...old,
                ...values
            };
            self.values.set(workTypeId.toString(), cleanObject(newValue, true));
            if (!noFlagsApply) this._internalResetRelatedWorkTypePercentValues(workTypeId);
        },
        _internalResetRelatedWorkTypePercentValues (workTypeId) {
            self.percentageWorkTypes.sort((a, b)=>a.of?.includes(b.id) ? 1 : b.of?.includes(a.id) ? -1 : 0).reduce((l, wT)=>{
                if (wT.id === workTypeId) return l;
                if (wT.of?.includes(workTypeId) || intersection(wT.of, l)?.length) {
                    this._internalResetPercentValues(wT.id);
                    l.push(wT.id);
                }
                return l;
            }, []);
        },
        _internalResetAutomatedWorkTypeWorkloadPercentValues (workTypeId) {
            const automation = self.workloadAutomation?.find((a)=>a.workTypeId === workTypeId);
            if (!automation) return;
            const existingValue = self.values?.get(workTypeId.toString());
            if (existingValue?.isOverridden(self.root.settings.useMinMax)) return;
            const v = sumTaskValues(self.root.sections?.filter((section)=>automation.ofSections.includes(section.id) && !section.isTurnedOff)?.reduce((values, section)=>{
                const sectionValues = section.taskValuesWithoutAutomatedWorkType(workTypeId);
                values.push(...automation.ofWorkTypes.map((wT)=>sectionValues.get(wT.toString()) || {}));
                return values;
            }, []) || [], self.root.chosenWorkTypesForcedState(automation.ofWorkTypes, automation.ofSections), self.root.settings.useMinMax);
            const withPercent = parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (_, key)=>safeRoundHours((v[key] || 0) * automation.parsedPercent));
            withPercent.forcedState = v.forcedState;
            this._internalSetWorkloadValue(workTypeId, withPercent, false, true);
        },
        clearWorkloadValues (workTypeId, keys, noChildClearing, noAutomationCalculations, noOverrides, clearCellAutomation) {
            const existingValue = self.values?.get(workTypeId.toString());
            if (!existingValue?.hasOtherValues(keys)) // remove whole object if no other valuea were added
            self.values?.delete(workTypeId.toString());
            else existingValue?.clearValues(keys, !noOverrides);
            if (clearCellAutomation && self.workloadAutomation) {
                self.workloadAutomation = self.workloadAutomation.length > 1 ? cast(self.workloadAutomation.filter((a)=>a.workTypeId !== workTypeId)) : undefined;
            }
            if (!noAutomationCalculations) this._internalResetRelatedWorkTypePercentValues(workTypeId);
            if (!noChildClearing) self.children?.forEach((child)=>child.clearWorkloadValues(workTypeId, keys, false, noAutomationCalculations, noOverrides, clearCellAutomation));
        },
        clearWorkloadValuesUp (workTypeId, keys) {
            if (!self.percentageWorkTypes?.find((wT)=>wT.id === workTypeId) || self.isValueOverridden(workTypeId, true)) this.clearWorkloadValues(workTypeId, keys, true);
            self.parent?.clearWorkloadValuesUp?.(workTypeId, keys);
        },
        clearForcedStateValuesUp (workTypeId) {
            const v = self.values?.get(workTypeId.toString());
            if (v?.forcedState) self.values?.set(workTypeId.toString(), omit(v, "forcedState"));
            self.parent?.clearForcedStateValuesUp?.(workTypeId);
        },
        setWorkloadValue (workTypeId, values, clearChildren, holdHours, manuallySet) {
            let newValues = values;
            const keys = Object.keys(newValues);
            const currentHours = self.values?.get(workTypeId.toString());
            const currentTaskHours = self.taskValues?.get(workTypeId.toString());
            // const hoursSet = self.getTotalTimeSetForWorkType(workTypeId);
            const { modifier } = self.root?.settings || {};
            const hadDifferentType = self.type !== TaskTypes.WORKLOAD;
            // const hasPercent = Boolean(
            //   self.percentageWorkTypes?.find(wT => wT.id === workTypeId),
            // );
            // if (!noOverrides && hasPercent) {
            // if (!self.valueOverrides?.includes(workTypeId))
            //   self.valueOverrides = cast([
            //     ...(self.valueOverrides || []),
            //     workTypeId,
            //   ]);
            // self.parent?._internalParentResetPercentValues?.(workTypeId, true);
            // }
            // if (!noOverrides) this.clearParentFixedPrices(); //@todo: check
            let taskHoldsHours = false;
            let taskWillHoldHours = false;
            let manuallySetKeys;
            let clearKeys = self.root.settings.useMinMax ? MIN_MAX_KEYS : AVG_KEYS;
            keys.forEach((key)=>{
                const selfHoldsHours = self.holdsHours[workTypeId]?.[key];
                taskHoldsHours = Boolean(taskHoldsHours || selfHoldsHours);
                if (self.hasChildren && (clearChildren || selfHoldsHours || currentHours?.[key] === 0)) {
                    taskWillHoldHours = Boolean(holdHours || selfHoldsHours);
                }
            });
            /**
       * @note temporary min-avg-max calculations
       * Should be applied after switching between proposal modes (avg / min-max / etc.)
       * */ if (manuallySet) {
                manuallySetKeys = [
                    ...keys
                ];
                newValues = modifyNewValuesWithCoefficients(newValues, keys, currentTaskHours, modifier, undefined, (keys.includes(MinAvgMaxKeys.MIN) || keys.includes(MinAvgMaxKeys.MAX)) && !taskHoldsHours);
                clearKeys = Object.keys(newValues);
            }
            self.parent?.clearWorkloadValuesUp?.(workTypeId, clearKeys);
            if (!self.children?.length || taskWillHoldHours) {
                if (currentTaskHours?.forcedState && !currentHours?.forcedState) {
                    newValues.forcedState = currentTaskHours.forcedState;
                }
                this._internalSetWorkloadValue(workTypeId, newValues, false, false, manuallySetKeys);
                self.children?.forEach((child)=>child.clearWorkloadValues(workTypeId, clearKeys, false, false, false, true));
            // clearKeys.forEach(key => {
            //   // this.clearFixedPrice(key, true); //@todo: check
            //   if (
            //     (hasPercent || typeof newValues[key] === "number" || (!applyHoursCoefficients && key === MinAvgMaxKeys.MAX && newValues[MinAvgMaxKeys.MIN])) &&
            //     self.hasChildren &&
            //     (clearChildren || currentHours?.[key] === 0)
            //   )
            //     self.children!.forEach(child =>
            //       child.clearWorkloadValues(workTypeId, key, false, false, noOverrides),
            //     );
            // });
            } else {
                const weights = weightedHoursRoundedSplit(newValues, self.children.map((child)=>child.getTotalHoursPerWorkType(workTypeId)), modifier, Rounding.FULL);
                self.children.forEach((child, i)=>child.setWorkloadValue(workTypeId, weights[i], clearChildren));
            }
            self.type = TaskTypes.WORKLOAD;
            if (manuallySet) {
                if (hadDifferentType) self.root._internalUpdateSelectedWorkTypes?.();
                else self.root._internalUpdateRelatedWorkTypes?.(workTypeId);
            }
        },
        _internalResetPercentValues (percentWorkTypeId, removeOverride) {
            const workType = self.percentageWorkTypes.find(({ id })=>percentWorkTypeId === id);
            if (!workType) return;
            const existingValue = self.values?.get(percentWorkTypeId.toString());
            if (!removeOverride && existingValue?.isOverridden(self.root.settings.useMinMax)) return;
            const keys = [];
            if (!self.root.settings.useMinMax || !existingValue?.avgSet) keys.push(...AVG_KEYS);
            if (self.root.settings.useMinMax || !existingValue?.minMaxSet) keys.push(...MIN_MAX_KEYS);
            if (!self.children?.length || workType.of.find((id)=>self.holdsHours[id])) {
                const workTypeValues = workType.of.filter((workTypeId)=>{
                    return Boolean(self.getBreakdownWorkType(workTypeId, true));
                }).map((workTypeId)=>self.holdsHours[workTypeId] && self.localValuesWithBuffers?.get(workTypeId.toString()) || EMPTY_MIN_AVG_MAX_VALUE);
                let forcedState;
                if (!self.root.settings.maxValuesAsBuffer) {
                    const childStates = workTypeValues.map((v)=>v.forcedState);
                    if (childStates.includes(WorkTypeValueState.MIN_MAX)) forcedState = WorkTypeValueState.MIN_MAX;
                    else if (self.root.displayTwoValues && workTypeValues.length && childStates.filter((s)=>s === WorkTypeValueState.SINGLE).length === workTypeValues.length) forcedState = WorkTypeValueState.SINGLE;
                }
                const sum = sumTaskValues(workTypeValues, forcedState, self.root.settings.useMinMax);
                const values = parseWorkloadValues(sum, (val)=>safeRoundHours(val * workType.percent / 100, self.root.settings.modifier));
                this._internalSetWorkloadValue(percentWorkTypeId, values, false, true);
            } else {
                // let clearKeys = self.root.settings.useMinMax ? MIN_MAX_KEYS : AVG_KEYS;
                this.clearWorkloadValues(percentWorkTypeId, keys, true, true, true);
            }
        },
        _internalParentResetPercentValues (percentWorkTypeId, removeOverride) {
            const workType = self.percentageWorkTypes.find(({ id })=>percentWorkTypeId === id);
            if (!workType) return;
            if (removeOverride) {
                const clearKeys = self.root.settings.useMinMax ? MIN_MAX_KEYS : AVG_KEYS;
                this.clearWorkloadValues(percentWorkTypeId, clearKeys, true, true, true);
            }
            if (workType.of.find((id)=>self.holdsViewedHours[id])) {
                this._internalResetPercentValues(percentWorkTypeId, removeOverride);
            }
            self.parent?._internalParentResetPercentValues?.(percentWorkTypeId, removeOverride);
        // if (removeOverride)
        //   this._internalRemoveOverride(percentWorkTypeId);
        },
        resetAllPercentValues () {
            self.children?.forEach((child)=>child.resetAllPercentValues());
            self.percentageWorkTypes.sort((a, b)=>a.of?.includes(b.id) ? 1 : b.of?.includes(a.id) ? -1 : 0).forEach(({ id, of })=>{
                if (of?.find((id)=>self.holdsViewedHours[id])) this._internalResetPercentValues(id);
            });
        },
        resetPercentValues (percentWorkTypeId, removeOverride, noParentChecking, manuallySet) {
            // if (removeOverride) {
            //   if (self.root.settings.useMinMax) {
            //     this.clearValues(percentWorkTypeId, "min", true, undefined, true);
            //     this.clearValues(percentWorkTypeId, "max", true, undefined, true);
            //   } else {
            //     this.clearValues(percentWorkTypeId, "avg", true, undefined, true);
            //   }
            // }
            const existingValue = self.values?.get(percentWorkTypeId.toString());
            if (removeOverride) {
                self.values?.get(percentWorkTypeId.toString())?.clearSetStatus(self.root.settings.useMinMax);
                if (!self.root.settings.useMinMax || !existingValue?.avgSet) self.values?.get(percentWorkTypeId.toString())?.clearOverride();
                if (self.root.settings.useMinMax || !existingValue?.minMaxSet) self.values?.get(percentWorkTypeId.toString())?.clearOverride(true);
            }
            if (self.hasOwnWorkloadCellAutomation(percentWorkTypeId)) {
                this._internalResetAutomatedWorkTypeWorkloadPercentValues(percentWorkTypeId);
                if (manuallySet) {
                    self.root._internalUpdateRelatedWorkTypes?.(percentWorkTypeId);
                }
                return;
            }
            const workType = self.percentageWorkTypes.find(({ id })=>percentWorkTypeId === id);
            if (!workType) return;
            const hasHiddenValue = self.root.settings.useMinMax ? !!(existingValue?.min || existingValue?.max) : !!existingValue?.avg;
            if (self.holdsViewedHours[workType.id] || workType.of.find((id)=>self.holdsViewedHours[id]) || hasHiddenValue) {
                this._internalResetPercentValues(percentWorkTypeId, removeOverride);
            }
            if (!self.isValueOverridden(workType.id) || removeOverride) self.children?.forEach((child)=>child.resetPercentValues(percentWorkTypeId, removeOverride, true));
            if (!noParentChecking) self.parent?._internalParentResetPercentValues?.(percentWorkTypeId, removeOverride);
            if (removeOverride) {
                this._internalResetRelatedWorkTypePercentValues(percentWorkTypeId);
            }
            if (manuallySet) {
                self.root._internalUpdateRelatedWorkTypes?.(percentWorkTypeId);
            }
        },
        _internalRemoveAllWorkTypeOverrides (workTypeId) {
            self.values?.get(workTypeId.toString())?.clearAllOverrides();
        },
        removeAllWorkTypeOverrides (workTypeId) {
            this._internalRemoveAllWorkTypeOverrides(workTypeId);
            self.children?.forEach((child)=>child.removeAllWorkTypeOverrides(workTypeId));
        },
        alterElementHours () {
            if (self.values) {
                const { daysModifier } = self.root?.settings || {};
                self.values = cast(roundingInTaskValuesMap(self.values, daysModifier, Rounding.DECIMALS));
            }
            if (self.children?.length) self.children.forEach((s)=>s.alterElementHours());
        }
    }))/** ================ Fixed Price Type Setters ================ */ .actions((self)=>({
        setFixedPrice (price, clearChildren, holdFixedPrice, manuallySet) {
            const hadFixedPrice = self.type === TaskTypes.FIXED_PRICE && self.hasFixedPrice;
            if (self.type !== TaskTypes.FIXED_PRICE) self.setType(TaskTypes.FIXED_PRICE);
            let newPrice = price;
            const keys = Object.keys(price);
            let taskHoldsPrice = false;
            let manuallySetKeys = [];
            let clearKeys = self.root.settings.useMinMax ? MIN_MAX_KEYS : AVG_KEYS;
            /**
       * @note temporary min-avg-max calculations
       * Should be applied after switching between proposal modes (avg / min-max / etc.)
       * */ if (manuallySet) {
                manuallySetKeys = [
                    ...keys
                ];
                newPrice = modifyNewValuesWithCoefficients(price, keys, self.fixedPrice || DEFAULT_MIN_AVG_MAX_VALUE);
                clearKeys = Object.keys(newPrice);
            }
            keys.forEach((key)=>{
                const selfHoldsHours = self.fixedPrice?.[key] === 0;
                if (self.hasChildren && (clearChildren || selfHoldsHours)) taskHoldsPrice = Boolean(holdFixedPrice || selfHoldsHours);
            });
            self.parent?.clearFixedPrice?.(clearKeys, true);
            if (!self.children?.length || taskHoldsPrice) {
                if (manuallySetKeys.includes(MinAvgMaxKeys.AVG)) newPrice.avgSet = true;
                if (manuallySetKeys.includes(MinAvgMaxKeys.MIN) || keys.includes(MinAvgMaxKeys.MAX)) newPrice.minMaxSet = true;
                if (self.fixedPriceAutomation) {
                    if (typeof newPrice.avg === "number") newPrice.avgOv = true;
                    if (typeof newPrice.min === "number" || typeof newPrice.max === "number") newPrice.minMaxOv = true;
                }
                self.fixedPrice = cast({
                    ...self.fixedPrice || {},
                    ...newPrice
                });
                self.children?.forEach((child)=>child.clearFixedPrice(keys));
            } else {
                const weights = weightedHoursRoundedSplit(newPrice, self.children.map((child)=>pick(child.fixedPrice, MIN_AVG_MAX_KEYS)), 1, Rounding.FULL);
                self.children.forEach((child, i)=>child.setFixedPrice(weights[i], undefined, undefined, manuallySet));
            }
            if (!hadFixedPrice) self.root._internalUpdateSelectedWorkTypes?.();
            else self.root._internalResetAutomatedFixedPriceValues();
        },
        clearFixedPrice (keys, cleanUpwardsInTree) {
            if (self.fixedPrice?.hasOtherValues(keys)) self.fixedPrice = undefined;
            else self.fixedPrice?.clearValues(keys);
            if (cleanUpwardsInTree) self.parent?.clearFixedPrice?.(keys, true);
            else self.children?.forEach((child)=>child.clearFixedPrice(keys));
        },
        setFixedPriceForcedState (newState) {
            if (self.children?.length) {
                self.children.forEach((child)=>child.setFixedPriceForcedState(newState));
            }
            self.fixedPrice = cast({
                ...self.fixedPrice || {},
                forcedState: newState
            });
        },
        setFixedPriceAutomation (percent, ofSections) {
            if (percent > 0) {
                if (self.type !== TaskTypes.FIXED_PRICE) self.setType(TaskTypes.FIXED_PRICE);
                self.fixedPrice = undefined;
                self.fixedPriceAutomation = cast({
                    percent,
                    ofSections
                });
            } else if (self.fixedPrice) self.fixedPriceAutomation = undefined;
            self.root._internalUpdateSelectedWorkTypes?.();
        },
        resetFixedPriceAutomation () {
            if (!self.root.settings.useMinMax || !self.fixedPrice?.avgSet) self.fixedPrice?.clearOverride();
            if (self.root.settings.useMinMax || !self.fixedPrice?.minMaxSet) self.fixedPrice?.clearOverride(true);
            this._internalResetAutomatedFixedPrice();
        },
        _internalResetAutomatedFixedPrice () {
            if (!self.fixedPriceAutomation || self.isFixedPriceOverridden) return;
            const v = sumTaskValues(self.root.sections?.filter((section)=>self.fixedPriceAutomation.ofSections.includes(section.id) && !section.isTurnedOff)?.map((section)=>{
                const o = parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (_, key)=>(section.nonAutomatedCalculatedPrice[key] || 0) + section.calculatedMaterialPrice);
                o.forcedState = section.nonAutomatedCalculatedPrice.forcedState;
                return o;
            }) || [], self.root.chosenFixedPriceForcedState(self.fixedPriceAutomation.ofSections), self.root.settings.useMinMax);
            const withPercent = parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (_, key)=>safeRoundHours((v[key] || 0) * self.fixedPriceAutomation.parsedPercent));
            withPercent.forcedState = v.forcedState;
            self.fixedPrice = cast({
                ...self.fixedPrice || {},
                ...withPercent
            });
        }
    }))/** ================ Material Type Setters ================ */ .actions((self)=>({
        setUnits (units, clearChildren) {
            self.units = units;
            if (clearChildren) self.children?.forEach((child)=>child.clearMaterialPrice());
            self.parent?.clearMaterialPrice?.(true);
            self.root._internalResetAutomatedFixedPriceValues();
        },
        setUnitPrice (unitPrice, clearChildren) {
            self.unitPrice = unitPrice;
            if (clearChildren) self.children?.forEach((child)=>child.clearMaterialPrice());
            self.parent?.clearMaterialPrice?.(true);
        },
        _internalClearMaterialInputTitles () {
            self.unitsName = undefined;
            self.unitPriceName = undefined;
            self.children?.forEach((child)=>child._internalClearMaterialInputTitles());
            self.root._internalResetAutomatedFixedPriceValues();
        },
        clearMaterialPrice (cleanUpwardsInTree) {
            self.units = undefined;
            self.unitPrice = undefined;
            if (cleanUpwardsInTree) self.parent?.clearMaterialPrice?.(true);
            else self.children?.forEach((child)=>child.clearMaterialPrice());
        }
    }))/** ================ Open & TurnOff state setters ================ */ .actions((self)=>({
        setOpenState (state) {
            if (self.root.hasSearchPhrase) {
                self.root.historyManager?.withoutUndo(()=>{
                    if (self.tmpIsOpened !== state) self.tmpIsOpened = state;
                    return;
                });
            }
            if (self.isOpened !== state) self.isOpened = state;
            self.root?.updateLastOpenedElement?.(state ? getPath(self) : undefined);
        },
        expandAllChildren (state) {
            if (!state || self.children?.length) this.setOpenState(state);
            self.children?.forEach((child)=>child.expandAllChildren(state));
        },
        setTurnOffState (state, manuallySet, noParentChecking) {
            if (!state && !self.isSelfTurnedOff) {
                // means either all children or one of the parents is off
                if (self.hasChildren && self.allChildrenOff) self.children?.forEach((child)=>child.setTurnOffState(state, false, true));
            }
            if (!noParentChecking && self.parentIsOff) self.parent?._internalSetParentTurnOffState(state, self.id);
            if (self.type === TaskTypes.FIXED_PRICE && self.root?.settings?.turnOffFixedPrice) self.root.settings.setTurnOffFixedPrice?.(state);
            if (self.type === TaskTypes.MATERIAL && self.root?.settings?.turnOffMaterials) self.root.settings.setTurnOffMaterials?.(state);
            this._internalSetTurnOffState(state);
            if (manuallySet) self.root._internalUpdateSelectedWorkTypes?.();
        },
        _internalTurnOffFixedPriceElements (state) {
            if (self.type === TaskTypes.FIXED_PRICE) this._internalSetTurnOffState(state);
            self.children?.forEach((child)=>child._internalTurnOffFixedPriceElements(state));
        },
        _internalTurnOffMaterialsElements (state) {
            if (self.type === TaskTypes.MATERIAL) this._internalSetTurnOffState(state);
            self.children?.forEach((child)=>child._internalTurnOffMaterialsElements(state));
        },
        _internalSetTurnOffState (state) {
            if (self.root?.displayTurnOffLabels?.length) {
                if (self.root.excludeTurnedOffElements) {
                    self.labelTurnOff = undefined;
                    self.clientLabelTurnOff = state;
                } else {
                    self.labelTurnOff = state;
                    self.keepLabelTurnOff = true;
                }
                return;
            }
            if (self.root.excludeTurnedOffElements) {
                self.turnOff = undefined;
                self.clientTurnOff = state;
            } else self.turnOff = state;
        },
        _internalSetParentTurnOffState (state, triggeringChildId) {
            if (self.parentIsOff) self.parent?._internalSetParentTurnOffState(state, self.id);
            if (!state) self.children?.forEach((child)=>{
                if (child.id !== triggeringChildId) child.setTurnOffState(true, false, true);
            });
            this._internalSetTurnOffState(state);
        }
    }))/** ================ Child CRUD actions ================ */ .actions((self)=>({
        addChild (useAutoFocus, transferData, data, index = 0, clearLibraryValues) {
            if (!self.children) self.children = cast([]);
            const values = {
                ...data?.values || self.defaultSystemValues
            };
            const d = {
                id: generateEntryId(index),
                lvl: (self.lvl || 0) + 1,
                type: self.type,
                name: data?.name || "",
                descriptions: data?.descriptions || {
                    [DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER]: {
                        content: ""
                    }
                },
                autoFocus: !data?.values && useAutoFocus,
                isOpened: false,
                hasSearchPhrase: self.root?.hasSearchPhrase
            };
            if (data?.componentId) d.componentId = data.componentId;
            if (transferData) {
                if (self.values) {
                    const tmpValuesIterator = self.values[Symbol.iterator]();
                    for (const [key, value] of tmpValuesIterator){
                        if (key) {
                            // values[Number(key)] = { min, max, avg };
                            values[Number(key)] = getSnapshot(value);
                        }
                    }
                    d.values = values;
                }
                // if(self.valueOverrides) d.valueOverrides = self.valueOverrides.slice();
                if (self.fixedPrice) d.fixedPrice = {
                    ...self.fixedPrice
                };
                if (self.units) d.units = self.units;
                if (self.unitPrice) d.unitPrice = self.unitPrice;
                if (self.fixedPriceAutomation) d.fixedPriceAutomation = getSnapshot(self.fixedPriceAutomation);
                if (self.workloadAutomation) d.workloadAutomation = self.workloadAutomation.map((a)=>getSnapshot(a));
            }
            // if(!transferData)
            //   self.percentageWorkTypes.forEach(({ id }) => {
            //     const v = d.values?.[id];
            //     if(!d.valueOverrides?.includes(id) && (v?.min || v?.avg || v?.max))
            //       d.valueOverrides = [...(d.valueOverrides || []), id];
            //   })
            self.children.push(d);
            if (clearLibraryValues && values) self.children.slice(-1)[0].setWorkloadValues(values, true);
            if (data?.children?.length) {
                self.children.slice(-1)[0].addChildren(data.children);
            }
            if (transferData) {
                self.values = undefined;
                // self.valueOverrides = undefined;
                self.fixedPrice = undefined;
                self.units = undefined;
                self.unitPrice = undefined;
                self.fixedPriceAutomation = undefined;
                self.workloadAutomation = undefined;
            }
            if (self.values && clearLibraryValues) {
                Object.keys(values).forEach((workTypeId)=>{
                    const selfV = self.values.get(workTypeId);
                    if (!selfV || !Object.values(values[workTypeId]).find((v)=>v)) return;
                    self.values?.delete(workTypeId);
                });
            }
            self.root?.updateLastOpenedElement?.(getPath(self));
            return getPath(self.children[self.children.length - 1]);
        },
        addChildren (tasksData, manuallySet) {
            //for importing multiple children from library component
            tasksData.forEach((data, index)=>this.addChild(false, false, data, index, true));
            self.isOpened = true;
            if (manuallySet) {
                self.resetAllPercentValues();
                self.root._internalUpdateSelectedWorkTypes?.();
            }
        },
        _internalPushChild (movedChild, position, skipPercentValueOverride) {
            if (!self.children) self.children = cast([]);
            const newChild = cleanObject(cloneDeep(movedChild)); // drops read-only status from movedChild
            const percentageWorkTypesIds = self.percentageWorkTypes.map((wT)=>wT.id);
            const updateValueOverrides = (el)=>{
                if (el.values) {
                    Object.keys(el.values).forEach((key)=>{
                        const hasPercent = percentageWorkTypesIds.includes(key);
                        if (hasPercent) {
                            if (el.values[key].avg) el.values[key].avgOv = true;
                            if (el.values[key].min || el.values[key].max) el.values[key].minMaxOv = true;
                        } else {
                            delete el.values[key].avgOv;
                            delete el.values[key].minMaxOv;
                        }
                    });
                }
                // if(el.valueOverrides?.length) {
                //   el.valueOverrides = el.valueOverrides.filter(workTypeId => (
                //     percentageWorkTypesIds.includes(workTypeId)
                //   ))
                // }
                //
                // percentageWorkTypesIds.forEach(id => {
                //   const v = el.values?.[id];
                //   if(!el.valueOverrides?.includes(id) && (v?.min || v?.avg || v?.max))
                //     el.valueOverrides = [...(el.valueOverrides || []), id];
                // })
                el.children?.forEach((child)=>updateValueOverrides(child));
            };
            if (!skipPercentValueOverride) updateValueOverrides(newChild);
            if (typeof position === "number" && position >= 0 && position < self.children.length) {
                self.children = cast([
                    ...self.children.slice(0, position),
                    newChild,
                    ...self.children.slice(position)
                ]);
            } else self.children.push(newChild);
            if (self.values) {
                self.values = undefined;
            }
            if (self.units || self.unitPrice) {
                self.units = undefined;
                self.unitPrice = undefined;
            }
            self.setParentType(newChild.type);
            return newChild;
        },
        pushChild (movedChild, position, skipPercentValueOverride) {
            this._internalPushChild(movedChild, position, skipPercentValueOverride);
        // self.resetAllPercentValues(); /** @note: probably not needed */
        },
        removeChild (id) {
            if (!self.children || self.blockChildReorder) return;
            const child = self.children.find((child)=>child.id === id);
            if (child) {
                destroy(child);
                self.root._internalUpdateSelectedWorkTypes?.();
            }
        },
        reorderChildren (reorderedChildren, childrenRemoved) {
            if (!self.blockChildReorder) self.children = updateSortedList(self.children, reorderedChildren);
            else self.blockChildReorder = undefined;
            if (!self.children?.length) {
                // reorder func can also remove children
                self.children = undefined;
                self.values = undefined;
            }
            if (childrenRemoved) self.root._internalUpdateSelectedWorkTypes?.();
        },
        switchBlockChildReorder () {
            self.blockChildReorder = self.blockChildReorder ? undefined : true;
        },
        switchParentBlockChildReorder () {
            self.root.historyManager?.withoutUndo(()=>{
                self.parent?.switchBlockChildReorder?.();
            });
        },
        removeSelf (moveModules = false) {
            if (!isAlive(self)) return;
            return self.parent?.removeChild(self.id, moveModules);
        },
        removeSelfWithUpStreamStatusCheck () {
            self.parent?.removeChild(self.id);
            if (self.parent?.libraryStatus === 1 && !self.parent?.children?.length) self.parent?.removeSelfWithUpStreamStatusCheck();
        },
        mergeWithOtherTask (task, manuallyTriggered) {
            if (task.values && self.values) self.values = Object.fromEntries(sumTaskChildrenData(self.values, new Map(Object.entries(task.values))));
            if (typeof self.id === "string" && typeof task.id === "number") self.id = task.id;
            // if (!self.content && task.content)
            //   self.content = task.content;
            if (!self.children?.length) self.children = undefined;
            // self.descriptions = self.descriptions.merge(task.descriptions);
            if (task.descriptions) {
                if (!self.descriptions) self.descriptions = cast({});
                Object.keys(task.descriptions).forEach((key)=>{
                    if (!self.descriptions.get(key)?.content && task.descriptions[key].content) self.descriptions.set(key, task.descriptions[key]);
                });
            }
            task.children?.forEach((child)=>{
                const target = self.getExistingChild(child.id, child.componentId, child.name);
                if (target) target.mergeWithOtherTask(child);
                else this.pushChild(child);
            });
            if (manuallyTriggered) self.root._internalUpdateSelectedWorkTypes?.();
        //@todo: handle type change if necessary
        },
        duplicateSelf (newName, libraryElementId) {
            const duplicate = clone(self, true);
            duplicate.setName(newName);
            duplicate.setId(libraryElementId || generateEntryId());
            self.parent?.pushChild(getSnapshot(duplicate), self.parentOrder + 1, true);
            self.root._internalUpdateSelectedWorkTypes?.();
        }
    }))/** ================ WorkTypeCrud actions ================ */ .actions((self)=>({
        _internalChangeWorkType (workTypeId, newWorkTypeId, changeBufferWorkTypes) {
            if (self.children?.length) self.children?.forEach((child)=>child.changeWorkType(workTypeId, newWorkTypeId, changeBufferWorkTypes));
            if (self.values && self.holdsHours?.[workTypeId] && Object.values(self.holdsHours[workTypeId])?.includes(true)) {
                const old = self.values.get(workTypeId.toString());
                if (old) {
                    self.values.set(newWorkTypeId.toString(), {
                        ...detach(old)
                    });
                    self.values.delete(workTypeId.toString());
                }
            }
            self.buffers?.forEach((b)=>changeBufferWorkTypes ? b.updateRelatedWorkTypeId(workTypeId, newWorkTypeId) : b.removeWorkType(workTypeId));
            self.workloadAutomation?.forEach((a)=>changeBufferWorkTypes ? a.updateRelatedWorkTypeId(workTypeId, newWorkTypeId) : a.removeWorkType(workTypeId));
        },
        changeWorkType (workTypeId, newWorkTypeId, changeBufferWorkTypes) {
            this._internalChangeWorkType(workTypeId, newWorkTypeId, changeBufferWorkTypes);
        },
        removeWorkType (workTypeId) {
            if (self.children?.length) self.children?.forEach((child)=>child.removeWorkType(workTypeId));
            self.values?.delete(workTypeId);
            self.buffers?.forEach((b)=>b.removeWorkType(workTypeId));
            self.workloadAutomation?.forEach((a)=>a.removeWorkType(workTypeId));
            self._internalResetRelatedWorkTypePercentValues(workTypeId);
        }
    }))/** ================ Search Actions ================ */ .actions((self)=>({
        _internalSearchTreeBySavedPhrases (searchPhrases, searchLabels, lvl) {
            let hasSearchPhrase = !searchPhrases?.length;
            let hasSearchLabels = !searchLabels?.length;
            const childHasSearchPhrase = self.children?.reduce((t, child)=>child._internalSearchTreeBySavedPhrases(searchPhrases, searchLabels, lvl + 1) || t, false) || false;
            if (searchPhrases && searchPhrases.length > 0) {
                searchPhrases.forEach((searchPhrase)=>{
                    const hasSearchPhraseInName = self.name && self.name?.toLowerCase().includes(searchPhrase.value.toLowerCase());
                    // const hasSearchPhraseInDesc = self.content && cleanHtmlContent(self.content).toLowerCase().includes(searchPhrase.value.toLowerCase());
                    let hasSearchPhraseInDesc = false;
                    if (self.descriptions) {
                        for (const { content } of self.descriptions.values()){
                            if (content && cleanHtmlContent(content).toLowerCase().includes(searchPhrase.value.toLowerCase())) {
                                hasSearchPhraseInDesc = true;
                                break;
                            }
                        }
                    }
                    if (hasSearchPhraseInName || hasSearchPhraseInDesc) hasSearchPhrase = true;
                });
            }
            if (searchLabels && searchLabels.length > 0) {
                if (intersection(searchLabels, self.labels).length > 0) hasSearchLabels = true;
            }
            self.hasSearchPhrase = hasSearchPhrase && hasSearchLabels;
            self.tmpIsOpened = childHasSearchPhrase;
            if (typeof self.lvl !== "number") self.lvl = lvl;
            return self.hasSearchPhrase || childHasSearchPhrase;
        },
        _internalTurnOffTreeByLabels (labels, lvl, exclude, isClient, parentIsVisible) {
            const hasFilters = labels && labels.length > 0;
            let elementState = !hasFilters || Boolean(!self.hasLabels && parentIsVisible);
            if (!exclude && self.hasLabels && hasFilters) elementState = intersection(labels, self.labels).length > 0;
            if (exclude && hasFilters) elementState = !self.hasLabels || intersection(labels, self.labels).length === 0;
            let childState = elementState;
            if (!hasFilters) self.keepLabelTurnOff = undefined;
            if (!hasFilters || self.childrenHaveLabels && (!self.allChildrenHaveLabels || exclude || !elementState)) childState = self.children?.reduce((t, child)=>child._internalTurnOffTreeByLabels(labels, lvl + 1, exclude, isClient, elementState) || t, false) || elementState;
            const state = !elementState && !childState;
            if (!self.keepLabelTurnOff) {
                if (isClient) self.clientLabelTurnOff = state || undefined;
                else self.labelTurnOff = state || undefined;
            }
            if (typeof self.lvl !== "number") self.lvl = lvl;
            return elementState || childState;
        }
    }))/** ================ Automation Actions ================ */ .actions((self)=>({
        updateAutomatedCellWorkload (workTypeId) {
            self._internalResetAutomatedWorkTypeWorkloadPercentValues(workTypeId);
            self._internalResetRelatedWorkTypePercentValues(workTypeId);
            self.children?.forEach((child)=>{
                if (child.hasCellAutomatedWorkType(workTypeId)) {
                    child.updateAutomatedCellWorkload(workTypeId);
                }
            });
        },
        updateAutomatedFixedPrices () {
            self._internalResetAutomatedFixedPrice();
            self.children?.forEach((child)=>{
                if (child.hasAutomatedFixedPrice) {
                    child.updateAutomatedFixedPrices();
                }
            });
        }
    }))/** ================ Label Actions ================ */ .actions((self)=>({
        addLabel (id) {
            if (!self.labels) self.labels = cast([]);
            self.labels.push(id);
        },
        removeLabel (id) {
            if (!self.labels?.length) return;
            const index = self.labels.indexOf(id);
            if (index >= 0) self.labels.splice(index, 1);
            if (!self.labels?.length) self.labels = undefined;
        },
        purgeLabel (id) {
            this.removeLabel(id);
            self.children?.forEach((child)=>{
                child.purgeLabel(id);
            });
        }
    }))/** ================ Buffer Actions ================ */ .actions((self)=>({
        addBuffer (buffer) {
            if (!self.buffers) {
                self.buffers = cast([
                    buffer
                ]);
            } else {
                self.buffers.push(cast(buffer));
            }
            this._internalResetBufferPercentValues?.(buffer.workTypes);
            self.root._internalUpdateSelectedWorkTypes?.(buffer.workTypes);
        },
        removeBuffer (id) {
            if (!self.buffers?.length) return;
            const bf = self.buffers.find((b)=>b.id === id);
            const wTS = bf?.workTypes;
            if (bf) {
                const workTypes = [
                    ...bf.workTypes
                ];
                destroy(bf);
                workTypes.forEach((wT)=>{
                    self?._internalResetPercentValues?.(wT);
                });
            }
            if (!self.buffers.length) self.buffers = undefined;
            self.root._internalUpdateSelectedWorkTypes?.(wTS);
        },
        reorderBuffers (list) {
            self.buffers = cast(updateSortedList(self.buffers, list, "id", true));
        },
        _internalResetBufferPercentValues (workTypeIds) {
            self.percentageWorkTypes.sort((a, b)=>a.of?.includes(b.id) ? 1 : b.of?.includes(a.id) ? -1 : 0).reduce((l, { id, of })=>{
                if (workTypeIds.includes(id) || intersection(workTypeIds, of)?.length || intersection(of, l)?.length) {
                    self.resetPercentValues(id);
                    l.push(id);
                }
                return l;
            }, []);
        // .forEach(({ id, of }) => {
        //   if (workTypeIds.includes(id) || intersection(workTypeIds, of)?.length)
        //     self.resetPercentValues(id);
        // });
        },
        turnOffAllBuffers (turnOff) {
            self.buffers?.forEach((ov)=>ov.setUseStateWithHistory(turnOff));
            self.children?.forEach((child)=>{
                child.turnOffAllBuffers(turnOff);
            });
        }
    }))/** ================ afterCreate() ================ */ .actions((self)=>({
        afterCreate () {
            if (typeof self.lvl !== "number" && typeof self.parent?.lvl === "number") {
                self._internalSetLevel(self.parent.lvl + 1);
            }
        }
    }));

const VisibleBreakdownColumnModel = types.model("VisibleBreakdownColumn", {
    name: types.enumeration("BreakdownColumns", Object.values(BreakdownColumns)),
    visible: types.boolean,
    width: types.maybeNull(types.number),
    /** name viewed in column headers; set by user */ displayName: types.maybe(types.string),
    /** cell can be pinned to left side of the table */ pinLeft: types.maybe(types.boolean),
    /** cell can be pinned to right side of the table */ pinRight: types.maybe(types.boolean),
    /** indicates if cell should be pinned to respective side */ applyPin: types.maybe(types.boolean),
    /** optional identifier, used for description columns */ descriptionId: types.maybe(types.string),
    /** marks a description column awaiting its name and scope choice */ autoFocus: types.maybe(types.boolean),
    /** Visibility Management setting */ hide: types.maybe(types.boolean)
}).views((self)=>({
        get root () {
            return getRoot(self);
        }
    })).views((self)=>({
        get id () {
            return self.descriptionId || self.name;
        },
        get descriptionIndex () {
            if (!self.descriptionId) return;
            return Number(self.descriptionId.split("_")[1]);
        },
        get removable () {
            return self.descriptionId && self.descriptionId !== DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER;
        },
        get forcedPin () {
            return [
                BreakdownColumns.NUMERATION,
                BreakdownColumns.TITLE
            ].includes(self.name);
        },
        get pinnable () {
            return ![
                BreakdownColumns.NUMERATION,
                BreakdownColumns.TITLE,
                BreakdownColumns.ESTIMATES
            ].includes(self.name);
        },
        get isVisible () {
            if (!isAlive(self)) return false;
            const apply = self.root.visibility.apply;
            const hideEmptySectionDescriptions = self.root.hideEmptySectionDescriptions;
            const visible = self.visible && (!apply || !self.hide);
            const parent = getParent(self, 2);
            if (self.name === BreakdownColumns.DESCRIPTION) return visible && (!hideEmptySectionDescriptions || (self.descriptionId ? parent.treeHasDescriptions[self.descriptionId] : parent.treeHasAnyDescriptions));
            if (self.name === BreakdownColumns.PRICE) return visible && !self.root.hideAllPrices;
            // return self.visible && parent.descriptionVisible && self.root.settings.descriptionMode === DescriptionModes.COLUMN;
            // if(self.name === BreakdownColumns.LABELS)
            //   return self.visible && parent.labelsVisible
            return visible;
        },
        get displayWidth () {
            const parent = getParent(self, 2);
            /** Price column has width dependent on section price */ if (self.name === BreakdownColumns.PRICE) return Math.max(parent.displayPriceCellSize, 104); //@todo: no "magical numbers"
            if (self.name === BreakdownColumns.TITLE) return self.width || 400; //@todo: no "magical numbers"
            if (self.name === BreakdownColumns.DESCRIPTION && self.root.settings.descriptionMode !== DescriptionModes.COLUMN) return 0;
            return self.width || 0;
        },
        get pinLeftPx () {
            if (!isAlive(self)) return;
            if (!self.applyPin || !self.pinLeft || !this.displayWidth || !this.isVisible) return;
            if (self.name === BreakdownColumns.DESCRIPTION && self.root.settings.descriptionMode !== DescriptionModes.COLUMN) return; // when structure does not use column mode; skip calculations
            const parent = getParent(self, 2);
            const index = parent.visibleBreakdownColumns.findIndex((c)=>c.id === this.id);
            if (index < 0) return;
            if (index === 0) return 0;
            let pin = 0;
            for(let i = 0; i < index; i++){
                if (parent.visibleBreakdownColumns[i].name === BreakdownColumns.NUMERATION) continue; // numeration doesn't behave like a column in app and does not contribute to width
                const column = parent.visibleBreakdownColumns[i];
                if (!column.pinLeft || !column.displayWidth) return;
                pin += column.displayWidth;
            }
            return pin;
        },
        get pinRightPx () {
            if (!isAlive(self)) return;
            if (!self.applyPin || !self.pinRight || !this.displayWidth || !this.isVisible) return;
            const parent = getParent(self, 2);
            const lastIndex = parent.visibleBreakdownColumns.length - 1;
            const index = parent.visibleBreakdownColumns.findIndex((c)=>c.id === this.id);
            if (index < 0) return;
            if (index === lastIndex) return 0;
            let pin = 0;
            for(let i = lastIndex; i > index; i--){
                const column = parent.visibleBreakdownColumns[i];
                if (!column.pinRight || !column.displayWidth) return;
                pin += column.displayWidth;
            }
            return pin;
        },
        // get maxWidth() {
        //   if(typeof this.pinLeftPx === "undefined" && typeof this.pinRightPx === "undefined")
        //     return;
        //
        //   return this.displayWidth;
        // },
        get nextVisiblePinnedColumn () {
            if (!isAlive(self)) return;
            const parent = getParent(self, 2);
            let index = parent.visibleBreakdownColumns.findIndex((c)=>c.id === this.id) + 1;
            let next = parent.visibleBreakdownColumns[index];
            while(next?.pinLeft){
                if (next.pinLeft && next.isVisible && next.displayWidth > 0) return next;
                index++;
                next = parent.visibleBreakdownColumns[index];
            }
            return;
        }
    })).actions((self)=>({
        setVisibility (visible) {
            self.visible = visible;
        },
        setWidth (width) {
            self.width = width;
        },
        setApplyPin (applyPin) {
            self.applyPin = applyPin;
        },
        setDisplayName (displayName) {
            self.displayName = displayName;
        },
        setAutofocus (autoFocus) {
            self.root.historyManager?.withoutUndo(()=>{
                self.autoFocus = autoFocus;
            });
        },
        setHide (hide) {
            self.hide = hide;
        }
    }));

const SectionModel = TaskModel.named("Section").props({
    lvl: types.optional(types.number, BreakdownTableDepths.SECTION),
    workTypes: types.array(SectionWorkTypeModel),
    usedBreakdownColumns: types.array(VisibleBreakdownColumnModel),
    hideEstimateCollapse: types.maybe(types.boolean),
    showCombinedHours: types.maybe(types.boolean),
    materialFirst: types.maybe(types.boolean),
    clearedMaterialInputNames: types.maybe(types.boolean),
    timelineColor: types.maybe(types.string),
    timelineFontColor: types.maybe(types.string),
    /** only for library; not serialized: */ startingLevel: types.maybe(types.number)
})/** ================ Common getters & helpers ================ */ .views((self)=>({
        get defaultSystemValues () {
            return self.workTypes.reduce((values, workType)=>({
                    ...values,
                    [workType.id.toString()]: DEFAULT_MIN_AVG_MAX_VALUE
                }), {});
        },
        get taskWorkTypes () {
            return self.workTypes.filter(({ invisible })=>!invisible);
        },
        get btTaskWorkTypes () {
            const mergedWorkType = this.taskWorkTypes.filter((wT)=>wT.id === BT_MERGED_VALUE);
            return mergedWorkType.length == 0 ? this.taskWorkTypes : mergedWorkType;
        },
        get allTaskWorkTypes () {
            return self.workTypes;
        },
        get allChildrenExpanded () {
            if (!self.children?.length) return false;
            for (const child of self.children){
                if (!child.isOpened && child.children?.length) return false;
            }
            return true;
        },
        get usedWorkTypes () {
            return this.taskWorkTypes.map(({ id })=>id);
        },
        get hiddenTaskWorkTypes () {
            return this.allTaskWorkTypes.filter((wT)=>wT.invisible);
        },
        get hiddenTaskWorkTypesWithValues () {
            return this.allTaskWorkTypes.filter((wT)=>wT.invisible && self.getDisplayHoursForWorkType(wT.id) > 0);
        },
        get sectionModulesHaveChildren () {
            return self.root.settings.sectionFormat === SectionFormats.ROW ? !!self.children?.length : !!self.children?.find((task)=>task.hasChildren);
        },
        hasWorkType (workTypeId, onlyVisible) {
            return !!self.workTypes.find((workType)=>workType.id === workTypeId && (!onlyVisible || !workType.invisible));
        },
        getWorkTypeById (workTypeId) {
            return self.workTypes.find((workType)=>workType.id === workTypeId);
        },
        getWorkTypeMaxTimePerMonth (workTypeId, resourceId) {
            const wT = self.workTypes.find((wT)=>wT.id === workTypeId);
            if (resourceId && wT?.resources?.length) return wT?.resources?.find((res)=>res.id === resourceId)?.maxTimePerMonth || 0;
            return wT?.maxTimePerMonth || 0;
        },
        getWorkTypeMaxTimePerWeek (workTypeId, resourceId) {
            const wT = self.workTypes.find((wT)=>wT.id === workTypeId);
            if (resourceId && wT?.resources?.length) {
                let res = wT?.resources?.find((res)=>res.id === resourceId);
                if (!res) {
                    wT.addResource(resourceId.toString());
                    res = wT?.resources?.find((res)=>res.id === resourceId);
                }
                return res?.maxTimePerWeek || 0;
            }
            return wT?.maxTimePerWeek || 0;
        },
        get treeHasAnyDescriptions () {
            return Object.values(this.treeHasDescriptions).includes(true);
        },
        get treeHasDescriptions () {
            return merge({}, self.hasDescriptions, self.childrenWithDescriptions);
        },
        get siblings () {
            return self.root.sections?.filter((m)=>m.id !== self.id)?.map((m)=>({
                    id: m.id,
                    componentId: m.componentId,
                    name: simplifyTextString(m.name)
                })) || [];
        },
        get parentOrder () {
            return self.root?.sections?.findIndex((m)=>self.id && m.id === self.id) || 0; // || (self.newId && m.newId === self.newId));
        },
        getWorkTypeVisibility (workTypeId) {
            const wT = self.workTypes.find((workType)=>workType.id === workTypeId);
            return wT ? !wT.invisible : false;
        },
        get lowDepthRoundingLevel () {
            return self.workTypes?.slice()?.filter((workType)=>!workType.invisible && workType.hasRounding && typeof workType.roundLevel === "number" && workType.roundLevel < self.root.settings.viewLevel)?.sort((a, b)=>a.roundLevel < b.roundLevel ? 1 : -1)?.[0]?.roundLevel;
        }
    }))/** ================ Automation getters ================ */ .views((self)=>({
        // get percentageWorkTypeTargets() {
        //   const targets: number[] = [];
        //   this.percentageWorkTypes.forEach(({ of }) => {
        //     targets.push(...difference(of, targets));
        //   });
        //   return targets;
        // },
        get percentageWorkTypes () {
            // return this.taskWorkTypes.filter(wT => wT.hasPercent);
            return self.workTypes.filter((wT)=>wT.hasPercent); //@todo: check if doesn't break anything
        },
        blacklistedAutomationWorkTypes (workTypeId) {
            // return self.taskWorkTypes
            const workTypes = self.workTypes.map((wT)=>{
                const cellAutomatedWorkType = self.cellAutomatedWorkTypes?.find((cWT)=>cWT.id === wT.id);
                return {
                    id: wT.id,
                    of: union(wT.of || [], cellAutomatedWorkType?.of || [])
                };
            });
            return workTypes.filter((workType)=>workType.of?.includes(workTypeId) || wTExistsDeep(workTypes, workTypeId, workType?.of)).map(({ id })=>id);
        }
    }))/** ================ Display getters ================ */ .views((self)=>({
        get displayUnitsName () {
            return self.unitsName;
        },
        get displayUnitPriceName () {
            return self.unitPriceName;
        },
        get displayPriceCellSize () {
            if (!self.root.renderPrice) return 104;
            let size = self.root.renderPrice(self.displayPrice);
            if (self.displayPriceTwoValues && self.showMaxPrice) size += self.root.renderPrice(self.displayPriceMax) + 16;
            return size;
        }
    }))/** ================ Visibility getters ================ */ .views((self)=>({
        get visibleBreakdownColumns () {
            if (!isAlive(self)) return [];
            return self.usedBreakdownColumns.filter((col)=>col.isVisible);
        },
        get usedDescriptionColumns () {
            if (!isAlive(self)) return [];
            return self.usedBreakdownColumns.filter((c)=>c.descriptionId && c.isVisible);
        },
        get allDescriptionColumnsHidden () {
            return !self.usedBreakdownColumns.find((c)=>c.descriptionId && !c.hide);
        },
        get lastDescriptionColumnIndex () {
            if (!isAlive(self)) return 0;
            return self.usedBreakdownColumns.map((c)=>c.name).lastIndexOf(BreakdownColumns.DESCRIPTION);
        },
        get lastPinnedCellLeft () {
            return this.visibleBreakdownColumns.filter((c)=>c.pinLeft && c.displayWidth && typeof c.pinLeftPx === "number").slice(-1)[0]?.id;
        },
        get usedAllDescriptionColumns () {
            return self.usedBreakdownColumns.filter((c)=>c.name === BreakdownColumns.DESCRIPTION).length >= MAX_DESCRIPTION_COLUMNS;
        },
        get unpinnedColumnsWidth () {
            return self.usedBreakdownColumns.filter((c)=>c.isVisible && c.pinLeft && typeof c.pinLeftPx !== "number").reduce((sum, c)=>sum + c.displayWidth, 0);
        },
        get showNumerationColumn () {
            return Boolean(self.usedBreakdownColumns.find((b)=>b.name === BreakdownColumns.NUMERATION)?.visible);
        },
        get showBreakdownPriceColumn () {
            return Boolean(self.usedBreakdownColumns.find((b)=>b.name === BreakdownColumns.PRICE)?.visible);
        },
        get showBreakdownEstimatesColumn () {
            return Boolean(self.usedBreakdownColumns.find((b)=>b.name === BreakdownColumns.ESTIMATES)?.visible);
        },
        get showBreakdownDescriptionColumn () {
            return Boolean(self.usedBreakdownColumns.find((b)=>b.name === BreakdownColumns.DESCRIPTION)?.visible);
        },
        get showBreakdownLabelsColumn () {
            return Boolean(self.usedBreakdownColumns.find((b)=>b.name === BreakdownColumns.LABELS)?.visible);
        }
    }))// .views(self => ({
//   get priceVisible() {
//     const { showBreakdownColumns } = self.root.settings;
//     const { apply } = self.root.visibility;
//     return showBreakdownColumns && self.showBreakdownPriceColumn && !self.root.hideAllPrices && (!apply || !self.hidePrice)
//   },
//   get descriptionVisible() {
//     const { apply } = self.root.visibility;
//     return !(self.root.hideEmptySectionDescriptions && !self.hasAnyDescription)
//       && !(apply && self.hideDescription);
//   },
//   get labelsVisible() {
//     const { apply } = self.root.visibility;
//     return !apply || !self.hideLabels;
//   },
// }))
/** ================ Serializers ================ */ .views((self)=>({
        serialize () {
            return cleanObject({
                id: self.id,
                type: self.type,
                prevType: self.prevType,
                lvl: self.lvl,
                name: self.name,
                descriptions: self.descriptions?.toJSON(),
                cNum: self.cNum,
                workTypes: self.workTypes.map((wT)=>wT.serialize()),
                usedBreakdownColumns: self.usedBreakdownColumns?.map((b)=>cleanObject({
                        ...getSnapshot(b)
                    })),
                labels: self.labels ? [
                    ...self.labels
                ] : undefined,
                buffers: self.buffers?.map((b)=>b.serialize()),
                fixedPrice: self.fixedPrice ? {
                    ...self.fixedPrice
                } : undefined,
                values: self.values ? serializeMapWithObjects(self.values, true) : undefined,
                fixedPriceAutomation: self.fixedPriceAutomation ? {
                    ...self.fixedPriceAutomation
                } : undefined,
                workloadAutomation: self.workloadAutomation?.map((b)=>cleanObject({
                        ...getSnapshot(b)
                    })),
                generatedStory: self.generatedStory || undefined,
                units: self.units || undefined,
                unitPrice: self.unitPrice || undefined,
                unitsName: self.unitsName,
                unitPriceName: self.unitPriceName,
                turnOff: self.turnOff,
                labelTurnOff: self.labelTurnOff,
                keepLabelTurnOff: self.keepLabelTurnOff,
                isOpened: self.isOpened,
                children: self.children?.map((child)=>child.serialize()),
                hideEstimateCollapse: self.hideEstimateCollapse,
                showCombinedHours: self.showCombinedHours,
                materialFirst: self.materialFirst,
                clearedMaterialInputNames: self.clearedMaterialInputNames,
                timelineColor: self.timelineColor,
                timelineFontColor: self.timelineFontColor
            });
        }
    }))/** ================ Common actions ================ */ .actions((self)=>({
        setName (name) {
            self.name = name;
        },
        setTimelineColor (color) {
            self.timelineColor = color;
        },
        setTimelineFontColor (fontColor) {
            self.timelineFontColor = fontColor;
        },
        addWorkType (workTypeId, invisible = false) {
            self.root?.getWorkTypeById(workTypeId)?.resetFixedValues();
            const existingWt = self.workTypes.find((workType)=>workType.id === workTypeId);
            if (existingWt) {
                existingWt.setInvisible(invisible);
            } else {
                const rootWT = self.root.workTypes.find((workType)=>workType.id === workTypeId);
                const data = {
                    id: workTypeId,
                    invisible
                };
                if (rootWT?.resources) {
                    data.resources = rootWT.resources.map((res)=>({
                            id: res.id
                        }));
                }
                self.workTypes.push(cast(data));
                self.root._internalUpdateTimelineSectionWorkTypeId(self.id, workTypeId);
            }
        },
        removeWorkType (workTypeId, manuallySet = false) {
            // if (!noValueRemoval)
            self.children?.forEach((child)=>child.removeWorkType(workTypeId));
            self.values?.delete(workTypeId);
            self.buffers?.forEach((b)=>b.removeWorkType(workTypeId));
            const relation = self.workTypes.find((workType)=>workType.id === workTypeId);
            if (relation) destroy(relation);
            self.workTypes.forEach((workType)=>workType._internalRemoveRelationOf(workTypeId));
            self._internalResetRelatedWorkTypePercentValues(workTypeId);
            self.root._internalUpdateTimelineSectionWorkTypeId(self.id, workTypeId, undefined, true);
            if (manuallySet) {
                self.root._internalUpdateRelatedWorkTypes?.(workTypeId);
            }
        },
        changeWorkType (workTypeId, newWorkTypeId, changeBufferWorkTypes, noSectionWorkTypeAdding, manuallySet = false) {
            self.root?.getWorkTypeById(newWorkTypeId)?.resetFixedValues();
            const newTarget = self.workTypes.find((workType)=>workType.id === newWorkTypeId);
            if (newTarget) self.removeWorkType(newWorkTypeId);
            const oldTarget = self.workTypes.find((workType)=>workType.id === workTypeId);
            if (oldTarget) {
                oldTarget.id = newWorkTypeId;
                self.workTypes.forEach((workType)=>workType.updateRelatedWorkTypeId(workTypeId, newWorkTypeId));
                self.root._internalUpdateTimelineSectionWorkTypeId(self.id, newWorkTypeId, workTypeId);
                self._internalChangeWorkType(workTypeId, newWorkTypeId, changeBufferWorkTypes);
            } else if (!noSectionWorkTypeAdding) this.addWorkType(newWorkTypeId, // Number(workTypeId) > 0,
            false); /** this triggers when work type is changed from empty one to new or existing one */ 
            if (manuallySet) {
                self.root._internalUpdateSelectedWorkTypes?.([
                    workTypeId,
                    newWorkTypeId
                ]);
            }
        },
        setWorkTypeVisibility (workTypeId, invisible) {
            const target = self.workTypes.find((workType)=>workType.id === workTypeId);
            if (target) target.setInvisible(invisible);
        },
        resetVisibility () {
            // self.turnOff = !self.hasChildren;
            self.isOpened = true;
        },
        pushChild (movedChild, position, skipPercentValueOverride) {
            const newChild = self._internalPushChild(movedChild, position, skipPercentValueOverride);
            if (newChild.type === TaskTypes.MATERIAL && self.children?.length === 1) {
                this.setMaterialFirst(true);
            }
            self.resetAllPercentValues(); //@todo: check if needed
        // if(updateStructureWorkTypes)
        //   self.root._internalUpdateSelectedWorkTypes?.();
        },
        pushChildren (movedChildren) {
            movedChildren.forEach((child)=>{
                const target = self.getExistingChild(child.id, child.componentId, child.name);
                const snapshot = getSnapshot(child);
                if (target) target.mergeWithOtherTask(snapshot);
                else self.pushChild(snapshot, 0);
            });
        },
        // addChildren(tasksData: ITask[]) {
        //   /** @note: for importing multiple children from library component */
        //   tasksData.forEach((data, index) => self.addChild(false, false, data, index));
        //   self.isOpened = true;
        // },
        reorderWorkTypes (reorderedWorkTypes) {
            self.workTypes = cast(updateSortedList(self.workTypes, reorderedWorkTypes));
        },
        expandAllChildren (state) {
            self.isOpened = state;
            self.children?.forEach((child)=>child.expandAllChildren(state));
        },
        setHideAllDescriptionColumns (hideDescriptions) {
            self.usedDescriptionColumns.forEach((column)=>{
                column.setHide(hideDescriptions);
            });
        },
        setHideEstimateCollapse (hideEstimateCollapse) {
            self.hideEstimateCollapse = hideEstimateCollapse;
        },
        setShowCombinedHours (showCombinedHours) {
            self.showCombinedHours = showCombinedHours;
        },
        setMaterialFirst (materialFirst, manuallySet) {
            if (self.materialFirst === materialFirst) return;
            self.materialFirst = materialFirst || undefined;
            if (manuallySet) {
                self.setMaterialFirstType(materialFirst);
                self.root._internalUpdateSelectedWorkTypes?.();
            }
        },
        hideAllWorkTypes (hideWorkType) {
            self.workTypes.forEach((workType)=>{
                workType.setHideWorkType(hideWorkType);
            });
        },
        setUnitsName (unitsName) {
            self.unitsName = unitsName;
            if (!self.clearedMaterialInputNames) this._internalClearChildMaterialInputTitles();
        },
        setUnitPriceName (unitPriceName) {
            self.unitPriceName = unitPriceName;
            if (!self.clearedMaterialInputNames) this._internalClearChildMaterialInputTitles();
        },
        _internalClearChildMaterialInputTitles () {
            self.clearedMaterialInputNames = true;
            self.children?.forEach((child)=>child._internalClearMaterialInputTitles());
        },
        reorderBreakdownColumns (list) {
            self.usedBreakdownColumns = cast(updateSortedList(self.usedBreakdownColumns, list, "name", true));
        },
        addTemporaryDescriptionColumn () {
            const lastColumnIndex = self.usedBreakdownColumns.filter((column)=>typeof column.descriptionIndex === "number").reverse()[0].descriptionIndex;
            const position = self.usedBreakdownColumns.findIndex((c)=>c.descriptionIndex === lastColumnIndex) + 1;
            const descriptionId = generateTaskDescriptionId(lastColumnIndex + 1);
            const displayName = `Description ${lastColumnIndex + 2}`;
            this.addDescriptionColumn(position, {
                descriptionId,
                ...DEFAULT_DESCRIPTION_COLUMN,
                visible: true,
                displayName
            });
            self.root.historyManager?.withoutUndo(()=>{
                self.usedBreakdownColumns[position].setAutofocus(true);
            });
        },
        addDescriptionColumn (position, columnData) {
            if (self.usedAllDescriptionColumns) return;
            if (position >= 0 && position < self.usedBreakdownColumns.length) {
                self.usedBreakdownColumns = cast([
                    ...self.usedBreakdownColumns.slice(0, position),
                    columnData,
                    ...self.usedBreakdownColumns.slice(position)
                ]);
            } else self.usedBreakdownColumns.push(columnData);
        },
        pinDescriptionColumn (columnId, state, pinRight = false) {
            const index = self.usedBreakdownColumns.findIndex((c)=>c.id === columnId);
            if (index < 0) return;
            if (!state && !self.usedBreakdownColumns[index + (pinRight ? -1 : 1)]?.applyPin) {
                self.usedBreakdownColumns[index].setApplyPin(false);
                return;
            }
            if (pinRight) {
                for(let i = self.usedBreakdownColumns.length - 1; i >= 2; i--){
                    if (!self.usedBreakdownColumns[i].pinRight) break;
                    if (!self.usedBreakdownColumns[i].applyPin && i >= index) self.usedBreakdownColumns[i].setApplyPin(true);
                    else if (i < index) self.usedBreakdownColumns[i].setApplyPin(false);
                }
            } else {
                for(let i = 2; i < self.usedBreakdownColumns.length; i++){
                    if (!self.usedBreakdownColumns[i].pinLeft) break;
                    if (!self.usedBreakdownColumns[i].applyPin && i <= index) self.usedBreakdownColumns[i].setApplyPin(true);
                    else if (i > index) self.usedBreakdownColumns[i].setApplyPin(false);
                }
            }
        },
        removeDescriptionColumn (columnId) {
            const column = self.usedBreakdownColumns.find((c)=>c.id === columnId);
            const descriptionId = column?.descriptionId;
            if (!column || typeof descriptionId !== "string" || descriptionId === DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER) return;
            destroy(column);
            self.removeDescription(descriptionId);
        },
        duplicateSelf (newName, libraryElementId) {
            const duplicate = clone(self, true);
            const oldId = self.id;
            duplicate.setName(newName);
            duplicate.setId(libraryElementId || generateEntryId());
            self.root?.duplicateSection(getSnapshot(duplicate), oldId, self.parentOrder + 1);
            self.root._internalUpdateSelectedWorkTypes?.();
        }
    }));

const TaskLabelModel = types.model("TaskLabel", {
    id: types.string,
    systemId: types.number,
    name: types.string,
    backgroundColor: types.maybe(types.string),
    fontColor: types.maybe(types.string)
}).actions((self)=>({
        update ({ name, fontColor, backgroundColor }) {
            self.name = name;
            self.fontColor = fontColor;
            self.backgroundColor = backgroundColor;
        },
        setName (name) {
            self.name = name;
        },
        setFontColor (fontColor) {
            self.fontColor = fontColor;
        },
        setBackgroundColor (backgroundColor) {
            self.backgroundColor = backgroundColor;
        },
        removeSelf () {
            const parent = getParent(self, 2);
            parent.removeLabel(self.systemId);
        }
    }));

const WorkTypeResourceModel = types.model("WorkTypeResourceBase", {
    id: types.identifier,
    utilizationOrder: types.number,
    systemId: types.maybe(types.number),
    name: types.string,
    timelineColor: types.maybe(types.string),
    timelineFontColor: types.maybe(types.string),
    rate: types.number,
    teamMembers: types.maybe(types.number),
    mta: types.maybe(MinAvgMaxValueModel),
    fixedTime: types.maybe(MinAvgMaxValueModel),
    fixedHours: types.maybe(MinAvgMaxValueExtendedModel),
    companyUnitCost: types.maybe(types.number),
    isHidden: types.maybe(types.boolean),
    hideOnTimeline: types.maybe(types.boolean),
    /** not serialized: */ clientRate: types.maybe(types.number),
    autoFocus: types.maybe(types.boolean)
})/** ================ Core ================ */ .views((self)=>({
        get root () {
            const root = getRoot(self);
            return root?.settings ? root : DEFAULT_ROOT_OBJECT;
        },
        get parent () {
            try {
                return getParent(self, 2);
            } catch (e) {
                return undefined;
            }
        }
    }))/** ================ Common getters & helpers ================ */ .views((self)=>({
        get workTypeId () {
            return self.parent.id;
        },
        get _internalParsedMta () {
            return parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (v, key)=>self.mta?.[key] || self.parent?.mta?.[key] || self.root.settings?.mta?.[key] || DEFAULT_MTA_VALUE);
        },
        get parsedMta () {
            return this._internalParsedMta;
        },
        get parsedWta () {
            return this._internalParsedMta;
        },
        get timeModifiedMta () {
            const { timeModifier, dayModifier } = self.root.settings;
            return parseObject(this.parsedMta, (v)=>v / (timeModifier || dayModifier || 1));
        },
        get hasClientRate () {
            return typeof self.clientRate === "number";
        },
        get hasFixedTime () {
            return !!self.fixedTime;
        },
        get isEmpty () {
            return !self.name; // || typeof self.id === "string";
        },
        get isOnlyResource () {
            return self.parent?.resources?.length === 1;
        },
        get resourceIndex () {
            return self.parent?.resources?.findIndex((r)=>r.id === self.id);
        },
        get treePath () {
            return self.parent ? [
                ...self.parent.treePath,
                self.id
            ] : [
                self.id
            ];
        },
        get maxTimePerMonth () {
            const key = self.root.timelineHoursKey;
            return this.parsedMta[key] * MaxTimeModifiers.HOUR * (self.teamMembers || 1);
        },
        get maxTimePerWeek () {
            return Number(Number(this.maxTimePerMonth / MONTH_WEEKS).toPrecision(12));
        },
        get hasFixedHours () {
            return self.parent?.hasFixedHours;
        },
        get usedRoundDecimals () {
            if (self.parent) return self.parent.usedRoundDecimals;
            return self.root.settings.usedRoundDecimals;
        }
    }))/** ================ Hours, Time & Cost getters ================ */ .views((self)=>({
        get averageRate () {
            return parseObject(DEFAULT_MIN_AVG_MAX_VALUE, typeof self.clientRate === "number" ? self.clientRate : self.rate);
        },
        get hours () {
            return {
                ...self.fixedHours || DEFAULT_MIN_AVG_MAX_VALUE
            };
        },
        get unbufferedHours () {
            if (!self.parent || typeof self.resourceIndex !== "number") return DEFAULT_MIN_AVG_MAX_VALUE;
            return self.parent.unbufferedResourceHours[self.resourceIndex];
        },
        get parsedHours () {
            const modifier = self.root.settings?.modifier;
            return parseObject(this.hours, (val, key)=>{
                if (typeof val !== "number" || key !== "avg" && key !== "min" && key !== "max") return val;
                return Number(roundFloat(val / modifier));
            });
        },
        get parentPercentTime () {
            // if (!self.parent?.usesSummaryPercent) return;
            return self.parent?.percentageWorkTypes?.reduce((t, wT)=>parseObject(t, (v, k)=>Math.max(v, wT.time[k] || 0)), DEFAULT_MIN_AVG_MAX_VALUE);
        },
        get time () {
            if (!isAlive(self)) return DEFAULT_MIN_AVG_MAX_VALUE;
            const useWeekTime = self.root.settings?.useWeekTime;
            if (self.root.settings.isTimelineTableUsed) {
                const timelineStartDate = self.root.settings?.timelineStartDate;
                return self.root.timelines ? parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (v, key)=>{
                    if (!self.root.timelines?.[key]) return v;
                    return differenceInCalendarMonths(self.root.timelines[key].lastTeamMemberEndDate(self.workTypeId, self.id), timelineStartDate) + 1;
                }) : DEFAULT_MIN_AVG_MAX_VALUE;
            }
            return parseObject(this.hours, (val, key)=>{
                if (typeof val !== "number" || key !== "avg" && key !== "min" && key !== "max") return val;
                return (self.fixedTime?.[key] || 0) / (useWeekTime ? 1 : MONTH_WEEKS) || this.parentPercentTime?.[key] || Math.ceil(val / MaxTimeModifiers.HOUR / (self.teamMembers || 1) / self.parsedMta[key] * (useWeekTime ? MONTH_WEEKS : 1) * 2) / 2;
            });
        },
        get price () {
            if (!isAlive(self)) return DEFAULT_MIN_AVG_MAX_VALUE;
            return parseObject(this.hours, (val, key)=>{
                if (typeof val !== "number" || key !== "avg" && key !== "min" && key !== "max") return val;
                return Number((val * (typeof self.clientRate === "number" ? self.clientRate : self.rate)).toFixed(2));
            });
        },
        get unbufferedPrice () {
            if (!isAlive(self)) return DEFAULT_MIN_AVG_MAX_VALUE;
            return parseObject(this.unbufferedHours, (hour)=>hour * (typeof self.clientRate === "number" ? self.clientRate : self.rate));
        },
        get projectUnitCost () {
            if (!isAlive(self)) return 0;
            const exchangeRateModifier = self.root.settings.exchangeRateModifier;
            return Number(((self.companyUnitCost || 0) * exchangeRateModifier).toPrecision(12));
        },
        get _internalCompanyUnbufferedCost () {
            if (!isAlive(self) || !self.companyUnitCost) return DEFAULT_MIN_AVG_MAX_VALUE;
            return parseObject(this.unbufferedHours, (hour)=>hour * this.projectUnitCost);
        },
        get companyUnbufferedCost () {
            return this._internalCompanyUnbufferedCost;
        },
        get _internalCompanyCost () {
            if (!isAlive(self) || !self.companyUnitCost) return DEFAULT_MIN_AVG_MAX_VALUE;
            return parseObject(this.hours, (val, key)=>{
                if (typeof val !== "number" || key !== "avg" && key !== "min" && key !== "max") return val;
                return val * this.projectUnitCost;
            });
        },
        get companyCost () {
            return this._internalCompanyCost;
        },
        get _internalCompanyUnbufferedProfit () {
            if (!isAlive(self) || !self.companyUnitCost) return this.unbufferedPrice;
            return parseObject(this.unbufferedPrice, (price, key)=>{
                return price - (this.companyUnbufferedCost[key] || 0);
            });
        },
        get companyUnbufferedProfit () {
            return this._internalCompanyUnbufferedProfit;
        },
        get _internalCompanyProfit () {
            if (!isAlive(self) || !self.companyUnitCost) return this.price;
            return parseObject(this.price, (val, key)=>{
                if (typeof val !== "number" || key !== "avg" && key !== "min" && key !== "max") return val;
                return val - (this.companyCost[key] || 0);
            });
        },
        get companyProfit () {
            return this._internalCompanyProfit;
        },
        get companyProfitability () {
            const key = self.root.settings?.useMinMax ? MinAvgMaxKeys.MIN : MinAvgMaxKeys.AVG;
            if (!isAlive(self) || !this.price[key]) return 100; // should result in "N/A" in inputs, as it's really infinite :P
            return roundFloat((this.companyProfit[key] || 0) / this.price[key] * 100, 0);
        }
    }))/** ================ Serializers ================ */ .views((self)=>({
        serialize () {
            return cleanObject({
                id: self.id,
                utilizationOrder: self.utilizationOrder,
                systemId: self.systemId,
                name: self.name,
                timelineColor: self.timelineColor,
                timelineFontColor: self.timelineFontColor,
                rate: self.rate,
                mta: self.mta ? {
                    ...self.mta
                } : undefined,
                teamMembers: self.teamMembers,
                fixedHours: self.fixedHours ? cleanObject({
                    ...self.fixedHours
                }) : undefined,
                fixedTime: self.fixedTime ? cleanObject({
                    ...self.fixedTime
                }) : undefined,
                companyUnitCost: self.companyUnitCost,
                isHidden: self.isHidden,
                hideOnTimeline: self.hideOnTimeline
            });
        }
    }))/** ================ Data display getters ================ */ .views((self)=>({
        get displayRate () {
            if (!isAlive(self)) return 0;
            const modifier = self.root.settings.modifier;
            return roundFloat((typeof self.clientRate === "number" ? self.clientRate : self.rate) * modifier, 0, true);
        },
        get displayPrice () {
            if (!isAlive(self)) return 0;
            const roundPrice = self.root.settings?.roundPrice;
            const v = self.root.settings.maxValuesAsBuffer && self.root.displayMinValue ? self.unbufferedPrice : self.price;
            return roundFloat(self.root.getDisplayValue(v), roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        },
        get displayUnbufferedPrice () {
            if (!isAlive(self)) return 0;
            const roundPrice = self.root.settings?.roundPrice;
            return roundFloat(self.root.getDisplayValue(self.unbufferedPrice), roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        },
        get displayPriceMax () {
            if (!isAlive(self)) return 0;
            const roundPrice = self.root.settings?.roundPrice;
            const v = self.root.settings.maxValuesAsBuffer ? self.root.getDisplayValue(self.price) : self.root.getDisplayValueMax(self.price);
            return roundFloat(v, roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        },
        get displayUnbufferedPriceMax () {
            if (!isAlive(self)) return 0;
            const roundPrice = self.root.settings?.roundPrice;
            const v = self.root.settings.maxValuesAsBuffer ? self.root.getDisplayValue(self.unbufferedPrice) : self.root.getDisplayValueMax(self.unbufferedPrice);
            return roundFloat(v, roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        },
        get showMaxPrice () {
            return (self.root.displayTwoValues || self.root.displayWorkTypeTwoValues[self.workTypeId.toString()]) && this.displayPrice !== this.displayPriceMax;
        },
        get displayHours () {
            const modifier = self.root.settings.modifier;
            return self.root.settings.maxValuesAsBuffer && self.root.displayMinValue ? Number((self.root.getDisplayValue(self.unbufferedHours) / modifier).toPrecision(12)) : self.root.getDisplayValue(self.parsedHours);
        },
        get displayUnbufferedHours () {
            const modifier = self.root.settings.modifier;
            return Number((self.root.getDisplayValue(self.unbufferedHours) / modifier).toPrecision(12));
        },
        get displayHoursMax () {
            return self.root.settings.maxValuesAsBuffer ? self.root.getDisplayValue(self.parsedHours) : self.root.getDisplayValueMax(self.parsedHours);
        },
        get displayUnbufferedHoursMax () {
            const modifier = self.root.settings.modifier;
            const v = self.root.settings.maxValuesAsBuffer ? self.root.getDisplayValue(self.unbufferedHours) : self.root.getDisplayValueMax(self.unbufferedHours);
            return Number((v / modifier).toPrecision(12));
        },
        get showMaxHours () {
            return (self.root.displayTwoValues || self.root.displayWorkTypeTwoValues[self.workTypeId.toString()]) && this.displayHours !== this.displayHoursMax;
        },
        get displayTime () {
            if (!isAlive(self)) return 0;
            return self.root.getDisplayValue(self.time);
        },
        get displayTimeMax () {
            if (!isAlive(self)) return 0;
            return self.root.getDisplayValueMax(self.time);
        },
        get showMaxTime () {
            return (self.root.displayTwoValues || (self.root.settings.isTimelineTableUsed ? self.root.displayTotalTwoValues : self.root.displayWorkTypeTwoValues[self.workTypeId.toString()])) && !self.root.settings.maxValuesAsBuffer && this.displayTime !== this.displayTimeMax;
        },
        get availableHours () {
            const useMinMax = self.root.settings?.useMinMax;
            return {
                min: useMinMax ? self.parent?.parsedHours.min : self.parent?.parsedHours.avg,
                max: self.parent?.parsedHours.max
            };
        },
        get displayCompanyUnitCost () {
            const { modifier } = self.root.settings;
            return roundFloat((self.companyUnitCost || 0) * modifier, 0, true);
        },
        get displayCompanyUnitCostWithModifier () {
            if (!isAlive(self)) return 0;
            const modifier = self.root.settings.modifier;
            const exchangeRateModifier = self.root.settings.exchangeRateModifier;
            return roundFloat((self.companyUnitCost || 0) * modifier * exchangeRateModifier, 0, true);
        },
        get displayCompanyCost () {
            const roundPrice = self.root.settings?.roundPrice;
            const v = self.root.settings.maxValuesAsBuffer && self.root.displayMinValue ? self.companyUnbufferedCost : self.companyCost;
            return roundFloat(self.root.getDisplayValue(v), roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        },
        get displayCompanyCostMax () {
            const roundPrice = self.root.settings?.roundPrice;
            const v = self.root.settings.maxValuesAsBuffer ? self.root.getDisplayValue(self.companyCost) : self.root.getDisplayValueMax(self.companyCost);
            return roundFloat(v, roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        },
        get showMaxCompanyCost () {
            return (self.root.displayTwoValues || self.root.displayWorkTypeTwoValues[self.workTypeId.toString()]) && this.displayCompanyCost !== this.displayCompanyCostMax;
        },
        get displayCompanyProfit () {
            const roundPrice = self.root.settings?.roundPrice;
            const v = self.root.settings.maxValuesAsBuffer && self.root.displayMinValue ? self.companyUnbufferedProfit : self.companyProfit;
            return roundFloat(self.root.getDisplayValue(v), roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        },
        get displayCompanyProfitMax () {
            const roundPrice = self.root.settings?.roundPrice;
            const v = self.root.settings.maxValuesAsBuffer ? self.root.getDisplayValue(self.companyProfit) : self.root.getDisplayValueMax(self.companyProfit);
            return roundFloat(v, roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        },
        get showMaxCompanyProfit () {
            return (self.root.displayTwoValues || self.root.displayWorkTypeTwoValues[self.workTypeId.toString()]) && this.displayCompanyProfit !== this.displayCompanyProfitMax;
        }
    }))/** ================ Common actions ================ */ .actions((self)=>({
        afterCreate () {
            if (!self.parent || !("setTimelineColor" in self.parent)) return;
            if (!self.timelineColor && self.parent.color) self.timelineColor = self.parent.color;
            if (!self.timelineFontColor) {
                self.timelineFontColor = getContrastingFontColor(self.timelineColor || self.parent.color);
            }
        },
        setUtilizationOrder (utilizationOrder) {
            self.utilizationOrder = utilizationOrder;
        },
        setTimelineColor (timelineColor) {
            self.timelineColor = timelineColor;
        },
        setTimelineFontColor (timelineFontColor) {
            self.timelineFontColor = timelineFontColor;
        },
        setIsHidden (isHidden) {
            self.isHidden = isHidden;
        },
        setHideOnTimeline (hideOnTimeline) {
            self.hideOnTimeline = hideOnTimeline;
        },
        setTeamMembers (members) {
            self.teamMembers = members;
        },
        setMta (mta) {
            const { timeModifier, dayModifier } = self.root.settings;
            const keys = Object.keys(mta);
            const mod = keys.reduce((t, m)=>({
                    ...t,
                    [m]: typeof mta[m] === "number" ? mta[m] * (timeModifier || dayModifier || 1) : null
                }), {});
            self.mta = cast({
                ...self.mta || EMPTY_MIN_AVG_MAX_VALUE,
                ...mod
            });
        },
        _internalSetFixedHours (hours, manuallySet, setOverride) {
            const modifier = self.root?.settings?.modifier;
            let newHours = hours;
            const keys = Object.keys(hours);
            if (manuallySet) {
                newHours = modifyNewValuesWithCoefficients(hours, keys, self.fixedHours || DEFAULT_MIN_AVG_MAX_VALUE, modifier);
                if (keys.includes(MinAvgMaxKeys.AVG)) {
                    newHours.avgSet = true;
                    if (setOverride) newHours.avgOv = true;
                }
                if (keys.includes(MinAvgMaxKeys.MIN) || keys.includes(MinAvgMaxKeys.MAX)) {
                    newHours.minMaxSet = true;
                    if (setOverride) newHours.avgOv = true;
                }
            }
            self.fixedHours = cast({
                ...self.fixedHours || DEFAULT_MIN_AVG_MAX_VALUE,
                ...newHours
            });
        },
        setFixedHours (hours) {
            this._internalSetFixedHours(hours, true, true);
            if (self.parent?.calculateAverageRate) self.parent.calculateAverageRate();
        },
        resetMta () {
            if (!self.mta) return;
            if (self.root?.settings?.useMinMax) {
                self.mta.min = null;
                self.mta.max = null;
            } else self.mta.avg = null;
            if (Object.values(self.mta).reduce((t, v)=>t && v === null, true)) self.mta = undefined;
        },
        setFixedTime (time) {
            const keys = Object.keys(time);
            const newTime = modifyNewValuesWithCoefficients(time, keys, self.fixedTime);
            self.fixedTime = cast({
                ...self.fixedTime || DEFAULT_MIN_AVG_MAX_VALUE,
                ...newTime
            });
        },
        setRate (rate, manuallySet) {
            self.rate = rate;
            if (manuallySet && self.parent?.calculateAverageRate) self.parent.calculateAverageRate();
            return self.parent?.hasAverageRate;
        },
        // setHoursOverwrite(hoursOverwritten: boolean, key: keyof IMinAvgMaxValue) {
        //   if (!self.hoursOverwritten && hoursOverwritten) {
        //     self.hoursOverwritten = cast({ [key]: hoursOverwritten });
        //     return;
        //   }
        //   if (!self.hoursOverwritten) return;
        //
        //   self.hoursOverwritten[key] = hoursOverwritten;
        //
        //   if (
        //     Object.values(self.hoursOverwritten).reduce(
        //       (t, v) => t && v === null,
        //       true,
        //     )
        //   )
        //     self.hoursOverwritten = undefined;
        // },
        _internalClearHoursOverride () {
            self.fixedHours?.clearOverride(self.root.settings?.useMinMax);
        },
        changeName (name, systemId) {
            self.name = name;
            self.systemId = systemId;
        },
        resetFixedTime () {
            self.fixedTime = undefined;
        },
        removeAutofocus () {
            self.root.historyManager?.withoutUndo(()=>{
                self.autoFocus = undefined;
            });
        },
        resetClientRate () {
            self.clientRate = undefined;
        },
        removeSelf () {
            if (!isAlive(self)) return;
            self.parent?.removeResource(self.id.toString());
        },
        setCompanyUnitCost (companyUnitCost) {
            self.companyUnitCost = companyUnitCost;
        },
        oneTimeReplaceCompanyUnitCost () {
            if (!self.companyUnitCost || self.root.usesNewPA) return;
            const exchangeRateModifier = self.root.settings.exchangeRateModifier;
            self.companyUnitCost = self.companyUnitCost / exchangeRateModifier;
        },
        _internalSetCompanyProfit (companyProfit, key) {
            this.setRate(self.hours[key] ? self.projectUnitCost + companyProfit / self.hours[key] : 0);
        },
        setCompanyProfit (companyProfit, key) {
            this._internalSetCompanyProfit(companyProfit, key);
        },
        _internalSetCompanyProfitability (companyProfitability) {
            if (companyProfitability >= 100) companyProfitability = 99.99;
            this.setRate(self.projectUnitCost / (1 - companyProfitability / 100));
        },
        setCompanyProfitability (companyProfitability) {
            this._internalSetCompanyProfitability(companyProfitability);
        }
    }));

const WorkTypeModel = WorkTypeResourceModel.named("WorkType").props({
    color: types.maybe(types.string),
    resources: types.maybe(types.array(types.late(()=>WorkTypeResourceModel))),
    isOpened: types.maybe(types.boolean),
    isTimelineOpened: types.maybe(types.boolean),
    turnOff: types.maybe(types.boolean),
    roundType: types.maybe(types.number),
    roundInterval: types.maybe(types.number),
    roundDecimals: types.optional(types.number, 2),
    applyRoundDecimals: types.maybe(types.boolean),
    fixedPrice: types.maybe(MinAvgMaxValueExtendedModel),
    fixedCompanyCost: types.maybe(types.number),
    /** not serialized */ btOpened: types.maybe(types.boolean),
    clientTurnOff: types.maybe(types.boolean)
})/** ================ Common getters & helpers ================ */ .views((self)=>({
        get treePath () {
            return [
                getWorkTypeTreePath(self.id)
            ];
        },
        get workTypeId () {
            return self.id;
        },
        get hasResources () {
            return !!self.resources?.length; // && self.root.settings?.hasTeamMembersCol;
        },
        get displayResources () {
            if (!self.root.visibility.apply) return self.resources || [];
            return self.resources?.filter((r)=>!r.isHidden) || [];
        },
        get timelineResources () {
            return self.resources?.slice().sort((wT1, wT2)=>wT1.utilizationOrder - wT2.utilizationOrder) || [];
        },
        get displayTimelineResources () {
            if (!self.root.visibility.apply) return this.timelineResources;
            return this.timelineResources.filter((r)=>!r.isHidden && !r.hideOnTimeline) || [];
        },
        get hasFixedPrice () {
            // if (self.inBreakdown || this.hasResources) return false;
            if (this.hasResources || !self.fixedPrice) return false;
            return self.root.settings?.useMinMax ? typeof self.fixedPrice?.min === "number" || typeof self.fixedPrice?.max === "number" : typeof self.fixedPrice?.avg === "number";
        },
        get hasFixedHours () {
            if (!self.fixedHours) return false;
            return self.root.settings.useMinMax ? typeof self.fixedHours?.min === "number" || typeof self.fixedHours?.max === "number" : typeof self.fixedHours?.avg === "number";
        },
        get hasFixedValues () {
            return this.hasFixedPrice || this.hasFixedHours;
        },
        get hasPercent () {
            if (this.hasFixedValues) return false;
            return this.percentTargetsInSections?.length > 0;
        },
        get hasGlobalPercent () {
            if (this.hasFixedValues) return false;
            return this.allPercentTargetsInSections?.length > 0;
        },
        get hasRounding () {
            return typeof self.roundType === "number" && typeof self.roundInterval === "number";
        },
        // get usesSummaryPercent() {
        //   return Boolean(!self.inBreakdown && self.percent && self.of?.length);
        // },
        // get areHoursOverwritten() {
        //   if (self.inBreakdown) return false;
        //   return Boolean(this.hasFixedHours || this.usesSummaryPercent);
        // },
        get resourceHoursOverwritten () {
            if (!self.resources?.length || self.root.settings.isTimelineTableUsed) return false;
            return Boolean(self.resources.find((r)=>self.root.settings.useMinMax ? r.fixedHours?.minMaxOv : r.fixedHours?.avgOv));
        },
        get percentTargetsInSections () {
            /** collect all WT dependencies thorough all sections */ // if (this.usesSummaryPercent) return [...self.of!];
            return self.root.sections?.reduce((workTypes, section)=>{
                const sectionWT = section.workTypes.find((wT)=>wT.id.toString() === self.id.toString());
                if (sectionWT?.percent && sectionWT?.of?.length) workTypes.push(...sectionWT.of.filter((o)=>!workTypes.includes(o)));
                return workTypes;
            }, []) || [];
        },
        get allPercentTargetsInSections () {
            /** collect all WT dependencies thorough all sections */ // if (this.usesSummaryPercent) return [...self.of!];
            return self.root.sections?.reduce((workTypes, section)=>{
                const sectionWT = section.workTypes.find((wT)=>wT.id.toString() === self.id.toString());
                const sectionWorkloadAutomation = section.cellAutomatedWorkTypes?.find((a)=>a.id.toString() === self.id.toString());
                if (sectionWT?.percent && sectionWT?.of?.length) workTypes.push(...sectionWT.of.filter((o)=>!workTypes.includes(o)));
                else if (sectionWorkloadAutomation && sectionWorkloadAutomation.of?.length) workTypes.push(...sectionWorkloadAutomation.of.filter((o)=>!workTypes.includes(o)));
                return workTypes;
            }, []) || [];
        },
        get cellAutomatedWorkTypes () {
            let counter = 0;
            const relatedWorkTypeIds = new Set();
            let hasAutomation = false;
            self.root.sections?.forEach((section)=>{
                const automatedWorkType = section.cellAutomatedWorkTypes?.find(({ id })=>id === self.id);
                if (!automatedWorkType) return;
                hasAutomation = true;
                counter += automatedWorkType.counter;
                automatedWorkType.of?.forEach((id)=>relatedWorkTypeIds.add(id));
            });
            if (!hasAutomation) return {
                of: [],
                counter: 0
            };
            const of = [
                ...relatedWorkTypeIds
            ].map((id)=>{
                const workType = self.root.workTypes?.find((value)=>value.id === id);
                return workType ? {
                    id,
                    name: workType.name
                } : undefined;
            }).filter((workType)=>Boolean(workType));
            return {
                of,
                counter
            };
        },
        get percentageWorkTypes () {
            return this.percentTargetsInSections.map((id)=>{
                const wT = self.root.workTypes?.find((wT)=>wT.id === id);
                if (!wT) return;
                return {
                    id: wT.id,
                    name: wT.name,
                    hours: wT.hours,
                    mta: wT.parsedMta,
                    time: wT.time
                };
            }).filter((wT)=>wT !== undefined);
        },
        get usedBuffers () {
            return self.root.settings.buffers?.filter(({ workTypes, used })=>used && workTypes.includes(self.id)) || [];
        },
        get usedBufferPercents () {
            return self.root.settings.buffers?.filter(({ workTypes, used })=>used && workTypes.includes(self.id))?.map(({ parsedPercent })=>parsedPercent) || [];
        },
        get usedRoundDecimals () {
            if (self.applyRoundDecimals) return typeof self.roundDecimals === "number" ? self.roundDecimals : 2;
            return self.root.settings.usedRoundDecimals;
        }
    }))/** ================ Hours, Time & Cost getters ================ */ .views((self)=>({
        get resourcesPrice () {
            if (!self.hasResources) return DEFAULT_MIN_AVG_MAX_VALUE;
            return sumObjectsData(...self.resources.map((res)=>res.price));
        },
        get price () {
            if (!isAlive(self)) return DEFAULT_MIN_AVG_MAX_VALUE;
            const priceObj = parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (_, key)=>{
                // if (!self.inBreakdown && !self.resources?.length && this.hasFixedPrice)
                if (self.hasFixedPrice) return self.fixedPrice?.[key] || 0;
                if (self.hasResources) return this.resourcesPrice[key];
                return Number(((self.hours[key] || 0) * (typeof self.clientRate === "number" ? self.clientRate : self.rate)).toFixed(2));
            });
            if (!self.hasFixedPrice && self.root.forcedStateWorkTypes?.[self.id]) priceObj.forcedState = self.root.forcedStateWorkTypes[self.id];
            return priceObj;
        },
        get unbufferedPrice () {
            if (!isAlive(self)) return DEFAULT_MIN_AVG_MAX_VALUE;
            // if (!self.inBreakdown && !self.resources?.length && this.hasFixedPrice)
            if (self.hasFixedPrice) return self.fixedPrice;
            return parseObject(this.unbufferedHours, (val, key)=>{
                if (typeof val !== "number" || key !== "avg" && key !== "min" && key !== "max") return val;
                return val * (typeof self.clientRate === "number" ? self.clientRate : self.rate);
            });
        //
        // return parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (
        //   v: number | null | undefined,
        //   key: keyof IMinAvgMaxValue,
        // ) => {
        //   if (!self.inBreakdown && !self.resources?.length && this.hasFixedPrice)
        //     return self.fixedPrice?.[key] || 0;
        //
        //   return (
        //     (this.unbufferedHours[key] || 0) *
        //     (typeof self.clientRate === "number" ? self.clientRate : self.rate)
        //   );
        // });
        },
        get originalPrice () {
            if (!isAlive(self)) return DEFAULT_MIN_AVG_MAX_VALUE;
            // const useCustomPrice = !self.inBreakdown && !self.resources?.length;
            const useCustomPrice = !self.hasResources;
            const parsePrice = (v, key)=>{
                if (useCustomPrice && self.fixedPrice?.[key]) return self.fixedPrice[key];
                if (self.hasResources) return this.resourcesPrice[key];
                return (self.hours[key] || 0) * (typeof self.clientRate === "number" ? self.clientRate : self.rate);
            };
            return parseObject(DEFAULT_MIN_AVG_MAX_VALUE, parsePrice);
        },
        get companyUnbufferedCost () {
            if (self.hasFixedPrice) return parseObject(DEFAULT_MIN_AVG_MAX_VALUE, ()=>self.fixedCompanyCost || 0);
            if (self.hasResources) return sumObjectsData(...self.resources.map((res)=>res.companyUnbufferedCost || DEFAULT_MIN_AVG_MAX_VALUE));
            return self._internalCompanyUnbufferedCost;
        },
        get companyCost () {
            if (self.hasFixedPrice) return parseObject(DEFAULT_MIN_AVG_MAX_VALUE, ()=>self.fixedCompanyCost || 0);
            if (self.hasResources) return sumObjectsData(...self.resources.map((res)=>res.companyCost || DEFAULT_MIN_AVG_MAX_VALUE));
            return self._internalCompanyCost;
        },
        get companyUnbufferedProfit () {
            if (self.hasFixedPrice) return parseObject(self.unbufferedPrice, (price)=>price - (self.fixedCompanyCost || 0));
            if (self.hasResources) return sumObjectsData(...self.resources.map((res)=>res.companyUnbufferedProfit));
            return self._internalCompanyUnbufferedProfit;
        },
        get companyProfit () {
            if (self.hasFixedPrice) return parseObject(self.price, (val, key)=>{
                if (typeof val !== "number" || key !== "avg" && key !== "min" && key !== "max") return val;
                return val - (self.fixedCompanyCost || 0);
            });
            if (self.hasResources) return sumObjectsData(...self.resources.map((res)=>res.companyProfit));
            return self._internalCompanyProfit;
        },
        get parsedMta () {
            if (self.hasResources) return self.resources.reduce((newMta, resource)=>sumObjectsData(newMta, resource.parsedMta), DEFAULT_MIN_AVG_MAX_VALUE);
            if (self.mta) return self._internalParsedMta;
            return self.root.settings?.mta;
        },
        get time () {
            const hasTeamMembersCol = self.root.settings?.hasTeamMembersCol;
            const useWeekTime = self.root.settings?.useWeekTime;
            let t;
            if (self.hasResources) t = parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (v, key)=>Math.max(...self.resources.map((res)=>res.time[key])));
            else if (isAlive(self) && self.root.settings.isTimelineTableUsed) {
                const timelineStartDate = self.root.settings?.timelineStartDate;
                return self.root.timelines ? parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (v, key)=>{
                    if (!self.root.timelines?.[key]) return v;
                    return differenceInCalendarMonths(self.root.timelines[key].lastWorkTypeEndDate(self.id), timelineStartDate) + 1;
                }) : DEFAULT_MIN_AVG_MAX_VALUE;
            }
            if (t) {
                if (self.root.displayTotalTwoValues && self.root.settings.isTimelineTableUsed) t.forcedState = WorkTypeValueState.MIN_MAX;
                else if (self.root.forcedStateWorkTypes?.[self.id]) t.forcedState = self.root.forcedStateWorkTypes[self.id];
                return t;
            }
            const tM = isAlive(self) && hasTeamMembersCol && self.teamMembers ? self.teamMembers : 1;
            return parseObject(self.hours, (val, key)=>{
                if (typeof val !== "number" || key !== "avg" && key !== "min" && key !== "max") return val;
                return (self.fixedTime?.[key] || 0) / (useWeekTime ? 1 : MONTH_WEEKS) || Math.ceil(val / MaxTimeModifiers.HOUR / tM / this.parsedMta[key] * (useWeekTime ? MONTH_WEEKS : 1) * 2) / 2;
            });
        },
        get breakdownHours () {
            return self.root?.getTotalHoursPerWorkType?.(self.id) || DEFAULT_MIN_AVG_MAX_VALUE;
        },
        get unbufferedBreakdownHours () {
            return self.root?.getUnbufferedTotalHoursPerWorkType?.(self.id) || DEFAULT_MIN_AVG_MAX_VALUE;
        },
        get hours () {
            if (!isAlive(self)) return DEFAULT_MIN_AVG_MAX_VALUE;
            const modifier = self.root.settings.modifier;
            let val = {
                ...this.breakdownHours
            };
            if (self.hasFixedHours) {
                val = {
                    ...self.fixedHours
                };
                if (self.usedBufferPercents?.length) val = parseObject(val, (val, key)=>{
                    if (typeof val !== "number" || key !== "avg" && key !== "min" && key !== "max") return val;
                    return self.usedBufferPercents.reduce((t, p)=>t + val * p, val);
                });
            }
            if (typeof self.roundType === "number" && typeof self.roundInterval === "number") {
                MIN_AVG_MAX_KEYS.forEach((vKey)=>{
                    const h = val[vKey];
                    if (!h) return;
                    val[vKey] = safeRoundHoursV2(h, modifier, self.roundType, self.roundInterval);
                });
            }
            return val;
        },
        get unbufferedHours () {
            if (!isAlive(self)) return DEFAULT_MIN_AVG_MAX_VALUE;
            // if (self.inBreakdown || !self.hasFixedHours)
            if (!self.hasFixedHours) return this.unbufferedBreakdownHours;
            // return self.fixedHours || this.percentHours || DEFAULT_MIN_AVG_MAX_VALUE;
            return self.fixedHours || DEFAULT_MIN_AVG_MAX_VALUE;
        },
        get unbufferedResourceHours () {
            if (!isAlive(self) || !self.resources?.length) return [];
            const modifier = self.root.settings.modifier;
            const total = sumObjectsData(...self.resources.map((res)=>res.hours));
            return weightedHoursRoundedSplit(self.unbufferedHours, self.resources.map((res)=>parseObject(total, (v, key)=>v ? res.hours?.[key] || 0 : 1)), modifier, 3);
        },
        get averageRate () {
            const resourceRates = self.resources?.map((res)=>res.displayRate) || [];
            if (!self.hasResources || Math.min(...resourceRates) === Math.max(...resourceRates)) return parseObject(DEFAULT_MIN_AVG_MAX_VALUE, typeof self.clientRate === "number" && !self.hasResources ? self.clientRate : self.rate);
            return parseObject(self.hours, (hours, key)=>{
                const modifier = hours > 0 ? 1 : self.resources.length;
                const resourceHours = self.resources?.reduce((total, res)=>total + (hours ? res.hours[key] / hours : 1) * (typeof res.clientRate === "number" ? res.clientRate : res.rate), 0);
                return resourceHours / modifier;
            });
        }
    }))/** ================ BT getters ================ */ .views((self)=>({
        get btHours () {
            const h = self.root?.getTotalBTHoursPerWorkType?.(self.id);
            if (!h || self.hasFixedHours) return null;
            // return self.inBreakdown || !self.fixedHours ? h : null;
            return h;
        },
        get btHourDiff () {
            if (!this.btHours) return EMPTY_MIN_AVG_MAX_VALUE;
            return parseObject(EMPTY_MIN_AVG_MAX_VALUE, (_, key)=>roundFloat((this.btHours || 0) - (self.hours?.[key] || 0)));
        },
        get btHourDiffPercent () {
            return parseObject(EMPTY_MIN_AVG_MAX_VALUE, (_, key)=>!self.hours?.[key] ? 0 : roundFloat(((this.btHours || 0) / (self.hours?.[key] || 0) - 1) * 100));
        },
        get btPrice () {
            if (!isAlive(self) || !this.btHours || self.hasFixedPrice) return null;
            // const useCustomPrice = !self.inBreakdown && !self.resources?.length;
            // if (useCustomPrice && self.hasFixedPrice) return null;
            // if (self.hasFixedPrice) return null;
            return this.btHours * (typeof self.clientRate === "number" ? self.clientRate : self.rate);
        },
        get btPriceDiff () {
            if (!this.btPrice) return EMPTY_MIN_AVG_MAX_VALUE;
            return parseObject(EMPTY_MIN_AVG_MAX_VALUE, (_, key)=>roundFloat((this.btPrice || 0) - (self.price?.[key] || 0)));
        },
        get btPriceDiffPercent () {
            return parseObject(EMPTY_MIN_AVG_MAX_VALUE, (_, key)=>!self.price?.[key] ? 0 : roundFloat(((this.btPrice || 0) / (self.price?.[key] || 0) - 1) * 100));
        },
        get btTrend () {
            return self.root.getAggregatedTrendPerWorkType?.(self.id) || DEFAULT_MIN_AVG_MAX_VALUE;
        },
        get forecastedBtHours () {
            // if (!self.inBreakdown && self.fixedHours)
            if (self.hasFixedHours) return null;
            const h = self.root?.getTotalHoursPerWorkType?.(self.id);
            if (!h) return null;
            const { modifier } = self.root.settings;
            return parseObject(h, (val, key)=>safeRoundHours(((this.btTrend[key] || 0) + 1) * val, modifier));
        },
        get parsedForecastedBTHours () {
            if (!isAlive(self) || !this.forecastedBtHours) return null;
            const { modifier } = self.root.settings;
            return parseObject(this.forecastedBtHours, (val)=>val / modifier);
        },
        get forecastedBtHourDiff () {
            if (!this.forecastedBtHours) return EMPTY_MIN_AVG_MAX_VALUE;
            return parseObject(this.forecastedBtHours, (val, key)=>!self.hours?.[key] ? 0 : roundFloat(((val || 0) / (self.hours?.[key] || 0) - 1) * 100));
        },
        get forecastedBtHourDiffPercent () {
            if (!this.forecastedBtHours) return EMPTY_MIN_AVG_MAX_VALUE;
            return parseObject(this.forecastedBtHourDiff, (val, key)=>roundFloat((val || 0) / (this.forecastedBtHours[key] || 0)));
        },
        get forecastedBtPrice () {
            if (!isAlive(self) || !this.forecastedBtHours || self.hasFixedPrice) return null;
            // const useCustomPrice = !self.inBreakdown && !self.resources?.length;
            // if (useCustomPrice && self.hasFixedPrice) return null;
            // if (self.hasFixedPrice) return null;
            return parseObject(this.forecastedBtHours, (val)=>val * (typeof self.clientRate === "number" ? self.clientRate : self.rate));
        },
        get displayHoursForecastedBTPrice () {
            if (!this.forecastedBtPrice) return null;
            const roundPrice = self.root.settings?.roundPrice;
            return parseObject(this.forecastedBtPrice, (val)=>roundFloat(val, roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice));
        },
        get forecastedBtPriceDiff () {
            if (!this.forecastedBtPrice) return EMPTY_MIN_AVG_MAX_VALUE;
            return parseObject(this.forecastedBtPrice, (val, key)=>(self.price?.[key] || 0) === 0 ? 0 : roundFloat(((val || 0) / (self.price?.[key] || 0) - 1) * 100));
        },
        get forecastedBtPriceDiffPercent () {
            if (!this.forecastedBtPrice) return EMPTY_MIN_AVG_MAX_VALUE;
            return parseObject(this.forecastedBtPrice, (val, key)=>roundFloat((val || 0) / (self.price?.[key] || 0)));
        }
    }))/** ================ Serializers ================ */ .views((self)=>({
        serialize () {
            return cleanObject({
                id: self.id,
                systemId: self.systemId,
                utilizationOrder: self.utilizationOrder,
                name: self.name,
                color: self.color,
                timelineColor: self.timelineColor,
                timelineFontColor: self.timelineFontColor,
                rate: self.rate,
                mta: self.mta ? {
                    ...self.mta
                } : undefined,
                teamMembers: self.teamMembers,
                fixedHours: self.fixedHours ? cleanObject({
                    ...self.fixedHours
                }) : undefined,
                fixedPrice: self.fixedPrice ? cleanObject({
                    ...self.fixedPrice
                }) : undefined,
                fixedTime: self.fixedTime ? cleanObject({
                    ...self.fixedTime
                }) : undefined,
                roundType: self.roundType,
                roundInterval: self.roundInterval,
                roundDecimals: self.roundDecimals,
                applyRoundDecimals: self.applyRoundDecimals,
                turnOff: self.turnOff,
                isOpened: self.isOpened,
                isTimelineOpened: self.isTimelineOpened,
                resources: self.resources?.map((role)=>role.serialize()),
                companyUnitCost: self.companyUnitCost,
                fixedCompanyCost: self.fixedCompanyCost,
                isHidden: self.isHidden,
                hideOnTimeline: self.hideOnTimeline
            });
        }
    }))/** ================ Data display getters ================ */ .views((self)=>({
        get displayRateCellSize () {
            const c = self.root.settings.currencyObj;
            if (!c) return 156;
            return `${c.symbolStart || ""}${self.displayPrice} ${c.symbolEnd || ""}`.length * 10;
        },
        get displayPriceCellSize () {
            if (!self.root.renderPrice) return 104;
            let size = self.root.renderPrice(self.displayPrice);
            if (self.showMaxPrice) size += self.root.renderPrice(self.displayPriceMax) + 16;
            return size;
        },
        get displayProfitCellSize () {
            if (!self.root.renderPrice) return 104;
            let size = self.root.renderPrice(self.displayCompanyProfit);
            if (self.showMaxCompanyProfit) size += self.root.renderPrice(self.displayCompanyProfitMax) + 16;
            return size;
        },
        get displayCompanyCostCellSize () {
            if (!self.root.renderPrice) return 104;
            let size = self.root.renderPrice(self.displayCompanyCost);
            if (self.showMaxCompanyCost) size += self.root.renderPrice(self.displayCompanyCostMax) + 16;
            return size;
        },
        get displayTeamMembers () {
            if (!self.hasResources) return self.teamMembers || 1;
            return self.resources?.reduce((total, res)=>total + (res.teamMembers || 1), 0);
        },
        get displayRate () {
            if (self.hasResources) return getMinMaxRange(self.resources?.map((res)=>res.displayRate) || []);
            if (!isAlive(self)) return 0;
            const modifier = self.root.settings.modifier;
            return roundFloat((typeof self.clientRate === "number" ? self.clientRate : self.rate) * modifier, 0, true);
        },
        get hasAverageRate () {
            return typeof self.displayRate === "string" && self.displayRate.includes("-");
        },
        get displayCompanyUnitCost () {
            if (!isAlive(self)) return 0;
            const modifier = self.root.settings.modifier;
            if (self.hasResources) return getMinMaxRange(self.resources?.map((res)=>res.displayCompanyUnitCost) || []);
            return roundFloat((self.companyUnitCost || 0) * modifier, 0, true);
        },
        get displayCompanyUnitCostWithModifier () {
            if (!isAlive(self)) return 0;
            const modifier = self.root.settings.modifier;
            const exchangeRateModifier = self.root.settings.exchangeRateModifier;
            if (self.hasResources) return getMinMaxRange(self.resources?.map((res)=>res.displayCompanyUnitCostWithModifier) || []);
            return roundFloat((self.companyUnitCost || 0) * modifier * exchangeRateModifier, 0, true);
        },
        get displayBTHours () {
            if (!isAlive(self) || !self.btHours) return 0;
            const { modifier } = self.root.settings;
            return self.btHours / modifier;
        },
        get displayBTPrice () {
            if (!isAlive(self) || !self.btPrice) return 0;
            const roundPrice = self.root.settings?.roundPrice;
            return roundFloat(self.btPrice, roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        }
    }))/** ================ Common actions ================ */ .actions((self)=>({
        afterCreate () {
            if (!self.timelineColor && self.color) self.timelineColor = self.color;
            if (!self.timelineFontColor) {
                self.timelineFontColor = getContrastingFontColor(self.timelineColor || self.color);
            }
        },
        removeSelf () {
            if (!isAlive(self)) return;
            self.root.removeWorkType?.(self.id, false);
        },
        setRoundDecimals (roundDecimals) {
            self.roundDecimals = roundDecimals;
        },
        setRounding (roundType, roundInterval) {
            self.roundType = roundType;
            self.roundInterval = roundInterval;
        },
        setRoundType (roundType) {
            self.roundType = roundType;
        },
        setRoundInterval (roundInterval) {
            self.roundInterval = roundInterval;
        },
        setApplyRoundDecimals (applyRoundDecimals) {
            self.applyRoundDecimals = applyRoundDecimals;
            if (typeof self.roundDecimals !== "number") self.roundDecimals = 2;
        },
        setFixedHours (hours) {
            // if (self.percent && self.of) self.cHoursOverride = true;
            self._internalSetFixedHours(hours, true);
            if (self.fixedPrice) {
                self.fixedPrice = undefined;
                self.fixedCompanyCost = undefined;
            }
            self.root.setWorkTypeVisibility?.(self.id, true);
            self.root._internalUpdateRelatedWorkTypes?.(self.id);
        },
        setFixedPrice (price, manuallySet) {
            if (self.hasResources) return;
            if (self.fixedHours) {
                self.fixedHours = undefined;
            }
            const keys = Object.keys(price);
            let newPrice = price;
            // let clearKeys = self.root.settings.useMinMax
            //   ? [MinAvgMaxKeys.MIN, MinAvgMaxKeys.MAX]
            //   : [MinAvgMaxKeys.AVG];
            /**
       * @note temporary min-avg-max calculations
       * Should be applied after switching between proposal modes (avg / min-max / etc.)
       * */ if (manuallySet) {
                newPrice = modifyNewValuesWithCoefficients(price, keys, self.fixedPrice || DEFAULT_MIN_AVG_MAX_VALUE);
                if (keys.includes(MinAvgMaxKeys.AVG)) newPrice.avgSet = true;
                if (keys.includes(MinAvgMaxKeys.MIN) || keys.includes(MinAvgMaxKeys.MAX)) newPrice.minMaxSet = true;
            }
            // if(clearKeys.reduce((t, key) => t && !(price[key] ?? self.fixedPrice?.[key]), true)) {
            //   self.fixedPrice = undefined;
            //   self.fixedCompanyCost = undefined;
            // } else {
            self.fixedPrice = cast({
                ...self.fixedPrice || DEFAULT_MIN_AVG_MAX_VALUE,
                ...newPrice
            });
            if (typeof self.fixedCompanyCost !== "number") {
                const p = self.root.settings.useMinMax ? newPrice.min : newPrice.avg;
                if (typeof p === "number") self.fixedCompanyCost = p / 2;
            }
            // }
            self.root.setWorkTypeVisibility?.(self.id, true);
            self.root._internalUpdateRelatedWorkTypes?.(self.id);
        },
        setRate (rate) {
            self.rate = rate;
            if (self.fixedPrice) {
                self.fixedPrice = undefined;
                self.fixedCompanyCost = undefined;
            }
        },
        calculateAverageRate () {
            if (!self.hasResources) return;
            const { roundPrice, modifier } = self.root.settings;
            const key = self.root.settings.useMinMax ? MinAvgMaxKeys.MIN : MinAvgMaxKeys.AVG;
            const h = self.hours[key];
            const total = self.resources?.reduce((total, res)=>total + (!res.hours[key] || !h ? 0 : res.hours[key] / h * (typeof res.clientRate === "number" ? res.clientRate : res.rate)), 0);
            this.setRate(safeRoundRate(total, modifier, roundPrice ? Rounding.FULL : Rounding.DECIMALS));
        },
        setSystemId (systemId) {
            self.systemId = systemId;
        },
        changeSystemId (systemId, name, color, rate, companyUnitCost) {
            self.systemId = systemId;
            self.name = name;
            self.autoFocus = false;
            if (color) {
                self.color = color;
                self.timelineColor = color;
                self.timelineFontColor = getContrastingFontColor(color);
            }
            if (rate) self.rate = rate;
            if (companyUnitCost) self.companyUnitCost = companyUnitCost;
        /**
       * @todo: I don't think it would do anything in this case, but check it later
       * ~Marcy
       */ // self.root._internalUpdateRelatedWorkTypes?.(self.id);
        },
        resetFixedHours (manuallySet) {
            self.fixedHours = undefined;
            if (manuallySet) {
                self.root.setWorkTypeVisibility?.(self.id, false);
                self.root._internalUpdateRelatedWorkTypes?.(self.id);
            }
        },
        resetFixedPrice (manuallySet) {
            self.fixedPrice = undefined;
            if (manuallySet) {
                if (!self.hasFixedHours) self.root.setWorkTypeVisibility?.(self.id, false);
                self.root._internalUpdateRelatedWorkTypes?.(self.id);
            }
        },
        resetFixedValues () {
            self.fixedHours = undefined;
            self.fixedPrice = undefined;
        },
        setBreakdownVisibility (showInBreakdown) {
            self.root.setWorkTypeVisibility?.(self.id, !showInBreakdown);
            self.root._internalUpdateRelatedWorkTypes?.(self.id);
        },
        setTurnOffState (state, clientSet) {
            if (clientSet) {
                self.turnOff = undefined;
                self.clientTurnOff = state;
            } else self.turnOff = state;
            self.root._internalResetPercentValues?.(self.id);
            self.root._internalUpdateRelatedWorkTypes?.(self.id);
        },
        setOpenState (state) {
            if (self.isOpened === state) return;
            self.isOpened = state;
        },
        setBtOpen () {
            self.btOpened = !self.btOpened;
        },
        setTimelineOpenState (state) {
            if (self.isTimelineOpened === state) return;
            self.isTimelineOpened = state;
        },
        expandAllChildren (state) {
            if (!state || self.resources?.length) this.setOpenState(state);
        },
        splitResources () {
            if (!self.resources?.length) return;
            const { modifier } = self.root.settings;
            const list = [];
            let lastOrder = 0;
            self.resources.map((res)=>{
                const o = lastOrder;
                lastOrder += res.teamMembers || 1;
                return [
                    o,
                    res
                ];
            }).forEach((res)=>{
                if (res[1].teamMembers > 1) {
                    const roundedHours = hoursRoundedSplit(res[1].hours, res[1].teamMembers, modifier, Rounding.FULL);
                    res[1]._internalClearHoursOverride();
                    res[1]._internalSetFixedHours(roundedHours[0], false);
                    let nameIndex = Math.max(lastNameUsageIndex(self.resources, res[1].name), lastNameUsageIndex(list.map((l)=>l[1]), res[1].name));
                    for(let i = 1; i < res[1].teamMembers; i++){
                        list.push([
                            res[0] + i,
                            {
                                id: generateResourceId(nameIndex),
                                name: incrementedUsedName(self.resources, res[1].name, nameIndex),
                                utilizationOrder: res[0] + i,
                                fixedHours: roundedHours[i],
                                rate: res[1].rate,
                                mta: res[1].mta ? clone(res[1].mta) : undefined
                            }
                        ]);
                        nameIndex++;
                    }
                    res[1].setUtilizationOrder(res[0]);
                    res[1].setTeamMembers(1);
                }
            });
            list.forEach(([index, resource])=>{
                self.resources.splice(index, 0, resource);
            });
        },
        _internalPostResourceCRUDOperations (noRateCalculations) {
            if (!self.resourceHoursOverwritten) {
                this._internalUpdateAllResourceHoursEqually();
            }
            if (!noRateCalculations) this.calculateAverageRate();
        },
        addResource (useAutoFocus, id, name, systemId, rate, companyUnitCost) {
            if (!self.resources?.length) self.resources = cast([]);
            self.teamMembers = undefined;
            let fixedHours = DEFAULT_MIN_AVG_MAX_VALUE;
            if (!self.resources.length) fixedHours = {
                ...self.hours
            };
            self.resources.push(cleanObject({
                id,
                systemId,
                name: name || "",
                utilizationOrder: self.resources?.length || 0,
                rate: rate ?? self.rate,
                fixedHours,
                timelineColor: self.timelineColor,
                timelineFontColor: self.timelineFontColor,
                mta: self.mta ? clone(self.mta) : undefined,
                autoFocus: useAutoFocus,
                companyUnitCost
            }));
            this._internalPostResourceCRUDOperations(true);
        },
        _internalUpdateResourceId (oldId, newId, newName) {
            if (!self.resources?.length || oldId === newId) return;
            const resource = self.resources.find((value)=>value.id === oldId);
            if (!resource || self.resources.some((value)=>value.id === newId)) return;
            self.resources = cast(self.resources.map((value)=>value.id === oldId ? Object.assign({}, getSnapshot(value), {
                    id: newId,
                    name: newName
                }) : getSnapshot(value)));
        },
        reorderResources (reorderedResources, utilizationOrder = false) {
            if (utilizationOrder) {
                reorderedResources.forEach(({ id }, index)=>{
                    self.resources?.find((resource)=>resource.id === id)?.setUtilizationOrder(index);
                });
                return;
            }
            self.resources = updateSortedList(self.resources, reorderedResources);
        },
        removeResource (id, skipSectionCheck) {
            if (!self.resources?.length) return;
            const resource = self.resources.find((r)=>r.id === id);
            if (resource) {
                const lastUtilizationOrder = resource.utilizationOrder;
                destroy(resource);
                self.root._internalRemoveAllTimelinesTeamMember?.(self.id, id);
                if (!self.resources?.length) {
                    self.resources = undefined;
                    this.calculateAverageRate();
                    return;
                }
                self.resources.forEach((resource)=>{
                    if (resource.utilizationOrder > lastUtilizationOrder) resource.setUtilizationOrder(resource.utilizationOrder - 1);
                });
                if (!skipSectionCheck) self.root._internalRemoveSectionResource?.(self.id, id);
                this._internalPostResourceCRUDOperations();
            }
        },
        updateResourceHours (resourceId, diff, key) {
            if (!self.resources?.length) return;
            let absDiff = Math.abs(diff);
            const index = self.resources.findIndex((resource)=>resource.id === resourceId);
            const isLast = index === self.resources.length - 1;
            if (index === 0 && isLast) return; //if is only resource
            if (diff < 0) {
                const nextRes = self.resources[isLast ? 0 : index + 1];
                nextRes._internalSetFixedHours({
                    [key]: nextRes.fixedHours[key] + absDiff
                }, true);
                return;
            }
            const update = (index, max)=>{
                for(let i = index; i < max; i++){
                    const v = self.resources[i].fixedHours?.[key] || 0;
                    if (v >= absDiff) {
                        self.resources[i]._internalSetFixedHours({
                            [key]: v - absDiff
                        }, true);
                        absDiff -= v;
                        break;
                    }
                    if (v) self.resources[i]._internalSetFixedHours({
                        [key]: 0
                    }, true);
                    absDiff -= v;
                    if (!absDiff) break;
                }
            };
            update(index + 1, self.resources.length);
            if (absDiff > 0) update(0, index);
        },
        _internalUpdateAllResourceHoursEqually () {
            if (!self.resources?.length) return;
            const modifier = self.root.settings.modifier;
            const roundedHours = weightedHoursRoundedSplit(self.hours, self.resources.map(()=>({
                    min: 1,
                    avg: 1,
                    max: 1
                })), modifier, Rounding.FULL);
            self.resources.forEach((res, i)=>{
                res._internalSetFixedHours(roundedHours[i]);
            });
        },
        _internalUpdateAllResourceHours () {
            if (!self.resources?.length) return;
            const { modifier } = self.root.settings;
            const total = sumObjectsData(...self.resources.map((res)=>res.hours));
            const h = weightedHoursRoundedSplit(// pick(self.hours, MIN_AVG_MAX_KEYS) as IMinAvgMaxValue,
            self.hours, self.resources.map((res)=>parseObject(total, (v, key)=>v ? res.hours?.[key] || 0 : 1)), modifier, Rounding.FULL);
            self.resources.forEach((res, i)=>{
                if (self.hours.forcedState) h[i].forcedState = self.hours.forcedState;
                res._internalSetFixedHours(h[i], true);
            });
        },
        setCompanyUnitCost (companyUnitCost) {
            if (self.hasFixedPrice) return;
            self.companyUnitCost = companyUnitCost;
        },
        setCompanyCost (companyCost) {
            if (!self.hasFixedPrice) return;
            self.fixedCompanyCost = companyCost;
        },
        setCompanyProfit (companyProfit, key) {
            if (self.hasFixedPrice) {
                this.setFixedPrice({
                    [key]: (self.fixedCompanyCost || 0) + companyProfit
                });
                return;
            }
            if (self.hasResources) {
                const total = self.companyProfit[key] || 0;
                self.resources.forEach((resource)=>{
                    const pr = resource.companyProfit[key];
                    const v = total ? pr / total * companyProfit : companyProfit / self.resources.length;
                    resource.setCompanyProfit(v, key);
                });
                return;
            }
            self._internalSetCompanyProfit(companyProfit, key);
        },
        setCompanyProfitability (companyProfitability) {
            /** @note: this will be either min or avg; no need to use max, as the profitability */ const { useMinMax } = self.root.settings;
            const key = useMinMax ? MinAvgMaxKeys.MIN : MinAvgMaxKeys.AVG;
            if (self.hasFixedPrice) {
                if (!self.fixedCompanyCost) return true;
                this.setFixedPrice({
                    [key]: self.fixedCompanyCost ? (companyProfitability / 100 + 1) * self.fixedCompanyCost : 0
                });
                return;
            }
            if (self.hasResources) {
                const c = self.companyCost[key] || 0;
                self.setCompanyProfit((companyProfitability / 100 + 1) * c - c, key);
                return;
            }
            self._internalSetCompanyProfitability(companyProfitability);
            return;
        }
    }));

const VisibleSummaryColumnModel = types.model("VisibleSummaryColumn", {
    name: types.enumeration("SummaryColumns", Object.values(SummaryColumns)),
    visible: types.boolean,
    noDev: types.maybe(types.boolean)
}).actions((self)=>({
        setVisibility (visible) {
            self.visible = visible;
        }
    }));

const VisibleProjectTableModel = types.model("VisibleProjectTable", {
    name: types.enumeration("Tables", Object.values(Tables)),
    visible: types.boolean
}).actions((self)=>({
        setVisibility (visible) {
            self.visible = visible;
        }
    }));

const VisibleProposalTotalModel = types.model("VisibleProposalTotalModel", {
    name: types.enumeration("Totals", Object.values(Totals)),
    visible: types.boolean,
    noDev: types.maybe(types.boolean)
}).actions((self)=>({
        setVisibility (visible) {
            self.visible = visible;
        }
    }));

const TimelineExpandedMonthModel = types.model("TimelineExpandedMonth", {
    weeks: types.map(types.boolean)
});

const DeltaPrimitive = types.custom({
    name: "QuillDelta",
    fromSnapshot (value) {
        return new Delta(value);
    },
    toSnapshot (value) {
        return value.ops.slice();
    },
    isTargetType (value) {
        return value instanceof Delta || value?.ops && typeof value.compose === "function";
    },
    getValidationMessage (value) {
        if (value && isArray(value)) return "" // OK
        ;
        return `'${value}' doesn't look like a valid QuillDelta operation list`;
    }
});
const AttrPrimitive = types.custom({
    name: "Attribute",
    fromSnapshot (value) {
        return value;
    },
    toSnapshot (value) {
        return value;
    },
    isTargetType (value) {
        return !!value;
    },
    getValidationMessage (value) {
        if (isObject(value)) return "" // OK
        ;
        return `'${value}' doesn't look like a valid object`;
    }
});
types.model("QuillOp", {
    insert: types.string,
    attributes: types.maybe(AttrPrimitive)
});

const DATE_FORMAT = "yyyy-MM-dd";
const parseTimelineDate = (value)=>{
    const date = parse(value, DATE_FORMAT, new Date());
    return isValid(date) && format(date, DATE_FORMAT) === value ? date : new Date(NaN);
};
const formatTimelineDate = (value)=>format(value, DATE_FORMAT);
const TimelineDatePrimitive = types.custom({
    name: "TimelineDate",
    fromSnapshot (value) {
        if (value instanceof Date) return value;
        if (typeof value === "number") return new Date(value);
        return parseTimelineDate(value);
    },
    toSnapshot (value) {
        return formatTimelineDate(value);
    },
    isTargetType (value) {
        return value instanceof Date;
    },
    getValidationMessage (value) {
        if (value instanceof Date) return isValid(value) ? "" : "Expected a valid Timeline date";
        if (typeof value === "number") return isValid(new Date(value)) ? "" : "Expected a valid Timeline date";
        if (typeof value === "string") {
            const date = parseTimelineDate(value);
            return isValid(date) ? "" : "Expected a date in yyyy-MM-dd format";
        }
        return "Expected a date in yyyy-MM-dd format";
    }
});

const SettingsModel = types.model("Settings", {
    currency: types.string,
    companyCurrency: types.string,
    mta: MinAvgMaxValueModel,
    timeModifier: types.number,
    useWeekTime: types.maybe(types.boolean),
    timelineMode: types.maybe(types.enumeration("TimelineMode", Object.values(TimelineMode))),
    timelineStartDate: types.optional(TimelineDatePrimitive, ()=>getFirstWorkdayOfWeek(new Date())),
    roundPrice: types.maybe(types.boolean),
    showPriceFromHiddenWorkTypes: types.maybe(types.boolean),
    applyRoundDecimals: types.maybe(types.boolean),
    roundDecimals: types.optional(types.number, 2, [
        null,
        undefined
    ]),
    usedSummaryColumns: types.array(VisibleSummaryColumnModel),
    usedTables: types.array(VisibleProjectTableModel),
    showSummary: types.maybe(types.boolean),
    showTimeline: types.maybe(types.boolean),
    showBreakdown: types.maybe(types.boolean),
    showTextDescription: types.maybe(types.boolean),
    /** @note: deprecated as of v 3.6.4 - use "usedBreakdownColumns" setting */ // showPrices: types.maybe(types.boolean),
    // titleCellWidth: types.number,
    // descriptionCellWidth: types.number,
    usedTotals: types.array(VisibleProposalTotalModel),
    fixedTotalTime: types.maybe(MinAvgMaxValueModel),
    useMinMax: types.maybe(types.boolean),
    /** 1 | 2 | 3 ==> module | feature | task */ viewLevel: types.number,
    valueLevel: types.number,
    priceFormat: types.optional(types.number, PriceFormats.EUROPEAN),
    sectionFormat: types.number /** 1 | 2 | 3 ==> row | bloc + summary + block */ ,
    descriptionMode: types.number /** 1 | 2 | 3 ==> tooltip | row | column  */ ,
    dayModifier: types.number,
    turnOffFixedPrice: types.maybe(types.boolean),
    turnOffMaterials: types.maybe(types.boolean),
    companyFixedBreakdownCost: types.optional(types.number, 0),
    companyBreakdownMaterialCost: types.optional(types.number, 0),
    useTimelineChart: types.maybe(types.boolean),
    useTimelineSections: types.maybe(types.boolean),
    useTimelineUtilization: types.maybe(types.boolean),
    useTimelineBilling: types.maybe(types.boolean),
    expandTimelineSections: types.maybe(types.boolean),
    expandTimelineUtilization: types.maybe(types.boolean),
    expandTimelineBilling: types.maybe(types.boolean),
    timelineExpandedMonths: types.maybe(types.map(TimelineExpandedMonthModel)),
    fixedCostTitle: types.maybe(types.string),
    materialsTitle: types.maybe(types.string),
    btWorkTypes: types.maybe(types.array(types.string)),
    buffers: types.maybe(types.array(BufferModel)),
    maxValuesAsBuffer: types.maybe(types.boolean),
    hadMaxValuesAsBufferSetting: types.maybe(types.boolean),
    showBreakdownParentValues: types.maybe(types.boolean),
    showBreakdownChildCounters: types.optional(types.boolean, true),
    dailyExchangeRate: types.maybe(types.number),
    savedDailyRate: types.maybe(types.string),
    exchangeRateTimestamp: types.maybe(types.number),
    customExchangeRate: types.maybe(types.number),
    customExchangeRateCurrencies: types.maybe(types.array(types.string)),
    useCustomExchangeRate: types.maybe(types.boolean)
}).views((self)=>({
        get modifier () {
            return (self.timeModifier || self.dayModifier) * MaxTimeModifiers.HOUR;
        },
        get daysModifier () {
            return self.dayModifier * MaxTimeModifiers.HOUR;
        },
        get maxMta () {
            return self.timeModifier ? MaxTimeModifiers.DAY * MaxTimeModifiers.MONTH : MaxTimeModifiers.MONTH;
        },
        get maxWta () {
            return self.timeModifier ? MaxTimeModifiers.DAY * MaxTimeModifiers.WEEK : MaxTimeModifiers.WEEK;
        },
        get currencyObj () {
            return getCurrency(self.currency);
        },
        get companyCurrencyObj () {
            return getCurrency(self.companyCurrency);
        },
        get showBreakdownColumns () {
            return self.valueLevel >= BreakdownTableDepths.SECTION;
        },
        get visibleSummaryColumns () {
            return self.usedSummaryColumns.filter((col)=>col.visible);
        },
        get hasTeamMembersCol () {
            return !!self.usedSummaryColumns.find((col)=>col.name === SummaryColumns.TEAM)?.visible;
        },
        get hasTimeCol () {
            return !!self.usedSummaryColumns.find((col)=>col.name === SummaryColumns.TIME)?.visible;
        },
        get visibleTables () {
            return self.usedTables.filter((col)=>col.visible);
        },
        get visibleTotals () {
            return self.usedTotals.filter((t)=>t.visible);
        },
        get useHoursTotal () {
            return Boolean(self.usedSummaryColumns.find((col)=>col.name === SummaryColumns.HOURS)?.visible && !self.usedSummaryColumns.find((col)=>col.name === SummaryColumns.COST)?.visible);
        },
        get isSummaryTableUsed () {
            return !!self.usedTables.find((table)=>table.name === Tables.SUMMARY)?.visible;
        },
        get isTimelineTableUsed () {
            return !!self.usedTables.find((table)=>table.name === Tables.TIMELINE)?.visible;
        },
        get hasFixedTotalTime () {
            return !!self.fixedTotalTime;
        },
        get usedRoundDecimals () {
            if (self.applyRoundDecimals) return typeof self.roundDecimals === "number" ? self.roundDecimals : 2;
            return 2;
        },
        get exchangeRateModifier () {
            if (self.currency === self.companyCurrency) return 1;
            return self.useCustomExchangeRate && typeof self.customExchangeRate === "number" ? self.customExchangeRate : self.dailyExchangeRate || 1;
        },
        getSavedDailyRates () {
            try {
                if (!self.savedDailyRate?.length) return [];
                return JSON.parse(self.savedDailyRate);
            } catch (err) {
                return [];
            }
        },
        hasOutdatedDailyExchangeRate () {
            if (self.currency === self.companyCurrency) return false;
            return !self.dailyExchangeRate || !self.exchangeRateTimestamp || Math.abs(differenceInCalendarDays(new Date(), self.exchangeRateTimestamp)) > 0 || this.getSavedDailyRates()?.filter((r)=>r.code === self.currency || r.code === self.companyCurrency)?.length < 2;
        },
        get maxTimelineEndDate () {
            return getFirstWorkdayOfMonth(addMonths(self.timelineStartDate, MAX_TIMELINE_LENGTH));
        },
        get usedThousandSeparator () {
            return self.priceFormat === PriceFormats.AMERICAN ? "," : " ";
        }
    })).actions((self)=>({
        _internalResetBufferPercentValues (workTypeIds) {
            const root = getRoot(self);
            root.sections.forEach((s)=>s._internalResetBufferPercentValues(workTypeIds));
        },
        setModifier (modifier) {
            self.timeModifier = modifier;
        },
        setDayModifier (modifier) {
            self.dayModifier = modifier;
        },
        setUseWeekTime (useWeekTime) {
            self.useWeekTime = useWeekTime;
        },
        setCurrency (currencyCode) {
            self.currency = currencyCode;
            this._internalHandleUseCustomCurrencyExchange();
        },
        setCompanyCurrency (currencyCode) {
            self.companyCurrency = currencyCode;
            this._internalHandleUseCustomCurrencyExchange();
        },
        _internalHandleUseCustomCurrencyExchange () {
            self.useCustomExchangeRate = self.customExchangeRateCurrencies?.length && self.companyCurrency === self.customExchangeRateCurrencies[0] && self.currency === self.customExchangeRateCurrencies[1] || undefined;
        },
        setShowSummary (showSummary) {
            self.showSummary = showSummary;
        },
        setShowTimeline (showTimeline) {
            self.showTimeline = showTimeline;
        },
        setShowBreakdown (showBreakdown) {
            self.showBreakdown = showBreakdown;
        },
        setShowTextDescription (showTextDescription) {
            self.showTextDescription = showTextDescription;
        },
        setMinMax (useMinMax) {
            self.useMinMax = useMinMax;
            if (useMinMax && self.maxValuesAsBuffer) {
                self.maxValuesAsBuffer = undefined;
                self.hadMaxValuesAsBufferSetting = true;
            }
            if (!useMinMax && self.hadMaxValuesAsBufferSetting) {
                self.hadMaxValuesAsBufferSetting = undefined;
                self.maxValuesAsBuffer = true;
            }
            const root = getRoot(self);
            // if(root.combinedForcedState)
            root.generateTimeline();
            root._internalUpdateSelectedWorkTypes();
        },
        setDescriptionMode (descriptionMode) {
            self.descriptionMode = descriptionMode;
        },
        // setTitleCellWidth(titleCellWidth: number) {
        //   self.titleCellWidth = titleCellWidth;
        // },
        // setDescriptionCellWidth(descriptionCellWidth: number) {
        //   self.descriptionCellWidth = descriptionCellWidth;
        // },
        // setLabelCellWidth(labelCellWidth: number) {
        //   self.labelCellWidth = labelCellWidth;
        // },
        setTurnOffFixedPrice (turnOffFixedPrice) {
            self.turnOffFixedPrice = turnOffFixedPrice;
        },
        setTurnOffMaterials (turnOffMaterials) {
            self.turnOffMaterials = turnOffMaterials;
        },
        setViewLevel (viewLevel) {
            self.viewLevel = viewLevel;
            self.valueLevel = viewLevel;
            const root = getRoot(self);
            if (root.visibility) root.visibility.setViewLevel(viewLevel);
        },
        setValueLevel (valueLevel) {
            const root = getRoot(self);
            //@todo: this may be unintuitive - check and revert changes if necessary
            if (valueLevel === self.valueLevel && valueLevel === 0) {
                root.setBreakdownColumnVisibility(BreakdownColumns.ESTIMATES, false);
                valueLevel = BreakdownTableDepths.PROJECT;
            } else if (self.valueLevel === 0 && valueLevel > 1 && !root.showBreakdownEstimatesColumn) root.setBreakdownColumnVisibility(BreakdownColumns.ESTIMATES, true);
            self.valueLevel = valueLevel;
            if (root.visibility) root.visibility.setValueLevel(valueLevel);
        },
        setPriceFormat (format) {
            self.priceFormat = format;
        },
        setSectionFormat (format) {
            if (self.sectionFormat === SectionFormats.ROW && format !== SectionFormats.ROW) {
                const root = getRoot(self);
                root?.resetSectionsVisibility?.();
            }
            self.sectionFormat = format;
            if (!self.valueLevel && format > SectionFormats.ROW || self.valueLevel === BreakdownTableDepths.PROJECT && format === SectionFormats.SUBTOTAL) self.valueLevel = BreakdownTableDepths.MODULE;
        },
        setTimelineMode (mode) {
            self.timelineMode = mode;
        },
        setTimelineStartDate (value) {
            self.timelineStartDate = value;
        },
        setFixedTotalTime (time) {
            const keys = Object.keys(time);
            const newTime = modifyNewValuesWithCoefficients(time, keys, self.fixedTotalTime);
            self.fixedTotalTime = cast({
                ...self.fixedTotalTime || DEFAULT_MIN_AVG_MAX_VALUE,
                ...newTime
            });
        },
        resetFixedTotalTime () {
            self.fixedTotalTime = undefined;
        },
        setShowPriceFromHiddenWorkTypes (showPriceFromHiddenWorkTypes) {
            self.showPriceFromHiddenWorkTypes = showPriceFromHiddenWorkTypes;
        },
        setApplyRoundDecimals (applyRoundDecimals) {
            self.applyRoundDecimals = applyRoundDecimals;
        },
        setRoundDecimals (roundDecimals) {
            self.roundDecimals = roundDecimals;
        },
        setPriceRounding (roundPrice) {
            self.roundPrice = roundPrice;
        },
        setUseTimelineSections (useTimelineSections) {
            self.useTimelineSections = useTimelineSections;
            self.expandTimelineSections = useTimelineSections;
        },
        setUseTimelineUtilization (useTimelineUtilization) {
            self.useTimelineUtilization = useTimelineUtilization;
        },
        setUseTimelineBilling (useTimelineBilling) {
            self.useTimelineBilling = useTimelineBilling;
        },
        setExpandTimelineSections (expandTimelineSections) {
            self.expandTimelineSections = expandTimelineSections;
        },
        setExpandTimelineUtilization (expandTimelineUtilization) {
            self.expandTimelineUtilization = expandTimelineUtilization;
        },
        setExpandTimelineBilling (expandTimelineBilling) {
            self.expandTimelineBilling = expandTimelineBilling;
        },
        setTimelineMonthExpanded (month, expanded) {
            const monthKey = getExpansionDateKey(month, true);
            if (expanded) {
                if (!self.timelineExpandedMonths) self.timelineExpandedMonths = cast({});
                const expandedMonths = self.timelineExpandedMonths;
                if (expandedMonths && !expandedMonths.has(monthKey)) expandedMonths.set(monthKey, {
                    weeks: {}
                });
            } else {
                self.timelineExpandedMonths?.delete(monthKey);
                if (self.timelineExpandedMonths?.size === 0) self.timelineExpandedMonths = undefined;
            }
        },
        clearTimelineExpandedMonths () {
            self.timelineExpandedMonths = undefined;
        },
        setTimelineWeekExpanded (month, week, expanded) {
            const monthKey = getExpansionDateKey(month, true);
            const weekKey = getExpansionDateKey(week, false);
            const expandedMonth = self.timelineExpandedMonths?.get(monthKey);
            if (!expandedMonth) return;
            if (expanded) expandedMonth.weeks.set(weekKey, true);
            else expandedMonth.weeks.delete(weekKey);
        },
        isTimelineMonthExpanded (month) {
            return Boolean(self.timelineExpandedMonths?.has(getExpansionDateKey(month, true)));
        },
        isTimelineWeekExpanded (month, week) {
            return Boolean(self.timelineExpandedMonths?.get(getExpansionDateKey(month, true))?.weeks.has(getExpansionDateKey(week, false)));
        },
        setUseTimelineChart (useTimelineChart) {
            self.useTimelineChart = useTimelineChart;
        },
        reorderSummaryColumns (list) {
            self.usedSummaryColumns = cast(updateSortedList(self.usedSummaryColumns, list, "name", true));
        },
        reorderProjectTables (list) {
            self.usedTables = cast(updateSortedList(self.usedTables, list, "name", true));
        },
        reorderTotals (list) {
            self.usedTotals = cast(updateSortedList(self.usedTotals, list, "name", true));
        },
        setCompanyBreakdownFixedCost (companyFixedBreakdownCost) {
            self.companyFixedBreakdownCost = companyFixedBreakdownCost;
        },
        setCompanyBreakdownMaterialCost (companyBreakdownMaterialCost) {
            self.companyBreakdownMaterialCost = companyBreakdownMaterialCost;
        },
        setFixedCostTitle (fixedCostTitle) {
            self.fixedCostTitle = fixedCostTitle;
        },
        setMaterialsTitle (materialsTitle) {
            self.materialsTitle = materialsTitle;
        },
        setShowBreakdownParentValues (showBreakdownParentValues) {
            self.showBreakdownParentValues = showBreakdownParentValues;
        },
        setShowBreakdownChildCounters (showBreakdownChildCounters) {
            self.showBreakdownChildCounters = showBreakdownChildCounters;
        },
        setBtWorkTypes (btWorkTypes) {
            self.btWorkTypes = cast(btWorkTypes);
        },
        setMaxValuesAsBuffer (maxValuesAsBuffer) {
            self.maxValuesAsBuffer = maxValuesAsBuffer;
            self.useMinMax = false;
        },
        addBuffer (buffer) {
            if (!self.buffers) {
                self.buffers = cast([
                    buffer
                ]);
            } else {
                self.buffers.push(cast(buffer));
            }
            const root = getRoot(self);
            root.sections.forEach((s)=>s._internalResetBufferPercentValues(buffer.workTypes));
            root._internalUpdateSelectedWorkTypes(buffer.workTypes);
        },
        removeBuffer (id) {
            if (!self.buffers?.length) return;
            const bf = self.buffers.find((b)=>b.id === id);
            const wTS = bf?.workTypes || [];
            if (bf) {
                const workTypes = [
                    ...bf.workTypes
                ];
                destroy(bf);
                const root = getRoot(self);
                root.sections.forEach((s)=>s._internalResetBufferPercentValues(workTypes));
            }
            if (!self.buffers.length) self.buffers = undefined;
            const root = getRoot(self);
            root._internalUpdateSelectedWorkTypes(wTS);
        },
        reorderBuffers (list) {
            self.buffers = cast(updateSortedList(self.buffers, list, "id", true));
        },
        setUseCustomExchangeRate (useCustomExchangeRate) {
            self.useCustomExchangeRate = useCustomExchangeRate || undefined;
            const customExchangeRateCurrencies = [
                self.companyCurrency,
                self.currency
            ];
            if (self.customExchangeRate && difference(self.customExchangeRateCurrencies, customExchangeRateCurrencies).length) {
                self.customExchangeRate = undefined;
                self.customExchangeRateCurrencies = cast(customExchangeRateCurrencies);
            }
        },
        setDailyExchangeRate (rates) {
            self.dailyExchangeRate = getExchangeRate(rates, self.currency, self.companyCurrency);
            self.savedDailyRate = JSON.stringify(rates.filter((r)=>r.code === self.currency || r.code === self.companyCurrency));
            self.exchangeRateTimestamp = new Date().getTime();
        },
        setDefaultDailyExchangeRate (timestamp) {
            self.dailyExchangeRate = 1;
            self.exchangeRateTimestamp = timestamp;
        },
        setCustomExchangeRate (rate) {
            self.customExchangeRate = rate;
            self.customExchangeRateCurrencies = cast([
                self.companyCurrency,
                self.currency
            ]);
        }
    }));

const TimelineDailyValueModel = types.model("TimelineDailyValue", {
    value: types.number,
    ov: types.maybe(types.boolean)
}).views((self)=>({
        serialize () {
            return cleanObject({
                value: self.value,
                ov: self.ov
            });
        }
    }));

const TimelineTeamMemberModel = types.model("TimelineTeamMember", {
    id: types.string,
    startDay: TimelineDatePrimitive,
    timeAllocation: types.number,
    workload: types.number,
    distribution: types.array(TimelineDailyValueModel),
    autoFocus: types.maybe(types.boolean),
    /** not serialized: */ valueMode: types.optional(types.boolean, false)
}).views((self)=>({
        get isAutomated () {
            try {
                return getParent(self, 2).isAutomated;
            } catch  {
                return false;
            }
        },
        resolveColor (fontColor = false) {
            const workType = getParent(self, 2);
            try {
                const root = getRoot(self);
                const summaryWorkType = root.workTypes?.find((value)=>value.id === workType.id);
                const summaryTeamMember = summaryWorkType?.resources?.find((value)=>String(value.id) === self.id);
                return resolveTimelineColor(summaryTeamMember?.[fontColor ? "timelineFontColor" : "timelineColor"], fontColor ? workType.fontColor : workType.color);
            } catch  {
                return resolveTimelineColor(undefined, undefined, fontColor ? workType.fontColor : workType.color);
            }
        },
        get color () {
            return this.resolveColor();
        },
        get fontColor () {
            return this.resolveColor(true);
        },
        get endDate () {
            return !self.distribution.length ? undefined : getTimelineEndDate(self.startDay, self.distribution.length);
        },
        get hourlyTimeAllocation () {
            if (this.isAutomated) {
                return self.distribution.length ? self.workload / self.distribution.length / MaxTimeModifiers.HOUR : 0;
            }
            return self.timeAllocation / MaxTimeModifiers.HOUR;
        },
        get name () {
            try {
                const root = getRoot(self);
                const workType = getParent(self, 2);
                return root.workTypes?.find((value)=>value.id === workType.id)?.resources?.find((value)=>String(value.id) === self.id)?.name || "";
            } catch  {
                return "";
            }
        },
        get parsedWorkload () {
            return self.workload / MaxTimeModifiers.HOUR;
        },
        get availableWorkload () {
            return self.distribution.reduce((total, value)=>total + (value.ov ? value.value : 0), 0);
        },
        get distributionByMonth () {
            return getChunkedDistribution(self.distribution, self.startDay);
        },
        serialize () {
            return cleanObject({
                id: self.id,
                startDay: formatTimelineDate(self.startDay),
                timeAllocation: self.timeAllocation,
                workload: self.workload,
                distribution: self.distribution.map((value)=>value.serialize())
            });
        }
    })).actions((self)=>({
        _internalRecalculateAutomatedWorkTypes (internalCall) {
            if (internalCall) return;
            try {
                getParent(self, 2)._internalRecalculateAutomatedWorkTypes();
            } catch  {
            // Standalone model instances have no section to recalculate.
            }
        },
        setDistributionFromWorkType (distribution) {
            applySnapshot(self.distribution, distribution);
        },
        setValueMode (value) {
            self.valueMode = value;
        },
        updateId (newId) {
            const oldId = self.id;
            if (oldId === newId) return;
            self.id = newId;
        },
        _internalUpdateId (newId) {
            self.id = newId;
        },
        redistributeWorkload (apparentMinutesPerDay) {
            if (self.isAutomated) {
                getParent(self, 2).recalculateAutomatedTeamMemberDistributions();
                return;
            }
            // const oldDistributionLength = self.distribution.length;
            const newDistribution = redistributeWorkload(self.distribution, self.timeAllocation, self.workload, apparentMinutesPerDay);
            applySnapshot(self.distribution, newDistribution);
        },
        setStartDay (value, internalCall) {
            self.startDay = value;
            getParent(self, 2)._internalEnsureStartDay(value);
            if (!internalCall) this._internalRecalculateAutomatedWorkTypes();
        },
        updateStartDates (oldTimelineStartDate, newTimelineStartDate) {
            self.startDay = shiftTimelineStartDate(self.startDay, oldTimelineStartDate, newTimelineStartDate);
        },
        setColor (value) {
            const root = getRoot(self);
            const workType = getParent(self, 2);
            root.workTypes?.find((value)=>value.id === workType.id)?.resources?.find((value)=>String(value.id) === self.id)?.setTimelineColor?.(value);
        },
        setFontColor (value) {
            const root = getRoot(self);
            const workType = getParent(self, 2);
            root.workTypes?.find((value)=>value.id === workType.id)?.resources?.find((value)=>String(value.id) === self.id)?.setTimelineFontColor?.(value);
        },
        setWorkload (value, internalCall) {
            validateValue(value, "workload");
            if (self.isAutomated) {
                self.workload = value;
                getParent(self, 2).recalculateAutomatedTeamMemberDistributions();
                if (!internalCall) {
                    this._internalRecalculateAutomatedWorkTypes();
                }
                return;
            }
            const recalculate = (source)=>redistributeWorkload(source, self.timeAllocation, value);
            let distribution;
            try {
                distribution = recalculate(self.distribution);
            } catch (error) {
                if (!(error instanceof RangeError)) throw error;
                distribution = recalculate(self.distribution.map(({ value: dailyValue })=>({
                        value: dailyValue
                    })));
            }
            self.workload = value;
            applySnapshot(self.distribution, distribution);
            if (!internalCall) this._internalRecalculateAutomatedWorkTypes();
        },
        setWorkloadWithDistribution (workload, distribution) {
            validateValue(workload, "workload");
            self.workload = workload;
            applySnapshot(self.distribution, distribution);
        },
        setTimelineAllocation (value, internalCall) {
            validatePositiveFinite(value, "timeline allocation");
            self.timeAllocation = value;
            if (self.isAutomated) {
                if (!internalCall) getParent(self, 2).recalculateTeamMemberWorkloads();
                return;
            }
            this.redistributeWorkload();
            if (!internalCall) this._internalRecalculateAutomatedWorkTypes();
        },
        transferWorkloadBetweenPeriods (workload, period, sourcePeriodStart, targetPeriodStart) {
            if (self.isAutomated) return;
            const sourceIndices = getDistributionPeriodIndices(self.startDay, period, sourcePeriodStart);
            const targetIndices = getDistributionPeriodIndices(self.startDay, period, targetPeriodStart);
            applySnapshot(self.distribution, transferDistributionWorkload(self.distribution, workload, sourceIndices, targetIndices, self.timeAllocation));
            this._internalRecalculateAutomatedWorkTypes();
        },
        stretchCurtail (newEndDate, providedProportion, parentStartDate, parentEndDate) {
            if (self.isAutomated) return;
            validateTimelineEndDate(newEndDate, self.startDay);
            const { distributionLength } = getNewDistributionLength(newEndDate, self.startDay, self.endDate, providedProportion, parentStartDate, parentEndDate);
            const previousTimeAllocation = self.timeAllocation;
            self.timeAllocation = Math.min(self.workload / distributionLength, 24 * MaxTimeModifiers.HOUR);
            if (self.workload > 0) this.redistributeWorkload(previousTimeAllocation);
            else applySnapshot(self.distribution, []);
            if (parentStartDate === undefined || parentEndDate === undefined) {
                this._internalRecalculateAutomatedWorkTypes();
            }
        },
        setCustomDailyWorkload (period, periodStart, periodWorkload) {
            if (self.isAutomated) return;
            if (!(periodStart instanceof Date) || periodWorkload === undefined) {
                throw new RangeError("Timeline period start and workload are required");
            }
            const indices = getDistributionPeriodIndices(self.startDay, period, periodStart);
            if (!indices.length) return;
            while(self.distribution.length <= indices[indices.length - 1]){
                self.distribution.push({
                    value: 0
                });
            }
            validateValue(periodWorkload, "period workload");
            const currentPeriodOverride = indices.reduce((total, index)=>total + (self.distribution[index]?.ov ? self.distribution[index].value : 0), 0);
            if (periodWorkload + self.availableWorkload - currentPeriodOverride > self.workload) {
                throw new RangeError("Timeline overridden workload exceeds workload");
            }
            indices.forEach((index)=>{
                self.distribution[index].ov = true;
                self.distribution[index].value = 0;
            });
            if (periodWorkload >= indices.length * self.timeAllocation) {
                const splitWorkload = weightedValueRoundedSplit(periodWorkload, indices.map(()=>1), MaxTimeModifiers.HOUR);
                indices.forEach((index, dayIndex)=>{
                    self.distribution[index].value = splitWorkload[dayIndex];
                });
            } else {
                let remaining = periodWorkload;
                indices.forEach((index)=>{
                    const value = Math.min(self.timeAllocation, remaining);
                    self.distribution[index].value = value;
                    remaining -= value;
                });
            }
            this.redistributeWorkload();
            this._internalRecalculateAutomatedWorkTypes();
        },
        resetCustomDailyWorkload (period, periodStart) {
            if (self.isAutomated) return;
            if (!periodStart) {
                throw new RangeError("Timeline period start is required");
            }
            getDistributionPeriodIndices(self.startDay, period, periodStart).filter((index)=>index < self.distribution.length).forEach((index)=>{
                self.distribution[index].ov = undefined;
            });
            this.redistributeWorkload();
            this._internalRecalculateAutomatedWorkTypes();
        },
        removeSelf () {
            const workType = getParent(self, 2);
            workType?.removeTeamMember(self.id);
        },
        removeAutofocus () {
            const root = getRoot(self);
            root.historyManager?.withoutUndo(()=>{
                self.autoFocus = undefined;
            });
        }
    }));

const TimelineWorkTypeModel = types.model("TimelineWorkType", {
    id: types.string,
    startDay: TimelineDatePrimitive,
    timeAllocation: types.number,
    distribution: types.array(TimelineDailyValueModel),
    expanded: types.maybe(types.boolean),
    teamMembers: types.array(TimelineTeamMemberModel),
    /** not serialized: */ valueMode: types.optional(types.boolean, false)
}).views((self)=>({
        resolveColor (fontColor = false) {
            const parent = getParent(self, 2);
            try {
                const root = getRoot(self);
                const summaryWorkType = root.workTypes?.find((workType)=>workType.id === self.id);
                return resolveTimelineColor(fontColor ? summaryWorkType?.timelineFontColor : summaryWorkType?.timelineColor, fontColor ? parent?.fontColor : parent?.color);
            } catch  {
                return resolveTimelineColor(undefined, undefined, fontColor ? parent?.fontColor : parent?.color);
            }
        },
        get color () {
            return this.resolveColor();
        },
        get fontColor () {
            return this.resolveColor(true);
        },
        get endDate () {
            if (!self.teamMembers?.length) return !self.distribution.length ? undefined : getTimelineEndDate(self.startDay, self.distribution.length);
            return this.visibleTeamMembers.reduce((latest, teamMember)=>teamMember.endDate && (!latest || isAfter(teamMember.endDate, latest)) ? teamMember.endDate : latest, undefined);
        },
        get hourlyTimeAllocation () {
            if (this.isAutomated) {
                return self.distribution.length ? this.workload / self.distribution.length / MaxTimeModifiers.HOUR : 0;
            }
            return self.timeAllocation / MaxTimeModifiers.HOUR;
        },
        get name () {
            try {
                const root = getRoot(self);
                return root.workTypes?.find((value)=>value.id === self.id)?.name || "";
            } catch  {
                return "";
            }
        },
        get percentageWorkTypes () {
            try {
                const root = getRoot(self);
                const section = getParent(self, 2);
                return root.sections?.find((value)=>value.id === section.id)?.workTypes?.find((value)=>value.id === self.id)?.percentageWorkTypes || [];
            } catch  {
                return [];
            }
        },
        get percentTargetsInSections () {
            try {
                const root = getRoot(self);
                const section = getParent(self, 2);
                return root.sections?.find((value)=>value.id === section.id)?.workTypes?.find((value)=>value.id === self.id && value.percent !== undefined)?.of || [];
            } catch  {
                return [];
            }
        },
        get isAutomated () {
            return this.percentTargetsInSections.length > 0;
        },
        get cellAutomatedWorkTypes () {
            try {
                const root = getRoot(self);
                const section = getParent(self, 2);
                const automatedWorkType = root.sections?.find((value)=>value.id === section.id)?.cellAutomatedWorkTypes?.find(({ id })=>id === self.id);
                if (!automatedWorkType) return {
                    of: [],
                    counter: 0
                };
                const of = [
                    ...new Set(automatedWorkType.of || [])
                ].map((id)=>{
                    const workType = root.workTypes?.find((value)=>value.id === id);
                    return workType ? {
                        id,
                        name: workType.name
                    } : undefined;
                }).filter((workType)=>Boolean(workType));
                return {
                    of,
                    counter: automatedWorkType.counter
                };
            } catch  {
                return {
                    of: [],
                    counter: 0
                };
            }
        },
        get hasPercent () {
            return this.isAutomated;
        },
        get isTurnedOff () {
            try {
                const root = getRoot(self);
                const workType = root.workTypes?.find((value)=>value.id === self.id);
                return Boolean(workType?.turnOff || workType?.clientTurnOff);
            } catch  {
                return false;
            }
        },
        get visibleTeamMembers () {
            try {
                const root = getRoot(self);
                if (!root.visibility?.apply) return self.teamMembers;
                const summaryWorkType = root.workTypes?.find((value)=>value.id === self.id);
                return self.teamMembers.filter((teamMember)=>{
                    const summaryTeamMember = summaryWorkType?.resources?.find((value)=>String(value.id) === teamMember.id);
                    return !summaryTeamMember || !summaryTeamMember.isHidden && !summaryTeamMember.hideOnTimeline;
                });
            } catch  {
                return self.teamMembers.slice();
            }
        },
        get workload () {
            try {
                const root = getRoot(self);
                const timelineRoot = getTimelineRoot(self);
                const section = getParent(self, 2);
                return root.sections?.find((value)=>value.id === section.id)?.getTotalHoursPerWorkType?.(self.id)?.[timelineRoot.workloadKey] || 0;
            } catch  {
                return 0;
            }
        },
        get parsedWorkload () {
            return this.workload / MaxTimeModifiers.HOUR;
        },
        get availableWorkload () {
            return self.distribution.reduce((total, value)=>total + (value.ov ? value.value : 0), 0);
        },
        get distributionByMonth () {
            return !self.teamMembers.length || this.isAutomated ? getChunkedDistribution(self.distribution, self.startDay) : mergeChunkedDistributions(this.visibleTeamMembers.map((teamMember)=>teamMember.distributionByMonth), self.startDay);
        },
        get currentlyAddedTeamMembers () {
            return self.teamMembers.map((teamMember)=>teamMember.id);
        },
        serialize () {
            return cleanObject({
                id: self.id,
                startDay: formatTimelineDate(self.startDay),
                timeAllocation: self.timeAllocation,
                distribution: self.distribution.map((value)=>value.serialize()),
                expanded: self.expanded,
                teamMembers: self.teamMembers.map((teamMember)=>teamMember.serialize())
            });
        }
    })).actions((self)=>({
        _internalRecalculateAutomatedWorkTypes (internalCall) {
            if (internalCall) return;
            try {
                getParent(self, 2)._internalRecalculateAutomatedWorkTypes(self.id);
            } catch  {
            // Standalone model instances have no section to recalculate.
            }
        },
        setValueMode (value) {
            self.valueMode = value;
        },
        updateId (newId) {
            self.id = newId;
        },
        redistributeWorkload (apparentMinutesPerDay) {
            if (self.isAutomated) {
                this.recalculateAutomatedDistribution();
                this.recalculateAutomatedTeamMemberDistributions();
                return;
            }
            const newDistribution = redistributeWorkload(self.distribution, self.timeAllocation, self.workload, apparentMinutesPerDay);
            applySnapshot(self.distribution, newDistribution);
        },
        setExpanded (value) {
            self.expanded = value;
        },
        setStartDay (value) {
            self.teamMembers.forEach((teamMember)=>teamMember.updateStartDates(self.startDay, value));
            self.startDay = value;
            getParent(self, 2)._internalEnsureStartDay(value);
            this._internalRecalculateAutomatedWorkTypes();
        },
        _internalEnsureStartDay (value) {
            if (!isBefore(value, self.startDay)) return;
            self.startDay = value;
            getParent(self, 2)._internalEnsureStartDay(value);
        },
        updateStartDates (oldTimelineStartDate, newTimelineStartDate) {
            self.startDay = shiftTimelineStartDate(self.startDay, oldTimelineStartDate, newTimelineStartDate);
            self.teamMembers.forEach((teamMember)=>teamMember.updateStartDates(oldTimelineStartDate, newTimelineStartDate));
        },
        setColor (value) {
            const root = getRoot(self);
            root.workTypes?.find((workType)=>workType.id === self.id)?.setTimelineColor?.(value);
        },
        setFontColor (value) {
            const root = getRoot(self);
            root.workTypes?.find((workType)=>workType.id === self.id)?.setTimelineFontColor?.(value);
        },
        updateTeamMemberWorkloads () {
            if (!self.teamMembers.length) return;
            const totalAllocation = self.teamMembers.reduce((total, teamMember)=>total + teamMember.timeAllocation, 0);
            validatePositiveFinite(totalAllocation, "Team Member allocation total");
            const splitWorkload = weightedValueRoundedSplit(self.workload, self.teamMembers.map((t)=>t.timeAllocation), MaxTimeModifiers.HOUR, Rounding.FULL);
            self.teamMembers.forEach((teamMember, index)=>{
                teamMember.setWorkload(splitWorkload[index], true);
            });
        },
        recalculateTeamMemberWorkloads () {
            if (!self.isAutomated) return;
            this.updateTeamMemberWorkloads();
            this.recalculateAutomatedTeamMemberDistributions();
        },
        transferTeamMemberWorkload (sourceId, targetId, workload, period, periodStart) {
            validatePositiveFinite(workload, "transferred workload");
            if (sourceId === targetId) {
                throw new RangeError("Timeline Team Members must be different");
            }
            const source = self.teamMembers.find((value)=>value.id === sourceId);
            const target = self.teamMembers.find((value)=>value.id === targetId);
            if (!source || !target) {
                throw new RangeError("Timeline Team Member was not found");
            }
            const hasPeriod = period !== undefined && periodStart !== undefined;
            const sourceWorkload = parseFloat((source.workload - workload).toFixed(2));
            const targetWorkload = parseFloat((target.workload + workload).toFixed(2));
            if (!hasPeriod || self.isAutomated) {
                source.setWorkload(sourceWorkload, true);
                target.setWorkload(targetWorkload, true);
                if (self.isAutomated) this.recalculateAutomatedTeamMemberDistributions();
                this._internalRecalculateAutomatedWorkTypes();
                return;
            }
            let nextSourceDistribution = source.distribution.map((value)=>({
                    value: value.value,
                    ov: value.ov
                }));
            const sourcePeriodIndices = getDistributionPeriodIndices(source.startDay, period, periodStart).filter((index)=>index < nextSourceDistribution.length).toReversed();
            if (!sourcePeriodIndices.length) {
                source.setWorkload(sourceWorkload, true);
                target.setWorkload(targetWorkload, true);
                this._internalRecalculateAutomatedWorkTypes();
                return;
            }
            const sourcePeriodWorkload = sourcePeriodIndices.reduce((total, index)=>total + (nextSourceDistribution[index]?.value ?? 0), 0);
            let remaining = workload > sourcePeriodWorkload ? 0 : workload;
            for (const index of sourcePeriodIndices){
                const value = nextSourceDistribution[index];
                if (!value) continue;
                value.ov = sourcePeriodIndices[sourcePeriodIndices.length - 1] < nextSourceDistribution.length;
                if (workload > sourcePeriodWorkload) {
                    value.value = 0;
                    continue;
                }
                if (remaining <= 0) break;
                const reduction = Math.min(value.value, remaining);
                value.value -= reduction;
                remaining -= reduction;
            }
            if (workload > sourcePeriodWorkload) {
                nextSourceDistribution = redistributeWorkload(nextSourceDistribution, source.timeAllocation, sourceWorkload);
            }
            const nextTargetDistribution = target.distribution.map((value)=>({
                    value: value.value,
                    ov: value.ov
                }));
            const targetPeriodIndices = getDistributionPeriodIndices(target.startDay, period, periodStart);
            if (!targetPeriodIndices.length) {
                source.setWorkload(sourceWorkload, true);
                target.setWorkload(targetWorkload, true);
                this._internalRecalculateAutomatedWorkTypes();
                return;
            }
            while(nextTargetDistribution.length <= targetPeriodIndices[targetPeriodIndices.length - 1]){
                nextTargetDistribution.push({
                    value: 0
                });
            }
            const targetPeriodWorkload = targetPeriodIndices.reduce((total, index)=>total + (nextTargetDistribution[index]?.value ?? 0), 0);
            if (workload + targetPeriodWorkload >= targetPeriodIndices.length * target.timeAllocation) {
                const splitWorkload = weightedValueRoundedSplit(workload + targetPeriodWorkload, targetPeriodIndices.map(()=>1), MaxTimeModifiers.HOUR, Rounding.FULL).reverse();
                targetPeriodIndices.forEach((index, dailyIndex)=>{
                    nextTargetDistribution[index] = {
                        value: splitWorkload[dailyIndex],
                        ov: true
                    };
                });
            } else {
                let remainingToAdd = workload;
                for(let index = Math.min(targetPeriodIndices[0], nextTargetDistribution.length); index <= targetPeriodIndices[targetPeriodIndices.length - 1]; index++){
                    if (!nextTargetDistribution[index]) nextTargetDistribution[index] = {
                        value: 0
                    };
                    if (!targetPeriodIndices.includes(index)) continue;
                    const available = Math.max(0, target.timeAllocation - nextTargetDistribution[index].value);
                    const added = Math.min(available, remainingToAdd);
                    nextTargetDistribution[index].value += added;
                    nextTargetDistribution[index].ov = true;
                    remainingToAdd -= added;
                }
            }
            source.setWorkloadWithDistribution(sourceWorkload, nextSourceDistribution);
            target.setWorkloadWithDistribution(targetWorkload, nextTargetDistribution);
            this._internalRecalculateAutomatedWorkTypes();
        },
        transferWorkloadBetweenPeriods (workload, period, sourcePeriodStart, targetPeriodStart) {
            if (self.isAutomated) return;
            const sourceIndices = getDistributionPeriodIndices(self.startDay, period, sourcePeriodStart);
            const targetIndices = getDistributionPeriodIndices(self.startDay, period, targetPeriodStart);
            applySnapshot(self.distribution, transferDistributionWorkload(self.distribution, workload, sourceIndices, targetIndices, self.timeAllocation));
            this._internalRecalculateAutomatedWorkTypes();
        },
        stretchCurtail (newEndDate, providedProportion, parentStartDate, parentEndDate) {
            if (self.isAutomated) return;
            validateTimelineEndDate(newEndDate, self.startDay);
            const { distributionLength, newProportion } = getNewDistributionLength(newEndDate, self.startDay, self.endDate, providedProportion, parentStartDate, parentEndDate);
            const previousTimeAllocation = self.timeAllocation;
            self.timeAllocation = Math.min(self.workload / distributionLength, MaxTimeModifiers.DAY * MaxTimeModifiers.HOUR);
            if (self.teamMembers.length) {
                self.teamMembers.forEach((teamMember)=>teamMember.stretchCurtail(newEndDate, newProportion, self.startDay, self.endDate ?? self.startDay));
            } else if (self.workload > 0) {
                this.redistributeWorkload(previousTimeAllocation);
            } else {
                applySnapshot(self.distribution, []);
            }
            if (parentStartDate === undefined || parentEndDate === undefined) this._internalRecalculateAutomatedWorkTypes();
        },
        recalculateWorkload () {
            if (self.isAutomated) {
                this.recalculateAutomatedDistribution();
                this.recalculateAutomatedTeamMemberDistributions();
                return;
            }
            if (self.teamMembers.length) {
                applySnapshot(self.distribution, []);
                this.updateTeamMemberWorkloads();
                self.teamMembers.forEach((teamMember)=>teamMember.redistributeWorkload(undefined));
            } else this.redistributeWorkload();
        },
        recalculateAutomatedDistribution () {
            const root = getRoot(self);
            const section = getParent(self, 2);
            const relation = root.sections?.find((value)=>value.id === section.id)?.workTypes?.find((value)=>value.id === self.id);
            if (!relation?.of?.length || relation.percent === undefined) return;
            const sourceWorkTypes = section.workTypes.filter((workType)=>relation.of.includes(workType.id));
            if (!sourceWorkTypes.length) return;
            const newStartDay = sourceWorkTypes.reduce((earliest, workType)=>workType.workload > 0 && (!earliest || isBefore(workType.startDay, earliest)) ? workType.startDay : earliest, undefined);
            if (newStartDay) self.startDay = newStartDay;
            const sourceDistributions = sourceWorkTypes.flatMap((workType)=>{
                const elements = workType.teamMembers.length ? workType.teamMembers : [
                    workType
                ];
                return elements.map((element)=>({
                        startDay: element.startDay,
                        distribution: element.distribution
                    }));
            });
            const distribution = mergeDistributions(sourceDistributions, self.startDay, relation.percent);
            applySnapshot(self.distribution, distribution);
        },
        recalculateAutomatedTeamMemberDistributions () {
            if (!self.teamMembers.length) return;
            self.teamMembers.forEach((member)=>{
                member.setStartDay(self.startDay, true);
            });
            const remainingWorkload = self.teamMembers.map((member)=>member.workload);
            const memberDistributions = self.teamMembers.map(()=>[]);
            self.distribution.forEach(({ value: dailyValue })=>{
                let remainingDaily = dailyValue;
                self.teamMembers.forEach((member, index)=>{
                    const capacity = remainingWorkload[index];
                    const share = index === self.teamMembers.length - 1 ? remainingDaily : safeRoundHours(self.workload > 0 ? dailyValue * member.workload / self.workload : 0);
                    const assigned = safeRoundHours(Math.max(0, Math.min(share, capacity, remainingDaily)));
                    memberDistributions[index].push({
                        value: assigned
                    });
                    remainingWorkload[index] = Math.max(0, safeRoundHours(capacity - assigned));
                    remainingDaily = Math.max(0, safeRoundHours(remainingDaily - assigned));
                });
                if (remainingDaily > 0) {
                    self.teamMembers.forEach((_, index)=>{
                        if (remainingDaily <= 0) return;
                        const capacity = remainingWorkload[index];
                        const assigned = safeRoundHours(Math.min(remainingDaily, capacity));
                        memberDistributions[index][memberDistributions[index].length - 1].value = safeRoundHours(memberDistributions[index][memberDistributions[index].length - 1].value + assigned);
                        remainingWorkload[index] = Math.max(0, safeRoundHours(capacity - assigned));
                        remainingDaily = Math.max(0, safeRoundHours(remainingDaily - assigned));
                    });
                }
            });
            self.teamMembers.forEach((member, index)=>member.setDistributionFromWorkType(memberDistributions[index]));
        },
        setTimelineAllocation (value, internalCall) {
            if (self.isAutomated) return;
            validatePositiveFinite(value, "timeline allocation");
            self.timeAllocation = value;
            self.teamMembers.forEach((teamMember)=>teamMember.setTimelineAllocation(value, true));
            this.redistributeWorkload();
            if (!internalCall) this._internalRecalculateAutomatedWorkTypes();
        },
        addTeamMember (id) {
            this._internalAddTeamMember(id, true);
            if (!self.isAutomated) this._internalRecalculateAutomatedWorkTypes();
            const section = getParent(self, 2);
            getRoot(self)._internalAddTimelineTeamMember?.(section.id, self.id, id);
        },
        removeTeamMember (id) {
            this._internalRemoveTeamMember(id);
            if (!self.isAutomated) this._internalRecalculateAutomatedWorkTypes();
            const section = getParent(self, 2);
            getRoot(self)._internalRemoveTimelineTeamMember?.(section.id, self.id, id);
        },
        _internalAddTeamMember (id, autoFocus) {
            if (self.teamMembers.some((teamMember)=>teamMember.id === id)) return;
            if (!self.teamMembers.length && !self.isAutomated) applySnapshot(self.distribution, []);
            self.teamMembers.push({
                id,
                autoFocus,
                valueMode: self.teamMembers.at(-1)?.valueMode ?? self.valueMode,
                startDay: self.startDay,
                timeAllocation: self.timeAllocation,
                workload: 0,
                distribution: []
            });
            this.updateTeamMemberWorkloads();
            if (self.isAutomated) this.recalculateAutomatedTeamMemberDistributions();
        },
        _internalRemoveTeamMember (id) {
            const teamMember = self.teamMembers.find((value)=>value.id === id);
            if (!teamMember) return;
            destroy(teamMember);
            if (self.teamMembers.length) {
                this.updateTeamMemberWorkloads();
                if (self.isAutomated) this.recalculateAutomatedTeamMemberDistributions();
            } else this.redistributeWorkload();
        },
        _internalChangeTeamMemberId (oldId, newId) {
            const teamMember = self.teamMembers.find((value)=>value.id === oldId);
            if (teamMember && !self.teamMembers.some((value)=>value.id === newId)) {
                teamMember._internalUpdateId(newId);
            }
        },
        reorderTeamMembers (reorderedTeamMembers) {
            self.teamMembers = cast(updateSortedList(self.teamMembers, reorderedTeamMembers, "id", true));
        },
        setCustomDailyWorkload (period, periodStart, periodWorkload) {
            if (self.isAutomated) return;
            if (!(periodStart instanceof Date) || periodWorkload === undefined) {
                throw new RangeError("Timeline period start and workload are required");
            }
            const indices = getDistributionPeriodIndices(self.startDay, period, periodStart);
            if (!indices.length) return;
            while(self.distribution.length <= indices[indices.length - 1]){
                self.distribution.push({
                    value: 0
                });
            }
            validateValue(periodWorkload, "period workload");
            const currentPeriodOverride = indices.reduce((total, index)=>total + (self.distribution[index]?.ov ? self.distribution[index].value : 0), 0);
            if (periodWorkload + self.availableWorkload - currentPeriodOverride > self.workload) {
                throw new RangeError("Timeline overridden workload exceeds workload");
            }
            indices.forEach((index)=>{
                self.distribution[index].ov = true;
                self.distribution[index].value = 0;
            });
            if (periodWorkload >= indices.length * self.timeAllocation) {
                const splitWorkload = weightedValueRoundedSplit(periodWorkload, indices.map(()=>1), MaxTimeModifiers.HOUR);
                indices.forEach((index, dayIndex)=>{
                    self.distribution[index].value = splitWorkload[dayIndex];
                });
            } else {
                let remaining = periodWorkload;
                indices.forEach((index)=>{
                    const value = Math.min(self.timeAllocation, remaining);
                    self.distribution[index].value = value;
                    remaining -= value;
                });
            }
            this.redistributeWorkload();
            this._internalRecalculateAutomatedWorkTypes();
        },
        resetCustomDailyWorkload (period, periodStart) {
            if (self.isAutomated) return;
            if (!periodStart) {
                throw new RangeError("Timeline period start is required");
            }
            getDistributionPeriodIndices(self.startDay, period, periodStart).filter((index)=>index < self.distribution.length).forEach((index)=>{
                self.distribution[index].ov = undefined;
            });
            this.redistributeWorkload();
            this._internalRecalculateAutomatedWorkTypes();
        }
    }));

const TimelineSectionModel = types.model("TimelineSection", {
    id: types.string,
    custom: types.maybe(types.boolean),
    customName: types.maybe(types.string),
    customColor: types.maybe(types.string),
    customFontColor: types.maybe(types.string),
    startDay: TimelineDatePrimitive,
    endDay: types.maybe(TimelineDatePrimitive),
    timeAllocation: types.number,
    hidden: types.maybe(types.boolean),
    expanded: types.maybe(types.boolean),
    workTypes: types.array(TimelineWorkTypeModel)
}).views((self)=>({
        get visibleWorkTypes () {
            try {
                const root = getRoot(self);
                return self.workTypes.filter((workType)=>{
                    if (root.excludeTurnedOffElements && workType.isTurnedOff) return false;
                    const summarySection = root.sections?.find((value)=>value.id === self.id);
                    const summaryWorkTypeRelation = summarySection?.workTypes?.find((value)=>value.id === workType.id);
                    if (summaryWorkTypeRelation?.invisible && workType.workload === 0) return false;
                    const summaryWorkType = root.workTypes?.find((value)=>value.id === workType.id);
                    if (summaryWorkType?.hasFixedPrice) return false;
                    if (!root.visibility?.apply) return true;
                    return !summaryWorkType || !summaryWorkType.isHidden && !summaryWorkType.hideOnTimeline;
                });
            } catch  {
                return self.workTypes.slice();
            }
        },
        get color () {
            if (self.custom) return self.customColor || DEFAULT_TIMELINE_COLOR;
            try {
                const root = getRoot(self);
                return root.sections?.find((value)=>value.id === self.id)?.timelineColor || DEFAULT_TIMELINE_COLOR;
            } catch  {
                return DEFAULT_TIMELINE_COLOR;
            }
        },
        get fontColor () {
            if (self.custom) return self.customFontColor || DEFAULT_TIMELINE_FONT_COLOR;
            try {
                const root = getRoot(self);
                return root.sections?.find((value)=>value.id === self.id)?.timelineFontColor || DEFAULT_TIMELINE_FONT_COLOR;
            } catch  {
                return DEFAULT_TIMELINE_FONT_COLOR;
            }
        },
        get endDate () {
            if (self.endDay && (self.custom || !self.workTypes.length)) {
                return self.endDay;
            }
            return this.visibleWorkTypes.reduce((latest, workType)=>workType.endDate && (!latest || isAfter(workType.endDate, latest)) ? workType.endDate : latest, undefined);
        },
        get hourlyTimeAllocation () {
            return self.timeAllocation / MaxTimeModifiers.HOUR;
        },
        get name () {
            if (self.custom) return self.customName || "";
            try {
                const root = getRoot(self);
                return root.sections?.find((value)=>value.id === self.id)?.name || "";
            } catch  {
                return "";
            }
        },
        get isTurnedOff () {
            if (self.custom) return false;
            try {
                const root = getRoot(self);
                return root.sections?.find((value)=>value.id === self.id)?.isTurnedOff;
            } catch  {
                return false;
            }
        },
        get workload () {
            try {
                const root = getRoot(self);
                const timelineRoot = getTimelineRoot(self);
                return root.sections?.find((value)=>value.id === self.id)?.combinedHours?.[timelineRoot.workloadKey] || 0;
            } catch  {
                return 0;
            }
        },
        get parsedWorkload () {
            return this.workload / MaxTimeModifiers.HOUR;
        },
        get distributionByMonth () {
            return mergeChunkedDistributions(this.visibleWorkTypes.map((workType)=>workType.distributionByMonth), self.startDay);
        },
        serialize () {
            return cleanObject({
                id: self.id,
                custom: self.custom,
                customName: self.customName,
                customColor: self.custom ? self.customColor : undefined,
                customFontColor: self.custom ? self.customFontColor : undefined,
                startDay: formatTimelineDate(self.startDay),
                endDay: self.endDay && formatTimelineDate(self.endDay),
                timeAllocation: self.timeAllocation,
                hidden: self.hidden,
                expanded: self.expanded,
                workTypes: self.workTypes.map((workType)=>workType.serialize())
            });
        }
    })).actions((self)=>({
        _internalEnsureStartDay (value) {
            if (isBefore(value, self.startDay)) self.startDay = value;
        },
        _internalSetId (newId) {
            self.id = newId;
        },
        recalculateWorkload (workTypeIds) {
            self.workTypes.forEach((workType)=>{
                if (!workTypeIds || workTypeIds.includes(workType.id)) {
                    workType.recalculateWorkload();
                }
            });
        },
        _internalRecalculateAutomatedWorkTypes (workTypeId) {
            cascadedPercentWorkTypesSort(self.workTypes, "percentTargetsInSections", workTypeId).forEach((workType)=>{
                if (!workType.isAutomated) return;
                workType.recalculateAutomatedDistribution();
                workType.recalculateAutomatedTeamMemberDistributions();
            });
        },
        stretchCurtail (newEndDate) {
            validateTimelineEndDate(newEndDate, self.startDay);
            if (self.custom || !self.workTypes.length) {
                self.endDay = newEndDate;
                return;
            }
            const originalStartDate = self.startDay;
            const originalEndDate = self.endDate ?? originalStartDate;
            const oldLength = countWorkdaysInInterval(originalStartDate, originalEndDate);
            const newLength = countWorkdaysInInterval(self.startDay, newEndDate);
            self.workTypes.forEach((workType)=>workType.stretchCurtail(newEndDate, newLength / oldLength, originalStartDate, originalEndDate));
            this._internalRecalculateAutomatedWorkTypes();
        },
        setExpanded (value) {
            self.expanded = value;
        },
        setHidden (value) {
            self.hidden = value;
        },
        setStartDay (value) {
            if (self.endDay) {
                self.endDay = shiftTimelineStartDate(self.endDay, self.startDay, value);
            }
            self.workTypes.forEach((workType)=>workType.updateStartDates(self.startDay, value));
            self.startDay = value;
            this._internalRecalculateAutomatedWorkTypes();
        },
        updateStartDates (oldTimelineStartDate, newTimelineStartDate) {
            if (self.endDay) {
                self.endDay = shiftTimelineStartDate(self.endDay, oldTimelineStartDate, newTimelineStartDate);
            }
            self.startDay = shiftTimelineStartDate(self.startDay, oldTimelineStartDate, newTimelineStartDate);
            self.workTypes.forEach((workType)=>workType.updateStartDates(oldTimelineStartDate, newTimelineStartDate));
            this._internalRecalculateAutomatedWorkTypes();
        },
        setColor (value) {
            const root = getRoot(self);
            if (self.custom) {
                self.customColor = value;
                root._internalUpdateTimelineSection?.(self.id, {
                    customColor: value
                });
            } else {
                root.sections?.find((section)=>section.id === self.id)?.setTimelineColor?.(value);
            }
        },
        setFontColor (value) {
            const root = getRoot(self);
            if (self.custom) {
                self.customFontColor = value;
                root._internalUpdateTimelineSection?.(self.id, {
                    customFontColor: value
                });
            } else {
                root.sections?.find((section)=>section.id === self.id)?.setTimelineFontColor?.(value);
            }
        },
        setCustomName (customName) {
            self.customName = customName;
            getRoot(self)._internalUpdateTimelineSection?.(self.id, {
                customName
            });
        },
        _internalUpdateProperties (updates) {
            if ("customColor" in updates) self.customColor = updates.customColor;
            if ("customFontColor" in updates) self.customFontColor = updates.customFontColor;
            if ("customName" in updates) self.customName = updates.customName;
        },
        setTimelineAllocation (value) {
            validatePositiveFinite(value, "timeline allocation");
            self.timeAllocation = value;
            self.workTypes.forEach((workType)=>workType.setTimelineAllocation(value, true));
            this._internalRecalculateAutomatedWorkTypes();
        },
        addWorkType (workTypeId) {
            self.workTypes.push({
                id: workTypeId,
                startDay: self.startDay,
                timeAllocation: self.timeAllocation,
                distribution: [],
                teamMembers: []
            });
            self.workTypes[self.workTypes.length - 1].redistributeWorkload();
            this._internalRecalculateAutomatedWorkTypes(workTypeId);
        },
        updateWorkTypeId (workTypeId, newWorkTypeId) {
            self.workTypes.find((workType)=>workType.id === workTypeId)?.updateId(newWorkTypeId);
        },
        removeWorkType (workTypeId) {
            const workType = self.workTypes.find((value)=>value.id === workTypeId);
            if (workType) destroy(workType);
            this._internalRecalculateAutomatedWorkTypes(workTypeId);
        },
        reorderWorkTypes (reorderedWorkTypes) {
            self.workTypes = cast(updateSortedList(self.workTypes, reorderedWorkTypes, "id", true));
        }
    }));

const TimelineModel = types.model("Timeline", {
    workloadKey: types.enumeration("MinAvgMaxKeys", Object.values(MinAvgMaxKeys)),
    sections: types.array(TimelineSectionModel)
}).views((self)=>({
        get visibleSections () {
            const root = getRoot(self);
            return self.sections.filter((section)=>!section.hidden && (!root.excludeTurnedOffElements || !section.isTurnedOff));
        },
        get lastSectionEndDate () {
            const root = getRoot(self);
            return this.visibleSections.reduce((latest, section)=>section.endDate && (!latest || section.endDate > latest) ? section.endDate : latest, root.settings?.timelineStartDate);
        },
        lastWorkTypeEndDate (workTypeId) {
            const root = getRoot(self);
            return this.visibleSections.reduce((latest, section)=>{
                const workType = section.visibleWorkTypes.find((value)=>value.id === workTypeId);
                if (!workType) return latest;
                return workType.endDate && (!latest || workType.endDate > latest) ? workType.endDate : latest;
            }, root.settings?.timelineStartDate);
        },
        lastTeamMemberEndDate (workTypeId, teamMemberId) {
            const root = getRoot(self);
            return this.visibleSections.reduce((latest, section)=>{
                const teamMember = section.visibleWorkTypes.find((workType)=>workType.id === workTypeId)?.visibleTeamMembers.find((member)=>member.id === teamMemberId);
                if (!teamMember) return latest;
                return teamMember.endDate && (!latest || teamMember.endDate > latest) ? teamMember.endDate : latest;
            }, root.settings?.timelineStartDate);
        },
        get totalDistribution () {
            const distributions = this.visibleSections.flatMap((section)=>section.visibleWorkTypes.flatMap((workType)=>workType.teamMembers.length ? workType.visibleTeamMembers.map((member)=>member.distributionByMonth) : [
                        workType.distributionByMonth
                    ]));
            const mergedMonths = mergeChunkedDistributions(distributions, getRoot(self).settings?.timelineStartDate);
            return mergedMonths.map((month)=>({
                    startDate: month.startDate,
                    endDate: month.endDate,
                    value: month.value,
                    weeks: month.weeks.map((week)=>({
                            startDate: week.startDate,
                            endDate: week.endDate,
                            value: week.value,
                            days: week.distribution.map((day)=>({
                                    startDate: day.startDate,
                                    value: day.value
                                }))
                        }))
                }));
        },
        getUtilization (workTypeId, teamMemberId) {
            const root = getRoot(self);
            const timelineStartDate = root.settings?.timelineStartDate;
            const lastSectionEndDate = teamMemberId ? this.lastTeamMemberEndDate(workTypeId, teamMemberId) : this.lastWorkTypeEndDate(workTypeId);
            if (!timelineStartDate || !lastSectionEndDate || timelineStartDate > lastSectionEndDate) return [];
            const applicableWorkTypes = this.visibleSections.flatMap((section)=>{
                const workType = section.visibleWorkTypes.find((value)=>value.id === workTypeId);
                if (!workType) return [];
                if (teamMemberId && !workType.visibleTeamMembers.some((member)=>member.id === teamMemberId)) {
                    return [];
                }
                return [
                    workType
                ];
            });
            const daysModifier = root.settings?.daysModifier ?? MaxTimeModifiers.HOUR;
            const hoursPerModifier = MaxTimeModifiers.HOUR;
            const distributions = this.visibleSections.flatMap((section)=>{
                const workType = section.visibleWorkTypes.find((value)=>value.id === workTypeId);
                if (!workType) return [];
                const element = teamMemberId ? workType.visibleTeamMembers.find((value)=>value.id === teamMemberId) : workType;
                return element ? [
                    element.distributionByMonth
                ] : [];
            });
            const mergedMonths = mergeChunkedDistributions(distributions, timelineStartDate);
            return mergedMonths.map((month)=>{
                const overlappingSectionCount = new Map();
                const monthDays = month.weeks.flatMap((week)=>week.distribution.map((day)=>day));
                const getDailyWeight = (day)=>{
                    const { value, startDate: date } = day;
                    if (isWeekend(date)) return 0;
                    const sectionCount = overlappingSectionCount.get(getDateKey(date)) || 0;
                    const weight = value && sectionCount ? value / sectionCount / daysModifier : 0;
                    return isEqual(date, lastSectionEndDate) && !day.ov && weight < 1 ? 1 : weight;
                };
                monthDays.forEach((day)=>{
                    if (day.empty) return;
                    const count = applicableWorkTypes.reduce((total, workType)=>{
                        const isActive = !isBefore(day.startDate, workType.startDay) && (!workType.endDate || !isAfter(day.startDate, workType.endDate));
                        if (!isActive) return total;
                        const teamMembers = teamMemberId ? workType.visibleTeamMembers.filter((member)=>member.id === teamMemberId) : workType.visibleTeamMembers;
                        const teamMemberCount = workType.visibleTeamMembers.length ? teamMembers.filter((teamMember)=>!isBefore(day.startDate, teamMember.startDay) && (!teamMember.endDate || !isAfter(day.startDate, teamMember.endDate))).length : 1;
                        return total + teamMemberCount;
                    }, 0);
                    overlappingSectionCount.set(getDateKey(day.startDate), count);
                });
                const monthWeights = monthDays.map((day)=>getDailyWeight(day));
                const getDailyOptimal = (day)=>{
                    if (isWeekend(day.startDate)) return 0;
                    return (overlappingSectionCount.get(getDateKey(day.startDate)) || 0) * daysModifier / hoursPerModifier;
                };
                const monthOptimal = monthDays.reduce((total, day)=>total + getDailyOptimal(day), 0);
                const monthDaysWithOverlap = monthDays.filter((day)=>!day.empty && (overlappingSectionCount.get(getDateKey(day.startDate)) || 0) > 0).length;
                const weeks = month.weeks.map((week)=>{
                    const days = week.distribution;
                    const weights = days.map((day)=>getDailyWeight(day));
                    const optimal = days.reduce((total, day)=>total + getDailyOptimal(day), 0);
                    const daysWithOverlap = days.filter((day)=>!day.empty && (overlappingSectionCount.get(getDateKey(day.startDate)) || 0) > 0).length;
                    return {
                        startDate: week.startDate,
                        endDate: week.endDate,
                        value: parseFloat((days.reduce((total, day)=>total + day.value, 0) / MaxTimeModifiers.HOUR).toFixed(2)),
                        weight: daysWithOverlap ? weights.reduce((total, dailyWeight)=>total + dailyWeight, 0) / daysWithOverlap : 0,
                        optimal,
                        empty: week.empty,
                        days: days.map((day, index)=>cleanObject({
                                startDate: day.startDate,
                                value: parseFloat((day.value / MaxTimeModifiers.HOUR).toFixed(2)),
                                weight: weights[index],
                                optimal: getDailyOptimal(day),
                                empty: day.empty
                            }))
                    };
                });
                return {
                    startDate: month.startDate,
                    endDate: month.endDate,
                    value: parseFloat((month.value / MaxTimeModifiers.HOUR).toFixed(2)),
                    weight: monthDaysWithOverlap ? monthWeights.reduce((total, dailyWeight)=>total + dailyWeight, 0) / monthDaysWithOverlap : 0,
                    optimal: monthOptimal,
                    empty: month.empty,
                    weeks
                };
            });
        },
        getUtilizationChartBlocks (workTypeId, teamMemberId) {
            const root = getRoot(self);
            const daysModifier = root.settings?.daysModifier ?? MaxTimeModifiers.HOUR;
            const sources = [];
            this.visibleSections.forEach((section)=>{
                section.visibleWorkTypes.forEach((workType)=>{
                    if (workType.id !== workTypeId) return;
                    const distributions = teamMemberId ? workType.visibleTeamMembers.filter((member)=>member.id === teamMemberId) : workType.teamMembers.length ? workType.visibleTeamMembers : [
                        workType
                    ];
                    distributions.forEach((element)=>{
                        const distribution = element.distribution;
                        if (!distribution.length) return;
                        const endDate = element.endDate;
                        if (!endDate) return;
                        sources.push({
                            startDate: nextWorkDay(element.startDay),
                            endDate: prevWorkDay(endDate),
                            value: distribution.reduce((total, daily)=>total + daily.value, 0),
                            weight: 0
                        });
                    });
                });
            });
            sources.sort((left, right)=>left.startDate.getTime() - right.startDate.getTime());
            return sources.reduce((blocks, source)=>{
                const block = blocks[blocks.length - 1];
                const hasGap = !block || countWorkdaysInInterval(block.endDate, source.startDate) > 1;
                if (hasGap) {
                    blocks.push({
                        ...source
                    });
                    return blocks;
                }
                block.endDate = new Date(Math.max(block.endDate.getTime(), source.endDate.getTime()));
                block.value += source.value;
                return blocks;
            }, []).map((block)=>({
                    ...block,
                    weight: block.value / Math.max(1, countWorkdaysInInterval(block.startDate, block.endDate)) / daysModifier * 4
                }));
        },
        get billingMonths () {
            const root = getRoot(self);
            const startDate = root.settings?.timelineStartDate;
            const endDate = this.lastSectionEndDate;
            if (!startDate || !endDate || isAfter(startDate, endDate)) return [];
            const dailyByDate = new Map();
            const addDistributionBilling = (workTypeId, distribution, distributionStartDate, rate)=>{
                let date = nextWorkDay(distributionStartDate);
                distribution.forEach((dailyValue)=>{
                    if (date >= startDate && date <= endDate) {
                        const billing = dailyByDate.get(date.getTime()) || {};
                        billing[workTypeId] = (billing[workTypeId] || 0) + dailyValue.value * rate;
                        dailyByDate.set(date.getTime(), billing);
                    }
                    date = addWorkDays(date, 1);
                });
            };
            this.visibleSections.forEach((section)=>{
                section.visibleWorkTypes.forEach((workType)=>{
                    const summaryWorkType = root.workTypes?.find((value)=>value.id === workType.id);
                    if (workType.teamMembers.length) {
                        workType.visibleTeamMembers.forEach((teamMember)=>{
                            const resource = summaryWorkType?.resources?.find((value)=>String(value.id) === teamMember.id);
                            addDistributionBilling(workType.id, teamMember.distribution, teamMember.startDay, resource?.clientRate ?? resource?.rate ?? 0);
                        });
                    } else {
                        addDistributionBilling(workType.id, workType.distribution, workType.startDay, summaryWorkType?.clientRate ?? summaryWorkType?.rate ?? 0);
                    }
                });
            });
            const billings = [];
            for(let monthStart = startDate; monthStart <= endDate; monthStart = getFirstWorkdayOfMonth(addMonths(monthStart, 1))){
                billings.push(createBillingMonth(monthStart, dailyByDate, endDate));
            }
            return billings;
        },
        get timelineBillingScale () {
            const root = getRoot(self);
            let highestValue = 0;
            this.billingMonths.forEach((billing)=>{
                const monthExpansion = root.settings?.timelineExpandedMonths?.get(getExpansionDateKey(billing.startDate, true));
                if (!monthExpansion) {
                    highestValue = Math.max(highestValue, billing.total);
                    return;
                }
                billing.weeks.forEach((week)=>{
                    const weekExpansion = monthExpansion.weeks.get(getExpansionDateKey(week.startDate, false));
                    if (!weekExpansion) {
                        highestValue = Math.max(highestValue, week.total);
                        return;
                    }
                    (week.days ?? []).forEach((day)=>{
                        highestValue = Math.max(highestValue, day.total);
                    });
                });
            });
            let scale = 10;
            let previousScale = 0;
            while(highestValue / scale >= 10){
                const nextScale = scale * (scale / 5 === previousScale ? 2 : 5);
                previousScale = scale;
                scale = nextScale;
            }
            return {
                scale,
                topValue: Math.ceil(highestValue / scale) * scale
            };
        },
        serialize () {
            return cleanObject({
                workloadKey: self.workloadKey,
                sections: self.sections.map((section)=>section.serialize())
            });
        }
    })).actions((self)=>({
        addSection (id, custom, workTypes) {
            const root = getRoot(self);
            const startDay = root.settings?.timelineStartDate || new Date();
            const timeAllocation = root.settings?.daysModifier ?? MaxTimeModifiers.HOUR;
            self.sections.push(cleanObject({
                id,
                custom,
                startDay,
                timeAllocation,
                workTypes: workTypes?.map((workType)=>({
                        id: workType.id,
                        startDay,
                        timeAllocation,
                        distribution: [],
                        teamMembers: workType.resources?.map((resource)=>({
                                id: resource.id,
                                startDay,
                                timeAllocation,
                                workload: 0,
                                distribution: []
                            })) || []
                    })) || []
            }));
        },
        duplicateSection (newSectionId, oldSectionId) {
            const index = self.sections.findIndex((s)=>s.id === oldSectionId);
            const newSection = clone(self.sections[index], true);
            newSection._internalSetId(newSectionId);
            const position = index + 1;
            if (typeof position === "number" && position >= 0 && position < self.sections.length) {
                self.sections = cast([
                    ...self.sections.slice(0, position),
                    newSection,
                    ...self.sections.slice(position)
                ]);
            } else self.sections.push(newSection);
        },
        removeSection (id) {
            const section = self.sections.find((value)=>value.id === id);
            if (section) destroy(section);
        },
        reorderSections (reorderedSections) {
            const reordered = updateSortedList(self.sections, reorderedSections, "id", true);
            self.sections = cast((reordered ?? []).map((section)=>getSnapshot(section)));
        },
        recalculateWorkload (workTypeIds) {
            self.sections.forEach((section)=>section.recalculateWorkload(workTypeIds));
        },
        updateStartDates (oldTimelineStartDate, newTimelineStartDate) {
            self.sections.forEach((section)=>section.updateStartDates(oldTimelineStartDate, newTimelineStartDate));
        }
    }));

const TimelineGroupModel = types.model("TimelineGroup", {
    min: types.maybe(TimelineModel),
    avg: types.maybe(TimelineModel),
    max: types.maybe(TimelineModel)
}).views((self)=>({
        serialize () {
            return cleanObject({
                min: self.min?.serialize(),
                avg: self.avg?.serialize(),
                max: self.max?.serialize()
            });
        }
    })).actions((self)=>({
        recalculateWorkload (workTypeIds) {
            self.min?.recalculateWorkload(workTypeIds);
            self.avg?.recalculateWorkload(workTypeIds);
            self.max?.recalculateWorkload(workTypeIds);
        }
    }));

const VisibilityModel = types.model("Visibility", {
    version: types.maybe(types.boolean),
    viewLevel: types.maybe(types.number),
    valueLevel: types.maybe(types.number),
    hideSummaryTime: types.maybe(types.boolean),
    hideSummaryTeam: types.maybe(types.boolean),
    hideSummaryHours: types.maybe(types.boolean),
    hideSummaryRate: types.maybe(types.boolean),
    hideSummaryCost: types.maybe(types.boolean),
    hideSummary: types.maybe(types.boolean),
    hideBreakdown: types.maybe(types.boolean),
    hideTimeline: types.maybe(types.boolean),
    hideTextDescription: types.maybe(types.boolean),
    hideTotalHours: types.maybe(types.boolean),
    hideTotalCost: types.maybe(types.boolean),
    hideTotalTime: types.maybe(types.boolean),
    hideTimelineSections: types.maybe(types.boolean),
    hideTimelineUtilization: types.maybe(types.boolean),
    hideTimelineBilling: types.maybe(types.boolean),
    hideMinValues: types.maybe(types.boolean),
    useAvgValues: types.maybe(types.boolean),
    hideMaxValues: types.maybe(types.boolean),
    hideEstimateCollapse: types.maybe(types.boolean),
    hideTextSearch: types.maybe(types.boolean),
    hideLabelSearch: types.maybe(types.boolean),
    hideLabelsFilter: types.maybe(types.boolean),
    hideSummaryFixedCost: types.maybe(types.boolean),
    hideSummaryMaterials: types.maybe(types.boolean),
    /** not serialized */ apply: types.maybe(types.boolean),
    preview: types.maybe(types.boolean)
}).actions((self)=>({
        applyVisibility (apply) {
            self.apply = apply || undefined;
        },
        previewVisibility (preview) {
            self.preview = preview || undefined;
        },
        setVersionVisibility (visible) {
            self.version = visible;
        },
        setHideSummaryTime (hideSummaryTime) {
            self.hideSummaryTime = hideSummaryTime;
        },
        setHideSummaryTeam (hideSummaryTeam) {
            self.hideSummaryTeam = hideSummaryTeam;
        },
        setHideSummaryHours (hideSummaryHours) {
            self.hideSummaryHours = hideSummaryHours;
        },
        setHideSummaryRate (hideSummaryRate) {
            self.hideSummaryRate = hideSummaryRate;
        },
        setHideSummaryCost (hideSummaryCost) {
            self.hideSummaryCost = hideSummaryCost;
        },
        setHideSummary (hideSummary) {
            self.hideSummary = hideSummary;
        },
        setHideBreakdown (hideBreakdown) {
            self.hideBreakdown = hideBreakdown;
        },
        setHideTimeline (hideTimeline) {
            self.hideTimeline = hideTimeline;
        },
        setHideTextDescription (hideTextDescription) {
            self.hideTextDescription = hideTextDescription;
        },
        setViewLevel (viewLevel) {
            self.viewLevel = viewLevel;
            // if (typeof self.valueLevel === "number" && self.valueLevel > viewLevel)
            self.valueLevel = viewLevel;
        },
        setValueLevel (valueLevel) {
            self.valueLevel = valueLevel;
        },
        setHideTotalHours (hideTotalHours) {
            self.hideTotalHours = hideTotalHours;
        },
        setHideTotalCost (hideTotalCost) {
            self.hideTotalCost = hideTotalCost;
        },
        setHideTotalTime (hideTotalTime) {
            self.hideTotalTime = hideTotalTime;
        },
        setHideTimelineSections (hideTimelineSections) {
            self.hideTimelineSections = hideTimelineSections;
        },
        setHideTimelineUtilization (hideTimelineUtilization) {
            self.hideTimelineUtilization = hideTimelineUtilization;
        },
        setHideTimelineBilling (hideTimelineBilling) {
            self.hideTimelineBilling = hideTimelineBilling;
        },
        setHideMinValues (hideMinValues) {
            self.hideMinValues = hideMinValues;
        },
        setUseAvgValues (useAvgValues) {
            self.useAvgValues = useAvgValues;
            self.hideMinValues = useAvgValues;
            self.hideMaxValues = useAvgValues;
        },
        setHideMaxValues (hideMaxValues) {
            self.hideMaxValues = hideMaxValues;
        },
        setHideEstimateCollapse (hideEstimateCollapse) {
            self.hideEstimateCollapse = hideEstimateCollapse || undefined;
        },
        setHideTextSearch (hideTextSearch) {
            self.hideTextSearch = hideTextSearch;
        },
        setHideLabelSearch (hideLabelSearch) {
            self.hideLabelSearch = hideLabelSearch;
        },
        setHideLabelsFilter (hideLabelsFilter) {
            self.hideLabelsFilter = hideLabelsFilter;
        },
        setHideSummaryFixedCost (hideSummaryFixedCost) {
            self.hideSummaryFixedCost = hideSummaryFixedCost;
        },
        setHideSummaryMaterials (hideSummaryMaterials) {
            self.hideSummaryMaterials = hideSummaryMaterials;
        }
    }));

const StructureModel = types.model("Structure", {
    sVersion: types.string,
    sBuild: types.string,
    sOriginBuild: types.maybe(types.string),
    /** only used in template structures */ templateParsed: types.maybe(types.boolean),
    /** is set to true when editor users are disallowed to see prices; not serialized */ hideAllPrices: types.maybe(types.boolean),
    /** is set to true on proposals; not serialized */ hideEmptySectionDescriptions: types.maybe(types.boolean),
    settings: SettingsModel,
    workTypes: types.array(WorkTypeModel),
    priceModifiers: types.maybe(types.array(PriceModifierModel)),
    textDescription: types.maybe(DeltaPrimitive),
    sections: types.array(SectionModel),
    visibility: types.maybe(VisibilityModel),
    timelines: types.maybe(TimelineGroupModel),
    usesNewPA: types.maybe(types.boolean),
    addedLabels: types.maybe(types.array(TaskLabelModel)),
    turnOffLabels: types.maybe(types.array(types.number)),
    excludeSelectedLabels: types.optional(types.boolean, false),
    /** Reserved for future use; not serialized. */ locked: types.maybe(types.boolean),
    /** not serialized: */ historyManager: types.optional(UndoManager, {}),
    maxHistoryLength: types.optional(types.number, 0),
    excludeTurnedOffElements: types.maybe(types.boolean),
    lastOpenedElement: types.maybe(types.string),
    lastAddedElement: types.maybe(types.string),
    allBuffersOff: types.maybe(types.boolean),
    searchPhrases: types.maybe(types.array(types.model({
        id: types.string,
        value: types.string
    }))),
    searchLabels: types.maybe(types.array(types.number)),
    clientTurnOffLabels: types.maybe(types.array(types.number)),
    clientExcludeSelectedLabels: types.maybe(types.boolean)
})/** ================ Common getters & helpers ================ @todo: sort & cleanup */ .views((self)=>({
        get usedWorkTypes () {
            return self.workTypes.map(({ id })=>id);
        },
        get usedLabels () {
            return union(...self.sections?.map((c)=>c.usedLabels || []) || []);
        },
        get breakdownWorkTypes () {
            return self.workTypes.filter(({ hasFixedValues })=>!hasFixedValues);
        },
        get usedBreakdownWorkTypes () {
            return this.breakdownWorkTypes.map(({ id })=>id);
        },
        get timelineWorkTypes () {
            return self.workTypes.slice().filter((workType)=>!workType.hasFixedPrice && (!self.excludeTurnedOffElements || !workType.turnOff && !workType.clientTurnOff) && (!self.visibility?.apply || !workType.isHidden && !workType.hideOnTimeline)).sort((wT1, wT2)=>wT1.utilizationOrder - wT2.utilizationOrder);
        },
        get workTypesHaveResources () {
            return !!self.workTypes.find((wT)=>wT.hasResources);
        },
        get workTypesHaveFixedSummaryHours () {
            return !!self.workTypes.find((wT)=>wT.hasFixedHours);
        },
        get allWorkTypesAreHidden () {
            return self.visibility?.apply && !self.workTypes.find((wT)=>!wT.isHidden);
        },
        hasWorkType (workTypeId) {
            return !!self.workTypes.find((workType)=>workType.id === workTypeId);
        },
        hasSystemWorkType (workTypeSystemId) {
            return !!self.workTypes.find((workType)=>workType.systemId === workTypeSystemId);
        },
        isWorkTypeOff (workTypeId) {
            return !!self.workTypes.find((workType)=>workType.id === workTypeId && workType.turnOff);
        },
        hasAverageRate (usedWorkTypes) {
            return !!self.workTypes.find((workType)=>usedWorkTypes.includes(workType.id) && workType.hasAverageRate);
        },
        sectionsWithWorkType (workTypeId) {
            return self.sections.reduce((amount, section)=>amount + (section.hasWorkType(workTypeId) ? 1 : 0), 0);
        },
        blacklistedAutomationWorkTypesPerSection (workTypeId) {
            return self.sections.reduce((t, s, i)=>{
                s.blacklistedAutomationWorkTypes(workTypeId).forEach((wT)=>{
                    const ex = t.find((tw)=>tw.id === wT);
                    const sName = s.name || `Section ${i + 1}`;
                    if (ex) ex.sections.push(sName);
                    else t.push({
                        id: wT,
                        sections: [
                            sName
                        ]
                    });
                });
                return t;
            }, []);
        },
        getByHashedIdPath (...pathHashedIds) {
            if (!pathHashedIds?.length) return undefined;
            let target = self.sections.find((section)=>hashCode(section.id).toString() === pathHashedIds[0]);
            for (const id of pathHashedIds.slice(1)){
                if (!target?.children?.length) return target;
                const newTarget = target.children.find((child)=>hashCode(child.id).toString() === id);
                if (!newTarget) return target;
                target = newTarget;
            }
            return target;
        },
        getWorkTypeById (workTypeId) {
            return self.workTypes.find((workType)=>workType.id === workTypeId);
        },
        getWorkTypeBySystemId (workTypeSystemId) {
            return self.workTypes.find((workType)=>workType.systemId === workTypeSystemId);
        },
        getSectionById (sectionId) {
            return self.sections.find((section)=>section.id === sectionId);
        },
        get allWorkTypesExpanded () {
            for (const wT of self.workTypes)if (!wT.isOpened && wT.resources?.length) return false;
            return true;
        },
        get hasMultipleSections () {
            return self.sections.length > 1;
        },
        _internalGetWorkTypeMta (workTypeId, key) {
            return self.workTypes.find((wT)=>wT.id === workTypeId)?.mta?.[key];
        },
        _internalGetWorkTypeResourceMta (workTypeId, resourceId, key) {
            return self.workTypes.find((wT)=>wT.id === workTypeId)?.resources?.find((res)=>res.id === resourceId)?.mta?.[key];
        },
        get applyVisibility () {
            return self.visibility?.apply || self.visibility?.preview;
        },
        get estimateViewLevel () {
            return this.applyVisibility && typeof self.visibility?.viewLevel === "number" ? Math.min(self.visibility?.viewLevel, self.settings?.viewLevel) : self.settings?.viewLevel;
        },
        get estimateValueLevel () {
            return this.applyVisibility && typeof self.visibility?.valueLevel === "number" ? Math.min(self.visibility?.valueLevel, self.settings?.valueLevel) : self.settings?.valueLevel;
        },
        get displayMinValue () {
            return (self.settings.useMinMax || self.settings.maxValuesAsBuffer) && (!this.applyVisibility || !self.visibility?.hideMinValues && !self.visibility?.useAvgValues);
        },
        get displayMaxValue () {
            return (self.settings.useMinMax || self.settings.maxValuesAsBuffer) && (!this.applyVisibility || !self.visibility?.hideMaxValues && !self.visibility?.useAvgValues);
        },
        get displayAvgValue () {
            return !self.settings.useMinMax || this.applyVisibility && self.visibility?.useAvgValues;
        },
        get displayAnyValue () {
            return this.displayMinValue || this.displayMaxValue || this.displayAvgValue;
        },
        get displayTwoValues () {
            return this.displayMinValue && this.displayMaxValue;
        },
        get usesMinMax () {
            return this.displayMinValue && this.displayMaxValue && !this.displayAvgValue;
        },
        get hasSearchPhrase () {
            return self.searchPhrases && self.searchPhrases.length > 0 || self.searchLabels && self.searchLabels.length > 0;
        },
        get displaySections () {
            const filterBySearchPhrase = this.hasSearchPhrase;
            const filterByTurnOff = self.excludeTurnedOffElements;
            // if(this.hasSearchPhrase)
            //   return self.sections?.filter(s => s.includesSearchPhrase) || [];
            // else
            //   return self.sections || [];
            if (filterBySearchPhrase || filterByTurnOff) return self.sections?.filter((s)=>(!filterBySearchPhrase || s.includesSearchPhrase) && (!filterByTurnOff || !s.isSavedTurnedOff)) || [];
            else return self.sections || [];
        },
        get usedBuffers () {
            return (self.settings.buffers?.filter((b)=>b.used)?.length || 0) + self.sections.reduce((buffers, section)=>buffers + (section.childUsedBuffers?.filter((b)=>b.used)?.length || 0) + (section.buffers?.filter((b)=>b.used)?.length || 0), 0);
        },
        get previouslyUsedBuffers () {
            return (self.settings.buffers?.filter((b)=>b.used || b.wasUsed)?.length || 0) + self.sections.reduce((buffers, section)=>buffers + (section.childPreviouslyUsedBuffers?.filter((b)=>b.used || b.wasUsed)?.length || 0) + (section.buffers?.filter((b)=>b.used || b.wasUsed)?.length || 0), 0);
        },
        hasUsedWorkTypeBuffers (workTypeId) {
            const bufferFilter = (b)=>b.used && b.percent !== 0 && b.workTypes.length > 0 && (!workTypeId || b.workTypes.includes(workTypeId));
            return Boolean(self.settings.buffers?.filter(bufferFilter)?.length || self.sections.find((section)=>section.usedBranchBuffers?.filter(bufferFilter)?.length));
        },
        get sectionsHaveAnyDescriptions () {
            return Boolean(self.sections?.find((section)=>section.treeHasAnyDescriptions));
        },
        get sectionDescriptionsKeys () {
            if (!self.sections?.length) return [];
            return uniq(concat(...self.sections.map((section)=>section.usedBreakdownColumns.filter((c)=>c.descriptionId && c.isVisible).map((c)=>c.descriptionId)))).sort((a, b)=>a > b ? 1 : -1);
        },
        get hasFixedPriceBreakdownElements () {
            return Boolean(self.sections.find((section)=>section.type === TaskTypes.FIXED_PRICE || section.hasFixedPriceElements));
        },
        get allFixedPriceBreakdownElementsOff () {
            return !self.sections.find((section)=>!section.allFixedPricesOff);
        },
        get turnOffFixedPrice () {
            return self.settings.turnOffFixedPrice || this.allFixedPriceBreakdownElementsOff;
        },
        get hasMaterialBreakdownElements () {
            return Boolean(self.sections.find((section)=>section.type === TaskTypes.MATERIAL || section.hasMaterialElements));
        },
        get allMaterialBreakdownElementsOff () {
            return !self.sections.find((section)=>!section.allMaterialsOff);
        },
        get turnOffMaterials () {
            return self.settings.turnOffMaterials || this.allMaterialBreakdownElementsOff;
        },
        getWorkTypeVisibility (workTypeId) {
            return !self.sections.find((section)=>section.hasWorkType(workTypeId) && !section.getWorkTypeVisibility(workTypeId));
        },
        getGlobalWorkTypeAutomationSettings (workTypeId) {
            const percents = [];
            const allOf = [];
            let sections = [];
            let differentPercents = false;
            let sectionsWithUniqueWorkTypeRelations = -1;
            self.sections.forEach((section)=>{
                const workType = section.workTypes.find((wT)=>wT.id === workTypeId);
                if (!workType || !workType.percent || !workType.of?.length) return;
                const ofDiff = difference(workType.of, allOf);
                if (ofDiff.length) sectionsWithUniqueWorkTypeRelations++;
                percents.push(workType.percent);
                allOf.push(...ofDiff);
                sections.push(section.id);
            });
            if (!percents.length) percents.push(10);
            const of = uniq(allOf);
            const min = Math.min(...percents);
            const max = Math.max(...percents);
            const percent = min === max ? min : undefined;
            const placeholder = min === max ? undefined : `${min}-${max}%`;
            if (!sections.length) sections = self.sections.filter((section)=>section.hasWorkType(workTypeId)).map((section)=>section.id); /** @note: maybe use sections with work type instead - && section.hasWorkType(workTypeId) */ 
            else if (/*sections.length < this.sectionsWithWorkType(workTypeId) || */ min !== max || sectionsWithUniqueWorkTypeRelations > 1) differentPercents = true;
            return {
                percent,
                of,
                placeholder,
                differentPercents,
                sections
            };
        },
        getGlobalWorkTypeRoundingSettings (workTypeId) {
            const workType = self.workTypes.find((wT)=>wT.id === workTypeId);
            if (workType?.hasRounding) {
                return {
                    roundType: workType.roundType,
                    roundInterval: workType.roundInterval,
                    roundLevel: BreakdownTableDepths.PROJECT,
                    differentIntervals: false,
                    sections: self.sections.filter((section)=>section.hasWorkType(workTypeId)).map((section)=>section.id)
                };
            }
            const roundTypes = [];
            const roundIntervals = [];
            const roundLevels = [];
            let sections = [];
            let differentIntervals = false;
            self.sections.forEach((section)=>{
                const sectionWorkType = section.workTypes.find((wT)=>wT.id === workTypeId);
                if (!sectionWorkType || !sectionWorkType.hasRounding) return;
                sections.push(section.id);
                if (typeof sectionWorkType.roundType === "number") roundTypes.push(sectionWorkType.roundType);
                if (typeof sectionWorkType.roundInterval === "number") roundIntervals.push(sectionWorkType.roundInterval);
                if (typeof sectionWorkType.roundLevel === "number") roundLevels.push(sectionWorkType.roundLevel);
            });
            if (!roundIntervals.length) roundIntervals.push(RoundIntervals.FULL);
            if (!roundTypes.length) roundTypes.push(Rounding.FULL);
            if (!roundLevels.length) roundLevels.push(BreakdownTableDepths.TASK);
            const roundType = Math.max(...roundTypes);
            const roundLevel = Math.max(...roundLevels);
            const min = Math.min(...roundIntervals);
            const max = Math.max(...roundIntervals);
            const roundInterval = min === max ? min : undefined;
            const placeholder = min === max ? undefined : `${min}-${max}`;
            if (!sections.length) sections = self.sections.filter((section)=>section.hasWorkType(workTypeId)).map((section)=>section.id); /** @note: maybe use sections with work type instead - && section.hasWorkType(workTypeId) */ 
            else if (min !== max) differentIntervals = true;
            return {
                roundType,
                roundInterval,
                roundLevel,
                placeholder,
                differentIntervals,
                sections
            };
        },
        get usedTimelineMode () {
            if (self.settings.timelineMode === TimelineMode.MAX && this.applyVisibility && self.visibility?.hideMaxValues) return TimelineMode.MIN;
            if (self.settings.timelineMode === TimelineMode.MIN && this.applyVisibility && self.visibility?.hideMinValues) return TimelineMode.MAX;
            return self.settings.timelineMode;
        }
    }))/** ================ PriceModifier views ================ */ .views((self)=>({
        get addedDiscounts () {
            return self.priceModifiers?.filter((d)=>d.type === PriceModifiers.DISCOUNT) || [];
        },
        get addedOverheads () {
            return self.priceModifiers?.filter((d)=>d.type === PriceModifiers.OVERHEAD) || [];
        },
        get visiblePriceModifiers () {
            return self.priceModifiers?.filter((d)=>d.used && (!self.visibility?.apply || !d.hide)) || [];
        },
        get applicablePriceModifiers () {
            return self.priceModifiers?.filter((d)=>!d.turnOff && d.used && (!self.visibility?.apply || !d.hide)) || [];
        }
    }))/** ================ Breakdown Columns Views ================ */ .views((self)=>({
        get usedBreakdownColumns () {
            const columns = self.sections.find((section)=>section.usedBreakdownColumns?.length)?.usedBreakdownColumns || [];
            return columns.map((column)=>({
                    id: column.id,
                    name: column.name,
                    displayName: column.displayName,
                    descriptionId: column.descriptionId,
                    descriptionIndex: column.descriptionIndex,
                    removable: column.removable,
                    pinnable: column.pinnable,
                    forcedPin: column.forcedPin,
                    pinRight: column.pinRight,
                    applyPin: Boolean(self.sections.find((section)=>section.usedBreakdownColumns.find((c)=>c.id === column.id)?.applyPin)),
                    visible: Boolean(self.sections.find((section)=>section.usedBreakdownColumns.find((c)=>c.id === column.id)?.isVisible))
                }));
        },
        get lastDescriptionColumnIndex () {
            return this.usedBreakdownColumns.map((c)=>c.name).lastIndexOf(BreakdownColumns.DESCRIPTION);
        },
        get usedAllDescriptionColumns () {
            return this.usedBreakdownColumns.filter((c)=>c.name === BreakdownColumns.DESCRIPTION).length >= MAX_DESCRIPTION_COLUMNS;
        },
        get usedDescriptionColumns () {
            return this.usedBreakdownColumns.filter((c)=>c.descriptionId && c.visible);
        },
        get visibleBreakdownColumns () {
            return this.usedBreakdownColumns.filter((col)=>col.visible);
        },
        get showBreakdownPriceColumn () {
            return Boolean(this.usedBreakdownColumns.find((c)=>c.name === BreakdownColumns.PRICE)?.visible);
        },
        get showBreakdownEstimatesColumn () {
            return Boolean(this.usedBreakdownColumns.find((c)=>c.name === BreakdownColumns.ESTIMATES)?.visible);
        },
        get showBreakdownLabelsColumn () {
            return Boolean(this.usedBreakdownColumns.find((c)=>c.name === BreakdownColumns.LABELS)?.visible);
        }
    }))/** ================ Forced state aggregation ================ */ .views((self)=>({
        get forcedStateWorkTypes () {
            const states = {};
            const availableSections = self.sections?.filter((section)=>section.type === TaskTypes.WORKLOAD && !section.isSelfOrChildrenTurnedOff) || [];
            self.workTypes.forEach((workType)=>{
                states[workType.id] = undefined;
                // if (self.settings.maxValuesAsBuffer) return;
                const sectionStates = availableSections.map((section)=>section.taskValues?.get(workType.id.toString())?.forcedState) || [];
                if (sectionStates.includes(WorkTypeValueState.MIN_MAX)) states[workType.id] = WorkTypeValueState.MIN_MAX;
                else if (self.displayTwoValues && availableSections.length && sectionStates.filter((s)=>s === WorkTypeValueState.SINGLE).length === availableSections.length) states[workType.id] = WorkTypeValueState.SINGLE;
            });
            return states;
        },
        get fixedPriceForcedState () {
            if (self.settings.maxValuesAsBuffer) return;
            const availableSections = self.sections?.filter((section)=>!section.isSelfOrChildrenTurnedOff) || [];
            const sectionStates = availableSections.map((child)=>child.calculatedFixedPriceSummary?.forcedState) || [];
            if (sectionStates.includes(WorkTypeValueState.MIN_MAX)) return WorkTypeValueState.MIN_MAX;
            else if (self.displayTwoValues && availableSections.length && sectionStates.filter((s)=>s === WorkTypeValueState.SINGLE).length === sectionStates.length) return WorkTypeValueState.SINGLE;
            return;
        },
        chosenWorkTypesForcedState (workTypeIds, ofSections) {
            const vals = [];
            const availableSections = self.sections?.filter((section)=>section.type === TaskTypes.WORKLOAD && !section.isSelfOrChildrenTurnedOff && ofSections.includes(section.id)) || [];
            self.workTypes.forEach((workType)=>{
                // if (self.settings.maxValuesAsBuffer || !workTypeIds.includes(workType.id)) return;
                if (!workTypeIds.includes(workType.id)) return;
                const sectionStates = availableSections.map((section)=>section.taskValuesWithoutAutomatedWorkType(workType.id)?.get(workType.id.toString())?.forcedState) || [];
                if (sectionStates.includes(WorkTypeValueState.MIN_MAX)) {
                    vals.push(WorkTypeValueState.MIN_MAX);
                    return;
                }
                if (self.displayTwoValues && availableSections.length && sectionStates.filter((s)=>s === WorkTypeValueState.SINGLE).length === availableSections.length) {
                    vals.push(WorkTypeValueState.SINGLE);
                    return;
                }
                vals.push(undefined);
            });
            if (vals.find((v)=>v === WorkTypeValueState.MIN_MAX)) return WorkTypeValueState.MIN_MAX;
            if (self.displayTwoValues && vals.filter((v)=>v === WorkTypeValueState.SINGLE).length === vals.length) return WorkTypeValueState.SINGLE;
            return;
        },
        chosenFixedPriceForcedState (ofSections) {
            if (self.settings.maxValuesAsBuffer) return;
            const availableSections = self.sections?.filter((section)=>!section.isSelfOrChildrenTurnedOff && ofSections.includes(section.id)) || [];
            const sectionStates = availableSections.map((child)=>child.nonAutomatedCalculatedFixedPrice?.forcedState) || [];
            if (sectionStates.includes(WorkTypeValueState.MIN_MAX)) return WorkTypeValueState.MIN_MAX;
            else if (self.displayTwoValues && availableSections.length && sectionStates.filter((s)=>s === WorkTypeValueState.SINGLE).length === sectionStates.length) return WorkTypeValueState.SINGLE;
            return;
        },
        get workloadForcedState () {
            if (self.settings.maxValuesAsBuffer) return;
            const vals = uniq(Object.values(this.forcedStateWorkTypes));
            if (vals.find((v)=>v === WorkTypeValueState.MIN_MAX)) return WorkTypeValueState.MIN_MAX;
            if (self.displayTwoValues && vals.length && vals.filter((v)=>v === WorkTypeValueState.SINGLE).length === vals.length) return WorkTypeValueState.SINGLE;
            return;
        },
        get priceForcedState () {
            if (self.settings.maxValuesAsBuffer) return;
            if (this.fixedPriceForcedState === WorkTypeValueState.MIN_MAX || this.workloadForcedState === WorkTypeValueState.MIN_MAX) return WorkTypeValueState.MIN_MAX;
            if (self.displayTwoValues && this.fixedPriceForcedState === WorkTypeValueState.SINGLE && this.workloadForcedState === WorkTypeValueState.SINGLE) return WorkTypeValueState.SINGLE;
            return;
        }
    }))/** ================ Fixed price ================ */ .views((self)=>({
        get breakdownFixedPrice () {
            return sumTaskValues(self.sections?.filter((section)=>self.allFixedPriceBreakdownElementsOff || !section.isTurnedOff)?.map((child)=>child.calculatedFixedPriceSummary) || [], self.fixedPriceForcedState, self.settings.useMinMax);
        },
        get hasBreakdownFixedPrice () {
            return self.settings.useMinMax ? !!(this.breakdownFixedPrice?.min || this.breakdownFixedPrice?.max) : !!this.breakdownFixedPrice?.avg;
        },
        get breakdownMaterialPrice () {
            return self.sections?.filter((child)=>self.allMaterialBreakdownElementsOff || !child.isTurnedOff)?.reduce((total, child)=>total + (child.calculatedMaterialPriceSummary || 0), 0);
        },
        get hasBreakdownMaterialPrice () {
            return Boolean(this.breakdownMaterialPrice);
        }
    }))/** ================ Utilities ================ */ .views((self)=>({
        getDisplayValue (values) {
            if (!self.settings.useMinMax && !self.settings.maxValuesAsBuffer && values?.forcedState === WorkTypeValueState.MIN_MAX) return values?.min || 0;
            if (self.displayAvgValue) return values?.avg || 0;
            if (self.displayMinValue) return values?.forcedState === WorkTypeValueState.SINGLE ? values?.avg || 0 : values?.min || 0;
            if (self.displayMaxValue) return values?.forcedState === WorkTypeValueState.SINGLE ? values?.avg || 0 : values?.max || 0;
            return 0;
        },
        getDisplayValueMax (values) {
            if (!self.settings.useMinMax && !self.settings.maxValuesAsBuffer && values?.forcedState === WorkTypeValueState.MIN_MAX) return values?.max || 0;
            if (self.displayMaxValue && !self.displayAvgValue && (!values?.forcedState || values.forcedState === WorkTypeValueState.MIN_MAX)) return values?.max || 0;
            return 0;
        }
    }))/** ================ Hours, Time & Cost getters ================ */ .views((self)=>({
        get totalHours () {
            const total = sumTaskValues(self.workTypes.filter((workType)=>!workType.turnOff).map((workType)=>workType.hours), self.workloadForcedState, self.settings.useMinMax);
            // const hours =
            //   self.workTypes?.reduce(
            //     (total, workType) =>
            //       workType.turnOff ? total : sumObjectsData(total, workType.hours),
            //     DEFAULT_MIN_AVG_MAX_VALUE,
            //   ) || DEFAULT_MIN_AVG_MAX_VALUE;
            const { modifier } = self.settings;
            return parseWorkloadValues(total, (val)=>Number((val / modifier).toFixed(2)));
        },
        get totalUnbufferedHours () {
            const total = sumTaskValues(self.workTypes.filter((workType)=>!workType.turnOff).map((workType)=>workType.unbufferedHours), self.workloadForcedState, self.settings.useMinMax);
            // const hours =
            //   self.workTypes?.reduce(
            //     (total, workType) =>
            //       workType.turnOff ? total : sumObjectsData(total, workType.unbufferedHours),
            //     DEFAULT_MIN_AVG_MAX_VALUE,
            //   ) || DEFAULT_MIN_AVG_MAX_VALUE;
            const { modifier } = self.settings;
            return parseWorkloadValues(total, (val)=>Number((val / modifier).toFixed(2)));
        },
        get totalTime () {
            // const o: IOverridableValue = parseObject(DEFAULT_MIN_AVG_MAX_VALUE, (v, key) => Math.max(
            //   0,
            //   ...self.workTypes.map(wT =>
            //     wT.turnOff
            //       ? 0
            //       : self.settings.fixedTotalTime?.[key] || wT.time[key] || 0,
            //   )
            // ));
            //
            // if (this.workloadForcedState)
            //   o.forcedState = this.workloadForcedState;
            // return o;
            const time = self.workTypes.filter((workType)=>!workType.turnOff).reduce((total, workType)=>{
                const time = workType.time;
                const forceAvgKey = time.forcedState === WorkTypeValueState.SINGLE || !self.settings.useMinMax && self.workloadForcedState === WorkTypeValueState.MIN_MAX && time.forcedState !== WorkTypeValueState.MIN_MAX;
                MIN_AVG_MAX_KEYS.forEach((key)=>{
                    total[key] = Math.max(total[key] || 0, self.settings.fixedTotalTime?.[key] || time[forceAvgKey && !self.settings.isTimelineTableUsed ? "avg" : key] || 0);
                });
                return total;
            }, {});
            if (self.workloadForcedState) time.forcedState = self.workloadForcedState;
            return time;
        },
        get totalCost () {
            const prices = self.workTypes.filter((workType)=>!workType.turnOff).map((workType)=>workType.price);
            if (self.hasBreakdownFixedPrice && !self.turnOffFixedPrice) prices.push(self.breakdownFixedPrice);
            if (self.hasBreakdownMaterialPrice && !self.turnOffMaterials) prices.push(parseObject(DEFAULT_MIN_AVG_MAX_VALUE, self.breakdownMaterialPrice));
            if (self.applicablePriceModifiers.length) prices.push(...self.applicablePriceModifiers.map((p)=>p.price));
            const total = sumTaskValues(prices, self.priceForcedState, self.settings.useMinMax);
            return parseWorkloadValues(total, (val)=>roundFloat(val, self.settings.roundPrice ? Rounding.FULL : Rounding.DECIMALS));
        // let total =
        //   self.workTypes?.reduce(
        //     (total, workType) =>
        //       workType.turnOff ? total : sumObjectsData(total, workType.price),
        //     DEFAULT_MIN_AVG_MAX_VALUE,
        //   ) || DEFAULT_MIN_AVG_MAX_VALUE;
        // if (self.hasBreakdownFixedPrice && !self.settings.turnOffFixedPrice)
        // if (self.hasBreakdownFixedPrice && !self.turnOffFixedPrice)
        //   total = sumObjectsData(total, self.breakdownFixedPrice);
        // if (self.hasBreakdownMaterialPrice && !self.settings.turnOffMaterials)
        // if (self.hasBreakdownMaterialPrice && !self.turnOffMaterials)
        //   total = parseObject(total, (value) => value + self.breakdownMaterialPrice);
        // const o: IOverridableValue = parseObject(total, price => roundFloat(
        //   price,
        //   self.settings.roundPrice ? Rounding.FULL : Rounding.DECIMALS, //@todo: check if needed
        //   !self.settings.roundPrice,
        // ));
        // if (this.workloadForcedState)
        //   o.forcedState = this.workloadForcedState;
        // return o;
        },
        get totalUnbufferedCost () {
            const prices = self.workTypes.filter((workType)=>!workType.turnOff).map((workType)=>workType.unbufferedPrice);
            if (self.hasBreakdownFixedPrice && !self.turnOffFixedPrice) prices.push(self.breakdownFixedPrice);
            if (self.hasBreakdownMaterialPrice && !self.turnOffMaterials) prices.push(parseObject(DEFAULT_MIN_AVG_MAX_VALUE, self.breakdownMaterialPrice));
            return sumTaskValues(prices, self.priceForcedState, self.settings.useMinMax);
        // return parseWorkloadValues(total, val => roundFloat(
        //   val,
        //   self.settings.roundPrice ? Rounding.FULL : Rounding.DECIMALS,
        // ))
        // let total =
        //   self.workTypes?.reduce(
        //     (total, workType) =>
        //       workType.turnOff ? total : sumObjectsData(total, workType.unbufferedPrice),
        //     DEFAULT_MIN_AVG_MAX_VALUE,
        //   ) || DEFAULT_MIN_AVG_MAX_VALUE;
        //
        // // if (self.hasBreakdownFixedPrice && !self.settings.turnOffFixedPrice)
        // if (self.hasBreakdownFixedPrice && !self.turnOffFixedPrice)
        //   total = sumObjectsData(total, self.breakdownFixedPrice);
        //
        // // if (self.hasBreakdownMaterialPrice && !self.settings.turnOffMaterials)
        // if (self.hasBreakdownMaterialPrice && !self.turnOffMaterials)
        //   total = parseObject(total, (value) => value + self.breakdownMaterialPrice);
        //
        // const o: IOverridableValue = parseObject(total, price => roundFloat(
        //   price,
        //   self.settings.roundPrice ? Rounding.FULL : Rounding.DECIMALS, //@todo: check if needed
        //   !self.settings.roundPrice,
        // ));
        // if (this.workloadForcedState)
        //   o.forcedState = this.workloadForcedState;
        // return o;
        },
        get totalBreakdownCost () {
            const prices = self.workTypes.filter((wT)=>!(wT.turnOff || wT.hasFixedValues)).map((wT)=>wT.price);
            const total = sumTaskValues(prices, self.workloadForcedState, self.settings.useMinMax);
            return parseWorkloadValues(total, (price)=>roundFloat(price, self.settings.roundPrice ? Rounding.FULL : Rounding.DECIMALS));
        },
        get unbufferedTaskValues () {
            return sumTaskChildrenDataV2(self.sections.filter((section)=>!section.isTurnedOff).map((section)=>section.unbufferedTaskValues), self.forcedStateWorkTypes, self.settings.useMinMax, self.settings.maxValuesAsBuffer);
        },
        get taskValues () {
            return sumTaskChildrenDataV2(self.sections.filter((section)=>!section.isTurnedOff).map((section)=>section.taskValues), self.forcedStateWorkTypes, self.settings.useMinMax, self.settings.maxValuesAsBuffer);
        },
        get hasDifferentBreakdownTotal () {
            const prices = self.sections.filter((section)=>!section.isTurnedOff).map((section)=>section.calculatedWorkloadPrice);
            const breakdownCost = sumTaskValues(prices, self.workloadForcedState, self.settings.useMinMax);
            const valueMin = self.getDisplayValue(breakdownCost);
            const totalValueMin = self.getDisplayValue(this.totalBreakdownCost);
            const valueMax = self.getDisplayValueMax(breakdownCost);
            const totalValueMax = self.getDisplayValueMax(this.totalBreakdownCost);
            return roundFloat(totalValueMin) !== roundFloat(valueMin) || this.displayTotalTwoValues && roundFloat(totalValueMax) !== roundFloat(valueMax);
        },
        getUnbufferedTotalHoursPerWorkType (workTypeId) {
            return {
                ...DEFAULT_MIN_AVG_MAX_VALUE,
                ...this.unbufferedTaskValues?.get(workTypeId.toString()) || {}
            };
        },
        getTotalHoursPerWorkType (workTypeId) {
            if (workTypeId == BT_MERGED_VALUE) {
                const objectsToSum = self.settings.btWorkTypes?.map((workType)=>this.taskValues?.get(workType.toString())) || [];
                const filteredObjectsToSum = objectsToSum.filter((obj)=>obj !== undefined);
                return sumObjectsData(...filteredObjectsToSum);
            }
            return {
                ...DEFAULT_MIN_AVG_MAX_VALUE,
                ...this.taskValues?.get(workTypeId.toString()) || {}
            };
        },
        getTotalPricePerWorkTypeForKey (workTypeId, key) {
            return self.sections.reduce((total, section)=>section.isTurnedOff ? total : total + section.getTotalPricePerWorkTypeForKey(workTypeId, key, true), 0);
        },
        get displayWorkTypeTwoValues () {
            return self.workTypes.reduce((states, workType)=>({
                    ...states,
                    [workType.id]: (!workType.hasFixedValues || self.settings.useMinMax) && (self.displayTwoValues && (self.settings.maxValuesAsBuffer || self.forcedStateWorkTypes[workType.id] !== WorkTypeValueState.SINGLE) || !self.settings.useMinMax && self.forcedStateWorkTypes[workType.id] === WorkTypeValueState.MIN_MAX)
                }), {});
        },
        get displayTotalTwoValues () {
            return self.displayTwoValues && (self.settings.maxValuesAsBuffer || self.workloadForcedState !== WorkTypeValueState.SINGLE) || !self.settings.useMinMax && self.workloadForcedState === WorkTypeValueState.MIN_MAX;
        },
        get displayFixedPriceTwoValues () {
            return self.displayTwoValues && self.fixedPriceForcedState !== WorkTypeValueState.SINGLE || !self.settings.useMinMax && self.fixedPriceForcedState === WorkTypeValueState.MIN_MAX;
        },
        get displayTotalPriceTwoValues () {
            return self.displayTwoValues && self.priceForcedState !== WorkTypeValueState.SINGLE || !self.settings.useMinMax && self.priceForcedState === WorkTypeValueState.MIN_MAX;
        }
    }))/** ================ Profitability getters ================ */ .views((self)=>({
        get companyBreakdownFixedProfit () {
            return parseObject(self.breakdownFixedPrice, (val)=>{
                if (typeof val !== "number") return val;
                return Number(roundFloat(val - (self.settings.companyFixedBreakdownCost || 0), self.settings.roundPrice ? Rounding.FULL : Rounding.DECIMALS, !self.settings.roundPrice));
            });
        },
        get companyBreakdownMaterialProfit () {
            // if (!isAlive(self) || !self.settings.companyBreakdownMaterialCost)
            //   return self.breakdownMaterialPrice;
            return Number(roundFloat(self.breakdownMaterialPrice - (self.settings.companyBreakdownMaterialCost || 0), self.settings.roundPrice ? Rounding.FULL : Rounding.DECIMALS, !self.settings.roundPrice));
        },
        get companyBreakdownFixedProfitability () {
            const { useMinMax } = self.settings;
            const key = useMinMax ? MinAvgMaxKeys.MIN : MinAvgMaxKeys.AVG;
            if (!self.breakdownFixedPrice[key]) return 100;
            return Number(roundFloat((this.companyBreakdownFixedProfit[key] || 0) / self.breakdownFixedPrice[key] * 100, 0));
        },
        get companyBreakdownMaterialProfitability () {
            if (!isAlive(self) || !self.breakdownMaterialPrice) return 100;
            return Number(roundFloat(this.companyBreakdownMaterialProfit / self.breakdownMaterialPrice * 100, 0));
        },
        get totalCompanyCost () {
            const prices = self.workTypes.filter((workType)=>!workType.turnOff && workType.companyCost).map((workType)=>workType.companyCost);
            if (self.hasBreakdownFixedPrice && !self.turnOffFixedPrice) prices.push(parseObject(DEFAULT_MIN_AVG_MAX_VALUE, self.settings.companyFixedBreakdownCost));
            if (self.hasBreakdownMaterialPrice && !self.turnOffMaterials) prices.push(parseObject(DEFAULT_MIN_AVG_MAX_VALUE, self.settings.companyBreakdownMaterialCost));
            const total = sumTaskValues(prices, self.workloadForcedState, self.settings.useMinMax);
            return parseWorkloadValues(total, (val)=>roundFloat(val, self.settings.roundPrice ? Rounding.FULL : Rounding.DECIMALS));
        // let total =
        //   self.workTypes?.reduce(
        //     (total, workType) =>
        //       workType.turnOff || !workType.companyCost
        //         ? total
        //         : sumObjectsData(total, workType.companyCost),
        //     DEFAULT_MIN_AVG_MAX_VALUE,
        //   ) || DEFAULT_MIN_AVG_MAX_VALUE;
        //
        // return parseObject(total, price => {
        //   // if(self.hasBreakdownFixedPrice && !self.settings.turnOffFixedPrice)
        //   if(self.hasBreakdownFixedPrice && !self.turnOffFixedPrice)
        //     price += self.settings.companyFixedBreakdownCost;
        //   // if(self.hasBreakdownMaterialPrice && !self.settings.turnOffMaterials)
        //   if(self.hasBreakdownMaterialPrice && !self.turnOffMaterials)
        //     price += self.settings.companyBreakdownMaterialCost;
        //
        //   return roundFloat(price, self.settings.roundPrice ? Rounding.FULL : Rounding.DECIMALS); //@todo: check if needed
        // });
        },
        get totalCompanyUnbufferedCost () {
            const prices = self.workTypes.filter((workType)=>!workType.turnOff && workType.companyUnbufferedCost).map((workType)=>workType.companyUnbufferedCost);
            if (self.hasBreakdownFixedPrice && !self.turnOffFixedPrice) prices.push(parseObject(DEFAULT_MIN_AVG_MAX_VALUE, self.settings.companyFixedBreakdownCost));
            if (self.hasBreakdownMaterialPrice && !self.turnOffMaterials) prices.push(parseObject(DEFAULT_MIN_AVG_MAX_VALUE, self.settings.companyBreakdownMaterialCost));
            const total = sumTaskValues(prices, self.workloadForcedState, self.settings.useMinMax);
            return parseWorkloadValues(total, (val)=>roundFloat(val, self.settings.roundPrice ? Rounding.FULL : Rounding.DECIMALS));
        },
        get totalCompanyProfit () {
            return parseWorkloadValues(self.totalCost, (price, key)=>Number(roundFloat(typeof this.totalCompanyCost[key] === "number" ? price - this.totalCompanyCost[key] : price, self.settings.roundPrice ? Rounding.FULL : Rounding.DECIMALS, !self.settings.roundPrice)));
        },
        get totalCompanyUnbufferedProfit () {
            return parseWorkloadValues(self.totalCost, (price, key)=>Number(roundFloat(typeof this.totalCompanyUnbufferedCost[key] === "number" ? price - this.totalCompanyUnbufferedCost[key] : price, self.settings.roundPrice ? Rounding.FULL : Rounding.DECIMALS, !self.settings.roundPrice)));
        },
        get totalCompanyProfitability () {
            const { useMinMax } = self.settings;
            const key = useMinMax ? MinAvgMaxKeys.MIN : MinAvgMaxKeys.AVG;
            if (!self.totalCost[key]) return 100; // should result in "N/A" in inputs, as it's really infinite :P
            return Number(roundFloat((this.totalCompanyProfit[key] || 0) / self.totalCost[key] * 100, 0));
        }
    }))/** ================ BT getters ================ */ .views((self)=>({
        get btWorkTypes () {
            const mergedWorkTypes = self.workTypes.filter((wT)=>wT.id == BT_MERGED_VALUE);
            return mergedWorkTypes.length == 0 ? self.workTypes : mergedWorkTypes;
        },
        get btTaskValues () {
            return sumBTData(...self.sections.filter((section)=>!section.isTurnedOff).map((section)=>section.btTaskValues));
        },
        getTotalBTHoursPerWorkType (workTypeId) {
            return this.btTaskValues?.get(workTypeId.toString()) ?? 0;
        },
        getTotalEstimatesWithBtHoursPerWorkType (workTypeId) {
            return self.sections.reduce((total, section)=>section.isTurnedOff || !this.getTotalBTHoursPerWorkType(workTypeId) ? total : sumObjectsData(total, section.getTotalHoursPerWorkType(workTypeId)), DEFAULT_MIN_AVG_MAX_VALUE);
        },
        totalBtHoursPerWorkType (workTypeId) {
            return self.sections.reduce((total, section)=>section.isTurnedOff ? total : total + this.getTotalBTHoursPerWorkType(workTypeId), 0);
        },
        getAggregatedTrendPerWorkType (workTypeId) {
            const btValues = this.totalBtHoursPerWorkType(workTypeId);
            return parseObject(this.getTotalEstimatesWithBtHoursPerWorkType(workTypeId), (value)=>btValues ? btValues / value - 1 : null);
        },
        get currentTimeline () {
            if (!self.timelines) return {};
            return self.settings.useMinMax ? self.settings.timelineMode === TimelineMode.MAX ? self.timelines.max || {} : self.timelines.min || {} : self.timelines.avg || {};
        },
        get timelineBillingScale () {
            const currentTimeline = !self.timelines ? undefined : self.settings.useMinMax ? self.settings.timelineMode === TimelineMode.MAX ? self.timelines.max : self.timelines.min : self.timelines.avg;
            return currentTimeline?.timelineBillingScale || {
                scale: 10,
                topValue: 0
            };
        }
    }))/** ================ Serializers ================ */ .views((self)=>({
        serializeCore () {
            return structuredClone(cleanObject({
                sVersion: self.sVersion,
                sBuild: self.sBuild,
                sOriginBuild: self.sOriginBuild,
                templateParsed: self.templateParsed,
                settings: cleanObject({
                    ...getSnapshot(self.settings)
                }),
                textDescription: self.textDescription?.ops,
                visibility: self.visibility ? cleanObject({
                    ...omit(getSnapshot(self.visibility), [
                        "apply",
                        "preview"
                    ])
                }) : undefined,
                workTypes: self.workTypes.map((workType)=>workType.serialize()),
                priceModifiers: self.priceModifiers?.map((priceModifier)=>priceModifier.serialize()),
                sections: self.sections.map((section)=>section.serialize()),
                usesNewPA: self.usesNewPA,
                addedLabels: self.addedLabels ? getSnapshot(self.addedLabels) : undefined,
                turnOffLabels: self.turnOffLabels?.length ? [
                    ...self.turnOffLabels
                ] : undefined,
                excludeSelectedLabels: self.excludeSelectedLabels,
                allBuffersOff: self.allBuffersOff,
                timelines: self.timelines ? self.timelines.serialize() : undefined
            }));
        },
        serialize () {
            return JSON.stringify(this.serializeCore());
        }
    }))/** ================ Data display getters ================ */ .views((self)=>({
        get displayBreakdownMaterialPrice () {
            const roundPrice = self.settings.roundPrice;
            return roundFloat(self.breakdownMaterialPrice || 0, roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        },
        get displayBreakdownFixedPrice () {
            const roundPrice = self.settings.roundPrice;
            return roundFloat(self.getDisplayValue(self.breakdownFixedPrice), roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        },
        get displayBreakdownMaxFixedPrice () {
            const roundPrice = self.settings.roundPrice;
            return roundFloat(self.getDisplayValueMax(self.breakdownFixedPrice), roundPrice ? Rounding.FULL : Rounding.DECIMALS, !roundPrice);
        },
        get showBreakdownMaxFixedPrice () {
            return (self.displayTwoValues || self.displayFixedPriceTwoValues) && !self.settings.maxValuesAsBuffer && this.displayBreakdownFixedPrice !== this.displayBreakdownMaxFixedPrice;
        },
        get displayTotalHours () {
            const v = self.settings.maxValuesAsBuffer && self.displayMinValue ? self.totalUnbufferedHours : self.totalHours;
            return Number(self.getDisplayValue(v).toPrecision(12));
        },
        get displayTotalHoursMax () {
            const v = self.settings.maxValuesAsBuffer ? self.getDisplayValue(self.totalHours) : self.getDisplayValueMax(self.totalHours);
            return Number(v.toPrecision(12));
        },
        get showMaxTotalHours () {
            return (self.displayTwoValues || self.displayTotalTwoValues) && this.displayTotalHours !== this.displayTotalHoursMax;
        },
        get displayTotalTime () {
            return Number(self.getDisplayValue(self.totalTime).toPrecision(12));
        },
        get displayTotalTimeMax () {
            return Number(self.getDisplayValueMax(self.totalTime).toPrecision(12));
        },
        get showMaxTotalTime () {
            return (self.displayTwoValues || self.displayTotalTwoValues) && !self.settings.maxValuesAsBuffer && this.displayTotalTime !== this.displayTotalTimeMax;
        },
        get displayTotalPrice () {
            const v = self.settings.maxValuesAsBuffer && self.displayMinValue ? self.totalUnbufferedCost : self.totalCost;
            return parsePrice(roundFloat(self.getDisplayValue(v), self.settings.roundPrice ? Rounding.FULL : Rounding.DECIMALS));
        },
        get displayTotalPriceMax () {
            const v = self.settings.maxValuesAsBuffer ? self.getDisplayValue(self.totalCost) : self.getDisplayValueMax(self.totalCost);
            return parsePrice(roundFloat(v, self.settings.roundPrice ? Rounding.FULL : Rounding.DECIMALS));
        },
        get showMaxTotalPrice () {
            return (self.displayTwoValues || self.displayTotalPriceTwoValues) && this.displayTotalPrice !== this.displayTotalPriceMax;
        },
        get displayTotalCompanyCost () {
            const v = self.settings.maxValuesAsBuffer && self.displayMinValue ? self.totalCompanyUnbufferedCost : self.totalCompanyCost;
            return parsePrice(self.getDisplayValue(v));
        },
        get displayTotalCompanyCostMax () {
            const v = self.settings.maxValuesAsBuffer ? self.getDisplayValue(self.totalCompanyCost) : self.getDisplayValueMax(self.totalCompanyCost);
            return parsePrice(v);
        },
        get showMaxTotalCompanyCost () {
            return (self.displayTwoValues || self.displayTotalTwoValues) && this.displayTotalCompanyCost !== this.displayTotalCompanyCostMax;
        },
        get displayTotalCompanyProfit () {
            const v = self.settings.maxValuesAsBuffer && self.displayMinValue ? self.totalCompanyUnbufferedCost : self.totalCompanyProfit;
            return parsePrice(self.getDisplayValue(v));
        },
        get displayTotalCompanyProfitMax () {
            const v = self.settings.maxValuesAsBuffer ? self.getDisplayValue(self.totalCompanyProfit) : self.getDisplayValueMax(self.totalCompanyProfit);
            return parsePrice(v);
        },
        get showMaxTotalCompanyProfit () {
            return (self.displayTwoValues || self.displayTotalTwoValues) && this.displayTotalCompanyProfit !== this.displayTotalCompanyProfitMax;
        },
        get displayCompanyBreakdownFixedProfit () {
            return self.getDisplayValue(self.companyBreakdownFixedProfit);
        },
        get displayCompanyBreakdownFixedMaxProfit () {
            return self.getDisplayValueMax(self.companyBreakdownFixedProfit);
        },
        get showMaxTotalCompanyBreakdownFixedProfit () {
            return (self.displayTwoValues || self.displayFixedPriceTwoValues) && this.displayCompanyBreakdownFixedProfit !== this.displayCompanyBreakdownFixedMaxProfit;
        },
        get displayCompanyBreakdownMaterialProfit () {
            return self.companyBreakdownMaterialProfit || 0;
        },
        get displayTurnOffLabels () {
            return (self.excludeTurnedOffElements ? self.clientTurnOffLabels : self.turnOffLabels) || [];
        },
        get displayExcludeSelectedLabels () {
            return Boolean(self.excludeTurnedOffElements ? self.clientExcludeSelectedLabels : self.excludeSelectedLabels);
        },
        get showFixedDecimalPrices () {
            return Boolean(self.settings.priceFormat === PriceFormats.AMERICAN && !self.settings.roundPrice && (self.workTypes.find((workType)=>workType.displayPrice !== parseInt(workType.displayPrice.toString()) || self.displayMaxValue && workType.displayPriceMax !== parseInt(workType.displayPriceMax.toString())) || this.displayBreakdownFixedPrice !== parseInt(this.displayBreakdownFixedPrice.toString()) || self.displayMaxValue && this.displayBreakdownMaxFixedPrice !== parseInt(this.displayBreakdownMaxFixedPrice.toString()) || this.displayBreakdownMaterialPrice !== parseInt(this.displayBreakdownMaterialPrice.toString())));
        }
    }))/** ================ Summary column width getters ================ */ .views((self)=>({
        renderPrice (displayPrice) {
            const c = self.settings.currencyObj;
            const p = parsePrice(displayPrice, self.settings.priceFormat === PriceFormats.AMERICAN, self.showFixedDecimalPrices);
            return `${c.symbolStart || ""}${p}${c.symbolEnd ? ` ${c.symbolEnd}` : ""}`.length * 10;
        },
        get displaySummaryRateCellSize () {
            return Math.max(156, ...self.workTypes.map((wT)=>self.visibility?.apply && wT.isHidden ? 0 : wT.displayRateCellSize));
        },
        get displaySummaryPriceCellSize () {
            return Math.max(104, this.displayBreakdownMaterialPriceSize, this.displayBreakdownFixedPriceSize, ...self.workTypes.map((wT)=>self.visibility?.apply && wT.isHidden ? 0 : wT.displayPriceCellSize), ...self.priceModifiers?.map((pM)=>self.visibility?.apply && pM.hide ? 0 : pM.displayPriceCellSize) || []);
        },
        get displaySummaryProfitCellSize () {
            return Math.max(104, this.displayBreakdownMaterialProfitSize, this.displayBreakdownFixedPriceProfitSize, ...self.workTypes.map((wT)=>self.visibility?.apply && wT.isHidden ? 0 : wT.displayProfitCellSize));
        },
        get displaySummaryCompanyCostCellSize () {
            return Math.max(104, this.displayBreakdownMaterialCompanyCostSize, this.displayBreakdownFixedPriceCompanyCostSize, ...self.workTypes.map((wT)=>self.visibility?.apply && wT.isHidden ? 0 : wT.displayCompanyCostCellSize));
        },
        get displayBreakdownMaterialPriceSize () {
            if (self.visibility?.apply && self.visibility?.hideSummaryMaterials) return 0;
            return this.renderPrice(self.displayBreakdownMaterialPrice);
        },
        get displayBreakdownMaterialProfitSize () {
            if (self.visibility?.apply && self.visibility?.hideSummaryMaterials) return 0;
            return this.renderPrice(self.displayCompanyBreakdownMaterialProfit);
        },
        get displayBreakdownMaterialCompanyCostSize () {
            if (self.visibility?.apply && self.visibility?.hideSummaryMaterials) return 0;
            return this.renderPrice(self.settings.companyBreakdownMaterialCost || 0);
        },
        get displayBreakdownFixedPriceSize () {
            if (self.visibility?.apply && self.visibility?.hideSummaryFixedCost) return 0;
            let size = this.renderPrice(self.displayBreakdownFixedPrice);
            if ((self.displayTwoValues || self.displayFixedPriceTwoValues) && self.showBreakdownMaxFixedPrice) size += this.renderPrice(self.displayBreakdownMaxFixedPrice) + 16;
            return size;
        },
        get displayBreakdownFixedPriceProfitSize () {
            if (self.visibility?.apply && self.visibility?.hideSummaryFixedCost) return 0;
            let size = this.renderPrice(self.displayCompanyBreakdownFixedProfit);
            if ((self.displayTwoValues || self.displayFixedPriceTwoValues) && self.showMaxTotalCompanyBreakdownFixedProfit) size += this.renderPrice(self.displayCompanyBreakdownFixedMaxProfit) + 16;
            return size;
        },
        get displayBreakdownFixedPriceCompanyCostSize () {
            if (self.visibility?.apply && self.visibility?.hideSummaryFixedCost) return 0;
            return this.renderPrice(self.settings.companyFixedBreakdownCost || 0);
        }
    }))/** ================ Buffer value getters ================ */ .views((self)=>({
        getBufferValueForWorkType (workTypeId, key, modifyByRate) {
            const workType = self.workTypes.find((wT)=>wT.id === workTypeId);
            if (!workType) return {};
            const valueModifier = modifyByRate && workType.averageRate[key] || 0;
            if (workType.hasFixedHours && workType.usedBufferPercents?.length) {
                const { modifier } = self.settings;
                // const value = (workType.fixedHours || workType.percentHours || DEFAULT_MIN_AVG_MAX_VALUE)[key] || 0;
                const value = (workType.fixedHours || DEFAULT_MIN_AVG_MAX_VALUE)[key] || 0;
                return workType.usedBuffers.reduce((res, buffer)=>({
                        ...res,
                        [buffer.id]: {
                            name: buffer.name,
                            itemBuffer: buffer.lvl >= BreakdownTableDepths.SECTION,
                            value: valueModifier ? Number((value * buffer.percent * valueModifier).toPrecision(12)) : Number(safeRoundHours(value * buffer.parsedPercent, modifier).toPrecision(12))
                        }
                    }), {});
            }
            let bVal = {};
            self.sections?.filter((section)=>!section.isTurnedOff).forEach((section)=>{
                bVal = mergeWith(bVal, section.getBufferedValue(workTypeId, key, valueModifier), mergeBufferFunc);
            });
            return bVal;
        },
        getTotalBufferValues (key, modifyByRate) {
            let bVal = {};
            self.workTypes.forEach((workType)=>{
                const workTypeKey = self.settings.maxValuesAsBuffer || self.forcedStateWorkTypes[workType.id] === WorkTypeValueState.SINGLE || !self.settings.useMinMax && self.forcedStateWorkTypes[workType.id] !== WorkTypeValueState.MIN_MAX ? MinAvgMaxKeys.AVG : key;
                bVal = mergeWith(bVal, this.getBufferValueForWorkType(workType.id, workTypeKey, modifyByRate), mergeBufferFunc);
            });
            return bVal;
        }
    }))/** ================ HistoryManager init ================ */ .actions((self)=>({
        initHistoryManager () {
            self.historyManager.withoutUndo(()=>{
                self.maxHistoryLength = MAX_UNDO_HISTORY_SIZE;
            });
        }
    }))/** ================ Common actions ================ */ .actions((self)=>({
        addSection () {
            const workTypes = self.sections.length ? self.sections.slice(-1)[0]?.workTypes?.map((wT)=>wT.serializeWithoutAutomation()) || [] : self.workTypes.map((wT)=>cleanObject({
                    id: wT.id,
                    // invisible: !wT.inBreakdown,
                    resources: wT.resources?.map((r)=>({
                            id: r.id
                        }))
                }));
            const usedBreakdownColumns = self.sections.length ? self.sections.slice(-1)[0]?.usedBreakdownColumns?.map((c)=>getSnapshot(c)) || [] : DEFAULT_BREAKDOWN_COLUMNS;
            const id = generateSectionId();
            self.sections.push({
                id,
                lvl: 0,
                name: `Section ${self.sections.length + 1}`,
                type: TaskTypes.WORKLOAD,
                workTypes,
                usedBreakdownColumns,
                isOpened: true
            });
            if (!self.timelines) return;
            [
                self.timelines.min,
                self.timelines.avg,
                self.timelines.max
            ].forEach((timeline)=>{
                if (!timeline) return;
                timeline?.addSection(id, false, workTypes);
            });
        },
        resetSectionsVisibility () {
            self.sections.forEach((section)=>section.resetVisibility());
        },
        removeChild (id, moveModules = false) {
            let target;
            if (moveModules) {
                const index = self.sections.findIndex((section)=>section.id === id);
                const origin = self.sections[index];
                target = self.sections[index + (index === 0 ? 1 : -1)];
                if (target && origin?.children) target.pushChildren(origin.children);
            }
            const section = self.sections.find((section)=>section.id === id);
            if (section) {
                destroy(section);
                if (self.timelines) [
                    self.timelines.min,
                    self.timelines.avg,
                    self.timelines.max
                ].forEach((timeline)=>{
                    timeline?.removeSection(id);
                });
                this._internalUpdateSelectedWorkTypes?.();
            }
            return target;
        },
        duplicateSection (newSection, oldSectionId, position) {
            if (!self.sections) self.sections = cast([]);
            if (typeof position === "number" && position >= 0 && position < self.sections.length) {
                self.sections = cast([
                    ...self.sections.slice(0, position),
                    newSection,
                    ...self.sections.slice(position)
                ]);
            } else self.sections.push(newSection);
            if (!self.timelines) return;
            [
                self.timelines.min,
                self.timelines.avg,
                self.timelines.max
            ].forEach((timeline)=>{
                timeline?.duplicateSection(newSection.id, oldSectionId);
            });
        },
        addWorkType (useAutoFocus, data, rate, companyUnitCost) {
            const id = generateWorkTypeId();
            self.workTypes.push(cleanObject({
                id,
                systemId: data?.id,
                name: data?.name || "",
                utilizationOrder: self.workTypes.length,
                rate: rate || 0,
                companyUnitCost,
                color: data?.backgroundColor,
                timelineColor: data?.backgroundColor,
                timelineFontColor: data?.backgroundColor ? getContrastingFontColor(data.backgroundColor) : undefined,
                // inBreakdown: addedToSingleSection,
                // autoFocus: !addedToSingleSection && useAutoFocus,
                autoFocus: useAutoFocus
            }));
            return id;
        },
        addWorkTypeToSections (workTypeId, visibleInSectionId) {
            self.sections.forEach((section)=>{
                if (section.hasWorkType(workTypeId) && section.id !== visibleInSectionId) return;
                section.addWorkType(workTypeId, Boolean(visibleInSectionId && section.id !== visibleInSectionId));
            });
            this._internalUpdateRelatedWorkTypes?.(workTypeId);
        },
        removeWorkType (workTypeId, noSections) {
            if (!noSections) self.sections.forEach((section)=>section.removeWorkType(workTypeId));
            self.settings.buffers?.forEach((ov)=>ov.removeWorkType(workTypeId));
            const workType = self.workTypes.find((workType)=>workType.id === workTypeId);
            if (workType) {
                const lastUtilizationOrder = workType.utilizationOrder;
                destroy(workType);
                self.workTypes.forEach((workType)=>{
                    if (workType.utilizationOrder > lastUtilizationOrder) workType.setUtilizationOrder(workType.utilizationOrder - 1);
                });
            }
            this._internalUpdateRelatedWorkTypes?.(workTypeId);
        },
        setWorkTypeAutomation (workTypeId, percent, of, sectionIds) {
            self.sections.forEach((section)=>{
                const workType = section.workTypes.find((wT)=>wT.id === workTypeId);
                if (!workType) return;
                if (!sectionIds?.length || sectionIds.includes(section.id)) {
                    workType.setAutomation(percent, of);
                    section.resetPercentValues(workType.id, true, false);
                } else if (workType.percent) {
                    workType.setAutomation(0, undefined);
                    section.resetPercentValues(workType.id, true, false);
                }
            });
            this._internalUpdateRelatedWorkTypes?.(workTypeId);
        },
        _internalClearSummaryWorkTypeRounding (workTypeId) {
            const workType = self.workTypes.find((wT)=>wT.id === workTypeId);
            if (workType?.hasRounding) workType.setRounding(undefined, undefined);
            this._internalUpdateRelatedWorkTypes?.(workTypeId);
        },
        setWorkTypeRounding (workTypeId, roundType, roundInterval, roundLevel, sectionIds) {
            const workType = self.workTypes.find((wT)=>wT.id === workTypeId);
            if (roundLevel === BreakdownTableDepths.PROJECT) {
                workType?.setRounding(roundType, roundInterval);
            } else {
                workType?.setRounding();
                self.sections.forEach((section)=>{
                    const sectionWorkType = section.workTypes.find((wT)=>wT.id === workTypeId);
                    if (!sectionWorkType) return;
                    if (typeof roundLevel === "number" && roundLevel > BreakdownTableDepths.PROJECT && (!sectionIds?.length || sectionIds.includes(section.id))) {
                        sectionWorkType.setRounding(roundType, roundInterval, roundLevel);
                    } else {
                        sectionWorkType.setRounding(undefined, undefined, undefined);
                    }
                });
            }
            this._internalUpdateRelatedWorkTypes?.(workTypeId);
        },
        addSectionsResource (workTypeId, newResourceId, sectionId) {
            self.sections.forEach((section)=>{
                if (sectionId && section.id !== sectionId) return;
                section.workTypes.find((wT)=>wT.id === workTypeId)?.addResource(newResourceId);
            });
            if (!self.timelines) return;
            [
                self.timelines.min,
                self.timelines.avg,
                self.timelines.max
            ].forEach((timeline)=>{
                timeline?.sections.filter((section)=>!sectionId || section.id === sectionId).forEach((section)=>section.workTypes.find((workType)=>workType.id === workTypeId)?._internalAddTeamMember(newResourceId));
            });
        },
        _internalRemoveSectionResource (workTypeId, resourceId) {
            self.sections.forEach((section)=>{
                section.workTypes.find((wT)=>wT.id === workTypeId)?.removeResource(resourceId);
            });
            [
                self.timelines?.min,
                self.timelines?.avg,
                self.timelines?.max
            ].forEach((timeline)=>timeline?.sections.forEach((section)=>section.workTypes.find((workType)=>workType.id === workTypeId)?._internalRemoveTeamMember(resourceId)));
        },
        _internalAddTimelineTeamMember (sectionId, workTypeId, teamMemberId) {
            [
                self.timelines?.min,
                self.timelines?.avg,
                self.timelines?.max
            ].forEach((timeline)=>timeline?.sections.find((section)=>section.id === sectionId)?.workTypes.find((workType)=>workType.id === workTypeId)?._internalAddTeamMember(teamMemberId));
        },
        _internalRemoveTimelineTeamMember (sectionId, workTypeId, teamMemberId) {
            [
                self.timelines?.min,
                self.timelines?.avg,
                self.timelines?.max
            ].forEach((timeline)=>timeline?.sections.find((section)=>section.id === sectionId)?.workTypes.find((workType)=>workType.id === workTypeId)?._internalRemoveTeamMember(teamMemberId));
            const stillUsed = [
                self.timelines?.min,
                self.timelines?.avg,
                self.timelines?.max
            ].filter((timeline)=>Boolean(timeline)).some((timeline)=>timeline.sections.some((section)=>section.workTypes.some((workType)=>workType.teamMembers.some((teamMember)=>teamMember.id === teamMemberId))));
            if (!stillUsed) self.workTypes.find((workType)=>workType.id === workTypeId)?.removeResource(teamMemberId);
        },
        _internalRemoveAllTimelinesTeamMember (workTypeId, teamMemberId) {
            [
                self.timelines?.min,
                self.timelines?.avg,
                self.timelines?.max
            ].forEach((timeline)=>timeline?.sections.forEach((section)=>section.workTypes.find((workType)=>workType.id === workTypeId)?._internalRemoveTeamMember(teamMemberId)));
        },
        changeTimelineTeamMember (sectionId, workTypeId, teamMemberId, newTeamMemberId, newName, systemId, rate, companyUnitCost) {
            const timelines = [
                self.timelines?.min,
                self.timelines?.avg,
                self.timelines?.max
            ].filter((timeline)=>Boolean(timeline));
            const updateTimelineTeamMemberId = (newId)=>timelines.forEach((timeline)=>timeline.sections.find((section)=>section.id === sectionId)?.workTypes.find((workType)=>workType.id === workTypeId)?._internalChangeTeamMemberId(teamMemberId, newId));
            const updateSectionResource = (newTeamMemberId)=>self.sections.forEach((section)=>{
                    if (sectionId && section.id !== sectionId) return;
                    section.workTypes.find((wT)=>wT.id === workTypeId)?.removeResource(teamMemberId);
                    section.workTypes.find((wT)=>wT.id === workTypeId)?.addResource(newTeamMemberId);
                });
            const stillUsed = timelines.map((timeline)=>timeline.sections.flatMap((section)=>section.workTypes.flatMap((workType)=>workType.teamMembers.filter((teamMember)=>teamMember.id === teamMemberId))).length);
            const workType = self.workTypes.find((value)=>value.id === workTypeId);
            const resource = workType?.resources?.find((value)=>value.id === teamMemberId);
            if (newTeamMemberId) {
                updateTimelineTeamMemberId(newTeamMemberId);
                updateSectionResource(newTeamMemberId);
                if (Math.max(...stillUsed) <= 1) {
                    workType?.removeResource(teamMemberId, true);
                }
                return newTeamMemberId;
            }
            if (!workType) return newTeamMemberId ?? teamMemberId;
            if (Math.max(...stillUsed) > 1) {
                newTeamMemberId = generateResourceId();
                workType.addResource(false, newTeamMemberId, newName, systemId, rate ?? resource.rate, companyUnitCost ?? resource.companyUnitCost);
                updateTimelineTeamMemberId(newTeamMemberId);
                updateSectionResource(newTeamMemberId);
            } else if (newName && resource) {
                resource.changeName(newName, systemId);
            }
            return newTeamMemberId ?? teamMemberId;
        },
        generateTimeline () {
            const workloadKeys = self.settings.useMinMax ? [
                MinAvgMaxKeys.MIN,
                MinAvgMaxKeys.MAX
            ] : [
                MinAvgMaxKeys.AVG
            ];
            if (self.timelines && workloadKeys.every((workloadKey)=>self.timelines?.[workloadKey])) {
                return;
            }
            if (!self.timelines) self.timelines = cast({});
            const generatedTimelines = workloadKeys.filter((workloadKey)=>!self.timelines?.[workloadKey]).map((workloadKey)=>{
                const timeline = {
                    workloadKey,
                    sections: []
                };
                if (workloadKey === MinAvgMaxKeys.MIN) self.timelines.min = cast(timeline);
                if (workloadKey === MinAvgMaxKeys.AVG) self.timelines.avg = cast(timeline);
                if (workloadKey === MinAvgMaxKeys.MAX) self.timelines.max = cast(timeline);
                return workloadKey;
            });
            generatedTimelines.forEach((workloadKey)=>{
                const timeline = self.timelines?.[workloadKey];
                if (!timeline) return;
                self.sections.forEach((section)=>{
                    timeline.addSection(section.id);
                    const timelineSection = timeline.sections.find((value)=>value.id === section.id);
                    if (!timelineSection) return;
                    section.workTypes.forEach((sectionWorkType)=>{
                        timelineSection.addWorkType(sectionWorkType.id);
                        const timelineWorkType = timelineSection.workTypes.find((value)=>value.id === sectionWorkType.id);
                        if (!timelineWorkType) return;
                        const summaryWorkType = self.workTypes.find((value)=>value.id === sectionWorkType.id);
                        const resourceIds = summaryWorkType?.displayTimelineResources?.map((resource)=>String(resource.id)) || sectionWorkType.resources?.map((resource)=>resource.id) || [];
                        resourceIds.forEach((resourceId)=>timelineWorkType._internalAddTeamMember(resourceId));
                    });
                });
            });
            generatedTimelines.forEach((workloadKey)=>self.timelines?.[workloadKey]?.recalculateWorkload());
        },
        _internalUpdateTimelines (workTypeIds) {
            if (!self.timelines) return;
            const variants = [
                self.timelines.min,
                self.timelines.avg,
                self.timelines.max
            ].filter((timeline)=>Boolean(timeline));
            variants.forEach((timeline)=>{
                // self.sections.forEach((section) => {
                //   let timelineSection = timeline.sections.find(
                //     (value) => value.id === section.id,
                //   );
                //   if (!timelineSection) {
                //     timeline.addSection(section.id);
                //     timelineSection = timeline.sections.find(
                //       (value) => value.id === section.id,
                //     );
                //   }
                //   if (!timelineSection) return;
                //
                //   const sectionWorkTypeIds = section.workTypes.map(
                //     (workType) => workType.id,
                //   );
                //   timelineSection.workTypes
                //     .filter((workType) => !sectionWorkTypeIds.includes(workType.id))
                //     .forEach((workType) =>
                //       timelineSection!.removeWorkType(workType.id),
                //     );
                //
                //   section.workTypes.forEach((sectionWorkType) => {
                //     let timelineWorkType = timelineSection!.workTypes.find(
                //       (workType) => workType.id === sectionWorkType.id,
                //     );
                //     if (!timelineWorkType) {
                //       timelineSection!.addWorkType(sectionWorkType.id);
                //       timelineWorkType = timelineSection!.workTypes.find(
                //         (workType) => workType.id === sectionWorkType.id,
                //       );
                //     }
                //     if (!timelineWorkType) return;
                //
                //     const summaryWorkType = self.workTypes.find(
                //       (workType) => workType.id === sectionWorkType.id,
                //     );
                //     const resourceIds = new Set(
                //       summaryWorkType?.displayTimelineResources?.map((resource) =>
                //         String(resource.id),
                //       ) || [],
                //     );
                //     timelineWorkType.teamMembers
                //       .filter((teamMember) => !resourceIds.has(teamMember.id))
                //       .forEach((teamMember) =>
                //         timelineWorkType!.removeTeamMember(teamMember.id),
                //       );
                //     resourceIds.forEach((resourceId) => {
                //       if (
                //         !timelineWorkType!.teamMembers.some(
                //           (teamMember) => teamMember.id === resourceId,
                //         )
                //       ) {
                //         timelineWorkType!._internalAddTeamMember(resourceId);
                //       }
                //     });
                //   });
                // });
                // const sectionIds = new Set(self.sections.map((section) => section.id));
                // timeline.sections
                //   .filter((section) => !sectionIds.has(section.id))
                //   .forEach((section) => timeline.removeSection(section.id));
                timeline.recalculateWorkload(workTypeIds);
            });
        },
        _internalUpdateTimelineSection (sectionId, updates) {
            [
                self.timelines?.min,
                self.timelines?.avg,
                self.timelines?.max
            ].forEach((timeline)=>timeline?.sections.find((section)=>section.id === sectionId)?._internalUpdateProperties(updates));
        },
        _internalUpdateTimelineSectionWorkTypeId (sectionId, workTypeId, oldWorkTypeId, remove) {
            if (!self.timelines) return;
            [
                self.timelines.min,
                self.timelines.avg,
                self.timelines.max
            ].forEach((timeline)=>{
                const section = timeline?.sections?.find((section)=>section.id === sectionId);
                if (remove) section?.removeWorkType(workTypeId);
                else if (oldWorkTypeId) section?.updateWorkTypeId(oldWorkTypeId, workTypeId);
                else section?.addWorkType(workTypeId);
            });
        },
        _internalUpdateRelatedWorkTypes (workTypeId) {
            // console.log("UPDATE 1")
            const updatedWorkTypeIds = [];
            cascadedPercentWorkTypesSort(self.workTypes, "allPercentTargetsInSections", workTypeId).forEach((wT)=>{
                // if (wT.hasPercent) wT.resetPercentHours();
                self.sections?.forEach((section)=>{
                    if (section.hasCellAutomatedWorkType(wT.id)) section.updateAutomatedCellWorkload(wT.id);
                });
                wT._internalUpdateAllResourceHours();
                wT.calculateAverageRate();
                if (wT.id === workTypeId || wT.hasPercent || wT.allPercentTargetsInSections.length > 0) {
                    updatedWorkTypeIds.push(wT.id);
                }
            });
            this._internalUpdateTimelines?.(updatedWorkTypeIds);
            this._internalResetAutomatedFixedPriceValues();
        },
        _internalUpdateSelectedWorkTypes (workTypeIds) {
            // console.log("UPDATE 2")
            const updatedWorkTypeIds = [];
            cascadedPercentWorkTypesSort(self.workTypes, "allPercentTargetsInSections").forEach((wT)=>{
                // if (wT.hasPercent) wT.resetPercentHours();
                self.sections?.forEach((section)=>{
                    if (section.hasCellAutomatedWorkType(wT.id)) section.updateAutomatedCellWorkload(wT.id);
                });
                wT._internalUpdateAllResourceHours();
                wT.calculateAverageRate();
                if (!workTypeIds || workTypeIds.includes(wT.id) || wT.hasPercent || wT.allPercentTargetsInSections.length > 0) {
                    updatedWorkTypeIds.push(wT.id);
                }
            });
            this._internalUpdateTimelines?.(workTypeIds ? updatedWorkTypeIds : undefined);
            this._internalResetAutomatedFixedPriceValues();
        },
        _internalResetAutomatedFixedPriceValues () {
            self.sections?.forEach((section)=>{
                if (section.hasAutomatedFixedPrice) section.updateAutomatedFixedPrices();
            });
        },
        checkAndRemoveSectionWorkType (workTypeId) {
            for (const section of self.sections){
                if (section.workTypes.find((workType)=>workType.id === workTypeId && !workType.invisible)) return false;
            }
            this.removeWorkType(workTypeId, true);
            return true;
        },
        setWorkTypeVisibility (workTypeId, invisible, resetWorkTypeFixedValues = false) {
            if (resetWorkTypeFixedValues) self?.getWorkTypeById(workTypeId)?.resetFixedValues();
            self.sections.forEach((section)=>section.setWorkTypeVisibility(workTypeId, invisible));
        },
        reorderWorkTypes (reorderedWorkTypes, utilizationOrder = false) {
            if (utilizationOrder) {
                reorderedWorkTypes.forEach(({ id }, index)=>{
                    self.workTypes.find((workType)=>workType.id === id)?.setUtilizationOrder(index);
                });
                return;
            }
            self.workTypes = cast(updateSortedList(self.workTypes, reorderedWorkTypes));
        },
        reorderSections (reorderedSections) {
            self.sections = cast(updateSortedList(self.sections, reorderedSections));
        },
        addLabel ({ id, ...labelProps }) {
            if (!self.addedLabels) self.addedLabels = cast([]);
            const generatedId = generateLabelId();
            const newLabel = {
                id: generatedId,
                systemId: id,
                ...labelProps
            };
            self.addedLabels.push(newLabel);
            return generatedId;
        },
        updateLabel (systemId, labelData) {
            if (!self.addedLabels?.length) return;
            const labels = self.addedLabels;
            labels.find((l)=>l.systemId === systemId)?.update(labelData);
            self.addedLabels = labels;
        },
        removeLabel (systemId) {
            if (!self.addedLabels) return;
            const label = self.addedLabels.find((l)=>l.systemId === systemId);
            if (!label) return;
            destroy(label);
            if (!self.addedLabels?.length) self.addedLabels = undefined;
        },
        purgeLabel (systemId) {
            if (!self.addedLabels) return;
            this.removeLabel(systemId);
            self.sections.forEach((section)=>{
                section.purgeLabel(systemId);
            });
        },
        expandAllWorkTypes () {
            const state = !self.allWorkTypesExpanded;
            self.workTypes?.forEach((wT)=>wT.expandAllChildren(state));
        },
        setTotalCompanyProfit (profit, key) {
            const total = self.totalCompanyProfit[key] || 0;
            self.workTypes.forEach((wT)=>{
                const pr = wT.companyProfit[key];
                const v = total ? pr / total * profit : profit / self.workTypes.length;
                wT.setCompanyProfit(v, key);
            });
        },
        setTotalCompanyProfitability (profitability) {
            const { useMinMax } = self.settings;
            const key = useMinMax ? MinAvgMaxKeys.MIN : MinAvgMaxKeys.AVG;
            if (profitability >= 100) profitability = 99.99;
            const c = self.totalCompanyCost[key] || 0;
            this.setTotalCompanyProfit(c / (1 - profitability / 100) - c, key);
            return;
        },
        setDefaultCompanyFixedBreakdownCost () {
            if (self.hasBreakdownFixedPrice && typeof self.settings.companyFixedBreakdownCost !== "number") self.settings.setCompanyBreakdownFixedCost(((self.settings.useMinMax ? self.breakdownFixedPrice?.min : self.breakdownFixedPrice?.avg) || 0) / 2);
        },
        splitWorkTypeResources () {
            self.workTypes.forEach((wT)=>wT.splitResources());
        },
        alterElementHours () {
            self.sections.forEach((s)=>s.alterElementHours());
            this._internalUpdateSelectedWorkTypes();
        },
        resetAllPercentValues () {
            self.sections.forEach((s)=>s.resetAllPercentValues());
        },
        _internalResetPercentValues (percentWorkTypeId) {
            self.sections.forEach((section)=>{
                section.workTypes.forEach((workType)=>{
                    if (workType.hasPercent && workType.of?.includes(percentWorkTypeId)) section.resetPercentValues(workType.id);
                });
            });
        },
        setTextDescription (ops) {
            self.textDescription = !ops ? ops : cast(ops);
        },
        applyTextDescriptionDelta (delta) {
            self.historyManager.withoutUndo(()=>{
                if (!self.textDescription) {
                    self.historyManager.addDeltaPatch("/textDescription", delta, undefined);
                    self.textDescription = delta;
                } else {
                    let newD = self.textDescription.compose(delta);
                    newD.ops = newD.ops.filter((o)=>o.insert);
                    if (!newD.ops.length) newD = new Delta([
                        {
                            insert: "\n"
                        }
                    ]);
                    const inverted = delta.invert(self.textDescription);
                    self.historyManager.addDeltaPatch("/textDescription", delta, inverted);
                    self.textDescription = newD;
                }
            });
        },
        setExcludeTurnedOffElements (excludeTurnedOffElements) {
            self.excludeTurnedOffElements = excludeTurnedOffElements;
        },
        setHideEmptySectionDescriptions (hideEmptySectionDescriptions) {
            self.hideEmptySectionDescriptions = hideEmptySectionDescriptions;
        },
        setSearchLabels (labels) {
            self.historyManager.withoutUndo(()=>{
                self.searchLabels = cast(labels);
                this._internalSearchTreeBySavedPhrases();
            });
        },
        setTurnOffLabels (labels, isClient) {
            if (isClient) self.clientTurnOffLabels = cast(labels);
            else self.turnOffLabels = cast(labels);
            this._internalTurnOffTreeByLabels(isClient);
            this._internalUpdateSelectedWorkTypes();
        },
        resetTurnOffLabels (isClient) {
            if (isClient) destroy(self.clientTurnOffLabels);
            else destroy(self.turnOffLabels);
            this._internalTurnOffTreeByLabels(isClient);
            this._internalUpdateSelectedWorkTypes();
        },
        setExcludeSelectedLabels (excludeSelectedLabels, isClient) {
            if (isClient) self.clientExcludeSelectedLabels = excludeSelectedLabels;
            else self.excludeSelectedLabels = excludeSelectedLabels;
            this._internalTurnOffTreeByLabels(isClient);
            this._internalUpdateSelectedWorkTypes();
        },
        addSearchPhrase (phrase) {
            self.historyManager.withoutUndo(()=>{
                if (!self.searchPhrases) self.searchPhrases = cast([]);
                self.searchPhrases.push({
                    id: hashCode(phrase + new Date().getTime()).toString(),
                    value: phrase
                });
                this._internalSearchTreeBySavedPhrases();
            });
        },
        removeSearchPhrase (id) {
            if (!self.searchPhrases?.length) return;
            self.historyManager.withoutUndo(()=>{
                self.searchPhrases = cast(self.searchPhrases.filter((p)=>p.id !== id));
                this._internalSearchTreeBySavedPhrases();
            });
        },
        removeAllSearchPhrases () {
            if (!self.searchPhrases?.length) return;
            self.historyManager.withoutUndo(()=>{
                self.searchPhrases = cast([]);
                this._internalSearchTreeBySavedPhrases();
            });
        },
        _internalSearchTreeBySavedPhrases () {
            self.sections.forEach((section)=>{
                section._internalSearchTreeBySavedPhrases(self.searchPhrases, self.searchLabels, 0);
            });
        },
        _internalTurnOffTreeByLabels (isClient) {
            self.sections.forEach((section)=>{
                section._internalTurnOffTreeByLabels(self.displayTurnOffLabels, 0, self.displayExcludeSelectedLabels, isClient);
            });
        },
        updateLastOpenedElement (elementPath) {
            self.historyManager.withoutUndo(()=>{
                if (!elementPath) {
                    const openedSection = self.sections.find((s)=>s.isOpened);
                    if (openedSection) elementPath = getPath(openedSection);
                }
                self.lastOpenedElement = elementPath;
            });
        },
        updateLastAddedElement (elementPath) {
            self.historyManager.withoutUndo(()=>{
                self.lastAddedElement = elementPath;
            });
        },
        turnOffAllBuffers (turnOff) {
            self.settings.buffers?.forEach((ov)=>ov.setUseStateWithHistory(turnOff));
            self.sections.forEach((section)=>{
                section.turnOffAllBuffers(turnOff);
                section._internalResetBufferPercentValues(self.workTypes.map((wT)=>wT.id));
            });
            self.allBuffersOff = turnOff;
            this._internalUpdateSelectedWorkTypes();
        },
        setTurnOffFixedPriceState (state) {
            if (!state && !self.settings.turnOffFixedPrice) {
                // means all breakdown FP are off
                self.sections.forEach((section)=>section?._internalTurnOffFixedPriceElements(state));
            }
            self.settings.setTurnOffFixedPrice(state);
        },
        setTurnOffMaterialsState (state) {
            if (!state && !self.settings.turnOffMaterials) {
                // means all breakdown Materials are off
                self.sections.forEach((section)=>section?._internalTurnOffMaterialsElements(state));
            }
            self.settings.setTurnOffMaterials(state);
        },
        convertExistingRatesToCurrency (rates, currencyFrom, currencyTo) {
            const { roundPrice, modifier } = self.settings;
            const rounding = roundPrice ? Rounding.FULL : Rounding.DECIMALS;
            const r = getExchangeRate(rates, currencyTo, currencyFrom);
            self.workTypes.forEach((workType)=>{
                if (workType.resources?.length) {
                    workType.resources?.forEach((resource)=>{
                        if (resource.rate) resource.setRate(safeRoundRate(resource.rate * r, modifier, rounding));
                    });
                    workType.calculateAverageRate();
                    return;
                }
                if (workType.rate) workType.setRate(safeRoundRate(workType.rate * r, modifier, rounding));
            });
        }
    }))/** ================ one-time updates ================ */ .actions((self)=>({
        oneTimeReplaceCompanyUnitCost () {
            if (self.usesNewPA) return;
            self.workTypes.forEach((workType)=>{
                workType.oneTimeReplaceCompanyUnitCost();
                workType.resources?.forEach((resource)=>{
                    resource.oneTimeReplaceCompanyUnitCost();
                });
            });
            self.usesNewPA = true;
        },
        setWorkTypeIdsFromDB (updateList) {
            self.workTypes.forEach((workType)=>{
                const systemId = updateList.find((u)=>u.oldId === workType.id)?.newId;
                if (systemId) workType.setSystemId(systemId);
            });
        }
    }))/** ================ Deserializer ================ */ .actions((self)=>({
        deserialize (structure) {
            if (structure.settings) {
                applySnapshot(self.settings, {
                    ...getSnapshot(self.settings),
                    ...structure.settings
                });
            }
            if (structure.workTypes) {
                detach(self.workTypes);
                applySnapshot(self.workTypes, structure.workTypes.map((wT)=>wT));
            }
            if (structure.sections) {
                detach(self.sections);
                applySnapshot(self.sections, structure.sections);
            }
            if (structure.timelines) {
                if (self.timelines) {
                    applySnapshot(self.timelines, structure.timelines);
                } else {
                    self.timelines = structure.timelines;
                }
            }
        }
    }))/** ================ Breakdown Column Actions ================ */ .actions((self)=>({
        setBreakdownColumnVisibility (columnId, state) {
            self.sections.forEach((section)=>section.usedBreakdownColumns.find((c)=>c.id === columnId)?.setVisibility(state));
        },
        setBreakdownColumnWidth (columnId, width) {
            self.sections.forEach((section)=>section.usedBreakdownColumns.find((c)=>c.id === columnId)?.setWidth(width));
        },
        setBreakdownColumnDisplayName (columnId, displayName) {
            self.sections.forEach((section)=>section.usedBreakdownColumns.find((c)=>c.id === columnId)?.setDisplayName(displayName));
        },
        addDescriptionColumn (sectionId) {
            const lastColumnIndex = self.usedBreakdownColumns.filter((column)=>typeof column.descriptionIndex === "number").reverse()[0].descriptionIndex;
            const position = self.usedBreakdownColumns.findIndex((c)=>c.descriptionIndex === lastColumnIndex) + 1;
            const descriptionId = generateTaskDescriptionId(lastColumnIndex + 1);
            const displayName = `Description ${lastColumnIndex + 2}`;
            self.sections.forEach((section)=>section.addDescriptionColumn(position, {
                    descriptionId,
                    ...DEFAULT_DESCRIPTION_COLUMN,
                    visible: Boolean(!sectionId || sectionId === section.id),
                    displayName
                }));
        },
        acceptNewDescriptionColumn (skipSectionId, columnData, position, visible) {
            self.sections.forEach((section)=>{
                if (section.id === skipSectionId) return;
                section.addDescriptionColumn(position, {
                    ...columnData,
                    visible
                });
            });
        },
        pinDescriptionColumn (columnId, state, pinRight = false) {
            self.sections.forEach((section)=>section.pinDescriptionColumn(columnId, state, pinRight));
        },
        removeDescriptionColumn (columnId) {
            self.sections.forEach((section)=>section.removeDescriptionColumn(columnId));
        }
    }))/** ================ PriceModifier actions ================ */ .actions((self)=>({
        addPriceModifier (newPriceModifier) {
            if (!self.priceModifiers) {
                self.priceModifiers = cast([
                    newPriceModifier
                ]);
                return;
            }
            self.priceModifiers.push(newPriceModifier);
        },
        removePriceModifier (priceModifierId) {
            if (!self.priceModifiers?.length) return;
            const ov = self.priceModifiers.find((ov)=>ov.id === priceModifierId);
            if (ov) {
                destroy(ov);
            }
            if (!self.priceModifiers.length) self.priceModifiers = undefined;
        },
        reorderPriceModifiers (list) {
            self.priceModifiers = cast(updateSortedList(self.priceModifiers, list, "id", true));
        }
    }))/** ================ Reactions ================ */ .actions((self)=>({
        afterCreate () {
            self.setDefaultCompanyFixedBreakdownCost();
            self.updateLastOpenedElement();
            const fixedBreakdownPriceDisposer = reaction(// @todo: use as action
            ()=>self.breakdownFixedPrice, ()=>{
                if (!isAlive(self)) return;
                self.setDefaultCompanyFixedBreakdownCost();
            });
            addDisposer(self, fixedBreakdownPriceDisposer);
        },
        attachSection (data) {
            self.sections.push(cast(data));
        }
    }));

const blankStructureGenerator = ({ workTypes, defaultCurrency, defaultCompanyCurrency, defaultLevelOfDetail, defaultTimeEquivalents, defaultTimeModifier, defaultPriceFormat, defaultSectionFormat, defaultMinMax = false, noSections = false, noPrices = false })=>{
    const mtaValue = defaultTimeEquivalents && Number(defaultTimeEquivalents.day) * Number(defaultTimeEquivalents.month);
    const viewLevel = defaultLevelOfDetail ?? DEFAULT_PROJECT_SETTINGS.viewLevel;
    const valueLevel = defaultLevelOfDetail ?? DEFAULT_PROJECT_SETTINGS.valueLevel;
    const structureWorkTypes = workTypes.map((workType, index)=>cleanObject({
            id: generateWorkTypeId(index),
            systemId: workType.id,
            utilizationOrder: index,
            name: workType.name,
            color: workType.backgroundColor,
            rate: workType.salesRate ? workType.salesRate / MaxTimeModifiers.HOUR : 0,
            companyUnitCost: workType.costRate ? workType.costRate / MaxTimeModifiers.HOUR : undefined
        }));
    return {
        sVersion: CURRENT_STRUCTURE_VERSION,
        sBuild: CURRENT_STRUCTURE_BUILD,
        sOriginBuild: CURRENT_STRUCTURE_BUILD,
        settings: {
            ...DEFAULT_PROJECT_SETTINGS,
            buffers: DEFAULT_BUFFERS.map((b)=>({
                    ...b,
                    workTypes: structureWorkTypes.map((wT)=>wT.id)
                })),
            currency: defaultCurrency || DEFAULT_PROJECT_SETTINGS.currency,
            companyCurrency: defaultCompanyCurrency || DEFAULT_PROJECT_SETTINGS.companyCurrency,
            sectionFormat: defaultSectionFormat || DEFAULT_PROJECT_SETTINGS.sectionFormat,
            priceFormat: defaultPriceFormat || DEFAULT_PROJECT_SETTINGS.priceFormat,
            timeModifier: typeof defaultTimeModifier === "number" ? defaultTimeModifier : DEFAULT_PROJECT_SETTINGS.timeModifier,
            useMinMax: defaultMinMax || DEFAULT_PROJECT_SETTINGS.useMinMax,
            dayModifier: Number(defaultTimeEquivalents?.day || DEFAULT_PROJECT_SETTINGS.dayModifier),
            mta: {
                min: mtaValue,
                avg: mtaValue,
                max: mtaValue
            },
            viewLevel,
            valueLevel
        },
        visibility: {
            viewLevel,
            valueLevel
        },
        workTypes: structureWorkTypes,
        sections: noSections ? [] : [
            {
                id: generateSectionId(),
                type: TaskTypes.WORKLOAD,
                lvl: BreakdownTableDepths.SECTION,
                isOpened: true,
                name: "Section 1",
                workTypes: structureWorkTypes.map((workType)=>({
                        id: workType.id
                    })),
                usedBreakdownColumns: DEFAULT_BREAKDOWN_COLUMNS.map((column)=>{
                    const c = {
                        ...column
                    };
                    if (c.name === BreakdownColumns.PRICE && noPrices) c.visible = false;
                    return c;
                })
            }
        ]
    };
};

function structureCreator(props, zipResult) {
    const { zippedTemplateStructure, workTypes, applyDefaultsToTemplate, defaultCurrency, defaultCompanyCurrency, defaultLevelOfDetail, defaultTimeEquivalents, defaultTimeModifier, defaultPriceFormat, preserveTimeModifiers = false, disableTimeline = false, noSections } = props;
    let structure;
    /** =============== Unzip template structure or create blank =============== */ if (typeof zippedTemplateStructure === "string" && zippedTemplateStructure.length) {
        const unzipped = unzip(zippedTemplateStructure);
        structure = JSON.parse(unzipped);
    } else return blankStructureGenerator({
        workTypes,
        defaultCurrency,
        defaultCompanyCurrency,
        defaultLevelOfDetail,
        defaultTimeEquivalents,
        defaultTimeModifier,
        defaultPriceFormat,
        noSections
    });
    if (!structure) throw "Creator was unable to use provided template/xls structure";
    const updateSectionBufferAndValues = (element, availableWorkTypes)=>{
        if (element.buffers) {
            element.buffers = element.buffers.map((b)=>({
                    ...b,
                    workTypes: intersection(b.workTypes, availableWorkTypes)
                }));
        }
        if (element.values) {
            element.values = pick(element.values, ...availableWorkTypes);
        }
        if ("children" in element) {
            element.children?.forEach((child)=>{
                updateSectionBufferAndValues(child, availableWorkTypes);
            });
        }
    };
    /** =============== Apply new used work types =============== */ const newWorkTypeIds = workTypes.map((w)=>w.id);
    const structureWorkTypeIds = structure.workTypes?.map((w)=>w.systemId) || [];
    const newWorkTypes = workTypes.filter((workType)=>!structureWorkTypeIds.includes(workType.id)).map((workType, index)=>({
            id: generateWorkTypeId(index),
            systemId: workType.id,
            utilizationOrder: (structure.workTypes?.length || 0) + index,
            name: workType.name,
            color: workType.backgroundColor,
            rate: workType.salesRate ? workType.salesRate / MaxTimeModifiers.HOUR : 0,
            companyUnitCost: workType.costRate ? workType.costRate / MaxTimeModifiers.HOUR : undefined
        })) || [];
    structure.workTypes = [
        ...structure.workTypes?.filter((workType)=>workType.systemId && newWorkTypeIds.includes(workType.systemId)) || [],
        ...newWorkTypes
    ];
    structure.workTypes.forEach((workType, workTypeIndex)=>{
        workType.utilizationOrder = workTypeIndex;
        workType.resources?.forEach((resource, resourceIndex)=>{
            resource.utilizationOrder = resourceIndex;
        });
    });
    structure.sections?.forEach((section)=>{
        section.workTypes = [
            ...section.workTypes.filter((wT)=>structure.workTypes.find((pWT)=>pWT.id === wT.id)),
            ...newWorkTypes.map((wT)=>({
                    id: wT.id
                }))
        ];
        section.usedBreakdownColumns.forEach((column)=>{
            if (column.applyPin && !(column.name === BreakdownColumns.TITLE || column.name === BreakdownColumns.PRICE)) column.applyPin = false;
        });
        updateSectionBufferAndValues(section, section.workTypes.map((wT)=>wT.id));
    });
    const postUpdateStructureWorkTypeIds = structure.workTypes.map((wT)=>wT.id);
    if (structure.settings.buffers) structure.settings.buffers = structure.settings.buffers.map((b)=>({
            ...b,
            workTypes: intersection(b.workTypes, postUpdateStructureWorkTypeIds)
        }));
    /** =============== Apply defaults =============== */ if (defaultCompanyCurrency) structure.settings.companyCurrency = defaultCompanyCurrency;
    if (defaultTimeEquivalents) structure.settings.dayModifier = Number(defaultTimeEquivalents?.day);
    if (workTypes) structure.workTypes.forEach((wT)=>{
        const workType = workTypes?.find((r)=>r.id === wT.systemId);
        const rate = workType?.salesRate;
        const companyUnitCost = workType?.costRate;
        if (rate && applyDefaultsToTemplate) wT.rate = rate / MaxTimeModifiers.HOUR;
        if (companyUnitCost) wT.companyUnitCost = companyUnitCost / MaxTimeModifiers.HOUR;
        else delete wT.companyUnitCost;
        wT.resources?.forEach((resource)=>{
            const role = workType?.roles?.find((r)=>r.id === resource.systemId);
            const rate = role?.salesRate;
            const companyUnitCost = role?.costRate;
            if (rate && applyDefaultsToTemplate) resource.rate = rate / MaxTimeModifiers.HOUR;
            if (companyUnitCost) resource.companyUnitCost = companyUnitCost / MaxTimeModifiers.HOUR;
            else delete resource.companyUnitCost;
        });
    });
    /** =============== Apply optional defaults =============== */ if (applyDefaultsToTemplate) {
        if (defaultCurrency) structure.settings.currency = defaultCurrency;
        if (defaultLevelOfDetail) {
            structure.settings.viewLevel = defaultLevelOfDetail;
            structure.settings.valueLevel = defaultLevelOfDetail;
        }
        if (defaultTimeEquivalents) {
            const mtaValue = Number(defaultTimeEquivalents.day) * Number(defaultTimeEquivalents.month);
            structure.settings.mta = {
                min: mtaValue,
                avg: mtaValue,
                max: mtaValue
            };
        }
        if (!preserveTimeModifiers && typeof defaultTimeModifier === "number") structure.settings.timeModifier = defaultTimeModifier;
        if (typeof defaultPriceFormat === "number") structure.settings.priceFormat = defaultPriceFormat;
        if (!structure.settings.buffers?.length) structure.settings.buffers = DEFAULT_BUFFERS.map((b)=>({
                ...b,
                workTypes: postUpdateStructureWorkTypeIds
            }));
    }
    if (disableTimeline) {
        const timelineIndex = structure.settings.usedTables.findIndex((t)=>t.name === Tables.TIMELINE);
        if (structure.settings.usedTables[timelineIndex].visible) structure.settings.usedTables[timelineIndex].visible = false;
    }
    /** =============== Perform cleanup of template structure =============== */ delete structure.settings.dailyExchangeRate;
    delete structure.settings.savedDailyRate;
    delete structure.settings.exchangeRateTimestamp;
    delete structure.settings.customExchangeRate;
    delete structure.settings.customExchangeRateCurrencies;
    delete structure.settings.useCustomExchangeRate;
    return zipResult ? zip(JSON.stringify(structure)) : structure;
}

export { AVG_PROPOSAL_VALUES_LOCK_BUILD, BufferModel, COMMENT_PATCH_PATHS, CURRENT_STRUCTURE_BUILD, CURRENT_STRUCTURE_VERSION, CommentGroupModel, CommentRootModel, DEFAULT_BREAKDOWN_COLUMNS, DEFAULT_BUFFERS, DEFAULT_DESCRIPTION_COLUMN, DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER, DEFAULT_MIN_AVG_MAX_VALUE, DEFAULT_PROJECT_SETTINGS, DEFAULT_ROOT_OBJECT, DEFAULT_TEXT_DESCRIPTION, DEFAULT_TIMELINE_COLOR, DEFAULT_TIMELINE_FONT_COLOR, ELEMENT_VISIBILITY_PATCH_PATHS, EMPTY_MIN_AVG_MAX_VALUE, FC_PREFIX, FILTERED_PATCH_PATHS, FixedPriceAutomationModel, HARD_ROUNDING_LOCK_BUILD, MAT_PREFIX, MinAvgFlagModel, MinAvgMaxFlagModel, MinAvgMaxValueExtendedModel, MinAvgMaxValueModel, PERCENT_UPDATE_BUILD, PriceModifierModel, REMOVE_ACTIONS, RISK_BUFFER_LOCK_BUILD, SectionModel, SectionWorkTypeModel, SectionWorkTypeResourceModel, SettingsModel, StructureModel, TIMELINE_MAX_MONTHS, TaskLabelModel, TaskModel, TimelineDailyValueModel, TimelineExpandedMonthModel, TimelineGroupModel, TimelineModel, TimelineSectionModel, TimelineTeamMemberModel, TimelineWorkTypeModel, VALUE_UPDATE_BUILD, VisibleBreakdownColumnModel, VisibleProjectTableModel, VisibleProposalTotalModel, VisibleSummaryColumnModel, WEEK_TIME_LOCK_BUILD, WorkTypeModel, WorkTypeResourceModel, WorkloadAutomationModel, addBilling, addWorkDays, applyCommentStorePatch, applyPatchExtended, blankStructureGenerator, buffersInTaskValuesMap, cascadedPercentWorkTypesSort, countWorkdaysInInterval, createBillingMonth, differenceInWorkDays, differentObjectValues, generateBufferId, generateEntryId, generateLabelId, generatePriceModifierId, generateResourceId, generateSectionId, generateTaskDescriptionId, generateTimelineCalendar, generateWorkTypeId, getChunkedDistribution, getDateKey, getDistributionPeriodIndices, getDuplicateIndex, getDuplicateName, getExpansionDateKey, getFirstWorkdayOfMonth, getFirstWorkdayOfWeek, getFixedCostPath, getLastWorkdayOfMonth, getLastWorkdayOfWeek, getMaterialsPath, getMinMaxRange, getNewDistributionLength, getPriceModifiersPath, getStructureMetadata, getTimelineEndDate, getTimelineRoot, getTotalsTreePath, getWorkTypeTreePath, hardRoundingInTaskValuesMap, hasOlderOriginBuild, hoursRoundedSplit, incrementedUsedName, isBetween, isFixedCostTreePath, isTotalsTreePath, isWorkTypeTreePath, lastNameUsageIndex, mergeBufferFunc, mergeChunkedDistributions, mergeDistributions, modifyNewValuesWithCoefficients, nextWorkDay, objectHasValue, parseObject, parseWorkloadValues, pickHigherBTTaskValue, pickHigherTaskValue, pickNonAutomatedTaskValuesMap, prevWorkDay, redistributeWorkload, resolveTimelineColor, roundedHoursSplit, roundingInTaskValuesMap, serializeMapWithObjects, serializeMapWithObjectsByKey, shiftTimelineStartDate, structureCreator, structureMigrator, sumArrayData, sumBTData, sumObjectsData, sumTaskChildrenData, sumTaskChildrenDataV2, sumTaskValues, transferDistributionWorkload, updateSortedList, validatePositiveFinite, validateTimelineEndDate, validateValue, valueRoundedSplit, wTExistsDeep, weightedHoursRoundedSplit, weightedValueRoundedSplit, workDaysInMonth };
